import * as i1$1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { InjectionToken, inject, ChangeDetectorRef, ElementRef, ChangeDetectionStrategy, Component, HostListener, Input, HostBinding, Renderer2, EventEmitter, Directive, Output, Self, Optional, ViewContainerRef, forwardRef, TemplateRef, ContentChildren, ViewChild, Inject, ContentChild, DestroyRef, Injectable, ViewChildren, NgModule } from '@angular/core';
import * as i1 from '@angular/forms';
import { Validators, NgControl, FormControl, FormArray, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import * as i2 from '@tc/interstellar-icon';
import { StlrIconModule } from '@tc/interstellar-icon';
import { ActiveDescendantKeyManager, FocusMonitor } from '@angular/cdk/a11y';
import { BehaviorSubject, Subject, takeUntil, Observable, first, merge, filter, of, take, map, timer, ReplaySubject, combineLatest, startWith } from 'rxjs';
import { generateUUID, isEqual, flattenArray } from '@tc/foundation/utilities';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { __decorate, __metadata } from 'tslib';
import * as i1$2 from '@tc/interstellar-flex-layout';
import { StlrFlexLayoutModule } from '@tc/interstellar-flex-layout';

const STLR_OPTION_PARENT_COMPONENT = new InjectionToken('STLR_OPTION_PARENT_COMPONENT');

const _c0$f = ["stlr-option", ""];
const _c1$9 = ["*"];
class StlrOptionComponent {
    constructor() {
        this._changeDetectorRef = inject(ChangeDetectorRef);
        this._elementRef = inject(ElementRef);
        this._parent = inject(STLR_OPTION_PARENT_COMPONENT, { optional: true });
        this._activeSubject = new BehaviorSubject(false);
        this.active$ = this._activeSubject.asObservable();
        this._destroy$ = new Subject();
        this._value = null;
        this._hostClass = true;
    }
    get _selectedAttribute() {
        return this._parent?.value === this.value ? '' : null;
    }
    get _getAriaSelectedAttribute() {
        return this._parent?.value === this.value ? true : null;
    }
    get active() {
        return this._parent?.activeOption?.value === this.value;
    }
    get value() {
        return this._value;
    }
    set value(value) {
        this._value = value;
    }
    onClick() {
        if (this._parent) {
            this._parent.handleChange(this.value);
            if (this._parent.setFocus)
                this._parent.setFocus();
        }
    }
    get displayText() {
        return this._elementRef.nativeElement.innerText.trim();
    }
    ngAfterContentInit() {
        this.active$.pipe(takeUntil(this._destroy$)).subscribe(() => {
            this._changeDetectorRef.markForCheck();
        });
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
    // Required for ActiveKeyDescendantKeyManager
    getLabel() {
        return this.displayText;
    }
    focus() {
        this._elementRef.nativeElement.focus();
    }
    setActiveStyles() {
        this._activeSubject.next(true);
    }
    setInactiveStyles() {
        this._activeSubject.next(false);
    }
    static { this.ɵfac = function StlrOptionComponent_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || StlrOptionComponent)(); }; }
    static { this.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: StlrOptionComponent, selectors: [["option", "stlr-option", ""], ["option", "stlrOption", ""]], hostVars: 6, hostBindings: function StlrOptionComponent_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("click", function StlrOptionComponent_click_HostBindingHandler() { return ctx.onClick(); });
        } if (rf & 2) {
            i0.ɵɵattribute("selected", ctx._selectedAttribute)("aria-selected", ctx._getAriaSelectedAttribute);
            i0.ɵɵclassProp("stlr-option", ctx._hostClass)("stlr-option--active", ctx.active);
        } }, inputs: { value: "value" }, standalone: false, attrs: _c0$f, ngContentSelectors: _c1$9, decls: 1, vars: 0, template: function StlrOptionComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵprojection(0);
        } }, encapsulation: 2, changeDetection: 0 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrOptionComponent, [{
        type: Component,
        args: [{ selector: 'option[stlr-option], option[stlrOption]', changeDetection: ChangeDetectionStrategy.OnPush, standalone: false, template: "<ng-content></ng-content>\n" }]
    }], null, { _hostClass: [{
            type: HostBinding,
            args: ['class.stlr-option']
        }], _selectedAttribute: [{
            type: HostBinding,
            args: ['attr.selected']
        }], _getAriaSelectedAttribute: [{
            type: HostBinding,
            args: ['attr.aria-selected']
        }], active: [{
            type: HostBinding,
            args: ['class.stlr-option--active']
        }], value: [{
            type: Input
        }], onClick: [{
            type: HostListener,
            args: ['click']
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(StlrOptionComponent, { className: "StlrOptionComponent", filePath: "lib/option/option.component.ts", lineNumber: 25 }); })();

const OPTION_API = [StlrOptionComponent];

const STLR_ERROR = new InjectionToken('STLR_ERROR');
const STLR_FORM_FIELD = new InjectionToken('STLR_FORM_FIELD');
const STLR_FORM_COMPONENT_BASE = new InjectionToken('STLR_FORM_COMPONENT_BASE');

/* eslint-disable @angular-eslint/no-input-rename */
class StlrFormComponentBase {
    constructor() {
        this.changeDetectorRef = inject(ChangeDetectorRef);
        this.renderer = inject(Renderer2);
        this.changed = new EventEmitter();
        this._name = null;
        this.ariaDescribedBy = null;
        this.ariaLabel = null;
        this.ariaLabelledBy = null;
        this._disabled = false;
        this.enterKeyHint = null;
        this._invalid = false;
        this.placeholder = null;
        this._required = false;
        this.tabindex = null;
        this._value = null;
    }
    set name(name) {
        this._name = name;
    }
    get name() {
        return this._name;
    }
    set disabled(isDisabled) {
        this._disabled = coerceBooleanProperty(isDisabled);
        this.changeDetectorRef.markForCheck();
    }
    get disabled() {
        return this._disabled;
    }
    set invalid(isInvalid) {
        this._invalid = coerceBooleanProperty(isInvalid);
        this.changeDetectorRef.markForCheck();
    }
    get invalid() {
        return this._invalid;
    }
    set required(isRequired) {
        this._required = coerceBooleanProperty(isRequired);
        this.changeDetectorRef.markForCheck();
    }
    get required() {
        return this._required;
    }
    get value() {
        return this._value;
    }
    set value(value) {
        this._value = value ?? null;
        this.changeDetectorRef.markForCheck();
    }
    ngOnInit() {
        this.id = `${this.controlType}-${generateUUID(2)}`;
    }
    static { this.ɵfac = function StlrFormComponentBase_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || StlrFormComponentBase)(); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: StlrFormComponentBase, hostVars: 6, hostBindings: function StlrFormComponentBase_HostBindings(rf, ctx) { if (rf & 2) {
            i0.ɵɵclassProp("disabled", ctx.disabled)("invalid", ctx.invalid)("required", ctx.required);
        } }, inputs: { name: "name", ariaDescribedBy: [0, "aria-describedby", "ariaDescribedBy"], ariaLabel: [0, "aria-label", "ariaLabel"], ariaLabelledBy: [0, "aria-labelledby", "ariaLabelledBy"], disabled: "disabled", enterKeyHint: "enterKeyHint", id: "id", invalid: "invalid", placeholder: "placeholder", required: "required", tabindex: "tabindex", value: "value" }, outputs: { changed: "changed" } }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrFormComponentBase, [{
        type: Directive
    }], null, { changed: [{
            type: Output
        }], name: [{
            type: Input
        }], ariaDescribedBy: [{
            type: Input,
            args: ['aria-describedby']
        }], ariaLabel: [{
            type: Input,
            args: ['aria-label']
        }], ariaLabelledBy: [{
            type: Input,
            args: ['aria-labelledby']
        }], disabled: [{
            type: Input
        }, {
            type: HostBinding,
            args: ['class.disabled']
        }], enterKeyHint: [{
            type: Input
        }], id: [{
            type: Input
        }], invalid: [{
            type: Input
        }, {
            type: HostBinding,
            args: ['class.invalid']
        }], placeholder: [{
            type: Input
        }], required: [{
            type: Input
        }, {
            type: HostBinding,
            args: ['class.required']
        }], tabindex: [{
            type: Input
        }], value: [{
            type: Input
        }] }); })();

class StlrFormComponentValueAccessor extends StlrFormComponentBase {
    set disabled(isDisabled) {
        super.disabled = isDisabled;
        this._setControlDisabled();
    }
    get disabled() {
        return this.ngControl?.control?.disabled ?? super.disabled;
    }
    set invalid(isInvalid) {
        super.invalid = isInvalid;
    }
    get invalid() {
        return this.ngControl?.control?.invalid ?? super.invalid;
    }
    set required(isRequired) {
        super.required = isRequired;
        this._setControlRequired();
    }
    get required() {
        return this.ngControl?.control?.hasValidator(Validators.required) ?? super.required;
    }
    constructor(ngControl) {
        super();
        this.ngControl = ngControl;
        this.destroy$ = new Subject();
        this._onChange = () => { };
        this._onTouched = () => { };
        if (ngControl)
            ngControl.valueAccessor = this;
    }
    ngOnInit() {
        super.ngOnInit();
        this._initStatusSubscription();
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
    registerOnChange(fn) {
        this._onChange = fn;
    }
    registerOnTouched(fn) {
        this._onTouched = fn;
    }
    setDisabledState(isDisabled) {
        super.disabled = isDisabled;
    }
    /**
     * Applies the value from the FormControl, to this component, e.g. control.setValue or the control's initial-value.
     * This also converts empty objects to null so Validators.required is accurately calculated.
     * @param value The value from the FormControl.
     */
    writeValue(value) {
        this.value = value && typeof value === 'object' && Object.keys(value).length === 0 ? null : value;
    }
    _setControlDisabled() {
        const control = this.ngControl?.control;
        if (control) {
            if (!control.disabled && super.disabled) {
                control.disable();
            }
            else if (control.disabled && !super.disabled) {
                control.enable();
            }
        }
    }
    _setControlRequired() {
        const control = this.ngControl?.control;
        if (control) {
            if (super.required && !control.hasValidator(Validators.required)) {
                control.addValidators(Validators.required);
                control.updateValueAndValidity();
            }
            else if (!super.required && control.hasValidator(Validators.required)) {
                control.removeValidators(Validators.required);
                control.updateValueAndValidity();
            }
        }
    }
    /**
     * This is necessary for subtree components, e.g. AutoBubbler->AutoComplete->InputElement bindings.
     */
    _initStatusSubscription() {
        this.ngControl?.control?.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.changeDetectorRef.markForCheck();
        });
    }
    static { this.ɵfac = function StlrFormComponentValueAccessor_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || StlrFormComponentValueAccessor)(i0.ɵɵdirectiveInject(i1.NgControl, 10)); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: StlrFormComponentValueAccessor, features: [i0.ɵɵInheritDefinitionFeature] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrFormComponentValueAccessor, [{
        type: Directive
    }], () => [{ type: i1.NgControl, decorators: [{
                type: Self
            }, {
                type: Optional
            }] }], null); })();

const _c0$e = ["input"];
const _c1$8 = ["panelTemplate"];
const _c2 = ["panel"];
const _c3 = ["*"];
function StlrAutocompleteComponent_ng_template_2_Template(rf, ctx) { if (rf & 1) {
    const _r2 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 6, 4);
    i0.ɵɵlistener("focusout", function StlrAutocompleteComponent_ng_template_2_Template_div_focusout_0_listener($event) { i0.ɵɵrestoreView(_r2); const ctx_r2 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r2.handlePanelBlur($event)); });
    i0.ɵɵelementContainer(2, 7);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    const rawContent_r4 = i0.ɵɵreference(5);
    const filteredContent_r5 = i0.ɵɵreference(7);
    i0.ɵɵproperty("tabindex", ctx_r2.tabindex ? ctx_r2.tabindex + 1 : null);
    i0.ɵɵattribute("aria-label", ctx_r2.ariaLabel)("aria-labelledby", ctx_r2.ariaLabelledBy)("aria-owns", ctx_r2.id);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r2.autoFilter ? filteredContent_r5 : rawContent_r4);
} }
function StlrAutocompleteComponent_ng_template_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵprojection(0);
} }
function StlrAutocompleteComponent_ng_template_6_For_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "option", 8);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const option_r6 = ctx.$implicit;
    i0.ɵɵproperty("value", option_r6.value);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", option_r6.displayText, " ");
} }
function StlrAutocompleteComponent_ng_template_6_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵrepeaterCreate(0, StlrAutocompleteComponent_ng_template_6_For_1_Template, 2, 2, "option", 8, i0.ɵɵrepeaterTrackByIdentity);
    i0.ɵɵpipe(2, "async");
} if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵrepeater(i0.ɵɵpipeBind1(2, 0, ctx_r2.filteredOptions$));
} }
const MAX_PANEL_HEIGHT = 256;
class StlrAutocompleteComponent extends StlrFormComponentValueAccessor {
    constructor() {
        super(...arguments);
        this.controlType = 'stlr-autocomplete';
        this._overlay = inject(Overlay);
        this._viewContainerRef = inject(ViewContainerRef);
        this._overlayRef = null;
        this._isInitialized = false;
        this._isFocused = false;
        this._closePanelSubject = new Subject();
        this._filteredOptionsSubject = new BehaviorSubject([]);
        this.filteredOptions$ = this._filteredOptionsSubject.asObservable();
        /** Called by the StlrOptionComponent to set the focus back on the input element after an option is selected. */
        this.setFocus = () => this.inputRef.nativeElement.focus();
        /**
         * A browser hint for auto-filling form data.
         */
        this.autocomplete = 'off';
        /**
         * Automatically applies a filter based on user input.
         * NOTE: Do not apply auto-filtering in tandem with custom (external) filtering.
         * Default = false.
         */
        this.autoFilter = false;
        /**
         * Determines how auto-filtering matches user input with options.
         * Default = 'contains'.
         */
        this.autoFilterMatching = 'contains';
        /**
         * Show autocomplete suggestions when entering (e.g. tabbing into) the Input Element.
         * Default = true.
         */
        this.openOnFocus = true;
        /**
         * Show autocomplete suggestions when clicking on the input element.
         * Default = true.
         */
        this.openOnClick = true;
        /**
         * Allow clicking the input element to close the drop down if it is open.
         * Default = true.
         */
        this.closeOnClick = true;
        /**
         * Show autocomplete suggestions when inputting characters into the input element.
         * Default = true
         */
        this.openOnInput = true;
        /**
         * Automatically fill the input element with the first matching item in the option list as the user types,
         * allowing the user to continue typing with further matches continually updating the input field.
         * Default = false.
         */
        this.useTypeAhead = false;
        /**
         * If the user inputs a value that does not exist in the option list and leaves the input, this allows
         * the input text to become the value of this control, and an optionCreated event is emitted.
         * The default displayFn inherently supports this, but a custom displayFn must support new
         * values for this to properly function. If this is true, and [allowPartialMatching=true], partial
         * matching takes precedence.
         * Default = false.
         */
        this.allowNewOptions = false;
        /**
         * Automatically selects the first matching entry using 'startsWith' when the user leaves the input.
         * If this is false, no option will be selected and the input will revert to its original value.
         * If this is true, and [allowNewOptions=true], this functionality takes precedence.
         * Default = false.
         */
        this.allowPartialMatching = false;
        /**
         * When [autoFilter=true], apply the filter whenever the drop down opens or re-opens
         * based on the current value. If false, the filter is not applied and all possible
         * options are displayed until the user changes the value. The best example of this
         * is when there is a pre-existing value and a user opens the drop down. In that case,
         * when this is false, the user would see all possible options. If this was true in that
         * case, the user would only see options the match the input element's current value.
         * Default = false.
         */
        this.applyFilterOnOpen = false;
        /**
         * Identifies how to handle non-option-matching input when [allowNewOptions=false].
         * When true, revert to the previous value.
         * When false, set the value to null.
         * Default = false.
         */
        this.revertOnNoMatch = false;
        this.width = null;
        /** @deprecated */
        this.noResultsMessage = 'No results found';
        /**
         * Translates a value into the associated display text. This is also
         * used by the Type Ahead function which uses the resulting value of
         * the matchFn and converts it into text displayed in the input element.
         * Default = match-existing-option OR pass-through-value.
         * @param value This component's value or the result of the matchFn.
         * @returns Display text or empty string (non-observable or observable).
         */
        this.displayFn = (value) => {
            const match = this.options?.find(opt => {
                if (typeof value === 'string' && typeof opt.value === 'string') {
                    return opt.value.toLowerCase() === value.toLowerCase();
                }
                // NOTE: Update tc/utilities isEqual to have option for ignoring case on strings - could get rid of the above code then
                return isEqual(opt.value, value);
            })?.displayText ?? null;
            return match ?? value; // Temporary until region selector is updated -> should be (match ?? '')
        };
        /**
         * Transforms a user's input into a value accepted by this component. This is
         * called by the internal _validate function and is also used to determine if
         * a value should be considered "new", i.e. [allowNewOptions=true].
         * Default = match existing option by its display text.
         * @param text The text from the input element.
         * @returns An existing value or null (non-observable or observable).
         */
        this.valueFn = (text) => {
            return this.options?.find(opt => opt.displayText.toLowerCase() === text?.toLowerCase())?.value ?? null;
        };
        /**
         * Used with [allowNewOptions = true]. Converts a text input into a desired value
         * that will be assigned to this control. This is useful for text-to-object conversion.
         * Default = text pass-through.
         * @param text The text from the input element.
         * @returns An existing value or null (non-observable or observable).
         */
        this.createValueFn = (text) => text;
        /**
         * Used by TypeAhead and PartialMatching to determine how to match the user's
         * input with options in the list. The first item in the returned array is selected.
         * This overrides the built-in matching functionality and does NOT apply to the auto-filter.
         * Default = compare option display text against the user's input using startsWith.
         * @param options A list of StlrOptionComponent.
         * @param matchOn Text input by the user (input.value).
         * @returns An array of values that match the user's input.
         */
        this.matchFn = (options, text) => options.filter(opt => opt.displayText.toLowerCase().startsWith(text.toLowerCase()))
            .map(opt => opt.value);
        /**
         * Used with [allowNewOptions = true]. Fires after the createValueFn is executed.
         * Allows the implementing app to process new values.
         */
        this.optionCreated = new EventEmitter();
        /**
         * Fires whenever the input text is modified. Allows for custom filtering.
         * If you are applying custom filtering, it is recommended to set [autoFilter] to false.
         */
        this.inputChanged = new EventEmitter();
        /**
         * Emits when this component loses focus. This takes into account both the input and panel.
         * This is needed by the country and region components.
         */
        this.focusLost = new EventEmitter();
        this._destroySubject = new Subject();
        /**
         * For internal use by other form components. Ex: The AutoBubbler has a
         * [closeOnAdd] input and sets this corresponding to that input's value.
         */
        this.closeOnSelect = true;
    }
    onResize() {
        this._applyPanelPositionAndSize();
    }
    /**
     * Any CSS sizing value. Default = Dynamically adjusted based on the input element width.
     */
    get panelWidth() {
        return this._panelWidth || this.inputRef.nativeElement.getBoundingClientRect().width;
    }
    set panelWidth(width) {
        this._panelWidth = width;
    }
    ngAfterContentInit() {
        if (this.autoFilter) {
            this._filteredOptionsSubject.next(this.options.toArray());
        }
        this.options.changes.pipe(takeUntil(this._destroySubject)).subscribe(() => {
            const filterValue = this.inputRef.nativeElement.value.slice(0, this.inputRef.nativeElement.selectionStart ?? undefined) ?? null;
            this._filterOptions(this.applyFilterOnOpen ? filterValue : null);
        });
        this._isInitialized = true;
        this._setDisplayText();
    }
    ngOnDestroy() {
        super.ngOnDestroy();
        this._keyManager?.destroy();
        this._closePanelSubject.next();
        this._closePanelSubject.complete();
        this._destroySubject.next();
        this._destroySubject.complete();
        if (this._overlayRef) {
            try {
                if (this._overlayRef.hasAttached()) {
                    this._overlayRef.detach();
                }
                this._overlayRef.dispose();
            }
            catch (e) {
                // Known issue with CDK 20.2.9 overlay cleanup in test environment
                // The position strategy's disconnect method may be undefined during parallel test execution
                // Safe to ignore as we're in teardown phase
            }
            finally {
                this._overlayRef = null;
            }
        }
    }
    get value() {
        return super.value;
    }
    set value(value) {
        super.value = value;
        this._setDisplayText();
        if (this.closeOnSelect)
            this._closePanelSubject.next();
    }
    writeValue(value) {
        super.writeValue(value);
        this._setDisplayText();
    }
    /** Internal. Used by the Option Component. */
    get activeOption() {
        return this._keyManager?.activeItem ?? null;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    get disabled() {
        return super.disabled;
    }
    set disabled(isDisabled) {
        super.disabled = isDisabled;
        this.reset();
    }
    /** @deprecated In favor of reset() function. */
    onReset() {
        this.reset();
    }
    /**
     * Resets the display text to the current value of this component and closes the drop down.
     */
    reset() {
        this._closePanelSubject.next();
        this._setDisplayText();
    }
    /**
     * Sets the value of this component to null. Will also close the drop down if [closeOnSelect] is true (the default).
     */
    clear() {
        this.handleChange(null);
    }
    /**
     * Closes the panel.
     */
    closePanel() {
        this._closePanelSubject.next();
    }
    /**
     * Updates this component with the passed value and executes change events.
     * This is called by the Option Component.
     * @param value The value to apply to this component.
     */
    handleChange(value) {
        const hasChanged = !isEqual(this.value, value);
        this.value = value;
        if (hasChanged) {
            if (this.ngControl?.control?.updateOn === 'change')
                this._onChange(this.value);
            this.changed.emit(this.value);
        }
    }
    handleInputKeydown(event) {
        const matches = this.inputRef.nativeElement.value ? this.matchFn(this.options.toArray() ?? [], this.inputRef.nativeElement.value) : [];
        if (event.key === 'Enter') {
            if (this.activeOption) {
                this.handleChange(this.activeOption.value);
                this.inputRef.nativeElement.setSelectionRange(this.inputRef.nativeElement.value.length, this.inputRef.nativeElement.value.length);
            }
            else if (this.useTypeAhead && matches.length > 0) {
                this.handleChange(matches[0]);
                this.inputRef.nativeElement.setSelectionRange(this.inputRef.nativeElement.value.length, this.inputRef.nativeElement.value.length);
            }
            else {
                this._validate(this.inputRef.nativeElement.value);
            }
            event.stopPropagation();
            event.preventDefault();
        }
        else if (event.key === ' ' && event.getModifierState('Control') && matches.length > 0) {
            this.handleChange(matches[0]);
            this.inputRef.nativeElement.setSelectionRange(this.inputRef.nativeElement.value.length, this.inputRef.nativeElement.value.length);
            event.stopPropagation();
            event.preventDefault();
        }
        else if (event.key === 'Escape') {
            this._setDisplayText();
            this._closePanelSubject.next();
            this.inputRef.nativeElement.focus();
            event.stopPropagation();
            event.preventDefault();
        }
        else if (event.key === 'Tab') {
            this._closePanelSubject.next();
        }
        else if (event.key === 'ArrowDown' && event.getModifierState('Alt') && !this._overlayRef?.hasAttached()) {
            this._openPanel();
            this.inputRef.nativeElement.focus();
            event.stopPropagation();
            event.preventDefault();
        }
        else if (event.key === 'ArrowUp' && event.getModifierState('Alt') && this._overlayRef?.hasAttached()) {
            this._closePanelSubject.next();
            this.inputRef.nativeElement.focus();
            event.stopPropagation();
            event.preventDefault();
        }
        else if (event.key === 'ArrowUp') {
            this._keyManager.setPreviousItemActive();
            this._scrollToActiveItem();
            event.stopPropagation();
            event.preventDefault();
        }
        else if (event.key === 'ArrowDown') {
            this._keyManager.setNextItemActive();
            this._scrollToActiveItem();
            event.stopPropagation();
            event.preventDefault();
        }
    }
    handleInput(event) {
        this._clearActiveItem();
        const element = event.target;
        this.inputChanged.emit(element.value);
        if (this.openOnInput && !this._overlayRef?.hasAttached()) {
            this._openPanel(); // Internally calls this._filterOptions
        }
        else if (this._overlayRef?.hasAttached()) {
            const filterValue = (element.value || null)?.slice(0, element.selectionStart ?? undefined) ?? null;
            this._filterOptions(filterValue);
        }
        if (this.useTypeAhead && event.inputType !== 'deleteContentBackward') {
            const matches = this.inputRef.nativeElement.value ? this.matchFn(this.options.toArray() ?? [], this.inputRef.nativeElement.value) : [];
            if (matches.length > 0) {
                const currentCaret = element.selectionStart;
                const displayValue = this.displayFn(matches[0]);
                if (displayValue instanceof Observable) {
                    displayValue.pipe(first()).subscribe(val => {
                        element.value = val ?? '';
                        element.setSelectionRange(currentCaret, element.value.length);
                    });
                }
                else {
                    element.value = displayValue ?? '';
                    element.setSelectionRange(currentCaret, element.value.length);
                }
            }
        }
    }
    handleInputChange(event) {
        const text = event.target.value;
        if (!this._isFocused) {
            this._validate(text ?? '');
        }
    }
    handleInputFocus() {
        this._isFocused = true;
        if (this.openOnFocus && !this._overlayRef?.hasAttached()) {
            this._openPanel();
        }
    }
    handleInputBlur(event) {
        if (event.relatedTarget !== this.panelRef?.nativeElement) {
            this._validate(this.inputRef.nativeElement.value);
            if (this.ngControl?.control?.updateOn === 'blur')
                this._onChange(this.value);
            this._isFocused = false;
            this._onTouched();
            this.focusLost.emit(event);
        }
    }
    handlePanelBlur(event) {
        if (event.relatedTarget !== this.inputRef.nativeElement) {
            this._validate(this.inputRef.nativeElement.value);
            if (this.ngControl?.control?.updateOn === 'blur')
                this._onChange(this.value);
            this._isFocused = false;
            this._onTouched();
            this.focusLost.emit(event);
        }
    }
    /**
     * Opens or closes the panel when the user clicks on the input element.
     * NOTE: Does not open the panel if the user is actively selecting text.
     */
    handleInputClick() {
        if ((this.inputRef.nativeElement.selectionStart ?? 0) === (this.inputRef.nativeElement.selectionEnd ?? 0)) {
            if (this.openOnClick && !this._overlayRef?.hasAttached()) {
                this._openPanel();
            }
            else if (this.closeOnClick && this._overlayRef?.hasAttached()) {
                this._closePanelSubject.next();
            }
        }
    }
    _validate(text) {
        const transformedValue = this.valueFn(text);
        if (transformedValue instanceof Observable) {
            transformedValue.pipe(first()).subscribe(val => {
                if (val != null) {
                    this.handleChange(val);
                }
                else {
                    this._applyPartialMatchOrNewOptionOrReset(text);
                }
            });
        }
        else {
            if (transformedValue != null) {
                this.handleChange(transformedValue);
            }
            else {
                this._applyPartialMatchOrNewOptionOrReset(text);
            }
        }
    }
    _applyPartialMatchOrNewOptionOrReset(text) {
        if (this.allowPartialMatching) {
            const matches = text ? this.matchFn(this.options?.toArray() ?? [], text) : [];
            if (matches.length > 0) {
                this.handleChange(matches[0]);
                return;
            }
        }
        if (this.allowNewOptions) {
            const createdValue = this.createValueFn(text);
            if (createdValue instanceof Observable) {
                createdValue.pipe(first()).subscribe(newValue => {
                    this._notifyOptionCreated(newValue);
                    this.handleChange(newValue);
                });
            }
            else {
                this._notifyOptionCreated(createdValue);
                this.handleChange(createdValue);
            }
            this._filterOptions(null);
        }
        else {
            this.handleChange(this.revertOnNoMatch ? this.value : null);
        }
    }
    _notifyOptionCreated(item) {
        if (item && !isEqual(this._lastOptionCreated, item)) {
            this.optionCreated.emit(item);
            this._lastOptionCreated = item;
        }
    }
    _handleOutsideClicks(event) {
        if (event.target === this.inputRef.nativeElement)
            return;
        this._closePanelSubject.next();
        this._isFocused = false;
    }
    _configureKeyManager() {
        if (this.options) {
            this._keyManager = new ActiveDescendantKeyManager(this.options)
                .withWrap()
                .withHomeAndEnd()
                .skipPredicate(item => this.autoFilter
                ? this._filteredOptionsSubject.value.find(opt => opt.value === item.value) == null
                : this.options.find(opt => opt.value === item.value) == null);
        }
    }
    _setActiveItem() {
        const activeItem = this.options?.find(opt => opt.value === this.value);
        if (activeItem) {
            this._keyManager?.setActiveItem(activeItem);
            this._scrollToActiveItem();
        }
        else {
            this._clearActiveItem();
        }
    }
    _clearActiveItem() {
        this._keyManager?.setActiveItem(-1);
    }
    _setDisplayText() {
        if (!this._isInitialized)
            return;
        const displayCalc = this.displayFn(this.value);
        if (displayCalc instanceof Observable) {
            displayCalc.pipe(first()).subscribe(val => {
                this.inputRef.nativeElement.value = val ?? '';
            });
        }
        else {
            this.inputRef.nativeElement.value = displayCalc ?? '';
        }
    }
    /**
     * If [autoFilter=true], applies a [startsWith] or [contains] filter to the options list.
     * If the text parameter is null, the filter is removed.
     */
    _filterOptions(text) {
        if (!this.autoFilter)
            return;
        const filteredOptions = text
            ? this.options?.filter(opt => {
                if (this.autoFilterMatching === 'contains') {
                    return opt.displayText.toLowerCase().includes(text.toLowerCase());
                }
                return opt.displayText.toLowerCase().startsWith(text.toLowerCase());
            }) ?? []
            : this.options?.toArray() ?? [];
        this._filteredOptionsSubject.next(filteredOptions);
        this._configureKeyManager();
        this._applyPanelPositionAndSize(true, true);
    }
    /** One-shot subscription. */
    _panelClosingActions() {
        return merge(this._closePanelSubject, this._overlayRef.detachments().pipe(filter(() => this._overlayRef.hasAttached())) ?? of()).pipe(take(1), map(() => {
            if (this._overlayRef?.hasAttached()) {
                this._overlayRef.detach();
            }
            this._closePanelSubject.next();
            this._clearActiveItem();
        }));
    }
    _scrollToActiveItem() {
        const index = this._keyManager.activeItemIndex;
        if (this.panelRef && index != null) {
            const activeElement = this.panelRef.nativeElement.children.item(index);
            activeElement?.scrollIntoView(false);
        }
    }
    _openPanel() {
        if (this.disabled || this._overlayRef?.hasAttached())
            return;
        if (!this._overlayRef) {
            this._portal = new TemplatePortal(this.panelTemplate, this._viewContainerRef);
            this._overlayRef = this._overlay.create(new OverlayConfig({
                positionStrategy: this._getPositionStrategy(),
                scrollStrategy: this._overlay.scrollStrategies.reposition()
            }));
        }
        this._overlayRef.attach(this._portal);
        this._panelClosingActions().subscribe();
        this._overlayRef.keydownEvents().pipe(takeUntil(this._closePanelSubject)).subscribe(event => this.handleInputKeydown(event));
        this._overlayRef.outsidePointerEvents().pipe(takeUntil(this._closePanelSubject)).subscribe(event => this._handleOutsideClicks(event));
        if (!this.autoFilter) {
            this._configureKeyManager();
            this._applyPanelPositionAndSize(true, true);
        }
        else {
            if (this.applyFilterOnOpen) {
                const filterValue = this.inputRef.nativeElement.value.slice(0, this.inputRef.nativeElement.selectionStart ?? undefined);
                this._filterOptions(filterValue);
            }
            else {
                this._filterOptions(null);
            }
        }
    }
    /**
     * Applies the appropriate panel position and size.
     * If specified, wait a tick for the panel to potentially attach.
     */
    _applyPanelPositionAndSize(waitATick = false, updateActiveItem = false) {
        const updateAction = () => {
            this._overlayRef?.updateSize({
                width: this.panelWidth,
                height: this.panelRef?.nativeElement.getBoundingClientRect().height ?? MAX_PANEL_HEIGHT
            });
            this._overlayRef?.updatePosition();
            if (updateActiveItem) {
                this._setActiveItem();
            }
        };
        if (waitATick) {
            timer(1).subscribe(() => updateAction());
        }
        else {
            updateAction();
        }
    }
    _getPositionStrategy() {
        const belowPositions = [
            { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
            { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' }
        ];
        const abovePositions = [
            { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', panelClass: 'autocomplete-panel-above' },
            { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', panelClass: 'autocomplete-panel-above' }
        ];
        const strategy = this._overlay
            .position()
            .flexibleConnectedTo(this.inputRef)
            .withPositions([...belowPositions, ...abovePositions])
            .withFlexibleDimensions(false)
            .withPush(false);
        return strategy;
    }
    static { this.ɵfac = /*@__PURE__*/ (() => { let ɵStlrAutocompleteComponent_BaseFactory; return function StlrAutocompleteComponent_Factory(__ngFactoryType__) { return (ɵStlrAutocompleteComponent_BaseFactory || (ɵStlrAutocompleteComponent_BaseFactory = i0.ɵɵgetInheritedFactory(StlrAutocompleteComponent)))(__ngFactoryType__ || StlrAutocompleteComponent); }; })(); }
    static { this.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: StlrAutocompleteComponent, selectors: [["stlr-autocomplete"]], contentQueries: function StlrAutocompleteComponent_ContentQueries(rf, ctx, dirIndex) { if (rf & 1) {
            i0.ɵɵcontentQuery(dirIndex, StlrOptionComponent, 5);
            i0.ɵɵcontentQuery(dirIndex, StlrOptionComponent, 5, ElementRef);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.options = _t);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.optionRefs = _t);
        } }, viewQuery: function StlrAutocompleteComponent_Query(rf, ctx) { if (rf & 1) {
            i0.ɵɵviewQuery(_c0$e, 7, ElementRef);
            i0.ɵɵviewQuery(_c1$8, 7, TemplateRef);
            i0.ɵɵviewQuery(_c2, 5, ElementRef);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.inputRef = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.panelTemplate = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.panelRef = _t.first);
        } }, hostVars: 2, hostBindings: function StlrAutocompleteComponent_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("resize", function StlrAutocompleteComponent_resize_HostBindingHandler() { return ctx.onResize(); }, i0.ɵɵresolveWindow);
        } if (rf & 2) {
            i0.ɵɵclassProp("full", ctx.width);
        } }, inputs: { autocomplete: "autocomplete", autoFilter: "autoFilter", autoFilterMatching: "autoFilterMatching", openOnFocus: "openOnFocus", openOnClick: "openOnClick", closeOnClick: "closeOnClick", openOnInput: "openOnInput", useTypeAhead: "useTypeAhead", allowNewOptions: "allowNewOptions", allowPartialMatching: "allowPartialMatching", applyFilterOnOpen: "applyFilterOnOpen", revertOnNoMatch: "revertOnNoMatch", width: "width", panelWidth: "panelWidth", noResultsMessage: "noResultsMessage", displayFn: "displayFn", valueFn: "valueFn", createValueFn: "createValueFn", matchFn: "matchFn" }, outputs: { optionCreated: "optionCreated", inputChanged: "inputChanged", focusLost: "focusLost" }, standalone: false, features: [i0.ɵɵProvidersFeature([
                {
                    provide: STLR_OPTION_PARENT_COMPONENT,
                    useExisting: forwardRef(() => StlrAutocompleteComponent),
                },
                {
                    provide: STLR_FORM_COMPONENT_BASE,
                    useExisting: forwardRef(() => StlrAutocompleteComponent),
                    multi: true
                },
            ]), i0.ɵɵInheritDefinitionFeature], ngContentSelectors: _c3, decls: 8, vars: 18, consts: [["input", ""], ["panelTemplate", ""], ["rawContent", ""], ["filteredContent", ""], ["panel", ""], [3, "blur", "changed", "click", "focus", "input", "keydown", "disabled", "id", "name", "required", "tabindex"], ["role", "listbox", 1, "stlr-autocomplete-panel", 3, "focusout", "tabindex"], [3, "ngTemplateOutlet"], ["stlr-option", "", 3, "value"]], template: function StlrAutocompleteComponent_Template(rf, ctx) { if (rf & 1) {
            const _r1 = i0.ɵɵgetCurrentView();
            i0.ɵɵprojectionDef();
            i0.ɵɵelementStart(0, "input", 5, 0);
            i0.ɵɵlistener("blur", function StlrAutocompleteComponent_Template_input_blur_0_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.handleInputBlur($event)); })("changed", function StlrAutocompleteComponent_Template_input_changed_0_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.handleInputChange($event)); })("click", function StlrAutocompleteComponent_Template_input_click_0_listener() { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.handleInputClick()); })("focus", function StlrAutocompleteComponent_Template_input_focus_0_listener() { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.handleInputFocus()); })("input", function StlrAutocompleteComponent_Template_input_input_0_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.handleInput($event)); })("keydown", function StlrAutocompleteComponent_Template_input_keydown_0_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.handleInputKeydown($event)); });
            i0.ɵɵelementEnd();
            i0.ɵɵtemplate(2, StlrAutocompleteComponent_ng_template_2_Template, 3, 5, "ng-template", null, 1, i0.ɵɵtemplateRefExtractor)(4, StlrAutocompleteComponent_ng_template_4_Template, 1, 0, "ng-template", null, 2, i0.ɵɵtemplateRefExtractor)(6, StlrAutocompleteComponent_ng_template_6_Template, 3, 2, "ng-template", null, 3, i0.ɵɵtemplateRefExtractor);
        } if (rf & 2) {
            i0.ɵɵclassProp("disabled", ctx.disabled)("invalid", ctx.invalid);
            i0.ɵɵproperty("disabled", ctx.disabled)("id", ctx.id)("name", ctx.name)("required", ctx.required)("tabindex", ctx.tabindex);
            i0.ɵɵattribute("aria-describedby", ctx.ariaDescribedBy)("aria-disabled", ctx.disabled)("aria-invalid", ctx.invalid)("aria-label", ctx.ariaLabel)("aria-labelledby", ctx.ariaLabelledBy)("aria-required", ctx.required)("autocomplete", ctx.autocomplete)("enterkeyhint", ctx.enterKeyHint)("placeholder", ctx.placeholder);
        } }, dependencies: [i1$1.NgTemplateOutlet, i1.NgSelectOption, i1.ɵNgSelectMultipleOption, StlrOptionComponent, i1$1.AsyncPipe], styles: [".full[_nghost-%COMP%]{width:100%}.full[_nghost-%COMP%]   input[_ngcontent-%COMP%]{width:100%}.ng-touched.ng-invalid[_nghost-%COMP%]   input[_ngcontent-%COMP%]:not(.disabled):not(:focus), .ng-dirty.ng-invalid[_nghost-%COMP%]   input[_ngcontent-%COMP%]:not(.disabled):not(:focus){border-color:var(--stlr-color__error);background-color:var(--stlr-color__error--extra-light)}input[_ngcontent-%COMP%]{box-sizing:border-box;padding:0 10px;border:solid 1px rgba(0,0,0,.25);background-color:#fff;color:var(--stlr-color__copy);appearance:none;border-radius:var(--stlr-border-radius);outline:solid 1px rgba(255,255,255,0);transition:all .2s;box-shadow:0 0 2px #59a9ff00;font-family:var(--stlr-font-family--copy)}input[_ngcontent-%COMP%]:not(textarea){height:2.25rem}input[_ngcontent-%COMP%]::-webkit-search-cancel-button{-webkit-appearance:none;height:16px;width:16px;display:block;background-color:#0000004d;border-radius:10px;background-repeat:no-repeat;background-position:center center;cursor:pointer;background-image:url(\"data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9.5 3.205L8.795 2.5L6 5.295L3.205 2.5L2.5 3.205L5.295 6L2.5 8.795L3.205 9.5L6 6.705L8.795 9.5L9.5 8.795L6.705 6L9.5 3.205Z' fill='white'/%3E%3C/svg%3E%0A\")}input[_ngcontent-%COMP%]:hover{box-shadow:0 0 6px #0000001a}input[_ngcontent-%COMP%]:focus{border:solid 1px var(--stlr-color__info);outline:solid 1px var(--stlr-color__info)}input[disabled][_ngcontent-%COMP%]{color:var(--stlr-color__disabled--light);border:solid 1px rgba(0,0,0,.2);background-color:#00000006}input.ng-invalid.ng-touched[_ngcontent-%COMP%], input.ng-invalid.ng-dirty[_ngcontent-%COMP%]{border-color:var(--stlr-color__error);background-color:var(--stlr-color__error--extra-light)}input.ng-invalid.ng-touched[_ngcontent-%COMP%]:focus, input.ng-invalid.ng-dirty[_ngcontent-%COMP%]:focus{background-color:#fff;border:solid 1px var(--stlr-color__info)}input[_ngcontent-%COMP%]:focus::placeholder{color:transparent}input[type=search][_ngcontent-%COMP%]::-webkit-search-decoration, input[type=search][_ngcontent-%COMP%]::-webkit-search-results-button, input[type=search][_ngcontent-%COMP%]::-webkit-search-results-decoration{display:none}.stlr-autocomplete-panel[_ngcontent-%COMP%]{-ms-overflow-style:-ms-autohiding-scrollbar;-webkit-overflow-scrolling:touch;background-color:var(--stlr-color__white);border-radius:0 0 var(--space--4);border:solid 1px var(--stlr-color__neutral--200);box-shadow:0 2px 4px #0000001a;max-height:256px;overflow:hidden;overflow-y:auto;position:absolute;width:100%;z-index:100}.stlr-autocomplete-panel[_ngcontent-%COMP%]     .stlr-option{display:flex;align-items:center;justify-content:flex-start;box-sizing:border-box;padding:var(--space--8) var(--space--16);font-size:var(--font--14);line-height:1.5;color:var(--stlr-color__copy);cursor:pointer;transition:all .2s ease-in-out}.stlr-autocomplete-panel[_ngcontent-%COMP%]     .stlr-option:hover{background-color:var(--stlr-color__neutral--100)}.stlr-autocomplete-panel[_ngcontent-%COMP%]     .stlr-option.stlr-option--selected{background-color:var(--stlr-color__neutral--200)}.stlr-autocomplete-panel[_ngcontent-%COMP%]     .stlr-option.stlr-option--disabled{color:var(--stlr-color__neutral--400);pointer-events:none;cursor:not-allowed}.stlr-autocomplete-panel[_ngcontent-%COMP%]     .stlr-option.stlr-option--active{background-color:var(--stlr-color__neutral--100)}.stlr-autocomplete-panel[_ngcontent-%COMP%]     .stlr-option.stlr-option--active.stlr-option--selected{background-color:var(--stlr-color__neutral--200)}.stlr-autocomplete-panel[_ngcontent-%COMP%]     .stlr-option.stlr-option--active.stlr-option--disabled{color:var(--stlr-color__neutral--400);pointer-events:none;cursor:not-allowed}.autocomplete-panel-above[_ngcontent-%COMP%]   .stlr-autocomplete-panel[_ngcontent-%COMP%]{border-bottom-left-radius:0;border-bottom-right-radius:0;transform-origin:center bottom}.cdk-overlay-pane[_ngcontent-%COMP%]:not(.autocomplete-panel-above)   .stlr-autocomplete-panel[_ngcontent-%COMP%]{border-top-left-radius:0;border-top-right-radius:0}"], changeDetection: 0 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrAutocompleteComponent, [{
        type: Component,
        args: [{ selector: 'stlr-autocomplete', changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                    {
                        provide: STLR_OPTION_PARENT_COMPONENT,
                        useExisting: forwardRef(() => StlrAutocompleteComponent),
                    },
                    {
                        provide: STLR_FORM_COMPONENT_BASE,
                        useExisting: forwardRef(() => StlrAutocompleteComponent),
                        multi: true
                    },
                ], standalone: false, template: "<input\n  #input\n  (blur)=\"handleInputBlur($event)\"\n  (changed)=\"handleInputChange($event)\"\n  (click)=\"handleInputClick()\"\n  (focus)=\"handleInputFocus()\"\n  (input)=\"handleInput($event)\"\n  (keydown)=\"handleInputKeydown($event)\"\n  [attr.aria-describedby]=\"ariaDescribedBy\"\n  [attr.aria-disabled]=\"disabled\"\n  [attr.aria-invalid]=\"invalid\"\n  [attr.aria-label]=\"ariaLabel\"\n  [attr.aria-labelledby]=\"ariaLabelledBy\"\n  [attr.aria-required]=\"required\"\n  [attr.autocomplete]=\"autocomplete\"\n  [attr.enterkeyhint]=\"enterKeyHint\"\n  [attr.placeholder]=\"placeholder\"\n  [class.disabled]=\"disabled\"\n  [class.invalid]=\"invalid\"\n  [disabled]=\"disabled\"\n  [id]=\"id\"\n  [name]=\"name\"\n  [required]=\"required\"\n  [tabindex]=\"tabindex\"\n  />\n\n<ng-template #panelTemplate>\n  <div\n    #panel\n    role=\"listbox\"\n    class=\"stlr-autocomplete-panel\"\n    (focusout)=\"handlePanelBlur($event)\"\n    [attr.aria-label]=\"ariaLabel\"\n    [attr.aria-labelledby]=\"ariaLabelledBy\"\n    [attr.aria-owns]=\"id\"\n    [tabindex]=\"tabindex ? (tabindex + 1) : null\">\n    <ng-container [ngTemplateOutlet]=\"autoFilter ? filteredContent : rawContent\"></ng-container>\n  </div>\n</ng-template>\n\n<ng-template #rawContent>\n  <ng-content></ng-content>\n</ng-template>\n\n<ng-template #filteredContent>\n  @for (option of filteredOptions$ | async; track option) {\n    <option stlr-option [value]=\"option.value\">\n      {{ option.displayText }}\n    </option>\n  }\n</ng-template>\n", styles: [":host.full{width:100%}:host.full input{width:100%}:host.ng-touched.ng-invalid input:not(.disabled):not(:focus),:host.ng-dirty.ng-invalid input:not(.disabled):not(:focus){border-color:var(--stlr-color__error);background-color:var(--stlr-color__error--extra-light)}input{box-sizing:border-box;padding:0 10px;border:solid 1px rgba(0,0,0,.25);background-color:#fff;color:var(--stlr-color__copy);appearance:none;border-radius:var(--stlr-border-radius);outline:solid 1px rgba(255,255,255,0);transition:all .2s;box-shadow:0 0 2px #59a9ff00;font-family:var(--stlr-font-family--copy)}input:not(textarea){height:2.25rem}input::-webkit-search-cancel-button{-webkit-appearance:none;height:16px;width:16px;display:block;background-color:#0000004d;border-radius:10px;background-repeat:no-repeat;background-position:center center;cursor:pointer;background-image:url(\"data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9.5 3.205L8.795 2.5L6 5.295L3.205 2.5L2.5 3.205L5.295 6L2.5 8.795L3.205 9.5L6 6.705L8.795 9.5L9.5 8.795L6.705 6L9.5 3.205Z' fill='white'/%3E%3C/svg%3E%0A\")}input:hover{box-shadow:0 0 6px #0000001a}input:focus{border:solid 1px var(--stlr-color__info);outline:solid 1px var(--stlr-color__info)}input[disabled]{color:var(--stlr-color__disabled--light);border:solid 1px rgba(0,0,0,.2);background-color:#00000006}input.ng-invalid.ng-touched,input.ng-invalid.ng-dirty{border-color:var(--stlr-color__error);background-color:var(--stlr-color__error--extra-light)}input.ng-invalid.ng-touched:focus,input.ng-invalid.ng-dirty:focus{background-color:#fff;border:solid 1px var(--stlr-color__info)}input:focus::placeholder{color:transparent}input[type=search]::-webkit-search-decoration,input[type=search]::-webkit-search-results-button,input[type=search]::-webkit-search-results-decoration{display:none}.stlr-autocomplete-panel{-ms-overflow-style:-ms-autohiding-scrollbar;-webkit-overflow-scrolling:touch;background-color:var(--stlr-color__white);border-radius:0 0 var(--space--4);border:solid 1px var(--stlr-color__neutral--200);box-shadow:0 2px 4px #0000001a;max-height:256px;overflow:hidden;overflow-y:auto;position:absolute;width:100%;z-index:100}.stlr-autocomplete-panel ::ng-deep .stlr-option{display:flex;align-items:center;justify-content:flex-start;box-sizing:border-box;padding:var(--space--8) var(--space--16);font-size:var(--font--14);line-height:1.5;color:var(--stlr-color__copy);cursor:pointer;transition:all .2s ease-in-out}.stlr-autocomplete-panel ::ng-deep .stlr-option:hover{background-color:var(--stlr-color__neutral--100)}.stlr-autocomplete-panel ::ng-deep .stlr-option.stlr-option--selected{background-color:var(--stlr-color__neutral--200)}.stlr-autocomplete-panel ::ng-deep .stlr-option.stlr-option--disabled{color:var(--stlr-color__neutral--400);pointer-events:none;cursor:not-allowed}.stlr-autocomplete-panel ::ng-deep .stlr-option.stlr-option--active{background-color:var(--stlr-color__neutral--100)}.stlr-autocomplete-panel ::ng-deep .stlr-option.stlr-option--active.stlr-option--selected{background-color:var(--stlr-color__neutral--200)}.stlr-autocomplete-panel ::ng-deep .stlr-option.stlr-option--active.stlr-option--disabled{color:var(--stlr-color__neutral--400);pointer-events:none;cursor:not-allowed}.autocomplete-panel-above .stlr-autocomplete-panel{border-bottom-left-radius:0;border-bottom-right-radius:0;transform-origin:center bottom}.cdk-overlay-pane:not(.autocomplete-panel-above) .stlr-autocomplete-panel{border-top-left-radius:0;border-top-right-radius:0}\n"] }]
    }], null, { onResize: [{
            type: HostListener,
            args: ['window:resize']
        }], autocomplete: [{
            type: Input
        }], autoFilter: [{
            type: Input
        }], autoFilterMatching: [{
            type: Input
        }], openOnFocus: [{
            type: Input
        }], openOnClick: [{
            type: Input
        }], closeOnClick: [{
            type: Input
        }], openOnInput: [{
            type: Input
        }], useTypeAhead: [{
            type: Input
        }], allowNewOptions: [{
            type: Input
        }], allowPartialMatching: [{
            type: Input
        }], applyFilterOnOpen: [{
            type: Input
        }], revertOnNoMatch: [{
            type: Input
        }], width: [{
            type: Input
        }, {
            type: HostBinding,
            args: ['class.full']
        }], panelWidth: [{
            type: Input
        }], noResultsMessage: [{
            type: Input
        }], displayFn: [{
            type: Input
        }], valueFn: [{
            type: Input
        }], createValueFn: [{
            type: Input
        }], matchFn: [{
            type: Input
        }], inputRef: [{
            type: ViewChild,
            args: ['input', { read: ElementRef, static: true }]
        }], panelTemplate: [{
            type: ViewChild,
            args: ['panelTemplate', { read: TemplateRef, static: true }]
        }], panelRef: [{
            type: ViewChild,
            args: ['panel', { read: ElementRef, static: false }]
        }], options: [{
            type: ContentChildren,
            args: [StlrOptionComponent, { descendants: true }]
        }], optionRefs: [{
            type: ContentChildren,
            args: [StlrOptionComponent, { descendants: true, read: ElementRef }]
        }], optionCreated: [{
            type: Output
        }], inputChanged: [{
            type: Output
        }], focusLost: [{
            type: Output
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(StlrAutocompleteComponent, { className: "StlrAutocompleteComponent", filePath: "lib/autocomplete/autocomplete.component.ts", lineNumber: 60 }); })();

const AUTOCOMPLETE_API = [StlrAutocompleteComponent];

class Colorable {
    get primary() {
        return this.color === 'primary';
    }
    get secondary() {
        return this.color === 'secondary';
    }
    get info() {
        return this.color === 'info';
    }
    get warn() {
        return this.color === 'warn';
    }
    get success() {
        return this.color === 'success';
    }
    get error() {
        return this.color === 'error';
    }
    get subdued() {
        return this.color === 'subdued';
    }
    get dark() {
        return this.color === 'dark';
    }
    get light() {
        return this.color === 'light';
    }
    constructor(defaultColor = 'primary') {
        this.defaultColor = defaultColor;
        this.color = defaultColor;
    }
    static { this.ɵfac = function Colorable_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || Colorable)(i0.ɵɵdirectiveInject('defaultValue')); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: Colorable, selectors: [["", "stlrColorable", ""]], hostVars: 18, hostBindings: function Colorable_HostBindings(rf, ctx) { if (rf & 2) {
            i0.ɵɵclassProp("primary", ctx.primary)("secondary", ctx.secondary)("info", ctx.info)("warn", ctx.warn)("success", ctx.success)("error", ctx.error)("subdued", ctx.subdued)("dark", ctx.dark)("light", ctx.light);
        } }, inputs: { color: "color" }, standalone: false }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(Colorable, [{
        type: Directive,
        args: [{
                selector: '[stlrColorable]',
                standalone: false
            }]
    }], () => [{ type: undefined, decorators: [{
                type: Inject,
                args: ['defaultValue']
            }] }], { color: [{
            type: Input
        }], primary: [{
            type: HostBinding,
            args: ['class.primary']
        }], secondary: [{
            type: HostBinding,
            args: ['class.secondary']
        }], info: [{
            type: HostBinding,
            args: ['class.info']
        }], warn: [{
            type: HostBinding,
            args: ['class.warn']
        }], success: [{
            type: HostBinding,
            args: ['class.success']
        }], error: [{
            type: HostBinding,
            args: ['class.error']
        }], subdued: [{
            type: HostBinding,
            args: ['class.subdued']
        }], dark: [{
            type: HostBinding,
            args: ['class.dark']
        }], light: [{
            type: HostBinding,
            args: ['class.light']
        }] }); })();

const STLR_BUBBLE = new InjectionToken('STLR_BUBBLE');
const STLR_BUBBLE_POPPER = new InjectionToken('STLR_BUBBLE_POPPER');
const STLR_BUBBLE_PREFIX = new InjectionToken('STLR_BUBBLE_PREFIX');
const STLR_BUBBLE_SUFFIX = new InjectionToken('STLR_BUBBLE_SUFFIX');

const _c0$d = ["*", [["", "stlrBubblePrefix", ""]], [["", "stlrBubblePopper", ""], ["", "stlrBubbleSuffix", ""]]];
const _c1$7 = ["*", "[stlrBubblePrefix]", "[stlrBubblePopper], [stlrBubbleSuffix]"];
function StlrAutoBubbleComponent_Conditional_0_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 0);
    i0.ɵɵprojection(1, 1);
    i0.ɵɵelementEnd();
} }
function StlrAutoBubbleComponent_Conditional_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 2);
    i0.ɵɵprojection(1, 2);
    i0.ɵɵelementEnd();
} }
class StlrAutoBubbleComponent extends Colorable {
    get disabled() {
        return this._disabled;
    }
    set disabled(value) {
        this._disabled = coerceBooleanProperty(value);
    }
    get removable() {
        return this._removable;
    }
    set removable(value) {
        this._removable = coerceBooleanProperty(value);
    }
    get value() {
        return this._value;
    }
    set value(value) {
        this._value = value;
    }
    _handleKeydown(event) {
        if ((this.focused && event.key === 'Backspace') ||
            event.key === 'Delete') {
            event.preventDefault();
            this.remove();
        }
    }
    get hasFocus() {
        return this._hasFocus;
    }
    get hasSuffix() {
        return !!(this.suffix || this.removeIcon);
    }
    constructor() {
        super();
        this._hostClass = true;
        this.blurred = new Subject();
        this.focused = new Subject();
        this._elementRef = inject(ElementRef);
        this._focusMonitor = inject(FocusMonitor);
        this._hasFocus = false;
        this._disabled = false;
        this.id = `stlr-autobubble-${generateUUID()}`;
        this._removable = true;
        this.tabIndex = -1;
        this.removed = new EventEmitter();
        this._focusMonitor
            .monitor(this._elementRef, true)
            .subscribe((origin) => {
            const hasFocus = origin !== null;
            if (hasFocus !== this._hasFocus) {
                this._hasFocus = hasFocus;
                if (hasFocus) {
                    this.focused.next(this);
                }
            }
        });
        this.color = this.color || 'dark';
    }
    ngOnDestroy() {
        this._focusMonitor.stopMonitoring(this._elementRef);
    }
    focus() {
        if (!this.disabled && this.removeIcon) {
            this.removeIcon.focus();
        }
    }
    remove() {
        if (this.removable) {
            this.removed.emit(this);
        }
    }
    static { this.ɵfac = function StlrAutoBubbleComponent_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || StlrAutoBubbleComponent)(); }; }
    static { this.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: StlrAutoBubbleComponent, selectors: [["stlr-autobubble"]], contentQueries: function StlrAutoBubbleComponent_ContentQueries(rf, ctx, dirIndex) { if (rf & 1) {
            i0.ɵɵcontentQuery(dirIndex, STLR_BUBBLE_PREFIX, 5);
            i0.ɵɵcontentQuery(dirIndex, STLR_BUBBLE_POPPER, 5);
            i0.ɵɵcontentQuery(dirIndex, STLR_BUBBLE_SUFFIX, 5);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.prefix = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.removeIcon = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.suffix = _t.first);
        } }, hostVars: 6, hostBindings: function StlrAutoBubbleComponent_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("keydown", function StlrAutoBubbleComponent_keydown_HostBindingHandler($event) { return ctx._handleKeydown($event); });
        } if (rf & 2) {
            i0.ɵɵdomProperty("id", ctx.id)("tabIndex", ctx.tabIndex);
            i0.ɵɵclassProp("stlr-autobubble", ctx._hostClass)("stlr-autobubble--disabled", ctx.disabled);
        } }, inputs: { disabled: "disabled", id: "id", removable: "removable", tabIndex: "tabIndex", value: "value" }, outputs: { removed: "removed" }, standalone: false, features: [i0.ɵɵProvidersFeature([{ provide: STLR_BUBBLE, useExisting: StlrAutoBubbleComponent }]), i0.ɵɵInheritDefinitionFeature], ngContentSelectors: _c1$7, decls: 4, vars: 2, consts: [[1, "stlr-autobubble--prefix"], [1, "stlr-autobubble--content-wrapper"], [1, "stlr-autobubble--suffix"]], template: function StlrAutoBubbleComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵprojectionDef(_c0$d);
            i0.ɵɵconditionalCreate(0, StlrAutoBubbleComponent_Conditional_0_Template, 2, 0, "span", 0);
            i0.ɵɵelementStart(1, "span", 1);
            i0.ɵɵprojection(2);
            i0.ɵɵelementEnd();
            i0.ɵɵconditionalCreate(3, StlrAutoBubbleComponent_Conditional_3_Template, 2, 0, "span", 2);
        } if (rf & 2) {
            i0.ɵɵconditional(ctx.prefix ? 0 : -1);
            i0.ɵɵadvance(3);
            i0.ɵɵconditional(ctx.hasSuffix ? 3 : -1);
        } }, styles: ["[_nghost-%COMP%]{--bubble__padding: var(--space--2) var(--space--4);--bubble__font-size: var(--font--12);--bubble__border-radius: var(--stlr-border-radius);--bubble__light-bg: white;--bubble__light-color: var(--stlr-color__copy);--bubble__dark-bg: var(--stlr-color__copy);--bubble__dark-color: white;--bubble__primary-bg: var(--stlr-color__primary--light);--bubble__primary-color: var(--stlr-color__primary--extra-dark);--bubble__secondary-bg: var(--stlr-color__secondary--light);--bubble__secondary-color: var(--stlr-color__secondary--extra-dark);--bubble__info-bg: var(--stlr-color__info--light);--bubble__info-color: var(--stlr-color__info--extra-dark);--bubble__warn-bg: var(--stlr-color__warn--light);--bubble__warn-color: var(--stlr-color__warn--extra-dark);--bubble__error-bg: var(--stlr-color__error--light);--bubble__error-color: var(--stlr-color__error--extra-dark);--bubble__success-bg: var(--stlr-color__success--light);--bubble__success-color: var(--stlr-color__success--extra-dark);--bubble__subdued-bg: var(--stlr-color__surface--extra-light);--bubble__subdued-color: var(--stlr-color__surface--extra-dark);--bubble__disabled-bg: var(--stlr-color__neutral--100);--bubble__disabled-color: var(--stlr-color__neutral--800);display:inline-flex;align-items:center;justify-content:center;vertical-align:middle;padding:var(--bubble__padding);border-radius:var(--bubble__border-radius);font-size:var(--bubble__font-size)}[_nghost-%COMP%]   .stlr-autobubble--content-wrapper[_ngcontent-%COMP%]{display:inline-flex;align-items:center;justify-content:center}.primary[_nghost-%COMP%]{background-color:var(--bubble__primary-bg);color:var(--bubble__primary-color)}.primary[_nghost-%COMP%]     .stlr-icon{color:var(--bubble__primary-color)!important}.secondary[_nghost-%COMP%]{background-color:var(--bubble__secondary-bg);color:var(--bubble__secondary-color)}.secondary[_nghost-%COMP%]     .stlr-icon{color:var(--bubble__secondary-color)!important}.info[_nghost-%COMP%]{background-color:var(--bubble__info-bg);color:var(--bubble__info-color)}.info[_nghost-%COMP%]     .stlr-icon{color:var(--bubble__info-color)!important}.error[_nghost-%COMP%]{background-color:var(--bubble__error-bg);color:var(--bubble__error-color)}.error[_nghost-%COMP%]     .stlr-icon{color:var(--bubble__error-color)!important}.warn[_nghost-%COMP%]{background-color:var(--bubble__warn-bg);color:var(--bubble__warn-color)}.warn[_nghost-%COMP%]     .stlr-icon{color:var(--bubble__warn-color)!important}.success[_nghost-%COMP%]{background-color:var(--bubble__success-bg);color:var(--bubble__success-color)}.success[_nghost-%COMP%]     .stlr-icon{color:var(--bubble__success-color)!important}.gray[_nghost-%COMP%]{background-color:var(--bubble__gray-bg);color:var(--bubble__gray-color)}.gray[_nghost-%COMP%]     .stlr-icon{color:var(--bubble__gray-color)!important}.surface[_nghost-%COMP%]{background-color:var(--bubble__surface-bg);color:var(--bubble__surface-color)}.surface[_nghost-%COMP%]     .stlr-icon{color:var(--bubble__surface-color)!important}.subdued[_nghost-%COMP%]{background-color:var(--bubble__subdued-bg);color:var(--bubble__subdued-color)}.subdued[_nghost-%COMP%]     .stlr-icon{color:var(--bubble__subdued-color)!important}.dark[_nghost-%COMP%]{background-color:var(--bubble__dark-bg);color:var(--bubble__dark-color)}.dark[_nghost-%COMP%]     .stlr-icon{color:var(--bubble__dark-color)!important}.light[_nghost-%COMP%]{background-color:var(--bubble__light-bg);color:var(--bubble__light-color)}.light[_nghost-%COMP%]     .stlr-icon{color:var(--bubble__light-color)!important}.link[_nghost-%COMP%]{background-color:var(--bubble__link-bg);color:var(--bubble__link-color)}.link[_nghost-%COMP%]     .stlr-icon{color:var(--bubble__link-color)!important}[disabled][_nghost-%COMP%]{background-color:var(--bubble__disabled-bg);color:var(--bubble__disabled-color)}[_nghost-%COMP%]   .stlr-autobubble--prefix[_ngcontent-%COMP%]{margin-right:.25rem}[_nghost-%COMP%]   .stlr-autobubble--suffix[_ngcontent-%COMP%]{margin-left:.25rem}[_nghost-%COMP%]   .stlr-autobubble--suffix[_ngcontent-%COMP%]     button, [_nghost-%COMP%]   .stlr-autobubble--suffix[_ngcontent-%COMP%]     .stlr-autobubble-popper{appearance:none;padding:0;border:0;background:none;cursor:pointer;outline:none}"], changeDetection: 0 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrAutoBubbleComponent, [{
        type: Component,
        args: [{ selector: 'stlr-autobubble', changeDetection: ChangeDetectionStrategy.OnPush, providers: [{ provide: STLR_BUBBLE, useExisting: StlrAutoBubbleComponent }], standalone: false, template: "@if (prefix) {\n  <span class=\"stlr-autobubble--prefix\">\n    <ng-content select=\"[stlrBubblePrefix]\"></ng-content>\n  </span>\n}\n\n<span class=\"stlr-autobubble--content-wrapper\">\n  <ng-content></ng-content>\n</span>\n\n@if (hasSuffix) {\n  <span class=\"stlr-autobubble--suffix\">\n    <ng-content select=\"[stlrBubblePopper], [stlrBubbleSuffix]\"></ng-content>\n  </span>\n}\n", styles: [":host{--bubble__padding: var(--space--2) var(--space--4);--bubble__font-size: var(--font--12);--bubble__border-radius: var(--stlr-border-radius);--bubble__light-bg: white;--bubble__light-color: var(--stlr-color__copy);--bubble__dark-bg: var(--stlr-color__copy);--bubble__dark-color: white;--bubble__primary-bg: var(--stlr-color__primary--light);--bubble__primary-color: var(--stlr-color__primary--extra-dark);--bubble__secondary-bg: var(--stlr-color__secondary--light);--bubble__secondary-color: var(--stlr-color__secondary--extra-dark);--bubble__info-bg: var(--stlr-color__info--light);--bubble__info-color: var(--stlr-color__info--extra-dark);--bubble__warn-bg: var(--stlr-color__warn--light);--bubble__warn-color: var(--stlr-color__warn--extra-dark);--bubble__error-bg: var(--stlr-color__error--light);--bubble__error-color: var(--stlr-color__error--extra-dark);--bubble__success-bg: var(--stlr-color__success--light);--bubble__success-color: var(--stlr-color__success--extra-dark);--bubble__subdued-bg: var(--stlr-color__surface--extra-light);--bubble__subdued-color: var(--stlr-color__surface--extra-dark);--bubble__disabled-bg: var(--stlr-color__neutral--100);--bubble__disabled-color: var(--stlr-color__neutral--800);display:inline-flex;align-items:center;justify-content:center;vertical-align:middle;padding:var(--bubble__padding);border-radius:var(--bubble__border-radius);font-size:var(--bubble__font-size)}:host .stlr-autobubble--content-wrapper{display:inline-flex;align-items:center;justify-content:center}:host.primary{background-color:var(--bubble__primary-bg);color:var(--bubble__primary-color)}:host.primary ::ng-deep .stlr-icon{color:var(--bubble__primary-color)!important}:host.secondary{background-color:var(--bubble__secondary-bg);color:var(--bubble__secondary-color)}:host.secondary ::ng-deep .stlr-icon{color:var(--bubble__secondary-color)!important}:host.info{background-color:var(--bubble__info-bg);color:var(--bubble__info-color)}:host.info ::ng-deep .stlr-icon{color:var(--bubble__info-color)!important}:host.error{background-color:var(--bubble__error-bg);color:var(--bubble__error-color)}:host.error ::ng-deep .stlr-icon{color:var(--bubble__error-color)!important}:host.warn{background-color:var(--bubble__warn-bg);color:var(--bubble__warn-color)}:host.warn ::ng-deep .stlr-icon{color:var(--bubble__warn-color)!important}:host.success{background-color:var(--bubble__success-bg);color:var(--bubble__success-color)}:host.success ::ng-deep .stlr-icon{color:var(--bubble__success-color)!important}:host.gray{background-color:var(--bubble__gray-bg);color:var(--bubble__gray-color)}:host.gray ::ng-deep .stlr-icon{color:var(--bubble__gray-color)!important}:host.surface{background-color:var(--bubble__surface-bg);color:var(--bubble__surface-color)}:host.surface ::ng-deep .stlr-icon{color:var(--bubble__surface-color)!important}:host.subdued{background-color:var(--bubble__subdued-bg);color:var(--bubble__subdued-color)}:host.subdued ::ng-deep .stlr-icon{color:var(--bubble__subdued-color)!important}:host.dark{background-color:var(--bubble__dark-bg);color:var(--bubble__dark-color)}:host.dark ::ng-deep .stlr-icon{color:var(--bubble__dark-color)!important}:host.light{background-color:var(--bubble__light-bg);color:var(--bubble__light-color)}:host.light ::ng-deep .stlr-icon{color:var(--bubble__light-color)!important}:host.link{background-color:var(--bubble__link-bg);color:var(--bubble__link-color)}:host.link ::ng-deep .stlr-icon{color:var(--bubble__link-color)!important}:host[disabled]{background-color:var(--bubble__disabled-bg);color:var(--bubble__disabled-color)}:host .stlr-autobubble--prefix{margin-right:.25rem}:host .stlr-autobubble--suffix{margin-left:.25rem}:host .stlr-autobubble--suffix ::ng-deep button,:host .stlr-autobubble--suffix ::ng-deep .stlr-autobubble-popper{appearance:none;padding:0;border:0;background:none;cursor:pointer;outline:none}\n"] }]
    }], () => [], { _hostClass: [{
            type: HostBinding,
            args: ['class.stlr-autobubble']
        }], disabled: [{
            type: HostBinding,
            args: ['class.stlr-autobubble--disabled']
        }, {
            type: Input
        }], id: [{
            type: HostBinding
        }, {
            type: Input
        }], removable: [{
            type: Input
        }], tabIndex: [{
            type: HostBinding
        }, {
            type: Input
        }], value: [{
            type: Input
        }], prefix: [{
            type: ContentChild,
            args: [STLR_BUBBLE_PREFIX]
        }], removeIcon: [{
            type: ContentChild,
            args: [STLR_BUBBLE_POPPER]
        }], suffix: [{
            type: ContentChild,
            args: [STLR_BUBBLE_SUFFIX]
        }], removed: [{
            type: Output
        }], _handleKeydown: [{
            type: HostListener,
            args: ['keydown', ['$event']]
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(StlrAutoBubbleComponent, { className: "StlrAutoBubbleComponent", filePath: "lib/autobubbler/components/autobubble/autobubble.component.ts", lineNumber: 34 }); })();

const _c0$c = [[["stlr-autobubble"]]];
const _c1$6 = ["stlr-autobubble"];
class StlrAutoBubbleGroupComponent {
    static { this.ɵfac = function StlrAutoBubbleGroupComponent_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || StlrAutoBubbleGroupComponent)(); }; }
    static { this.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: StlrAutoBubbleGroupComponent, selectors: [["stlr-autobubble-group"]], standalone: false, ngContentSelectors: _c1$6, decls: 2, vars: 0, consts: [[1, "stlr-autobubble-group__bubbles"]], template: function StlrAutoBubbleGroupComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵprojectionDef(_c0$c);
            i0.ɵɵelementStart(0, "div", 0);
            i0.ɵɵprojection(1);
            i0.ɵɵelementEnd();
        } }, styles: [".stlr-autobubble-group__bubbles[_ngcontent-%COMP%]{display:flex;flex-wrap:wrap}.stlr-autobubble-group__bubbles[_ngcontent-%COMP%]     .stlr-autobubble{margin:0 .5rem .5rem 0}"], changeDetection: 0 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrAutoBubbleGroupComponent, [{
        type: Component,
        args: [{ selector: 'stlr-autobubble-group', changeDetection: ChangeDetectionStrategy.OnPush, standalone: false, template: "<div class=\"stlr-autobubble-group__bubbles\">\n  <ng-content select=\"stlr-autobubble\"></ng-content>\n</div>\n", styles: [".stlr-autobubble-group__bubbles{display:flex;flex-wrap:wrap}.stlr-autobubble-group__bubbles ::ng-deep .stlr-autobubble{margin:0 .5rem .5rem 0}\n"] }]
    }], null, null); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(StlrAutoBubbleGroupComponent, { className: "StlrAutoBubbleGroupComponent", filePath: "lib/autobubbler/components/autobubble-group/autobubble-group.component.ts", lineNumber: 13 }); })();

class StlrBubblePopperDirective {
    _handleClick(event) {
        if (!this.disabled) {
            event.stopPropagation();
            event.preventDefault();
            this._parentBubble.remove();
        }
    }
    _handleKeydown(event) {
        if (!this.disabled && event.key === 'Enter') {
            event.stopPropagation();
            event.preventDefault();
            this._parentBubble.remove();
        }
    }
    get tabIndex() {
        return this.disabled
            ? undefined
            : this._tabIndex || this.defaultTabIndex;
    }
    set tabIndex(value) {
        this._tabIndex = value;
    }
    get disabled() {
        return this._disabled || this._parentBubble.disabled;
    }
    set disabled(value) {
        this._disabled = coerceBooleanProperty(value);
    }
    constructor() {
        this._renderer = inject(Renderer2);
        this._hostClass = true;
        this.defaultTabIndex = -1;
        this.elementRef = inject(ElementRef);
        this._disabled = false;
        this._parentBubble = inject(STLR_BUBBLE);
        if (this.elementRef.nativeElement.nodeName === 'BUTTON') {
            this._renderer.setAttribute(this.elementRef, 'type', 'button');
        }
    }
    focus() {
        this.elementRef.nativeElement.focus();
    }
    static { this.ɵfac = function StlrBubblePopperDirective_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || StlrBubblePopperDirective)(); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: StlrBubblePopperDirective, selectors: [["", "stlrBubblePopper", ""]], hostVars: 3, hostBindings: function StlrBubblePopperDirective_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("click", function StlrBubblePopperDirective_click_HostBindingHandler($event) { return ctx._handleClick($event); })("keydown", function StlrBubblePopperDirective_keydown_HostBindingHandler($event) { return ctx._handleKeydown($event); });
        } if (rf & 2) {
            i0.ɵɵdomProperty("tabIndex", ctx.tabIndex);
            i0.ɵɵclassProp("stlr-autobubble-popper", ctx._hostClass);
        } }, inputs: { tabIndex: "tabIndex", disabled: "disabled" }, standalone: false, features: [i0.ɵɵProvidersFeature([
                { provide: STLR_BUBBLE_POPPER, useExisting: StlrBubblePopperDirective },
            ])] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrBubblePopperDirective, [{
        type: Directive,
        args: [{
                selector: '[stlrBubblePopper]',
                providers: [
                    { provide: STLR_BUBBLE_POPPER, useExisting: StlrBubblePopperDirective },
                ],
                standalone: false
            }]
    }], () => [], { _hostClass: [{
            type: HostBinding,
            args: ['class.stlr-autobubble-popper']
        }], _handleClick: [{
            type: HostListener,
            args: ['click', ['$event']]
        }], _handleKeydown: [{
            type: HostListener,
            args: ['keydown', ['$event']]
        }], tabIndex: [{
            type: HostBinding
        }, {
            type: Input
        }], disabled: [{
            type: Input
        }] }); })();

function StlrAutobubblerComponent_For_2_Template(rf, ctx) { if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "stlr-autobubble", 3);
    i0.ɵɵlistener("removed", function StlrAutobubblerComponent_For_2_Template_stlr_autobubble_removed_0_listener($event) { i0.ɵɵrestoreView(_r1); const ctx_r1 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r1.remove($event)); });
    i0.ɵɵtext(1);
    i0.ɵɵelementStart(2, "stlr-icon", 4);
    i0.ɵɵtext(3, "close");
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const bubble_r3 = ctx.$implicit;
    i0.ɵɵproperty("value", bubble_r3);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", bubble_r3, " ");
} }
function StlrAutobubblerComponent_For_5_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "option", 2);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const option_r4 = ctx.$implicit;
    i0.ɵɵproperty("value", option_r4.value);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", option_r4.displayText, " ");
} }
class StlrAutobubblerComponent extends StlrFormComponentValueAccessor {
    constructor() {
        super(...arguments);
        this.controlType = 'stlr-autobubbler';
        this._contentInitialized = false;
        this._elementRef = inject(ElementRef);
        this.selectableOptionsSubject = new ReplaySubject(1);
        this.selectableOptions$ = this.selectableOptionsSubject.asObservable();
        this._hostClass = true;
        /**
         * Allow the user to input their own options.
         * Default = true.
         */
        this.allowNewOptions = true;
        /**
         * Automatically applies a filter based on user input.
         * NOTE: Do not apply auto-filtering in tandem with custom (external) filtering.
         * Default = true.
         */
        this.autoFilter = true;
        /**
         * Determines how auto-filtering matches user input with options.
         * Default = 'contains'.
         */
        this.autoFilterMatching = 'contains';
        /**
         * Closes the drop down when the user selects an item the list. Allowing the panel to remain
         * open would allow the user to continue selecting more options without going to the trouble
         * of reopening the drop down.
         * Default = false.
         */
        this.closeOnAdd = false;
        /**
         * Allow clicking the input element to close the drop down if it is open.
         * Default = true.
         */
        this.closeOnClick = true;
        /**
         * Used by TypeAhead to determine how to match the user's input with options in the list.
         * The first item in the returned array is selected. This overrides the built-in matching
         * functionality and does not apply to the auto-filter.
         * Default = compare option display text against the user's input using startsWith.
         * @param options A list of StlrOptionComponent.
         * @param matchOn Text input by the user (input.value).
         * @returns An array of values that match the user's input.
         */
        this.matchFn = (options, text) => options.filter(opt => opt.displayText.toLowerCase().startsWith(text.toLowerCase()))
            .map(opt => opt.value);
        /**
         * Show options when clicking on the input element.
         * Default = true.
         */
        this.openOnClick = true;
        /**
         * Show options when entering (e.g. tabbing into) the input element.
         * Default = false.
         */
        this.openOnFocus = false;
        /**
         * Show options when inputting characters into the input element.
         * Default = false.
         */
        this.openOnInput = false;
        /**
         * Filters out selected items.
         * Default = false.
         */
        this.updateOptions = false;
        /**
         * Automatically fill the input element with the first matching item in the option list as the user types,
         * allowing the user to continue typing with further matches continually updating the input field.
         * Default = false.
         */
        this.useTypeAhead = false;
    }
    /**
     * Handles focusing out of this component for _onTouched() and updateOn:blur.
     */
    onFocusOut(event) {
        const element = event.relatedTarget;
        if (element == null ||
            (!this._elementRef.nativeElement.contains(element)
                && element !== this.autocompleteComp?.panelRef?.nativeElement
                && !this.autocompleteComp?.optionRefs?.toArray().map(opt => opt.nativeElement).includes(element))) {
            if (this.ngControl?.control?.updateOn === 'blur')
                this._onChange(this.value);
            this._onTouched();
        }
    }
    ngAfterContentInit() {
        this._contentInitialized = true;
        this.updateSelectableOptions();
    }
    ngOnChanges(changes) {
        if (changes['updateOptions']) {
            this.updateSelectableOptions();
        }
        if (changes['closeOnAdd'] && this.autocompleteComp) {
            this.autocompleteComp.closeOnSelect = changes['closeOnAdd'].currentValue;
        }
    }
    get empty() {
        return !this.value?.length;
    }
    get value() {
        return super.value;
    }
    set value(value) {
        super.value = value;
        this.autocompleteComp?.clear();
        this.updateSelectableOptions();
    }
    add(text) {
        if (!text)
            return;
        if (this.value?.includes(text)) {
            this.autocompleteComp?.clear();
            return;
        }
        const value = this.value ? [...this.value, text] : [text];
        this._handleChange(value);
    }
    remove(item) {
        const index = this.value?.indexOf(item.value) ?? -1;
        if (this.value && index > -1) {
            const updatedItems = [
                ...this.value.slice(0, index),
                ...this.value.slice(index + 1)
            ];
            this._handleChange(updatedItems);
        }
    }
    reset(value) {
        this._handleChange(value ?? null);
        this.autocompleteComp?.closePanel();
    }
    _handleChange(value) {
        const hasChanged = !isEqual(this.value, value);
        this.value = value;
        if (hasChanged) {
            if (this.ngControl?.control?.updateOn === 'change')
                this._onChange(this.value);
            this.changed.emit(this.value);
        }
    }
    updateSelectableOptions() {
        if (!this._contentInitialized)
            return;
        const options = this.updateOptions && this.value && this.options
            ? this.options.filter(opt => !this.value.some(val => val === opt.value)) ?? []
            : this.options?.toArray() ?? [];
        this.selectableOptionsSubject.next(options);
    }
    static { this.ɵfac = /*@__PURE__*/ (() => { let ɵStlrAutobubblerComponent_BaseFactory; return function StlrAutobubblerComponent_Factory(__ngFactoryType__) { return (ɵStlrAutobubblerComponent_BaseFactory || (ɵStlrAutobubblerComponent_BaseFactory = i0.ɵɵgetInheritedFactory(StlrAutobubblerComponent)))(__ngFactoryType__ || StlrAutobubblerComponent); }; })(); }
    static { this.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: StlrAutobubblerComponent, selectors: [["stlr-autobubbler"]], contentQueries: function StlrAutobubblerComponent_ContentQueries(rf, ctx, dirIndex) { if (rf & 1) {
            i0.ɵɵcontentQuery(dirIndex, StlrOptionComponent, 4);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.options = _t);
        } }, viewQuery: function StlrAutobubblerComponent_Query(rf, ctx) { if (rf & 1) {
            i0.ɵɵviewQuery(StlrAutocompleteComponent, 7);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.autocompleteComp = _t.first);
        } }, hostVars: 2, hostBindings: function StlrAutobubblerComponent_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("focusout", function StlrAutobubblerComponent_focusout_HostBindingHandler($event) { return ctx.onFocusOut($event); });
        } if (rf & 2) {
            i0.ɵɵclassProp("stlr-autobubbler", ctx._hostClass);
        } }, inputs: { allowNewOptions: "allowNewOptions", autoFilter: "autoFilter", autoFilterMatching: "autoFilterMatching", closeOnAdd: "closeOnAdd", closeOnClick: "closeOnClick", matchFn: "matchFn", openOnClick: "openOnClick", openOnFocus: "openOnFocus", openOnInput: "openOnInput", updateOptions: "updateOptions", useTypeAhead: "useTypeAhead" }, standalone: false, features: [i0.ɵɵProvidersFeature([
                {
                    provide: STLR_FORM_COMPONENT_BASE,
                    useExisting: forwardRef(() => StlrAutobubblerComponent),
                    multi: true
                },
                {
                    provide: STLR_OPTION_PARENT_COMPONENT,
                    useExisting: forwardRef(() => StlrAutobubblerComponent),
                    multi: true
                },
            ]), i0.ɵɵInheritDefinitionFeature, i0.ɵɵNgOnChangesFeature], decls: 7, vars: 26, consts: [["color", "subdued", "removable", "true", 3, "value"], [3, "changed", "optionCreated", "allowNewOptions", "allowPartialMatching", "applyFilterOnOpen", "aria-describedby", "aria-label", "aria-labelledby", "autocomplete", "autoFilter", "autoFilterMatching", "closeOnClick", "disabled", "enterKeyHint", "id", "invalid", "matchFn", "name", "openOnClick", "openOnFocus", "openOnInput", "placeholder", "revertOnNoMatch", "required", "tabindex", "useTypeAhead"], ["stlrOption", "", 3, "value"], ["color", "subdued", "removable", "true", 3, "removed", "value"], ["size", "xs", "stlrBubblePopper", ""]], template: function StlrAutobubblerComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵelementStart(0, "stlr-autobubble-group");
            i0.ɵɵrepeaterCreate(1, StlrAutobubblerComponent_For_2_Template, 4, 2, "stlr-autobubble", 0, i0.ɵɵrepeaterTrackByIdentity);
            i0.ɵɵelementEnd();
            i0.ɵɵelementStart(3, "stlr-autocomplete", 1);
            i0.ɵɵlistener("changed", function StlrAutobubblerComponent_Template_stlr_autocomplete_changed_3_listener($event) { return ctx.add($event); })("optionCreated", function StlrAutobubblerComponent_Template_stlr_autocomplete_optionCreated_3_listener($event) { return ctx.add($event); });
            i0.ɵɵrepeaterCreate(4, StlrAutobubblerComponent_For_5_Template, 2, 2, "option", 2, i0.ɵɵrepeaterTrackByIdentity);
            i0.ɵɵpipe(6, "async");
            i0.ɵɵelementEnd();
        } if (rf & 2) {
            i0.ɵɵadvance();
            i0.ɵɵrepeater(ctx.value);
            i0.ɵɵadvance(2);
            i0.ɵɵproperty("allowNewOptions", ctx.allowNewOptions)("allowPartialMatching", false)("applyFilterOnOpen", false);
            i0.ɵɵariaProperty("aria-describedby", ctx.ariaDescribedBy)("aria-label", ctx.ariaLabel)("aria-labelledby", ctx.ariaLabelledBy);
            i0.ɵɵproperty("autocomplete", "off")("autoFilter", ctx.autoFilter)("autoFilterMatching", ctx.autoFilterMatching)("closeOnClick", ctx.closeOnClick)("disabled", ctx.disabled)("enterKeyHint", ctx.enterKeyHint)("id", ctx.id)("invalid", ctx.invalid)("matchFn", ctx.matchFn)("name", ctx.name)("openOnClick", ctx.openOnClick)("openOnFocus", ctx.openOnFocus)("openOnInput", ctx.openOnInput)("placeholder", ctx.placeholder)("revertOnNoMatch", false)("required", ctx.required)("tabindex", ctx.tabindex)("useTypeAhead", ctx.useTypeAhead);
            i0.ɵɵadvance();
            i0.ɵɵrepeater(i0.ɵɵpipeBind1(6, 24, ctx.selectableOptions$));
        } }, dependencies: [i1.NgSelectOption, i1.ɵNgSelectMultipleOption, i2.StlrIconComponent, StlrAutoBubbleComponent, StlrAutoBubbleGroupComponent, StlrBubblePopperDirective, StlrAutocompleteComponent, StlrOptionComponent, i1$1.AsyncPipe], styles: [".ng-touched.ng-invalid[_nghost-%COMP%]     input:not(.disabled):not(:focus), .ng-dirty.ng-invalid[_nghost-%COMP%]     input:not(.disabled):not(:focus){border-color:var(--stlr-color__error);background-color:var(--stlr-color__error--extra-light)}"], changeDetection: 0 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrAutobubblerComponent, [{
        type: Component,
        args: [{ selector: 'stlr-autobubbler', changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                    {
                        provide: STLR_FORM_COMPONENT_BASE,
                        useExisting: forwardRef(() => StlrAutobubblerComponent),
                        multi: true
                    },
                    {
                        provide: STLR_OPTION_PARENT_COMPONENT,
                        useExisting: forwardRef(() => StlrAutobubblerComponent),
                        multi: true
                    },
                ], standalone: false, template: "<stlr-autobubble-group>\n  @for (bubble of value; track bubble) {\n    <stlr-autobubble\n      color=\"subdued\"\n      removable=\"true\"\n      (removed)=\"remove($event)\"\n      [value]=\"bubble\"\n      >\n      {{ bubble }}\n      <stlr-icon size=\"xs\" stlrBubblePopper>close</stlr-icon>\n    </stlr-autobubble>\n  }\n</stlr-autobubble-group>\n\n<stlr-autocomplete\n  (changed)=\"add($event)\"\n  (optionCreated)=\"add($event)\"\n  [allowNewOptions]=\"allowNewOptions\"\n  [allowPartialMatching]=\"false\"\n  [applyFilterOnOpen]=\"false\"\n  [aria-describedby]=\"ariaDescribedBy\"\n  [aria-label]=\"ariaLabel\"\n  [aria-labelledby]=\"ariaLabelledBy\"\n  [autocomplete]=\"'off'\"\n  [autoFilter]=\"autoFilter\"\n  [autoFilterMatching]=\"autoFilterMatching\"\n  [closeOnClick]=\"closeOnClick\"\n  [disabled]=\"disabled\"\n  [enterKeyHint]=\"enterKeyHint\"\n  [id]=\"id\"\n  [invalid]=\"invalid\"\n  [matchFn]=\"matchFn\"\n  [name]=\"name\"\n  [openOnClick]=\"openOnClick\"\n  [openOnFocus]=\"openOnFocus\"\n  [openOnInput]=\"openOnInput\"\n  [placeholder]=\"placeholder\"\n  [revertOnNoMatch]=\"false\"\n  [required]=\"required\"\n  [tabindex]=\"tabindex\"\n  [useTypeAhead]=\"useTypeAhead\"\n  >\n  @for (option of selectableOptions$ | async; track option) {\n    <option stlrOption [value]=\"option.value\">\n      {{ option.displayText }}\n    </option>\n  }\n</stlr-autocomplete>\n", styles: [":host.ng-touched.ng-invalid ::ng-deep input:not(.disabled):not(:focus),:host.ng-dirty.ng-invalid ::ng-deep input:not(.disabled):not(:focus){border-color:var(--stlr-color__error);background-color:var(--stlr-color__error--extra-light)}\n"] }]
    }], null, { _hostClass: [{
            type: HostBinding,
            args: ['class.stlr-autobubbler']
        }], onFocusOut: [{
            type: HostListener,
            args: ['focusout', ['$event']]
        }], options: [{
            type: ContentChildren,
            args: [StlrOptionComponent]
        }], autocompleteComp: [{
            type: ViewChild,
            args: [StlrAutocompleteComponent, { static: true }]
        }], allowNewOptions: [{
            type: Input
        }], autoFilter: [{
            type: Input
        }], autoFilterMatching: [{
            type: Input
        }], closeOnAdd: [{
            type: Input
        }], closeOnClick: [{
            type: Input
        }], matchFn: [{
            type: Input
        }], openOnClick: [{
            type: Input
        }], openOnFocus: [{
            type: Input
        }], openOnInput: [{
            type: Input
        }], updateOptions: [{
            type: Input
        }], useTypeAhead: [{
            type: Input
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(StlrAutobubblerComponent, { className: "StlrAutobubblerComponent", filePath: "lib/autobubbler/autobubbler.component.ts", lineNumber: 46 }); })();

class StlrBubblePrefixDirective {
    constructor() {
        this._hostClass = true;
    }
    static { this.ɵfac = function StlrBubblePrefixDirective_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || StlrBubblePrefixDirective)(); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: StlrBubblePrefixDirective, selectors: [["", "stlrBubblePrefix", ""]], hostVars: 2, hostBindings: function StlrBubblePrefixDirective_HostBindings(rf, ctx) { if (rf & 2) {
            i0.ɵɵclassProp("stlr-bubble-prefix", ctx._hostClass);
        } }, standalone: false, features: [i0.ɵɵProvidersFeature([
                { provide: STLR_BUBBLE_PREFIX, useExisting: StlrBubblePrefixDirective },
            ])] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrBubblePrefixDirective, [{
        type: Directive,
        args: [{
                selector: '[stlrBubblePrefix]',
                providers: [
                    { provide: STLR_BUBBLE_PREFIX, useExisting: StlrBubblePrefixDirective },
                ],
                standalone: false
            }]
    }], null, { _hostClass: [{
            type: HostBinding,
            args: ['class.stlr-bubble-prefix']
        }] }); })();

class StlrBubbleSuffixDirective {
    constructor() {
        this._hostClass = true;
    }
    static { this.ɵfac = function StlrBubbleSuffixDirective_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || StlrBubbleSuffixDirective)(); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: StlrBubbleSuffixDirective, selectors: [["", "stlrBubbleSuffix", ""]], hostVars: 2, hostBindings: function StlrBubbleSuffixDirective_HostBindings(rf, ctx) { if (rf & 2) {
            i0.ɵɵclassProp("stlr-bubble-suffix", ctx._hostClass);
        } }, standalone: false, features: [i0.ɵɵProvidersFeature([
                { provide: STLR_BUBBLE_SUFFIX, useExisting: StlrBubbleSuffixDirective },
            ])] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrBubbleSuffixDirective, [{
        type: Directive,
        args: [{
                selector: '[stlrBubbleSuffix]',
                providers: [
                    { provide: STLR_BUBBLE_SUFFIX, useExisting: StlrBubbleSuffixDirective },
                ],
                standalone: false
            }]
    }], null, { _hostClass: [{
            type: HostBinding,
            args: ['class.stlr-bubble-suffix']
        }] }); })();

const AUTOBUBBLER_API = [
    StlrAutoBubbleComponent,
    StlrAutoBubbleGroupComponent,
    StlrAutobubblerComponent,
    StlrBubblePrefixDirective,
    StlrBubblePopperDirective,
    StlrBubbleSuffixDirective,
];

const _c0$b = ["*"];
class StlrCheckboxComponent extends StlrFormComponentValueAccessor {
    constructor() {
        super(...arguments);
        this.controlType = 'stlr-checkbox';
    }
    set checked(value) {
        super.value = value;
    }
    get checked() {
        return super.value ?? false;
    }
    handleChange(event) {
        const checked = event.target.checked;
        const hasChanged = !isEqual(this.checked, checked);
        this.checked = checked;
        if (hasChanged) {
            if (this.ngControl?.control?.updateOn === 'change')
                this._onChange(this.checked);
            this.changed.emit(this.checked);
        }
    }
    handleBlur() {
        if (this.ngControl?.control?.updateOn === 'blur')
            this._onChange(this.value);
        this._onTouched();
    }
    static { this.ɵfac = /*@__PURE__*/ (() => { let ɵStlrCheckboxComponent_BaseFactory; return function StlrCheckboxComponent_Factory(__ngFactoryType__) { return (ɵStlrCheckboxComponent_BaseFactory || (ɵStlrCheckboxComponent_BaseFactory = i0.ɵɵgetInheritedFactory(StlrCheckboxComponent)))(__ngFactoryType__ || StlrCheckboxComponent); }; })(); }
    static { this.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: StlrCheckboxComponent, selectors: [["stlr-checkbox"]], inputs: { data: "data", checked: [2, "checked", "checked", (value) => coerceBooleanProperty(value)] }, standalone: false, features: [i0.ɵɵProvidersFeature([
                {
                    provide: STLR_FORM_COMPONENT_BASE,
                    useExisting: forwardRef(() => StlrCheckboxComponent),
                    multi: true
                },
            ]), i0.ɵɵInheritDefinitionFeature], ngContentSelectors: _c0$b, decls: 5, vars: 18, consts: [["input", ""], ["type", "checkbox", 3, "blur", "change", "checked", "disabled", "id", "name", "required", "tabindex"], [1, "label-container"]], template: function StlrCheckboxComponent_Template(rf, ctx) { if (rf & 1) {
            const _r1 = i0.ɵɵgetCurrentView();
            i0.ɵɵprojectionDef();
            i0.ɵɵelementStart(0, "label")(1, "input", 1, 0);
            i0.ɵɵlistener("blur", function StlrCheckboxComponent_Template_input_blur_1_listener() { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.handleBlur()); })("change", function StlrCheckboxComponent_Template_input_change_1_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.handleChange($event)); });
            i0.ɵɵelementEnd();
            i0.ɵɵelementStart(3, "span", 2);
            i0.ɵɵprojection(4);
            i0.ɵɵelementEnd()();
        } if (rf & 2) {
            i0.ɵɵattribute("disabled", ctx.disabled ? "" : null);
            i0.ɵɵadvance();
            i0.ɵɵclassProp("disabled", ctx.disabled)("invalid", ctx.invalid);
            i0.ɵɵproperty("checked", ctx.checked)("disabled", ctx.disabled)("id", ctx.id)("name", ctx.name)("required", ctx.required)("tabindex", ctx.tabindex);
            i0.ɵɵattribute("aria-checked", ctx.value ?? false)("aria-describedby", ctx.ariaDescribedBy)("aria-disabled", ctx.disabled)("aria-invalid", ctx.invalid)("aria-label", ctx.ariaLabel)("aria-labelledby", ctx.ariaLabelledBy)("aria-required", ctx.required);
        } }, styles: [".ng-touched.ng-invalid[_nghost-%COMP%]   label[_ngcontent-%COMP%] > input[_ngcontent-%COMP%]:not(.disabled), .ng-dirty.ng-invalid[_nghost-%COMP%]   label[_ngcontent-%COMP%] > input[_ngcontent-%COMP%]:not(.disabled){color:var(--stlr-color__error)}.ng-touched.ng-invalid[_nghost-%COMP%]   input[_ngcontent-%COMP%]:not(.disabled), .ng-dirty.ng-invalid[_nghost-%COMP%]   input[_ngcontent-%COMP%]:not(.disabled){border-color:var(--stlr-color__error)}[_nghost-%COMP%]   label[_ngcontent-%COMP%]{position:relative;display:inline-flex;align-items:center;font-size:.85em;line-height:1;transition:all .2s;cursor:pointer}[_nghost-%COMP%]   label[_ngcontent-%COMP%]   input[_ngcontent-%COMP%]{z-index:2}[_nghost-%COMP%]   label[_ngcontent-%COMP%]{display:flex;flex-direction:row;justify-content:flex-start;align-items:center}[_nghost-%COMP%]   label[disabled][_ngcontent-%COMP%]{cursor:inherit}[_nghost-%COMP%]   label[disabled][_ngcontent-%COMP%]:after{opacity:0}[_nghost-%COMP%]   label[_ngcontent-%COMP%]   input[_ngcontent-%COMP%]{position:relative;height:16px;width:16px;background-color:#fff;border:2px solid var(--stlr-color__info--dark);border-radius:3px;appearance:none;outline:none;transition:all .2s;flex-shrink:0;cursor:pointer}[_nghost-%COMP%]   label[_ngcontent-%COMP%]   input[_ngcontent-%COMP%]:checked{background-color:var(--stlr-color__info);border:2px solid var(--stlr-color__info)}[_nghost-%COMP%]   label[_ngcontent-%COMP%]   input[disabled][_ngcontent-%COMP%]{color:var(--stlr-color__disabled);border:solid 2px var(--stlr-color__disabled);background-color:#fff3;cursor:default}[_nghost-%COMP%]   label[_ngcontent-%COMP%]   input[_ngcontent-%COMP%]{margin:0}[_nghost-%COMP%]   label[_ngcontent-%COMP%]   input[_ngcontent-%COMP%]:before{font-family:Material Icons;content:\"check\";position:absolute;left:-2px;top:-2px;text-align:center;color:#fff;font-size:16px;font-weight:400;font-style:normal;line-height:1;text-transform:none;letter-spacing:normal;word-wrap:normal;white-space:nowrap;direction:ltr;display:none;-webkit-font-smoothing:antialiased;text-rendering:optimizeLegibility;-moz-osx-font-smoothing:grayscale;font-feature-settings:\"liga\"}[_nghost-%COMP%]   label[_ngcontent-%COMP%]   input[_ngcontent-%COMP%]:checked:before{display:inline-block}[_nghost-%COMP%]   label[_ngcontent-%COMP%]   input[_ngcontent-%COMP%]:disabled:before{color:var(--stlr-color__disabled)}[_nghost-%COMP%]   label[_ngcontent-%COMP%]:focus-within   input[_ngcontent-%COMP%]{outline:solid 2px var(--stlr-color__info);outline-offset:4px}[_nghost-%COMP%]   .label-container[_ngcontent-%COMP%]{margin-inline-start:var(--space--8)}"], changeDetection: 0 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrCheckboxComponent, [{
        type: Component,
        args: [{ selector: 'stlr-checkbox', changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                    {
                        provide: STLR_FORM_COMPONENT_BASE,
                        useExisting: forwardRef(() => StlrCheckboxComponent),
                        multi: true
                    },
                ], standalone: false, template: "<label [attr.disabled]=\"disabled ? '' : null\">\n    <input\n        #input\n        type=\"checkbox\"\n        (blur)=\"handleBlur()\"\n        (change)=\"handleChange($event)\"\n        [attr.aria-checked]=\"value ?? false\"\n        [attr.aria-describedby]=\"ariaDescribedBy\"\n        [attr.aria-disabled]=\"disabled\"\n        [attr.aria-invalid]=\"invalid\"\n        [attr.aria-label]=\"ariaLabel\"\n        [attr.aria-labelledby]=\"ariaLabelledBy\"\n        [attr.aria-required]=\"required\"\n        [class.disabled]=\"disabled\"\n        [class.invalid]=\"invalid\"\n        [checked]=\"checked\"\n        [disabled]=\"disabled\"\n        [id]=\"id\"\n        [name]=\"name\"\n        [required]=\"required\"\n        [tabindex]=\"tabindex\"\n    />\n    <span class=\"label-container\">\n        <ng-content></ng-content>\n    </span>\n</label>", styles: [":host.ng-touched.ng-invalid label>input:not(.disabled),:host.ng-dirty.ng-invalid label>input:not(.disabled){color:var(--stlr-color__error)}:host.ng-touched.ng-invalid input:not(.disabled),:host.ng-dirty.ng-invalid input:not(.disabled){border-color:var(--stlr-color__error)}:host label{position:relative;display:inline-flex;align-items:center;font-size:.85em;line-height:1;transition:all .2s;cursor:pointer}:host label input{z-index:2}:host label{display:flex;flex-direction:row;justify-content:flex-start;align-items:center}:host label[disabled]{cursor:inherit}:host label[disabled]:after{opacity:0}:host label input{position:relative;height:16px;width:16px;background-color:#fff;border:2px solid var(--stlr-color__info--dark);border-radius:3px;appearance:none;outline:none;transition:all .2s;flex-shrink:0;cursor:pointer}:host label input:checked{background-color:var(--stlr-color__info);border:2px solid var(--stlr-color__info)}:host label input[disabled]{color:var(--stlr-color__disabled);border:solid 2px var(--stlr-color__disabled);background-color:#fff3;cursor:default}:host label input{margin:0}:host label input:before{font-family:Material Icons;content:\"check\";position:absolute;left:-2px;top:-2px;text-align:center;color:#fff;font-size:16px;font-weight:400;font-style:normal;line-height:1;text-transform:none;letter-spacing:normal;word-wrap:normal;white-space:nowrap;direction:ltr;display:none;-webkit-font-smoothing:antialiased;text-rendering:optimizeLegibility;-moz-osx-font-smoothing:grayscale;font-feature-settings:\"liga\"}:host label input:checked:before{display:inline-block}:host label input:disabled:before{color:var(--stlr-color__disabled)}:host label:focus-within input{outline:solid 2px var(--stlr-color__info);outline-offset:4px}:host .label-container{margin-inline-start:var(--space--8)}\n"] }]
    }], null, { data: [{
            type: Input
        }], checked: [{
            type: Input,
            args: [{ transform: (value) => coerceBooleanProperty(value) }]
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(StlrCheckboxComponent, { className: "StlrCheckboxComponent", filePath: "lib/checkbox/checkbox.component.ts", lineNumber: 27 }); })();

const CHECKBOX_API = [StlrCheckboxComponent];

class StlrHintDirective {
    constructor() {
        this._hostClass = true;
        this.id = `stlr-hint-${generateUUID()}`;
        this.align = 'start';
    }
    get _hostClassEnd() {
        return this.align === 'end';
    }
    static { this.ɵfac = function StlrHintDirective_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || StlrHintDirective)(); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: StlrHintDirective, selectors: [["stlr-hint", 3, "validationFor", ""]], hostVars: 5, hostBindings: function StlrHintDirective_HostBindings(rf, ctx) { if (rf & 2) {
            i0.ɵɵdomProperty("id", ctx.id);
            i0.ɵɵclassProp("stlr-hint", ctx._hostClass)("stlr-hint--end", ctx._hostClassEnd);
        } }, inputs: { id: "id", align: "align" }, standalone: false }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrHintDirective, [{
        type: Directive,
        args: [{
                // eslint-disable-next-line @angular-eslint/directive-selector
                selector: 'stlr-hint:not([validationFor])',
                standalone: false
            }]
    }], null, { _hostClass: [{
            type: HostBinding,
            args: ['class.stlr-hint']
        }], _hostClassEnd: [{
            type: HostBinding,
            args: ['class.stlr-hint--end']
        }], id: [{
            type: HostBinding
        }, {
            type: Input
        }], align: [{
            type: Input
        }] }); })();

class StlrLabelDirective {
    static { this.ɵfac = function StlrLabelDirective_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || StlrLabelDirective)(); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: StlrLabelDirective, selectors: [["stlr-label"]], inputs: { size: "size", labelFor: "labelFor" }, standalone: false }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrLabelDirective, [{
        type: Directive,
        args: [{
                // eslint-disable-next-line @angular-eslint/directive-selector
                selector: 'stlr-label',
                standalone: false
            }]
    }], null, { size: [{
            type: Input
        }], labelFor: [{
            type: Input
        }] }); })();

const STLR_PREFIX = new InjectionToken('STLR_PREFIX');
class StlrPrefixDirective {
    constructor() {
        this._hostClass = true;
    }
    static { this.ɵfac = function StlrPrefixDirective_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || StlrPrefixDirective)(); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: StlrPrefixDirective, selectors: [["", "stlrFormFieldPrefix", ""]], hostVars: 2, hostBindings: function StlrPrefixDirective_HostBindings(rf, ctx) { if (rf & 2) {
            i0.ɵɵclassProp("stlr-form-field--prefix", ctx._hostClass);
        } }, standalone: false, features: [i0.ɵɵProvidersFeature([{ provide: STLR_PREFIX, useExisting: StlrPrefixDirective }])] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrPrefixDirective, [{
        type: Directive,
        args: [{
                selector: '[stlrFormFieldPrefix]',
                providers: [{ provide: STLR_PREFIX, useExisting: StlrPrefixDirective }],
                standalone: false
            }]
    }], null, { _hostClass: [{
            type: HostBinding,
            args: ['class.stlr-form-field--prefix']
        }] }); })();

const STLR_SUFFIX = new InjectionToken('STLR_SUFFIX');
class StlrSuffixDirective {
    constructor() {
        this._hostClass = true;
    }
    static { this.ɵfac = function StlrSuffixDirective_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || StlrSuffixDirective)(); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: StlrSuffixDirective, selectors: [["", "stlrFormFieldSuffix", ""]], hostVars: 2, hostBindings: function StlrSuffixDirective_HostBindings(rf, ctx) { if (rf & 2) {
            i0.ɵɵclassProp("stlr-form-field--suffix", ctx._hostClass);
        } }, standalone: false, features: [i0.ɵɵProvidersFeature([{ provide: STLR_SUFFIX, useExisting: StlrSuffixDirective }])] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrSuffixDirective, [{
        type: Directive,
        args: [{
                selector: '[stlrFormFieldSuffix]',
                providers: [{ provide: STLR_SUFFIX, useExisting: StlrSuffixDirective }],
                standalone: false
            }]
    }], null, { _hostClass: [{
            type: HostBinding,
            args: ['class.stlr-form-field--suffix']
        }] }); })();

const _c0$a = ["*", [["stlr-label"]], [["", "stlrFormFieldPrefix", ""]], [["", "stlrFormFieldSuffix", ""]], [["stlr-hint", 3, "align", "end"]], [["stlr-hint", "align", "end"]], [["stlr-error"]]];
const _c1$5 = ["*", "stlr-label", "[stlrFormFieldPrefix]", "[stlrFormFieldSuffix]", "stlr-hint:not([align='end'])", "stlr-hint[align='end']", "stlr-error"];
function StlrFormFieldComponent_Conditional_1_Conditional_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 6);
    i0.ɵɵtext(1, " * ");
    i0.ɵɵelementEnd();
} }
function StlrFormFieldComponent_Conditional_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "label");
    i0.ɵɵprojection(1, 1);
    i0.ɵɵconditionalCreate(2, StlrFormFieldComponent_Conditional_1_Conditional_2_Template, 2, 0, "span", 6);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵattribute("id", ctx_r0.labelId)("for", ctx_r0._formComponent == null ? null : ctx_r0._formComponent.id);
    i0.ɵɵadvance(2);
    i0.ɵɵconditional(ctx_r0.showRequired ? 2 : -1);
} }
function StlrFormFieldComponent_Conditional_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 2);
    i0.ɵɵprojection(1, 2);
    i0.ɵɵelementEnd();
} }
function StlrFormFieldComponent_Conditional_6_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 4);
    i0.ɵɵprojection(1, 3);
    i0.ɵɵelementEnd();
} }
function StlrFormFieldComponent_Conditional_7_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 5)(1, "div", 7)(2, "div", 8);
    i0.ɵɵprojection(3, 4);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(4, "div", 9);
    i0.ɵɵprojection(5, 5);
    i0.ɵɵelementEnd()();
    i0.ɵɵelementStart(6, "div", 10)(7, "div", 8);
    i0.ɵɵprojection(8, 6);
    i0.ɵɵelementEnd()()();
} }
class StlrFormFieldComponent {
    constructor() {
        this._destroyRef = inject(DestroyRef);
        this.elementRef = inject(ElementRef);
        this.labelId = `stlr-label-${generateUUID(2)}`;
        this._hasSubscriptSubject = new ReplaySubject(1);
        this.hasSubscript$ = this._hasSubscriptSubject.asObservable();
        this.orientation = 'vertical';
        this.showRequiredIndicator = false;
        //#region Host Bindings
        this._hostClass = true;
    }
    get _disabledClass() {
        return Boolean(this._ngControl?.disabled);
    }
    get _fullWidthClass() {
        return this.width === 'full';
    }
    //#endregion
    //#region Form Class Host Bindings
    get _isNgUntouched() {
        return Boolean(this._ngControl?.untouched);
    }
    get _isNgTouched() {
        return Boolean(this._ngControl?.touched);
    }
    get _isNgPristine() {
        return Boolean(this._ngControl?.pristine);
    }
    get _isNgDirty() {
        return Boolean(this._ngControl?.dirty);
    }
    get _isNgValid() {
        return Boolean(this._ngControl?.valid);
    }
    get _isNgInvalid() {
        return Boolean(this._ngControl?.invalid);
    }
    //#endregion
    ngAfterContentInit() {
        if (this.hasLabel && this._formComponent) {
            this._formComponent.ariaDescribedBy = this.labelId;
        }
        // startWith forces an initial emission since ".changes" does
        // not fire when the query-list is first initialized at this
        // point.
        combineLatest([
            this._hintChildren.changes.pipe(startWith(0)),
            this._errorChildren.changes.pipe(startWith(0))
        ]).pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
            this._hasSubscriptSubject.next(Boolean(this._hintChildren.length) ||
                Boolean(this._errorChildren.length));
        });
    }
    get showRequired() {
        return this.showRequiredIndicator &&
            Boolean(this._ngControl?.errors && this._ngControl.errors['required']);
    }
    get hasLabel() {
        return Boolean(this._labelChild);
    }
    get hasPrefix() {
        return Boolean(this._prefixChild);
    }
    get hasSuffix() {
        return Boolean(this._suffixChild);
    }
    static { this.ɵfac = function StlrFormFieldComponent_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || StlrFormFieldComponent)(); }; }
    static { this.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: StlrFormFieldComponent, selectors: [["stlr-form-field"]], contentQueries: function StlrFormFieldComponent_ContentQueries(rf, ctx, dirIndex) { if (rf & 1) {
            i0.ɵɵcontentQuery(dirIndex, STLR_FORM_COMPONENT_BASE, 5);
            i0.ɵɵcontentQuery(dirIndex, NgControl, 5);
            i0.ɵɵcontentQuery(dirIndex, StlrLabelDirective, 5);
            i0.ɵɵcontentQuery(dirIndex, STLR_PREFIX, 5);
            i0.ɵɵcontentQuery(dirIndex, STLR_SUFFIX, 5);
            i0.ɵɵcontentQuery(dirIndex, StlrHintDirective, 5);
            i0.ɵɵcontentQuery(dirIndex, STLR_ERROR, 5);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._formComponent = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._ngControl = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._labelChild = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._prefixChild = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._suffixChild = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._hintChildren = _t);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._errorChildren = _t);
        } }, hostVars: 18, hostBindings: function StlrFormFieldComponent_HostBindings(rf, ctx) { if (rf & 2) {
            i0.ɵɵclassProp("stlr-form-field", ctx._hostClass)("stlr-form-field--disabled", ctx._disabledClass)("stlr-form-field--full-width", ctx._fullWidthClass)("ng-untouched", ctx._isNgUntouched)("ng-touched", ctx._isNgTouched)("ng-pristine", ctx._isNgPristine)("ng-dirty", ctx._isNgDirty)("ng-valid", ctx._isNgValid)("ng-invalid", ctx._isNgInvalid);
        } }, inputs: { orientation: "orientation", width: "width", showRequiredIndicator: [2, "showRequiredIndicator", "showRequiredIndicator", (value) => coerceBooleanProperty(value)] }, standalone: false, features: [i0.ɵɵProvidersFeature([
                {
                    provide: STLR_FORM_FIELD,
                    useExisting: forwardRef(() => StlrFormFieldComponent)
                }
            ])], ngContentSelectors: _c1$5, decls: 9, vars: 12, consts: [[1, "stlr-form-field--wrapper"], [1, "stlr-form-field--content-wrapper"], [1, "stlr-form-field--prefix-wrapper"], [1, "stlr-form-field--content"], [1, "stlr-form-field--suffix-wrapper"], [1, "stlr-form-field--sub"], [1, "stlr-form-field--required"], [1, "stlr-form-field--hints"], [1, "align-start"], [1, "align-end"], [1, "stlr-form-field--errors"]], template: function StlrFormFieldComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵprojectionDef(_c0$a);
            i0.ɵɵelementStart(0, "div", 0);
            i0.ɵɵconditionalCreate(1, StlrFormFieldComponent_Conditional_1_Template, 3, 3, "label");
            i0.ɵɵelementStart(2, "div", 1);
            i0.ɵɵconditionalCreate(3, StlrFormFieldComponent_Conditional_3_Template, 2, 0, "div", 2);
            i0.ɵɵelementStart(4, "div", 3);
            i0.ɵɵprojection(5);
            i0.ɵɵelementEnd();
            i0.ɵɵconditionalCreate(6, StlrFormFieldComponent_Conditional_6_Template, 2, 0, "div", 4);
            i0.ɵɵelementEnd();
            i0.ɵɵconditionalCreate(7, StlrFormFieldComponent_Conditional_7_Template, 9, 0, "div", 5);
            i0.ɵɵpipe(8, "async");
            i0.ɵɵelementEnd();
        } if (rf & 2) {
            i0.ɵɵclassProp("stlr-form-field--horizontal", ctx.orientation === "horizontal");
            i0.ɵɵadvance();
            i0.ɵɵconditional(ctx.hasLabel ? 1 : -1);
            i0.ɵɵadvance(2);
            i0.ɵɵconditional(ctx.hasPrefix ? 3 : -1);
            i0.ɵɵadvance();
            i0.ɵɵclassProp("stlr-form-field--content--has-prefix", ctx.hasPrefix)("stlr-form-field--content--has-suffix", ctx.hasSuffix);
            i0.ɵɵadvance(2);
            i0.ɵɵconditional(ctx.hasSuffix ? 6 : -1);
            i0.ɵɵadvance();
            i0.ɵɵconditional(i0.ɵɵpipeBind1(8, 10, ctx.hasSubscript$) ? 7 : -1);
        } }, dependencies: [i1$1.AsyncPipe], styles: ["[_nghost-%COMP%]{display:flex;flex-direction:column;gap:var(--space--8)}[_nghost-%COMP%]   .stlr-form-field--wrapper[_ngcontent-%COMP%]{display:inline-flex;flex-direction:column;gap:var(--space--8);flex:1 1 auto}[_nghost-%COMP%]   .stlr-form-field--wrapper.stlr-form-field--horizontal[_ngcontent-%COMP%]{flex-direction:row;align-items:center;justify-content:flex-start}[_nghost-%COMP%]   label[_ngcontent-%COMP%]{font-size:var(--font--14);display:flex;align-items:center;justify-content:flex-start;gap:var(--space--8)}.stlr-form-field--full-width[_nghost-%COMP%]{width:100%}[_nghost-%COMP%]   .stlr-form-field--required[_ngcontent-%COMP%]{color:var(--stlr-color__error);font-size:var(--font--12)}[_nghost-%COMP%]   .stlr-form-field--optional[_ngcontent-%COMP%]{color:var(--stlr-color__disabled);font-size:var(--font--12)}[_nghost-%COMP%]   .stlr-form-field--hints[_ngcontent-%COMP%]{display:flex;flex-direction:row;justify-content:space-between}[_nghost-%COMP%]   .stlr-form-field--errors[_ngcontent-%COMP%]{display:none}[_nghost-%COMP%]:has(input.ng-touched.ng-invalid)   label[_ngcontent-%COMP%], [_nghost-%COMP%]:has(input.ng-dirty.ng-invalid)   label[_ngcontent-%COMP%], [_nghost-%COMP%]:has(select.ng-touched.ng-invalid)   label[_ngcontent-%COMP%], [_nghost-%COMP%]:has(select.ng-dirty.ng-invalid)   label[_ngcontent-%COMP%], [_nghost-%COMP%]:has(textarea.ng-touched.ng-invalid)   label[_ngcontent-%COMP%], [_nghost-%COMP%]:has(textarea.ng-dirty.ng-invalid)   label[_ngcontent-%COMP%], .ng-touched.ng-invalid[_nghost-%COMP%]   label[_ngcontent-%COMP%], .ng-dirty.ng-invalid[_nghost-%COMP%]   label[_ngcontent-%COMP%]{color:var(--stlr-color__error)}[_nghost-%COMP%]:has(input.ng-touched.ng-invalid)   .stlr-form-field--prefix[_ngcontent-%COMP%]     .stlr-icon, [_nghost-%COMP%]:has(input.ng-touched.ng-invalid)   .stlr-form-field--suffix[_ngcontent-%COMP%]     .stlr-icon, [_nghost-%COMP%]:has(input.ng-dirty.ng-invalid)   .stlr-form-field--prefix[_ngcontent-%COMP%]     .stlr-icon, [_nghost-%COMP%]:has(input.ng-dirty.ng-invalid)   .stlr-form-field--suffix[_ngcontent-%COMP%]     .stlr-icon, [_nghost-%COMP%]:has(select.ng-touched.ng-invalid)   .stlr-form-field--prefix[_ngcontent-%COMP%]     .stlr-icon, [_nghost-%COMP%]:has(select.ng-touched.ng-invalid)   .stlr-form-field--suffix[_ngcontent-%COMP%]     .stlr-icon, [_nghost-%COMP%]:has(select.ng-dirty.ng-invalid)   .stlr-form-field--prefix[_ngcontent-%COMP%]     .stlr-icon, [_nghost-%COMP%]:has(select.ng-dirty.ng-invalid)   .stlr-form-field--suffix[_ngcontent-%COMP%]     .stlr-icon, [_nghost-%COMP%]:has(textarea.ng-touched.ng-invalid)   .stlr-form-field--prefix[_ngcontent-%COMP%]     .stlr-icon, [_nghost-%COMP%]:has(textarea.ng-touched.ng-invalid)   .stlr-form-field--suffix[_ngcontent-%COMP%]     .stlr-icon, [_nghost-%COMP%]:has(textarea.ng-dirty.ng-invalid)   .stlr-form-field--prefix[_ngcontent-%COMP%]     .stlr-icon, [_nghost-%COMP%]:has(textarea.ng-dirty.ng-invalid)   .stlr-form-field--suffix[_ngcontent-%COMP%]     .stlr-icon, .ng-touched.ng-invalid[_nghost-%COMP%]   .stlr-form-field--prefix[_ngcontent-%COMP%]     .stlr-icon, .ng-touched.ng-invalid[_nghost-%COMP%]   .stlr-form-field--suffix[_ngcontent-%COMP%]     .stlr-icon, .ng-dirty.ng-invalid[_nghost-%COMP%]   .stlr-form-field--prefix[_ngcontent-%COMP%]     .stlr-icon, .ng-dirty.ng-invalid[_nghost-%COMP%]   .stlr-form-field--suffix[_ngcontent-%COMP%]     .stlr-icon{color:var(--stlr-color__error)}[_nghost-%COMP%]:has(input.ng-touched.ng-invalid)   .stlr-form-field--hints[_ngcontent-%COMP%], [_nghost-%COMP%]:has(input.ng-dirty.ng-invalid)   .stlr-form-field--hints[_ngcontent-%COMP%], [_nghost-%COMP%]:has(select.ng-touched.ng-invalid)   .stlr-form-field--hints[_ngcontent-%COMP%], [_nghost-%COMP%]:has(select.ng-dirty.ng-invalid)   .stlr-form-field--hints[_ngcontent-%COMP%], [_nghost-%COMP%]:has(textarea.ng-touched.ng-invalid)   .stlr-form-field--hints[_ngcontent-%COMP%], [_nghost-%COMP%]:has(textarea.ng-dirty.ng-invalid)   .stlr-form-field--hints[_ngcontent-%COMP%], .ng-touched.ng-invalid[_nghost-%COMP%]   .stlr-form-field--hints[_ngcontent-%COMP%], .ng-dirty.ng-invalid[_nghost-%COMP%]   .stlr-form-field--hints[_ngcontent-%COMP%]{display:none}[_nghost-%COMP%]:has(input.ng-touched.ng-invalid)   .stlr-form-field--errors[_ngcontent-%COMP%], [_nghost-%COMP%]:has(input.ng-dirty.ng-invalid)   .stlr-form-field--errors[_ngcontent-%COMP%], [_nghost-%COMP%]:has(select.ng-touched.ng-invalid)   .stlr-form-field--errors[_ngcontent-%COMP%], [_nghost-%COMP%]:has(select.ng-dirty.ng-invalid)   .stlr-form-field--errors[_ngcontent-%COMP%], [_nghost-%COMP%]:has(textarea.ng-touched.ng-invalid)   .stlr-form-field--errors[_ngcontent-%COMP%], [_nghost-%COMP%]:has(textarea.ng-dirty.ng-invalid)   .stlr-form-field--errors[_ngcontent-%COMP%], .ng-touched.ng-invalid[_nghost-%COMP%]   .stlr-form-field--errors[_ngcontent-%COMP%], .ng-dirty.ng-invalid[_nghost-%COMP%]   .stlr-form-field--errors[_ngcontent-%COMP%]{display:flex;flex-direction:row;justify-content:space-between}[_nghost-%COMP%]:has(input.ng-touched.ng-invalid)   .stlr-form-field--errors[_ngcontent-%COMP%]     .stlr-error, [_nghost-%COMP%]:has(input.ng-dirty.ng-invalid)   .stlr-form-field--errors[_ngcontent-%COMP%]     .stlr-error, [_nghost-%COMP%]:has(select.ng-touched.ng-invalid)   .stlr-form-field--errors[_ngcontent-%COMP%]     .stlr-error, [_nghost-%COMP%]:has(select.ng-dirty.ng-invalid)   .stlr-form-field--errors[_ngcontent-%COMP%]     .stlr-error, [_nghost-%COMP%]:has(textarea.ng-touched.ng-invalid)   .stlr-form-field--errors[_ngcontent-%COMP%]     .stlr-error, [_nghost-%COMP%]:has(textarea.ng-dirty.ng-invalid)   .stlr-form-field--errors[_ngcontent-%COMP%]     .stlr-error, .ng-touched.ng-invalid[_nghost-%COMP%]   .stlr-form-field--errors[_ngcontent-%COMP%]     .stlr-error, .ng-dirty.ng-invalid[_nghost-%COMP%]   .stlr-form-field--errors[_ngcontent-%COMP%]     .stlr-error{color:var(--stlr-color__error)}[_nghost-%COMP%]:has(input.ng-touched.ng-invalid)   .stlr-form-field--errors[_ngcontent-%COMP%]     .stlr-error.stlr-error--has-error, [_nghost-%COMP%]:has(input.ng-dirty.ng-invalid)   .stlr-form-field--errors[_ngcontent-%COMP%]     .stlr-error.stlr-error--has-error, [_nghost-%COMP%]:has(select.ng-touched.ng-invalid)   .stlr-form-field--errors[_ngcontent-%COMP%]     .stlr-error.stlr-error--has-error, [_nghost-%COMP%]:has(select.ng-dirty.ng-invalid)   .stlr-form-field--errors[_ngcontent-%COMP%]     .stlr-error.stlr-error--has-error, [_nghost-%COMP%]:has(textarea.ng-touched.ng-invalid)   .stlr-form-field--errors[_ngcontent-%COMP%]     .stlr-error.stlr-error--has-error, [_nghost-%COMP%]:has(textarea.ng-dirty.ng-invalid)   .stlr-form-field--errors[_ngcontent-%COMP%]     .stlr-error.stlr-error--has-error, .ng-touched.ng-invalid[_nghost-%COMP%]   .stlr-form-field--errors[_ngcontent-%COMP%]     .stlr-error.stlr-error--has-error, .ng-dirty.ng-invalid[_nghost-%COMP%]   .stlr-form-field--errors[_ngcontent-%COMP%]     .stlr-error.stlr-error--has-error{display:block}[_nghost-%COMP%]:has(input.ng-touched.ng-invalid)   .stlr-form-field--errors[_ngcontent-%COMP%]     .stlr-error:not(.stlr-error--has-error), [_nghost-%COMP%]:has(input.ng-dirty.ng-invalid)   .stlr-form-field--errors[_ngcontent-%COMP%]     .stlr-error:not(.stlr-error--has-error), [_nghost-%COMP%]:has(select.ng-touched.ng-invalid)   .stlr-form-field--errors[_ngcontent-%COMP%]     .stlr-error:not(.stlr-error--has-error), [_nghost-%COMP%]:has(select.ng-dirty.ng-invalid)   .stlr-form-field--errors[_ngcontent-%COMP%]     .stlr-error:not(.stlr-error--has-error), [_nghost-%COMP%]:has(textarea.ng-touched.ng-invalid)   .stlr-form-field--errors[_ngcontent-%COMP%]     .stlr-error:not(.stlr-error--has-error), [_nghost-%COMP%]:has(textarea.ng-dirty.ng-invalid)   .stlr-form-field--errors[_ngcontent-%COMP%]     .stlr-error:not(.stlr-error--has-error), .ng-touched.ng-invalid[_nghost-%COMP%]   .stlr-form-field--errors[_ngcontent-%COMP%]     .stlr-error:not(.stlr-error--has-error), .ng-dirty.ng-invalid[_nghost-%COMP%]   .stlr-form-field--errors[_ngcontent-%COMP%]     .stlr-error:not(.stlr-error--has-error){display:none}.stlr-form-field--content-wrapper[_ngcontent-%COMP%]{display:flex;align-items:baseline;box-sizing:border-box;flex:1 1 auto;position:relative}.stlr-form-field--content-wrapper[_ngcontent-%COMP%]     input, .stlr-form-field--content-wrapper[_ngcontent-%COMP%]     select, .stlr-form-field--content-wrapper[_ngcontent-%COMP%]     textarea{width:100%;box-sizing:border-box}.stlr-form-field--content-wrapper[_ngcontent-%COMP%]     .stlr-form-field--prefix, .stlr-form-field--content-wrapper[_ngcontent-%COMP%]     .stlr-form-field--suffix{position:absolute;top:calc(50% - .7rem);z-index:1;padding:0 6px}.stlr-form-field--content-wrapper[_ngcontent-%COMP%]     .stlr-form-field--prefix .stlr-icon, .stlr-form-field--content-wrapper[_ngcontent-%COMP%]     .stlr-form-field--suffix .stlr-icon{font-size:1.25rem}.stlr-form-field--content-wrapper[_ngcontent-%COMP%]     .stlr-form-field--prefix, .stlr-form-field--content-wrapper[_ngcontent-%COMP%]     [dir=rtl] .stlr-form-field--suffix{left:2px}.stlr-form-field--content-wrapper[_ngcontent-%COMP%]     .stlr-form-field--suffix, .stlr-form-field--content-wrapper[_ngcontent-%COMP%]     [dir=rtl] .stlr-form-field--prefix{right:2px}.stlr-form-field--content-wrapper[_ngcontent-%COMP%]    :has(input[icon]) .stlr-form-field--prefix, .stlr-form-field--content-wrapper[_ngcontent-%COMP%]    :has(input[icon]) .stlr-form-field--suffix{display:none}.stlr-form-field--content-wrapper[_ngcontent-%COMP%]     .stlr-form-field--content--has-prefix input:not(.has-prefix){padding-left:34px}.stlr-form-field--content-wrapper[_ngcontent-%COMP%]     .stlr-form-field--content--has-suffix input:not(.has-suffix){padding-right:34px}.stlr-form-field--content-wrapper[_ngcontent-%COMP%]   .stlr-form-field--content[_ngcontent-%COMP%]{flex:1 1 auto;align-self:stretch}.stlr-form-field--sub[_ngcontent-%COMP%]{width:100%}.stlr-form-field--hints[_ngcontent-%COMP%] > .align-start[_ngcontent-%COMP%], .stlr-form-field--errors[_ngcontent-%COMP%] > .align-start[_ngcontent-%COMP%]{display:flex;flex-direction:column;align-items:start}.stlr-form-field--hints[_ngcontent-%COMP%] > .align-end[_ngcontent-%COMP%], .stlr-form-field--errors[_ngcontent-%COMP%] > .align-end[_ngcontent-%COMP%]{display:flex;flex-direction:column;align-items:end}.stlr-form-field--hints[_ngcontent-%COMP%]     .stlr-hint, .stlr-form-field--hints[_ngcontent-%COMP%]     .stlr-error, .stlr-form-field--errors[_ngcontent-%COMP%]     .stlr-hint, .stlr-form-field--errors[_ngcontent-%COMP%]     .stlr-error{font-size:var(--font--12)}"], changeDetection: 0 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrFormFieldComponent, [{
        type: Component,
        args: [{ selector: 'stlr-form-field', changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                    {
                        provide: STLR_FORM_FIELD,
                        useExisting: forwardRef(() => StlrFormFieldComponent)
                    }
                ], standalone: false, template: "<div\n    class=\"stlr-form-field--wrapper\"\n    [class.stlr-form-field--horizontal]=\"orientation === 'horizontal'\"\n>\n\n    @if (hasLabel) {\n\n        <label [attr.id]=\"labelId\" [attr.for]=\"_formComponent?.id\">\n\n            <ng-content select=\"stlr-label\"></ng-content>\n    \n            @if (showRequired) {\n                <span class=\"stlr-form-field--required\">\n                    *\n                </span>\n            }\n            \n        </label>\n\n    }\n\n    <div class=\"stlr-form-field--content-wrapper\">\n\n        @if (hasPrefix) {\n            <div class=\"stlr-form-field--prefix-wrapper\">\n                <ng-content select=\"[stlrFormFieldPrefix]\"></ng-content>\n            </div>\n        }\n\n        <div\n            class=\"stlr-form-field--content\"\n            [class.stlr-form-field--content--has-prefix]=\"hasPrefix\"\n            [class.stlr-form-field--content--has-suffix]=\"hasSuffix\"\n        >\n            <ng-content></ng-content>\n        </div>\n\n        @if (hasSuffix) {\n            <div class=\"stlr-form-field--suffix-wrapper\">\n                <ng-content select=\"[stlrFormFieldSuffix]\"></ng-content>\n            </div>\n        }\n        \n    </div>\n\n    @if (hasSubscript$ | async) {\n\n        <div class=\"stlr-form-field--sub\">\n\n            <div class=\"stlr-form-field--hints\">\n                <div class=\"align-start\">\n                    <ng-content select=\"stlr-hint:not([align='end'])\"></ng-content>\n                </div>\n                <div class=\"align-end\">\n                    <ng-content select=\"stlr-hint[align='end']\"></ng-content>\n                </div>\n            </div>\n    \n            <div class=\"stlr-form-field--errors\">\n                <div class=\"align-start\">\n                    <ng-content select=\"stlr-error\"></ng-content>\n                </div>\n            </div>\n    \n        </div>\n\n    }\n\n</div>\n", styles: [":host{display:flex;flex-direction:column;gap:var(--space--8)}:host .stlr-form-field--wrapper{display:inline-flex;flex-direction:column;gap:var(--space--8);flex:1 1 auto}:host .stlr-form-field--wrapper.stlr-form-field--horizontal{flex-direction:row;align-items:center;justify-content:flex-start}:host label{font-size:var(--font--14);display:flex;align-items:center;justify-content:flex-start;gap:var(--space--8)}:host.stlr-form-field--full-width{width:100%}:host .stlr-form-field--required{color:var(--stlr-color__error);font-size:var(--font--12)}:host .stlr-form-field--optional{color:var(--stlr-color__disabled);font-size:var(--font--12)}:host .stlr-form-field--hints{display:flex;flex-direction:row;justify-content:space-between}:host .stlr-form-field--errors{display:none}:host:has(input.ng-touched.ng-invalid) label,:host:has(input.ng-dirty.ng-invalid) label,:host:has(select.ng-touched.ng-invalid) label,:host:has(select.ng-dirty.ng-invalid) label,:host:has(textarea.ng-touched.ng-invalid) label,:host:has(textarea.ng-dirty.ng-invalid) label,:host.ng-touched.ng-invalid label,:host.ng-dirty.ng-invalid label{color:var(--stlr-color__error)}:host:has(input.ng-touched.ng-invalid) .stlr-form-field--prefix ::ng-deep .stlr-icon,:host:has(input.ng-touched.ng-invalid) .stlr-form-field--suffix ::ng-deep .stlr-icon,:host:has(input.ng-dirty.ng-invalid) .stlr-form-field--prefix ::ng-deep .stlr-icon,:host:has(input.ng-dirty.ng-invalid) .stlr-form-field--suffix ::ng-deep .stlr-icon,:host:has(select.ng-touched.ng-invalid) .stlr-form-field--prefix ::ng-deep .stlr-icon,:host:has(select.ng-touched.ng-invalid) .stlr-form-field--suffix ::ng-deep .stlr-icon,:host:has(select.ng-dirty.ng-invalid) .stlr-form-field--prefix ::ng-deep .stlr-icon,:host:has(select.ng-dirty.ng-invalid) .stlr-form-field--suffix ::ng-deep .stlr-icon,:host:has(textarea.ng-touched.ng-invalid) .stlr-form-field--prefix ::ng-deep .stlr-icon,:host:has(textarea.ng-touched.ng-invalid) .stlr-form-field--suffix ::ng-deep .stlr-icon,:host:has(textarea.ng-dirty.ng-invalid) .stlr-form-field--prefix ::ng-deep .stlr-icon,:host:has(textarea.ng-dirty.ng-invalid) .stlr-form-field--suffix ::ng-deep .stlr-icon,:host.ng-touched.ng-invalid .stlr-form-field--prefix ::ng-deep .stlr-icon,:host.ng-touched.ng-invalid .stlr-form-field--suffix ::ng-deep .stlr-icon,:host.ng-dirty.ng-invalid .stlr-form-field--prefix ::ng-deep .stlr-icon,:host.ng-dirty.ng-invalid .stlr-form-field--suffix ::ng-deep .stlr-icon{color:var(--stlr-color__error)}:host:has(input.ng-touched.ng-invalid) .stlr-form-field--hints,:host:has(input.ng-dirty.ng-invalid) .stlr-form-field--hints,:host:has(select.ng-touched.ng-invalid) .stlr-form-field--hints,:host:has(select.ng-dirty.ng-invalid) .stlr-form-field--hints,:host:has(textarea.ng-touched.ng-invalid) .stlr-form-field--hints,:host:has(textarea.ng-dirty.ng-invalid) .stlr-form-field--hints,:host.ng-touched.ng-invalid .stlr-form-field--hints,:host.ng-dirty.ng-invalid .stlr-form-field--hints{display:none}:host:has(input.ng-touched.ng-invalid) .stlr-form-field--errors,:host:has(input.ng-dirty.ng-invalid) .stlr-form-field--errors,:host:has(select.ng-touched.ng-invalid) .stlr-form-field--errors,:host:has(select.ng-dirty.ng-invalid) .stlr-form-field--errors,:host:has(textarea.ng-touched.ng-invalid) .stlr-form-field--errors,:host:has(textarea.ng-dirty.ng-invalid) .stlr-form-field--errors,:host.ng-touched.ng-invalid .stlr-form-field--errors,:host.ng-dirty.ng-invalid .stlr-form-field--errors{display:flex;flex-direction:row;justify-content:space-between}:host:has(input.ng-touched.ng-invalid) .stlr-form-field--errors ::ng-deep .stlr-error,:host:has(input.ng-dirty.ng-invalid) .stlr-form-field--errors ::ng-deep .stlr-error,:host:has(select.ng-touched.ng-invalid) .stlr-form-field--errors ::ng-deep .stlr-error,:host:has(select.ng-dirty.ng-invalid) .stlr-form-field--errors ::ng-deep .stlr-error,:host:has(textarea.ng-touched.ng-invalid) .stlr-form-field--errors ::ng-deep .stlr-error,:host:has(textarea.ng-dirty.ng-invalid) .stlr-form-field--errors ::ng-deep .stlr-error,:host.ng-touched.ng-invalid .stlr-form-field--errors ::ng-deep .stlr-error,:host.ng-dirty.ng-invalid .stlr-form-field--errors ::ng-deep .stlr-error{color:var(--stlr-color__error)}:host:has(input.ng-touched.ng-invalid) .stlr-form-field--errors ::ng-deep .stlr-error.stlr-error--has-error,:host:has(input.ng-dirty.ng-invalid) .stlr-form-field--errors ::ng-deep .stlr-error.stlr-error--has-error,:host:has(select.ng-touched.ng-invalid) .stlr-form-field--errors ::ng-deep .stlr-error.stlr-error--has-error,:host:has(select.ng-dirty.ng-invalid) .stlr-form-field--errors ::ng-deep .stlr-error.stlr-error--has-error,:host:has(textarea.ng-touched.ng-invalid) .stlr-form-field--errors ::ng-deep .stlr-error.stlr-error--has-error,:host:has(textarea.ng-dirty.ng-invalid) .stlr-form-field--errors ::ng-deep .stlr-error.stlr-error--has-error,:host.ng-touched.ng-invalid .stlr-form-field--errors ::ng-deep .stlr-error.stlr-error--has-error,:host.ng-dirty.ng-invalid .stlr-form-field--errors ::ng-deep .stlr-error.stlr-error--has-error{display:block}:host:has(input.ng-touched.ng-invalid) .stlr-form-field--errors ::ng-deep .stlr-error:not(.stlr-error--has-error),:host:has(input.ng-dirty.ng-invalid) .stlr-form-field--errors ::ng-deep .stlr-error:not(.stlr-error--has-error),:host:has(select.ng-touched.ng-invalid) .stlr-form-field--errors ::ng-deep .stlr-error:not(.stlr-error--has-error),:host:has(select.ng-dirty.ng-invalid) .stlr-form-field--errors ::ng-deep .stlr-error:not(.stlr-error--has-error),:host:has(textarea.ng-touched.ng-invalid) .stlr-form-field--errors ::ng-deep .stlr-error:not(.stlr-error--has-error),:host:has(textarea.ng-dirty.ng-invalid) .stlr-form-field--errors ::ng-deep .stlr-error:not(.stlr-error--has-error),:host.ng-touched.ng-invalid .stlr-form-field--errors ::ng-deep .stlr-error:not(.stlr-error--has-error),:host.ng-dirty.ng-invalid .stlr-form-field--errors ::ng-deep .stlr-error:not(.stlr-error--has-error){display:none}.stlr-form-field--content-wrapper{display:flex;align-items:baseline;box-sizing:border-box;flex:1 1 auto;position:relative}.stlr-form-field--content-wrapper ::ng-deep input,.stlr-form-field--content-wrapper ::ng-deep select,.stlr-form-field--content-wrapper ::ng-deep textarea{width:100%;box-sizing:border-box}.stlr-form-field--content-wrapper ::ng-deep .stlr-form-field--prefix,.stlr-form-field--content-wrapper ::ng-deep .stlr-form-field--suffix{position:absolute;top:calc(50% - .7rem);z-index:1;padding:0 6px}.stlr-form-field--content-wrapper ::ng-deep .stlr-form-field--prefix .stlr-icon,.stlr-form-field--content-wrapper ::ng-deep .stlr-form-field--suffix .stlr-icon{font-size:1.25rem}.stlr-form-field--content-wrapper ::ng-deep .stlr-form-field--prefix,.stlr-form-field--content-wrapper ::ng-deep [dir=rtl] .stlr-form-field--suffix{left:2px}.stlr-form-field--content-wrapper ::ng-deep .stlr-form-field--suffix,.stlr-form-field--content-wrapper ::ng-deep [dir=rtl] .stlr-form-field--prefix{right:2px}.stlr-form-field--content-wrapper ::ng-deep:has(input[icon]) .stlr-form-field--prefix,.stlr-form-field--content-wrapper ::ng-deep:has(input[icon]) .stlr-form-field--suffix{display:none}.stlr-form-field--content-wrapper ::ng-deep .stlr-form-field--content--has-prefix input:not(.has-prefix){padding-left:34px}.stlr-form-field--content-wrapper ::ng-deep .stlr-form-field--content--has-suffix input:not(.has-suffix){padding-right:34px}.stlr-form-field--content-wrapper .stlr-form-field--content{flex:1 1 auto;align-self:stretch}.stlr-form-field--sub{width:100%}.stlr-form-field--hints>.align-start,.stlr-form-field--errors>.align-start{display:flex;flex-direction:column;align-items:start}.stlr-form-field--hints>.align-end,.stlr-form-field--errors>.align-end{display:flex;flex-direction:column;align-items:end}.stlr-form-field--hints ::ng-deep .stlr-hint,.stlr-form-field--hints ::ng-deep .stlr-error,.stlr-form-field--errors ::ng-deep .stlr-hint,.stlr-form-field--errors ::ng-deep .stlr-error{font-size:var(--font--12)}\n"] }]
    }], null, { orientation: [{
            type: Input
        }], width: [{
            type: Input
        }], showRequiredIndicator: [{
            type: Input,
            args: [{ transform: (value) => coerceBooleanProperty(value) }]
        }], _hintChildren: [{
            type: ContentChildren,
            args: [StlrHintDirective, { descendants: true }]
        }], _errorChildren: [{
            type: ContentChildren,
            args: [STLR_ERROR, { descendants: true }]
        }], _formComponent: [{
            type: ContentChild,
            args: [STLR_FORM_COMPONENT_BASE]
        }], _ngControl: [{
            type: ContentChild,
            args: [NgControl]
        }], _labelChild: [{
            type: ContentChild,
            args: [StlrLabelDirective, { descendants: true }]
        }], _prefixChild: [{
            type: ContentChild,
            args: [STLR_PREFIX, { descendants: true }]
        }], _suffixChild: [{
            type: ContentChild,
            args: [STLR_SUFFIX, { descendants: true }]
        }], _hostClass: [{
            type: HostBinding,
            args: ['class.stlr-form-field']
        }], _disabledClass: [{
            type: HostBinding,
            args: ['class.stlr-form-field--disabled']
        }], _fullWidthClass: [{
            type: HostBinding,
            args: ['class.stlr-form-field--full-width']
        }], _isNgUntouched: [{
            type: HostBinding,
            args: ['class.ng-untouched']
        }], _isNgTouched: [{
            type: HostBinding,
            args: ['class.ng-touched']
        }], _isNgPristine: [{
            type: HostBinding,
            args: ['class.ng-pristine']
        }], _isNgDirty: [{
            type: HostBinding,
            args: ['class.ng-dirty']
        }], _isNgValid: [{
            type: HostBinding,
            args: ['class.ng-valid']
        }], _isNgInvalid: [{
            type: HostBinding,
            args: ['class.ng-invalid']
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(StlrFormFieldComponent, { className: "StlrFormFieldComponent", filePath: "lib/form-field/form-field.component.ts", lineNumber: 47 }); })();

class StlrErrorDirective {
    constructor() {
        this._formField = inject(STLR_FORM_FIELD, { optional: true });
        this._hostClass = true;
        this.id = `stlr-error-${generateUUID()}`;
    }
    get hasError() {
        return Boolean(this.displayForErrorCode && this._formField?._ngControl?.hasError(this.displayForErrorCode));
    }
    static { this.ɵfac = function StlrErrorDirective_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || StlrErrorDirective)(); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: StlrErrorDirective, selectors: [["stlr-error"], ["stlr-hint", "validationFor", ""]], hostVars: 4, hostBindings: function StlrErrorDirective_HostBindings(rf, ctx) { if (rf & 2) {
            i0.ɵɵclassProp("stlr-error", ctx._hostClass)("stlr-error--has-error", ctx.hasError);
        } }, inputs: { id: "id", displayForErrorCode: "displayForErrorCode", validationFor: "validationFor", validationId: "validationId" }, standalone: false, features: [i0.ɵɵProvidersFeature([
                {
                    provide: STLR_ERROR,
                    useExisting: StlrErrorDirective
                },
                {
                    provide: STLR_FORM_FIELD,
                    useExisting: forwardRef(() => StlrFormFieldComponent)
                },
            ])] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrErrorDirective, [{
        type: Directive,
        args: [{
                // eslint-disable-next-line @angular-eslint/directive-selector
                selector: 'stlr-error, stlr-hint[validationFor]',
                providers: [
                    {
                        provide: STLR_ERROR,
                        useExisting: StlrErrorDirective
                    },
                    {
                        provide: STLR_FORM_FIELD,
                        useExisting: forwardRef(() => StlrFormFieldComponent)
                    },
                ],
                standalone: false
            }]
    }], null, { _hostClass: [{
            type: HostBinding,
            args: ['class.stlr-error']
        }], hasError: [{
            type: HostBinding,
            args: ['class.stlr-error--has-error']
        }], id: [{
            type: Input
        }], displayForErrorCode: [{
            type: Input
        }], validationFor: [{
            type: Input
        }], validationId: [{
            type: Input
        }] }); })();

const FORM_FIELD_API = [
    StlrErrorDirective,
    StlrFormFieldComponent,
    StlrHintDirective,
    StlrLabelDirective,
    StlrPrefixDirective,
    StlrSuffixDirective,
];

const _c0$9 = ["stlr-input", ""];
class StlrInputComponent extends StlrFormComponentValueAccessor {
    constructor() {
        super(...arguments);
        this.controlType = 'stlr-input';
        /**
         * Allows access to the component's native element so compound components can handle focus events
         */
        this.elementRef = inject(ElementRef);
        this.tabindex = null;
        this.placeholder = null;
        this.enterKeyHint = null;
        this.ariaDescribedBy = null;
        this.ariaLabel = null;
        this.ariaLabelledBy = null;
        /**
         * @deprecated Use `stlrFormFieldPrefix` instead.
         */
        this.icon = null;
        /**
         * @deprecated Use `stlrFormFieldPrefix` and `stlrFormFieldSuffix` instead.
         */
        this.iconPosition = 'start';
        this.width = null;
        this.type = 'text';
    }
    onInput(event) {
        const value = event.target.value;
        const hasChanged = !isEqual(this.value, value);
        this.value = value;
        if (hasChanged) {
            if (this.ngControl?.control?.updateOn === 'change')
                this._onChange(this.value);
            this.changed.emit(this.value);
        }
    }
    onBlur() {
        if (this.ngControl?.control?.updateOn === 'blur')
            this._onChange(this.value);
        this._onTouched();
    }
    get name() {
        return super.name;
    }
    set name(name) {
        super.name = name;
    }
    get value() {
        return super.value ?? '';
    }
    set value(value) {
        super.value = value;
    }
    get disabled() {
        return super.disabled;
    }
    set disabled(isDisabled) {
        super.disabled = isDisabled;
    }
    get required() {
        return super.required;
    }
    set required(isRequired) {
        super.required = isRequired;
    }
    get invalid() {
        return super.invalid;
    }
    set invalid(isInvalid) {
        super.invalid = isInvalid;
    }
    ngOnInit() {
        super.ngOnInit();
        if (this.icon != null) {
            const parentNode = this.renderer.parentNode(this.elementRef.nativeElement);
            const divElement = this.renderer.createElement('div');
            this.renderer.addClass(divElement, 'input-container');
            if (this.width != null) {
                this.renderer.setAttribute(divElement, 'width', this.width);
            }
            this.renderer.insertBefore(parentNode, divElement, this.elementRef.nativeElement);
            const iconElement = this.renderer.createElement('div');
            iconElement.innerText = this.icon;
            this.renderer.addClass(iconElement, 'icon');
            this.renderer.setAttribute(iconElement, 'position', this.iconPosition);
            this.renderer.setAttribute(iconElement, 'aria-hidden', 'true');
            if (this.iconPosition === 'start') {
                this.renderer.appendChild(divElement, iconElement);
                this.renderer.appendChild(divElement, this.elementRef.nativeElement);
            }
            else if (this.iconPosition === 'end') {
                this.renderer.appendChild(divElement, this.elementRef.nativeElement);
                this.renderer.appendChild(divElement, iconElement);
            }
        }
    }
    static { this.ɵfac = /*@__PURE__*/ (() => { let ɵStlrInputComponent_BaseFactory; return function StlrInputComponent_Factory(__ngFactoryType__) { return (ɵStlrInputComponent_BaseFactory || (ɵStlrInputComponent_BaseFactory = i0.ɵɵgetInheritedFactory(StlrInputComponent)))(__ngFactoryType__ || StlrInputComponent); }; })(); }
    static { this.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: StlrInputComponent, selectors: [["input", "stlr-input", ""], ["input", "stlrInput", ""], ["textarea", "stlr-input", ""], ["textarea", "stlrInput", ""]], hostVars: 24, hostBindings: function StlrInputComponent_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("input", function StlrInputComponent_input_HostBindingHandler($event) { return ctx.onInput($event); })("blur", function StlrInputComponent_blur_HostBindingHandler() { return ctx.onBlur(); });
        } if (rf & 2) {
            i0.ɵɵdomProperty("id", ctx.id)("name", ctx.name)("value", ctx.value)("disabled", ctx.disabled)("required", ctx.required)("tabIndex", ctx.tabindex)("type", ctx.type);
            i0.ɵɵattribute("aria-disabled", ctx.disabled)("aria-required", ctx.required)("aria-invalid", ctx.invalid)("placeholder", ctx.placeholder)("enterkeyhint", ctx.enterKeyHint)("aria-describedby", ctx.ariaDescribedBy)("aria-label", ctx.ariaLabel)("aria-labelledby", ctx.ariaLabelledBy)("icon-position", ctx.iconPosition);
            i0.ɵɵclassProp("disabled", ctx.disabled)("required", ctx.required)("invalid", ctx.invalid)("full", ctx.width);
        } }, inputs: { icon: "icon", iconPosition: "iconPosition", width: "width", type: "type" }, standalone: false, features: [i0.ɵɵProvidersFeature([
                {
                    provide: STLR_FORM_COMPONENT_BASE,
                    useExisting: forwardRef(() => StlrInputComponent),
                    multi: true
                },
            ]), i0.ɵɵInheritDefinitionFeature], attrs: _c0$9, decls: 0, vars: 0, template: function StlrInputComponent_Template(rf, ctx) { }, styles: ["[_nghost-%COMP%]{box-sizing:border-box;padding:0 10px;border:solid 1px rgba(0,0,0,.25);background-color:#fff;color:var(--stlr-color__copy);appearance:none;border-radius:var(--stlr-border-radius);outline:solid 1px rgba(255,255,255,0);transition:all .2s;box-shadow:0 0 2px #59a9ff00;font-family:var(--stlr-font-family--copy)}[_nghost-%COMP%]:not(textarea){height:2.25rem}[_nghost-%COMP%]::-webkit-search-cancel-button{-webkit-appearance:none;height:16px;width:16px;display:block;background-color:#0000004d;border-radius:10px;background-repeat:no-repeat;background-position:center center;cursor:pointer;background-image:url(\"data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9.5 3.205L8.795 2.5L6 5.295L3.205 2.5L2.5 3.205L5.295 6L2.5 8.795L3.205 9.5L6 6.705L8.795 9.5L9.5 8.795L6.705 6L9.5 3.205Z' fill='white'/%3E%3C/svg%3E%0A\")}[_nghost-%COMP%]:hover{box-shadow:0 0 6px #0000001a}[_nghost-%COMP%]:focus{border:solid 1px var(--stlr-color__info);outline:solid 1px var(--stlr-color__info)}[disabled][_nghost-%COMP%]{color:var(--stlr-color__disabled--light);border:solid 1px rgba(0,0,0,.2);background-color:#00000006}.ng-invalid.ng-touched[_nghost-%COMP%], .ng-invalid.ng-dirty[_nghost-%COMP%]{border-color:var(--stlr-color__error);background-color:var(--stlr-color__error--extra-light)}.ng-invalid.ng-touched[_nghost-%COMP%]:focus, .ng-invalid.ng-dirty[_nghost-%COMP%]:focus{background-color:#fff;border:solid 1px var(--stlr-color__info)}[_nghost-%COMP%]:focus::placeholder{color:transparent}[_nghost-%COMP%]:is(textarea){padding:10px}.ng-touched.ng-invalid[_nghost-%COMP%]:not(.disabled):not(:focus), .ng-dirty.ng-invalid[_nghost-%COMP%]:not(.disabled):not(:focus){border-color:var(--stlr-color__error);background-color:var(--stlr-color__error--extra-light)}.full[_nghost-%COMP%]{width:100%}[icon][_nghost-%COMP%]:not([icon-position=end]){padding-left:calc(10px + 1.5rem)}[icon][icon-position=end][_nghost-%COMP%]{padding-right:calc(10px + 1.5rem)}[_nghost-%COMP%]   input[type=search][_ngcontent-%COMP%]::-webkit-search-decoration, [_nghost-%COMP%]   input[type=search][_ngcontent-%COMP%]::-webkit-search-results-button, [_nghost-%COMP%]   input[type=search][_ngcontent-%COMP%]::-webkit-search-results-decoration{display:none}[_nghost-%COMP%]   input[type=search][_ngcontent-%COMP%]{-webkit-appearance:none}.input-container[_ngcontent-%COMP%]{position:relative;display:flex;flex-direction:row;justify-content:flex-start;align-items:center;width:inherit}.full[_ngcontent-%COMP%]   .input-container[_ngcontent-%COMP%]{width:100%}.icon[_ngcontent-%COMP%]{position:absolute;top:0;bottom:0;margin:auto}.icon[position=start][_ngcontent-%COMP%]{left:10px}.icon[position=end][_ngcontent-%COMP%]{right:10px}.icon[_ngcontent-%COMP%]{z-index:0;font-family:Material Symbols Outlined Variable;font-weight:400;font-style:normal;font-size:1.25rem;height:1.25rem;width:1.25rem;display:inline-block;line-height:1;text-transform:none;letter-spacing:normal;word-wrap:normal;white-space:nowrap;direction:ltr;color:var(--stlr-color__copy);-webkit-font-smoothing:antialiased;text-rendering:optimizeLegibility;-moz-osx-font-smoothing:grayscale;font-feature-settings:\"liga\";font-variation-settings:\"FILL\" 0,\"wght\" 300,\"GRAD\" 0,\"opsz\" 48}"], changeDetection: 0 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrInputComponent, [{
        type: Component,
        args: [{ selector: 
                // eslint-disable-next-line @angular-eslint/component-selector
                'input[stlr-input], input[stlrInput], textarea[stlr-input], textarea[stlrInput]', template: '', changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                    {
                        provide: STLR_FORM_COMPONENT_BASE,
                        useExisting: forwardRef(() => StlrInputComponent),
                        multi: true
                    },
                ], standalone: false, styles: [":host{box-sizing:border-box;padding:0 10px;border:solid 1px rgba(0,0,0,.25);background-color:#fff;color:var(--stlr-color__copy);appearance:none;border-radius:var(--stlr-border-radius);outline:solid 1px rgba(255,255,255,0);transition:all .2s;box-shadow:0 0 2px #59a9ff00;font-family:var(--stlr-font-family--copy)}:host:not(textarea){height:2.25rem}:host::-webkit-search-cancel-button{-webkit-appearance:none;height:16px;width:16px;display:block;background-color:#0000004d;border-radius:10px;background-repeat:no-repeat;background-position:center center;cursor:pointer;background-image:url(\"data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9.5 3.205L8.795 2.5L6 5.295L3.205 2.5L2.5 3.205L5.295 6L2.5 8.795L3.205 9.5L6 6.705L8.795 9.5L9.5 8.795L6.705 6L9.5 3.205Z' fill='white'/%3E%3C/svg%3E%0A\")}:host:hover{box-shadow:0 0 6px #0000001a}:host:focus{border:solid 1px var(--stlr-color__info);outline:solid 1px var(--stlr-color__info)}:host[disabled]{color:var(--stlr-color__disabled--light);border:solid 1px rgba(0,0,0,.2);background-color:#00000006}:host.ng-invalid.ng-touched,:host.ng-invalid.ng-dirty{border-color:var(--stlr-color__error);background-color:var(--stlr-color__error--extra-light)}:host.ng-invalid.ng-touched:focus,:host.ng-invalid.ng-dirty:focus{background-color:#fff;border:solid 1px var(--stlr-color__info)}:host:focus::placeholder{color:transparent}:host:is(textarea){padding:10px}:host.ng-touched.ng-invalid:not(.disabled):not(:focus),:host.ng-dirty.ng-invalid:not(.disabled):not(:focus){border-color:var(--stlr-color__error);background-color:var(--stlr-color__error--extra-light)}:host.full{width:100%}:host[icon]:not([icon-position=end]){padding-left:calc(10px + 1.5rem)}:host[icon][icon-position=end]{padding-right:calc(10px + 1.5rem)}:host input[type=search]::-webkit-search-decoration,:host input[type=search]::-webkit-search-results-button,:host input[type=search]::-webkit-search-results-decoration{display:none}:host input[type=search]{-webkit-appearance:none}.input-container{position:relative;display:flex;flex-direction:row;justify-content:flex-start;align-items:center;width:inherit}.full .input-container{width:100%}.icon{position:absolute;top:0;bottom:0;margin:auto}.icon[position=start]{left:10px}.icon[position=end]{right:10px}.icon{z-index:0;font-family:Material Symbols Outlined Variable;font-weight:400;font-style:normal;font-size:1.25rem;height:1.25rem;width:1.25rem;display:inline-block;line-height:1;text-transform:none;letter-spacing:normal;word-wrap:normal;white-space:nowrap;direction:ltr;color:var(--stlr-color__copy);-webkit-font-smoothing:antialiased;text-rendering:optimizeLegibility;-moz-osx-font-smoothing:grayscale;font-feature-settings:\"liga\";font-variation-settings:\"FILL\" 0,\"wght\" 300,\"GRAD\" 0,\"opsz\" 48}\n"] }]
    }], null, { onInput: [{
            type: HostListener,
            args: ['input', ['$event']]
        }], onBlur: [{
            type: HostListener,
            args: ['blur']
        }], id: [{
            type: HostBinding
        }], name: [{
            type: HostBinding
        }], value: [{
            type: HostBinding
        }], disabled: [{
            type: HostBinding,
            args: ['disabled']
        }, {
            type: HostBinding,
            args: ['class.disabled']
        }, {
            type: HostBinding,
            args: ['attr.aria-disabled']
        }], required: [{
            type: HostBinding,
            args: ['required']
        }, {
            type: HostBinding,
            args: ['class.required']
        }, {
            type: HostBinding,
            args: ['attr.aria-required']
        }], invalid: [{
            type: HostBinding,
            args: ['class.invalid']
        }, {
            type: HostBinding,
            args: ['attr.aria-invalid']
        }], tabindex: [{
            type: HostBinding
        }], placeholder: [{
            type: HostBinding,
            args: ['attr.placeholder']
        }], enterKeyHint: [{
            type: HostBinding,
            args: ['attr.enterkeyhint']
        }], ariaDescribedBy: [{
            type: HostBinding,
            args: ['attr.aria-describedby']
        }], ariaLabel: [{
            type: HostBinding,
            args: ['attr.aria-label']
        }], ariaLabelledBy: [{
            type: HostBinding,
            args: ['attr.aria-labelledby']
        }], icon: [{
            type: Input
        }], iconPosition: [{
            type: Input
        }, {
            type: HostBinding,
            args: ['attr.icon-position']
        }], width: [{
            type: Input
        }, {
            type: HostBinding,
            args: ['class.full']
        }], type: [{
            type: Input
        }, {
            type: HostBinding,
            args: ['type']
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(StlrInputComponent, { className: "StlrInputComponent", filePath: "lib/input/input.component.ts", lineNumber: 35 }); })();

const INPUT_API = [
    StlrInputComponent
];

const _c0$8 = ["*"];
class StlrFormFieldGroupComponent {
    static { this.ɵfac = function StlrFormFieldGroupComponent_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || StlrFormFieldGroupComponent)(); }; }
    static { this.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: StlrFormFieldGroupComponent, selectors: [["stlr-form-field-group"]], standalone: false, ngContentSelectors: _c0$8, decls: 1, vars: 0, template: function StlrFormFieldGroupComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵprojection(0);
        } }, styles: ["[_nghost-%COMP%]    {display:flex;flex-direction:row;width:100%;gap:10px}@media(max-width:960px){[_nghost-%COMP%]    {flex-direction:column}}"] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrFormFieldGroupComponent, [{
        type: Component,
        args: [{ selector: 'stlr-form-field-group', standalone: false, template: "<ng-content></ng-content>\n", styles: [":host ::ng-deep{display:flex;flex-direction:row;width:100%;gap:10px}@media(max-width:960px){:host ::ng-deep{flex-direction:column}}\n"] }]
    }], null, null); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(StlrFormFieldGroupComponent, { className: "StlrFormFieldGroupComponent", filePath: "lib/legacy/form-field-group/form-field-group.component.ts", lineNumber: 9 }); })();

const _c0$7 = ["stlr-form", ""];
const _c1$4 = ["*"];
/**
 * @deprecated Use a regular form and `FormGroup` instead.
 */
class StlrFormComponent {
    constructor() {
        /**
         * When a button with a type of "submit" is clicked, the form group validation functionality is executed.
         * This event emits true or false based on whether or not the formGroup validates without errors.
         */
        this.validated = new EventEmitter();
        /**
         * When a button with a type of "reset" is clicked, the form group reset functionality is executed
         * and this output event is fired.
         */
        this.resetForm = new EventEmitter();
        /**
         * A list of errors produced by the form group.
         */
        this.submitErrors = new EventEmitter();
    }
    validate() {
        if (this.formGroup == null) {
            throw new Error('Interstellar form has not been assigned a valid FormGroup');
        }
        this.validateAllFields();
        this.formGroup.markAsTouched();
        this.formGroup.updateValueAndValidity();
        if (this.formGroup.valid) {
            this.submitErrors.emit([]);
            this.validated.emit(true);
        }
        else {
            if (this.formGroup.errors == null) {
                this.submitErrors.emit(['invalid']);
            }
            else {
                this.submitErrors.emit(Object.keys(this.formGroup.errors));
            }
            this.validated.emit(false);
        }
    }
    reset() {
        if (this.formGroup == null) {
            throw new Error('Form has not been assigned a valid FormGroup');
        }
        if (this.resetObject == null) {
            this.formGroup.reset();
        }
        else {
            this.formGroup.reset(this.resetObject);
        }
        this.resetForm.emit();
    }
    validateAllFields(formGroupOrArray) {
        const fGroupOrArray = formGroupOrArray ?? this.formGroup;
        if (!fGroupOrArray) {
            throw new Error('Form group or array is undefined');
        }
        Object.keys(fGroupOrArray.controls).forEach((field) => {
            const control = fGroupOrArray.get(field);
            if (control instanceof FormControl) {
                control.markAsTouched();
                control.updateValueAndValidity();
            }
            else if (control instanceof FormArray) {
                this.validateAllFields(control);
            }
            else if (control instanceof FormGroup) {
                this.validateAllFields(control);
            }
        });
    }
    static { this.ɵfac = function StlrFormComponent_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || StlrFormComponent)(); }; }
    static { this.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: StlrFormComponent, selectors: [["form", "stlr-form", ""]], inputs: { formGroup: "formGroup", resetObject: "resetObject" }, outputs: { validated: "validated", resetForm: "resetForm", submitErrors: "submitErrors" }, standalone: false, attrs: _c0$7, ngContentSelectors: _c1$4, decls: 1, vars: 0, template: function StlrFormComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵprojection(0);
        } }, encapsulation: 2 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrFormComponent, [{
        type: Component,
        args: [{ selector: 'form[stlr-form]', standalone: false, template: "<ng-content></ng-content>\n" }]
    }], null, { validated: [{
            type: Output
        }], resetForm: [{
            type: Output
        }], submitErrors: [{
            type: Output
        }], formGroup: [{
            type: Input
        }], resetObject: [{
            type: Input
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(StlrFormComponent, { className: "StlrFormComponent", filePath: "lib/legacy/form/form.component.ts", lineNumber: 14 }); })();

const _c0$6 = ["stlr-label-block", ""];
const _c1$3 = ["*"];
class StlrLabelBlockComponent {
    static { this.ɵfac = function StlrLabelBlockComponent_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || StlrLabelBlockComponent)(); }; }
    static { this.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: StlrLabelBlockComponent, selectors: [["label", "stlr-label-block", ""]], hostVars: 1, hostBindings: function StlrLabelBlockComponent_HostBindings(rf, ctx) { if (rf & 2) {
            i0.ɵɵattribute("type", ctx.type);
        } }, inputs: { type: "type" }, standalone: false, attrs: _c0$6, ngContentSelectors: _c1$3, decls: 1, vars: 0, template: function StlrLabelBlockComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵprojection(0);
        } }, styles: ["[_nghost-%COMP%]{position:relative;display:inline-flex;align-items:center;font-size:.85em;line-height:1;transition:all .2s;cursor:pointer}[_nghost-%COMP%]   input[_ngcontent-%COMP%]{z-index:2}[_nghost-%COMP%]{padding:10px}[_nghost-%COMP%]     input{margin:0 5px 0 0;z-index:2}.is-selected[_nghost-%COMP%]{border:solid 1px var(--stlr-color__info)}.is-selected[_nghost-%COMP%]:after{opacity:.08}[type=legacy][_nghost-%COMP%]{padding:5px;background-color:#0000;font-size:14px;color:var(--stlr-color__copy--subdued)}[type=legacy][_nghost-%COMP%]   small[_ngcontent-%COMP%]{color:var(--stlr-color__disabled)}"], changeDetection: 0 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrLabelBlockComponent, [{
        type: Component,
        args: [{ selector: 'label[stlr-label-block]', changeDetection: ChangeDetectionStrategy.OnPush, standalone: false, template: "<ng-content></ng-content>", styles: [":host{position:relative;display:inline-flex;align-items:center;font-size:.85em;line-height:1;transition:all .2s;cursor:pointer}:host input{z-index:2}:host{padding:10px}:host ::ng-deep input{margin:0 5px 0 0;z-index:2}:host.is-selected{border:solid 1px var(--stlr-color__info)}:host.is-selected:after{opacity:.08}:host[type=legacy]{padding:5px;background-color:#0000;font-size:14px;color:var(--stlr-color__copy--subdued)}:host[type=legacy] small{color:var(--stlr-color__disabled)}\n"] }]
    }], null, { type: [{
            type: HostBinding,
            args: ['attr.type']
        }, {
            type: Input
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(StlrLabelBlockComponent, { className: "StlrLabelBlockComponent", filePath: "lib/legacy/label-block/label-block.component.ts", lineNumber: 16 }); })();

/**
 * @deprecated Use `new FormGroup()` directly from `@angular/forms` instead.
 */
function stellarFormGroup(controls, validatorOrOpts, asyncValidator) {
    return new FormGroup(controls, validatorOrOpts, asyncValidator);
}
/**
 * @deprecated Use `new FormControl()` directly from `@angular/forms` instead.
 */
function stellarFormControl(value, validators, asyncValidators) {
    // @ts-ignore
    return new FormControl(value, validators, asyncValidators);
}
/**
 * @deprecated Use `new FormArray()` directly from `@angular/forms` instead.
 * @param controls
 * @param validatorOrOptions
 * @param asyncValidators
 */
function stellarFormArray(controls, validatorOrOptions, asyncValidators) {
    return new FormArray(controls, validatorOrOptions, asyncValidators);
}
/**
 * @deprecated Use `new FormGroup()` directly from `@angular/forms` instead.
 * @param form
 */
function forEachControlIn(form) {
    const controls = form != null && form.controls != null
        ? Array.isArray(form.controls)
            ? form.controls
            : Object.getOwnPropertyNames(form.controls).map((name) => {
                // @ts-ignore
                return form.controls[name];
            })
        : [];
    const composer = {
        /**
         * Enumerate which methods to call.
         * @param methods which methods to call - as typed string enum
         *
         * @example
         *
         * forEachControlIn(form).call('markAsPristine', 'markAsTouched', 'disable')
         */
        call(...methods) {
            if (controls != null && Array.isArray(controls)) {
                controls.forEach((c) => {
                    methods.forEach((m) => {
                        if (c[m] && typeof c[m] === 'function') {
                            c[m]();
                        }
                    });
                    // catch the case where we have a control that is form array/group - so for each of the children call methods
                    if (c.controls != null) {
                        forEachControlIn(c).call(...methods);
                    }
                });
            }
            return composer;
        },
        markAsDirtySimultaneouslyWith(c) {
            if (c != null) {
                const markAsDirtyOriginal = c.markAsDirty.bind(c);
                c.markAsDirty = () => {
                    markAsDirtyOriginal();
                    composer.call('markAsDirty');
                };
                const markAsPristineOriginal = c.markAsPristine.bind(c);
                c.markAsPristine = () => {
                    markAsPristineOriginal();
                    composer.call('markAsPristine');
                };
            }
            return composer;
        },
        markAsTouchedSimultaneouslyWith(c, touchIsChildInitiated) {
            if (c != null) {
                const markAsTouchedOriginal = c.markAsTouched.bind(c);
                c.markAsTouched = () => {
                    markAsTouchedOriginal();
                    if (!touchIsChildInitiated || !touchIsChildInitiated()) {
                        composer.call('markAsTouched');
                    }
                };
                const markAsUntouchedOriginal = c.markAsUntouched.bind(c);
                c.markAsUntouched = () => {
                    markAsUntouchedOriginal();
                    composer.call('markAsUntouched');
                };
            }
            return composer;
        },
        /**
         * Get the errors in the controls from our form and append their errors to the `form` (in forEachControlIn(form) form)
         * @param ctrl the control that should be invalid if on of our controls is
         */
        addValidatorsTo(ctrl) {
            if (ctrl != null) {
                ctrl.validator = Validators.compose([
                    ctrl.validator,
                    () => {
                        // could overwrite some errors - but we only need it to know the "parent" form is valid or not
                        const errors = controls.reduce((e, next) => ({ ...e, ...next.errors }), {});
                        return controls.some((c) => c.errors != null) ? errors : null;
                    },
                ]);
            }
            return composer;
        },
    };
    return composer;
}

const LEGACY_API = [
    StlrFormComponent,
    StlrFormFieldGroupComponent,
    StlrLabelBlockComponent,
];

/* eslint-disable @angular-eslint/directive-selector */
class StlrPhoneValidatorDirective {
    constructor() {
        this.maxLength = () => {
            const value = this.phoneComponent.value;
            return value?.phoneNumber && value.phoneNumber.length > this.phoneComponent.maxLength ? { maxlength: true } : null;
        };
        this.minLength = () => {
            const value = this.phoneComponent.value;
            return value?.phoneNumber && value.phoneNumber.length < this.phoneComponent.minLength ? { minlength: true } : null;
        };
        this.pattern = () => {
            const value = this.phoneComponent.value;
            const regex = typeof this.phoneComponent.pattern === 'string' ? new RegExp(this.phoneComponent.pattern) : this.phoneComponent.pattern;
            const valid = value?.phoneNumber ? regex.test(value.phoneNumber) : true;
            return !valid ? { pattern: true } : null;
        };
        this.countryCodeRequired = () => {
            const value = this.phoneComponent.value;
            return value?.phoneCode == null ? { countryCode: true } : null;
        };
        this.phoneNumberRequired = () => {
            const value = this.phoneComponent.value;
            return value?.phoneNumber == null ? { phoneNumber: true } : null;
        };
    }
    validate() {
        const errors = {
            ...this.maxLength(),
            ...this.minLength(),
            ...this.pattern(),
            ...this.countryCodeRequired(),
            ...this.phoneNumberRequired()
        };
        return Object.keys(errors).length > 0 ? errors : null;
    }
    static { this.ɵfac = function StlrPhoneValidatorDirective_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || StlrPhoneValidatorDirective)(); }; }
    static { this.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: StlrPhoneValidatorDirective, selectors: [["stlr-phone", "formControl", ""], ["stlr-phone", "formControlName", ""], ["stlr-phone", "ngModel", ""]], standalone: false, features: [i0.ɵɵProvidersFeature([
                {
                    provide: NG_VALIDATORS,
                    useExisting: forwardRef(() => StlrPhoneValidatorDirective),
                    multi: true
                }
            ])] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrPhoneValidatorDirective, [{
        type: Directive,
        args: [{
                selector: 'stlr-phone[formControl],stlr-phone[formControlName],stlr-phone[ngModel]',
                providers: [
                    {
                        provide: NG_VALIDATORS,
                        useExisting: forwardRef(() => StlrPhoneValidatorDirective),
                        multi: true
                    }
                ],
                standalone: false
            }]
    }], null, null); })();

const CountryData = [
    {
        name: "Afghanistan",
        code: "AF",
        regionName: "Province",
        regions: [
            {
                name: "Balkh"
            },
            {
                name: "Bāmyān"
            },
            {
                name: "Bādghīs"
            },
            {
                name: "Badakhshān"
            },
            {
                name: "Baghlān"
            },
            {
                name: "Dāykundī"
            },
            {
                name: "Farāh"
            },
            {
                name: "Fāryāb"
            },
            {
                name: "Ghaznī"
            },
            {
                name: "Ghōr"
            },
            {
                name: "Helmand"
            },
            {
                name: "Herāt"
            },
            {
                name: "Jowzjān"
            },
            {
                name: "Kābul"
            },
            {
                name: "Kandahār"
            },
            {
                name: "Kāpīsā"
            },
            {
                name: "Kunduz"
            },
            {
                name: "Khōst"
            },
            {
                name: "Kunar"
            },
            {
                name: "Laghmān"
            },
            {
                name: "Lōgar"
            },
            {
                name: "Nangarhār"
            },
            {
                name: "Nīmrōz"
            },
            {
                name: "Nūristān"
            },
            {
                name: "Panjshayr"
            },
            {
                name: "Parwān"
            },
            {
                name: "Paktiyā"
            },
            {
                name: "Paktīkā"
            },
            {
                name: "Samangān"
            },
            {
                name: "Sar-e Pul"
            },
            {
                name: "Takhār"
            },
            {
                name: "Uruzgān"
            },
            {
                name: "Wardak"
            },
            {
                name: "Zābul"
            }
        ],
        phoneCodes: [
            93
        ]
    },
    {
        name: "Åland Islands",
        code: "AX",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            358
        ]
    },
    {
        name: "Albania",
        code: "AL",
        regionName: "District",
        regions: [
            {
                name: "Berat"
            },
            {
                name: "Durrës"
            },
            {
                name: "Elbasan"
            },
            {
                name: "Fier"
            },
            {
                name: "Gjirokastër"
            },
            {
                name: "Korçë"
            },
            {
                name: "Kukës"
            },
            {
                name: "Lezhë"
            },
            {
                name: "Dibër"
            },
            {
                name: "Shkodër"
            },
            {
                name: "Tiranë"
            },
            {
                name: "Vlorë"
            },
            {
                name: "Berat"
            },
            {
                name: "Bulqizë"
            },
            {
                name: "Dibër"
            },
            {
                name: "Delvinë"
            },
            {
                name: "Durrës"
            },
            {
                name: "Devoll"
            },
            {
                name: "Elbasan"
            },
            {
                name: "Kolonjë"
            },
            {
                name: "Fier"
            },
            {
                name: "Gjirokastër"
            },
            {
                name: "Gramsh"
            },
            {
                name: "Has"
            },
            {
                name: "Kavajë"
            },
            {
                name: "Kurbin"
            },
            {
                name: "Kuçovë"
            },
            {
                name: "Korçë"
            },
            {
                name: "Krujë"
            },
            {
                name: "Kukës"
            },
            {
                name: "Librazhd"
            },
            {
                name: "Lezhë"
            },
            {
                name: "Lushnjë"
            },
            {
                name: "Mallakastër"
            },
            {
                name: "Malësi e Madhe"
            },
            {
                name: "Mirditë"
            },
            {
                name: "Mat"
            },
            {
                name: "Pogradec"
            },
            {
                name: "Peqin"
            },
            {
                name: "Përmet"
            },
            {
                name: "Pukë"
            },
            {
                name: "Shkodër"
            },
            {
                name: "Skrapar"
            },
            {
                name: "Sarandë"
            },
            {
                name: "Tepelenë"
            },
            {
                name: "Tropojë"
            },
            {
                name: "Tiranë"
            },
            {
                name: "Vlorë"
            }
        ],
        phoneCodes: [
            355
        ]
    },
    {
        name: "Algeria",
        code: "DZ",
        regionName: "Province",
        regions: [
            {
                name: "Adrar"
            },
            {
                name: "Chlef"
            },
            {
                name: "Laghouat"
            },
            {
                name: "Oum el Bouaghi"
            },
            {
                name: "Batna"
            },
            {
                name: "Béjaïa"
            },
            {
                name: "Biskra"
            },
            {
                name: "Béchar"
            },
            {
                name: "Blida"
            },
            {
                name: "Bouira"
            },
            {
                name: "Tamanghasset"
            },
            {
                name: "Tébessa"
            },
            {
                name: "Tlemcen"
            },
            {
                name: "Tiaret"
            },
            {
                name: "Tizi Ouzou"
            },
            {
                name: "Alger"
            },
            {
                name: "Djelfa"
            },
            {
                name: "Jijel"
            },
            {
                name: "Sétif"
            },
            {
                name: "Saïda"
            },
            {
                name: "Skikda"
            },
            {
                name: "Sidi Bel Abbès"
            },
            {
                name: "Annaba"
            },
            {
                name: "Guelma"
            },
            {
                name: "Constantine"
            },
            {
                name: "Médéa"
            },
            {
                name: "Mostaganem"
            },
            {
                name: "Msila"
            },
            {
                name: "Mascara"
            },
            {
                name: "Ouargla"
            },
            {
                name: "Oran"
            },
            {
                name: "El Bayadh"
            },
            {
                name: "Illizi"
            },
            {
                name: "Bordj Bou Arréridj"
            },
            {
                name: "Boumerdès"
            },
            {
                name: "El Tarf"
            },
            {
                name: "Tindouf"
            },
            {
                name: "Tissemsilt"
            },
            {
                name: "El Oued"
            },
            {
                name: "Khenchela"
            },
            {
                name: "Souk Ahras"
            },
            {
                name: "Tipaza"
            },
            {
                name: "Mila"
            },
            {
                name: "Aïn Defla"
            },
            {
                name: "Naama"
            },
            {
                name: "Aïn Témouchent"
            },
            {
                name: "Ghardaïa"
            },
            {
                name: "Relizane"
            }
        ],
        phoneCodes: [
            213
        ]
    },
    {
        name: "American Samoa",
        code: "AS",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            1
        ]
    },
    {
        name: "Andorra",
        code: "AD",
        regionName: "Parish",
        regions: [
            {
                name: "Canillo"
            },
            {
                name: "Encamp"
            },
            {
                name: "La Massana"
            },
            {
                name: "Ordino"
            },
            {
                name: "Sant Julià de Lòria"
            },
            {
                name: "Andorra la Vella"
            },
            {
                name: "Escaldes-Engordany"
            }
        ],
        phoneCodes: [
            376
        ]
    },
    {
        name: "Angola",
        code: "AO",
        regionName: "Province",
        regions: [
            {
                name: "Bengo"
            },
            {
                name: "Benguela"
            },
            {
                name: "Bié"
            },
            {
                name: "Cabinda"
            },
            {
                name: "Cuando-Cubango"
            },
            {
                name: "Cunene"
            },
            {
                name: "Cuanza Norte"
            },
            {
                name: "Cuanza Sul"
            },
            {
                name: "Huambo"
            },
            {
                name: "Huíla"
            },
            {
                name: "Lunda Norte"
            },
            {
                name: "Lunda Sul"
            },
            {
                name: "Luanda"
            },
            {
                name: "Malange"
            },
            {
                name: "Moxico"
            },
            {
                name: "Namibe"
            },
            {
                name: "Uíge"
            },
            {
                name: "Zaire"
            }
        ],
        phoneCodes: [
            244
        ]
    },
    {
        name: "Anguilla",
        code: "AI",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            1
        ]
    },
    {
        name: "Antarctica",
        code: "AQ",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            672
        ]
    },
    {
        name: "Antigua and Barbuda",
        code: "AG",
        regionName: "Parish",
        regions: [
            {
                name: "Saint George"
            },
            {
                name: "Saint John"
            },
            {
                name: "Saint Mary"
            },
            {
                name: "Saint Paul"
            },
            {
                name: "Saint Peter"
            },
            {
                name: "Saint Philip"
            },
            {
                name: "Barbuda"
            },
            {
                name: "Redonda"
            }
        ],
        phoneCodes: [
            1
        ]
    },
    {
        name: "Argentina",
        code: "AR",
        regionName: "Province",
        regions: [
            {
                name: "Salta"
            },
            {
                name: "Buenos Aires"
            },
            {
                name: "Ciudad Autónoma de Buenos Aires"
            },
            {
                name: "San Luis"
            },
            {
                name: "Entre Rios"
            },
            {
                name: "Santiago del Estero"
            },
            {
                name: "Chaco"
            },
            {
                name: "San Juan"
            },
            {
                name: "Catamarca"
            },
            {
                name: "La Pampa"
            },
            {
                name: "Mendoza"
            },
            {
                name: "Misiones"
            },
            {
                name: "Formosa"
            },
            {
                name: "Neuquen"
            },
            {
                name: "Rio Negro"
            },
            {
                name: "Santa Fe"
            },
            {
                name: "Tucuman"
            },
            {
                name: "Chubut"
            },
            {
                name: "Tierra del Fuego"
            },
            {
                name: "Corrientes"
            },
            {
                name: "Cordoba"
            },
            {
                name: "Jujuy"
            },
            {
                name: "Santa Cruz"
            }
        ],
        phoneCodes: [
            54
        ]
    },
    {
        name: "Armenia",
        code: "AM",
        regionName: "Province",
        regions: [
            {
                name: "Aragacotn"
            },
            {
                name: "Ararat"
            },
            {
                name: "Armavir"
            },
            {
                name: "Erevan"
            },
            {
                name: "Gegarkunik'"
            },
            {
                name: "Kotayk'"
            },
            {
                name: "Lory"
            },
            {
                name: "Sirak"
            },
            {
                name: "Syunik'"
            },
            {
                name: "Tavus"
            },
            {
                name: "Vayoc Jor"
            }
        ],
        phoneCodes: [
            374
        ]
    },
    {
        name: "Aruba",
        code: "AW",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            297
        ]
    },
    {
        name: "Australia",
        code: "AU",
        regionName: "State",
        regions: [
            {
                name: "Australian Capital Territory"
            },
            {
                name: "New South Wales"
            },
            {
                name: "Northern Territory"
            },
            {
                name: "Queensland"
            },
            {
                name: "South Australia"
            },
            {
                name: "Tasmania"
            },
            {
                name: "Victoria"
            },
            {
                name: "Western Australia"
            }
        ],
        phoneCodes: [
            61
        ]
    },
    {
        name: "Austria",
        code: "AT",
        regionName: "State",
        regions: [
            {
                name: "Burgenland"
            },
            {
                name: "Kärnten"
            },
            {
                name: "Niederösterreich"
            },
            {
                name: "Oberösterreich"
            },
            {
                name: "Salzburg"
            },
            {
                name: "Steiermark"
            },
            {
                name: "Tirol"
            },
            {
                name: "Vorarlberg"
            },
            {
                name: "Wien"
            }
        ],
        phoneCodes: [
            43
        ]
    },
    {
        name: "Azerbaijan",
        code: "AZ",
        regionName: "Rayon",
        regions: [
            {
                name: "Abşeron"
            },
            {
                name: "Ağstafa"
            },
            {
                name: "Ağcabədi"
            },
            {
                name: "Ağdam"
            },
            {
                name: "Ağdaş"
            },
            {
                name: "Ağsu"
            },
            {
                name: "Astara"
            },
            {
                name: "Bakı"
            },
            {
                name: "Babək"
            },
            {
                name: "Balakən"
            },
            {
                name: "Bərdə"
            },
            {
                name: "Beyləqan"
            },
            {
                name: "Biləsuvar"
            },
            {
                name: "Cəbrayıl"
            },
            {
                name: "Cəlilabab"
            },
            {
                name: "Culfa"
            },
            {
                name: "Daşkəsən"
            },
            {
                name: "Füzuli"
            },
            {
                name: "Gəncə"
            },
            {
                name: "Gədəbəy"
            },
            {
                name: "Goranboy"
            },
            {
                name: "Göyçay"
            },
            {
                name: "Göygöl"
            },
            {
                name: "Hacıqabul"
            },
            {
                name: "İmişli"
            },
            {
                name: "İsmayıllı"
            },
            {
                name: "Kəlbəcər"
            },
            {
                name: "Kǝngǝrli"
            },
            {
                name: "Kürdəmir"
            },
            {
                name: "Lənkəran"
            },
            {
                name: "Laçın"
            },
            {
                name: "Lənkəran"
            },
            {
                name: "Lerik"
            },
            {
                name: "Masallı"
            },
            {
                name: "Mingəçevir"
            },
            {
                name: "Naftalan"
            },
            {
                name: "Neftçala"
            },
            {
                name: "Naxçıvan"
            },
            {
                name: "Naxçıvan"
            },
            {
                name: "Oğuz"
            },
            {
                name: "Ordubad"
            },
            {
                name: "Qəbələ"
            },
            {
                name: "Qax"
            },
            {
                name: "Qazax"
            },
            {
                name: "Quba"
            },
            {
                name: "Qubadlı"
            },
            {
                name: "Qobustan"
            },
            {
                name: "Qusar"
            },
            {
                name: "Şəki"
            },
            {
                name: "Sabirabad"
            },
            {
                name: "Sədərək"
            },
            {
                name: "Şahbuz"
            },
            {
                name: "Şəki"
            },
            {
                name: "Salyan"
            },
            {
                name: "Şərur"
            },
            {
                name: "Saatlı"
            },
            {
                name: "Şabran"
            },
            {
                name: "Siyəzən"
            },
            {
                name: "Şəmkir"
            },
            {
                name: "Sumqayıt"
            },
            {
                name: "Şamaxı"
            },
            {
                name: "Samux"
            },
            {
                name: "Şirvan"
            },
            {
                name: "Şuşa"
            },
            {
                name: "Tərtər"
            },
            {
                name: "Tovuz"
            },
            {
                name: "Ucar"
            },
            {
                name: "Xankəndi"
            },
            {
                name: "Xaçmaz"
            },
            {
                name: "Xocalı"
            },
            {
                name: "Xızı"
            },
            {
                name: "Xocavənd"
            },
            {
                name: "Yardımlı"
            },
            {
                name: "Yevlax"
            },
            {
                name: "Yevlax"
            },
            {
                name: "Zəngilan"
            },
            {
                name: "Zaqatala"
            },
            {
                name: "Zərdab"
            }
        ],
        phoneCodes: [
            994
        ]
    },
    {
        name: "Bahamas",
        code: "BS",
        regionName: "District",
        regions: [
            {
                name: "Acklins"
            },
            {
                name: "Bimini"
            },
            {
                name: "Black Point"
            },
            {
                name: "Berry Islands"
            },
            {
                name: "Central Eleuthera"
            },
            {
                name: "Cat Island"
            },
            {
                name: "Crooked Island and Long Cay"
            },
            {
                name: "Central Abaco"
            },
            {
                name: "Central Andros"
            },
            {
                name: "East Grand Bahama"
            },
            {
                name: "Exuma"
            },
            {
                name: "City of Freeport"
            },
            {
                name: "Grand Cay"
            },
            {
                name: "Harbour Island"
            },
            {
                name: "Hope Town"
            },
            {
                name: "Inagua"
            },
            {
                name: "Long Island"
            },
            {
                name: "Mangrove Cay"
            },
            {
                name: "Mayaguana"
            },
            {
                name: "Moore's Island"
            },
            {
                name: "North Eleuthera"
            },
            {
                name: "North Abaco"
            },
            {
                name: "North Andros"
            },
            {
                name: "Rum Cay"
            },
            {
                name: "Ragged Island"
            },
            {
                name: "South Andros"
            },
            {
                name: "South Eleuthera"
            },
            {
                name: "South Abaco"
            },
            {
                name: "San Salvador"
            },
            {
                name: "Spanish Wells"
            },
            {
                name: "West Grand Bahama"
            }
        ],
        phoneCodes: [
            1
        ]
    },
    {
        name: "Bahrain",
        code: "BH",
        regionName: "Governorate",
        regions: [
            {
                name: "Al Manāmah (Al ‘Āşimah)"
            },
            {
                name: "Al Janūbīyah"
            },
            {
                name: "Al Muḩarraq"
            },
            {
                name: "Al Wusţá"
            },
            {
                name: "Ash Shamālīyah"
            }
        ],
        phoneCodes: [
            973
        ]
    },
    {
        name: "Bangladesh",
        code: "BD",
        regionName: "District",
        regions: [
            {
                name: "Bandarban"
            },
            {
                name: "Barguna"
            },
            {
                name: "Bogra"
            },
            {
                name: "Brahmanbaria"
            },
            {
                name: "Bagerhat"
            },
            {
                name: "Barisal"
            },
            {
                name: "Bhola"
            },
            {
                name: "Comilla"
            },
            {
                name: "Chandpur"
            },
            {
                name: "Chittagong"
            },
            {
                name: "Cox's Bazar"
            },
            {
                name: "Chuadanga"
            },
            {
                name: "Dhaka"
            },
            {
                name: "Dinajpur"
            },
            {
                name: "Faridpur"
            },
            {
                name: "Feni"
            },
            {
                name: "Gopalganj"
            },
            {
                name: "Gazipur"
            },
            {
                name: "Gaibandha"
            },
            {
                name: "Habiganj"
            },
            {
                name: "Jamalpur"
            },
            {
                name: "Jessore"
            },
            {
                name: "Jhenaidah"
            },
            {
                name: "Jaipurhat"
            },
            {
                name: "Jhalakati"
            },
            {
                name: "Kishorganj"
            },
            {
                name: "Khulna"
            },
            {
                name: "Kurigram"
            },
            {
                name: "Khagrachari"
            },
            {
                name: "Kushtia"
            },
            {
                name: "Lakshmipur"
            },
            {
                name: "Lalmonirhat"
            },
            {
                name: "Manikganj"
            },
            {
                name: "Mymensingh"
            },
            {
                name: "Munshiganj"
            },
            {
                name: "Madaripur"
            },
            {
                name: "Magura"
            },
            {
                name: "Moulvibazar"
            },
            {
                name: "Meherpur"
            },
            {
                name: "Narayanganj"
            },
            {
                name: "Netrakona"
            },
            {
                name: "Narsingdi"
            },
            {
                name: "Narail"
            },
            {
                name: "Natore"
            },
            {
                name: "Nawabganj"
            },
            {
                name: "Nilphamari"
            },
            {
                name: "Noakhali"
            },
            {
                name: "Naogaon"
            },
            {
                name: "Pabna"
            },
            {
                name: "Pirojpur"
            },
            {
                name: "Patuakhali"
            },
            {
                name: "Panchagarh"
            },
            {
                name: "Rajbari"
            },
            {
                name: "Rajshahi"
            },
            {
                name: "Rangpur"
            },
            {
                name: "Rangamati"
            },
            {
                name: "Sherpur"
            },
            {
                name: "Satkhira"
            },
            {
                name: "Sirajganj"
            },
            {
                name: "Sylhet"
            },
            {
                name: "Sunamganj"
            },
            {
                name: "Shariatpur"
            },
            {
                name: "Tangail"
            },
            {
                name: "Thakurgaon"
            },
            {
                name: "Barisal"
            },
            {
                name: "Chittagong"
            },
            {
                name: "Dhaka"
            },
            {
                name: "Khulna"
            },
            {
                name: "Rajshahi"
            },
            {
                name: "Rangpur"
            },
            {
                name: "Sylhet"
            }
        ],
        phoneCodes: [
            880
        ]
    },
    {
        name: "Barbados",
        code: "BB",
        regionName: "Parish",
        regions: [
            {
                name: "Christ Church"
            },
            {
                name: "Saint Andrew"
            },
            {
                name: "Saint George"
            },
            {
                name: "Saint James"
            },
            {
                name: "Saint John"
            },
            {
                name: "Saint Joseph"
            },
            {
                name: "Saint Lucy"
            },
            {
                name: "Saint Michael"
            },
            {
                name: "Saint Peter"
            },
            {
                name: "Saint Philip"
            },
            {
                name: "Saint Thomas"
            }
        ],
        phoneCodes: [
            1
        ]
    },
    {
        name: "Belarus",
        code: "BY",
        regionName: "Oblast",
        regions: [
            {
                name: "Bresckaja voblasć"
            },
            {
                name: "Horad Minsk"
            },
            {
                name: "Homieĺskaja voblasć"
            },
            {
                name: "Hrodzienskaja voblasć"
            },
            {
                name: "Mahilioŭskaja voblasć"
            },
            {
                name: "Minskaja voblasć"
            },
            {
                name: "Viciebskaja voblasć"
            }
        ],
        phoneCodes: [
            375
        ]
    },
    {
        name: "Belgium",
        code: "BE",
        regionName: "Province",
        regions: [
            {
                name: "Bruxelles-Capitale, Région de;Brussels Hoofdstedelijk Gewest"
            },
            {
                name: "Antwerpen"
            },
            {
                name: "Vlaams-Brabant"
            },
            {
                name: "Vlaams Gewest"
            },
            {
                name: "Limburg"
            },
            {
                name: "Oost-Vlaanderen"
            },
            {
                name: "West-Vlaanderen"
            },
            {
                name: "wallonne, Région"
            },
            {
                name: "Brabant wallon"
            },
            {
                name: "Hainaut"
            },
            {
                name: "Liège"
            },
            {
                name: "Luxembourg"
            },
            {
                name: "Namur"
            }
        ],
        phoneCodes: [
            32
        ]
    },
    {
        name: "Belize",
        code: "BZ",
        regionName: "District",
        regions: [
            {
                name: "Belize"
            },
            {
                name: "Cayo"
            },
            {
                name: "Corozal"
            },
            {
                name: "Orange Walk"
            },
            {
                name: "Stann Creek"
            },
            {
                name: "Toledo"
            }
        ],
        phoneCodes: [
            501
        ]
    },
    {
        name: "Benin",
        code: "BJ",
        regionName: "Department",
        regions: [
            {
                name: "Atakora"
            },
            {
                name: "Alibori"
            },
            {
                name: "Atlantique"
            },
            {
                name: "Borgou"
            },
            {
                name: "Collines"
            },
            {
                name: "Donga"
            },
            {
                name: "Kouffo"
            },
            {
                name: "Littoral"
            },
            {
                name: "Mono"
            },
            {
                name: "Ouémé"
            },
            {
                name: "Plateau"
            },
            {
                name: "Zou"
            }
        ],
        phoneCodes: [
            229
        ]
    },
    {
        name: "Bermuda",
        code: "BM",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            1
        ]
    },
    {
        name: "Bhutan",
        code: "BT",
        regionName: "District",
        regions: [
            {
                name: "Paro"
            },
            {
                name: "Chhukha"
            },
            {
                name: "Ha"
            },
            {
                name: "Samtee"
            },
            {
                name: "Thimphu"
            },
            {
                name: "Tsirang"
            },
            {
                name: "Dagana"
            },
            {
                name: "Punakha"
            },
            {
                name: "Wangdue Phodrang"
            },
            {
                name: "Sarpang"
            },
            {
                name: "Trongsa"
            },
            {
                name: "Bumthang"
            },
            {
                name: "Zhemgang"
            },
            {
                name: "Trashigang"
            },
            {
                name: "Monggar"
            },
            {
                name: "Pemagatshel"
            },
            {
                name: "Lhuentse"
            },
            {
                name: "Samdrup Jongkha"
            },
            {
                name: "Gasa"
            },
            {
                name: "Trashi Yangtse"
            }
        ],
        phoneCodes: [
            975
        ]
    },
    {
        name: "Bolivia (Plurinational State of)",
        code: "BO",
        regionName: "Department",
        regions: [
            {
                name: "El Beni"
            },
            {
                name: "Cochabamba"
            },
            {
                name: "Chuquisaca"
            },
            {
                name: "La Paz"
            },
            {
                name: "Pando"
            },
            {
                name: "Oruro"
            },
            {
                name: "Potosí"
            },
            {
                name: "Santa Cruz"
            },
            {
                name: "Tarija"
            }
        ],
        phoneCodes: [
            591
        ]
    },
    {
        name: "Bonaire, Sint Eustatius and Saba",
        code: "BQ",
        regionName: "Special municipality",
        regions: [
            {
                name: "Bonaire"
            },
            {
                name: "Saba"
            },
            {
                name: "Sint Eustatius"
            }
        ],
        phoneCodes: [
            599
        ]
    },
    {
        name: "Bosnia and Herzegovina",
        code: "BA",
        regionName: "Canton",
        regions: [
            {
                name: "Unsko-sanski kanton"
            },
            {
                name: "Posavski kanton"
            },
            {
                name: "Tuzlanski kanton"
            },
            {
                name: "Zeničko-dobojski kanton"
            },
            {
                name: "Bosansko-podrinjski kanton"
            },
            {
                name: "Srednjobosanski kanton"
            },
            {
                name: "Hercegovačko-neretvanski kanton"
            },
            {
                name: "Zapadnohercegovački kanton"
            },
            {
                name: "Kanton Sarajevo"
            },
            {
                name: "Kanton br. 10 (Livanjski kanton)"
            },
            {
                name: "Federacija Bosne i Hercegovine"
            },
            {
                name: "Brčko distrikt"
            },
            {
                name: "Republika Srpska"
            }
        ],
        phoneCodes: [
            387
        ]
    },
    {
        name: "Botswana",
        code: "BW",
        regionName: "District",
        regions: [
            {
                name: "Central"
            },
            {
                name: "Ghanzi"
            },
            {
                name: "Kgalagadi"
            },
            {
                name: "Kgatleng"
            },
            {
                name: "Kweneng"
            },
            {
                name: "North-East"
            },
            {
                name: "North-West"
            },
            {
                name: "South-East"
            },
            {
                name: "Southern"
            }
        ],
        phoneCodes: [
            267
        ]
    },
    {
        name: "Bouvet Island",
        code: "BV",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            47
        ]
    },
    {
        name: "Brazil",
        code: "BR",
        regionName: "State",
        regions: [
            {
                name: "Acre"
            },
            {
                name: "Alagoas"
            },
            {
                name: "Amazonas"
            },
            {
                name: "Amapá"
            },
            {
                name: "Bahia"
            },
            {
                name: "Ceará"
            },
            {
                name: "Distrito Federal"
            },
            {
                name: "Espírito Santo"
            },
            {
                name: "Fernando de Noronha"
            },
            {
                name: "Goiás"
            },
            {
                name: "Maranhão"
            },
            {
                name: "Minas Gerais"
            },
            {
                name: "Mato Grosso do Sul"
            },
            {
                name: "Mato Grosso"
            },
            {
                name: "Pará"
            },
            {
                name: "Paraíba"
            },
            {
                name: "Pernambuco"
            },
            {
                name: "Piauí"
            },
            {
                name: "Paraná"
            },
            {
                name: "Rio de Janeiro"
            },
            {
                name: "Rio Grande do Norte"
            },
            {
                name: "Rondônia"
            },
            {
                name: "Roraima"
            },
            {
                name: "Rio Grande do Sul"
            },
            {
                name: "Santa Catarina"
            },
            {
                name: "Sergipe"
            },
            {
                name: "São Paulo"
            },
            {
                name: "Tocantins"
            }
        ],
        phoneCodes: [
            55
        ]
    },
    {
        name: "British Indian Ocean Territory",
        code: "IO",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            246
        ]
    },
    {
        name: "United States Minor Outlying Islands",
        code: "UM",
        regionName: "Territory",
        regions: [
            {
                name: "Johnston Atoll"
            },
            {
                name: "Midway Islands"
            },
            {
                name: "Navassa Island"
            },
            {
                name: "Wake Island"
            },
            {
                name: "Baker Island"
            },
            {
                name: "Howland Island"
            },
            {
                name: "Jarvis Island"
            },
            {
                name: "Kingman Reef"
            },
            {
                name: "Palmyra Atoll"
            }
        ],
        phoneCodes: [
            1
        ]
    },
    {
        name: "Virgin Islands (British)",
        code: "VG",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            1
        ]
    },
    {
        name: "Virgin Islands (U.S.)",
        code: "VI",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            1
        ]
    },
    {
        name: "Brunei Darussalam",
        code: "BN",
        regionName: "District",
        regions: [
            {
                name: "Belait"
            },
            {
                name: "Brunei-Muara"
            },
            {
                name: "Temburong"
            },
            {
                name: "Tutong"
            }
        ],
        phoneCodes: [
            673
        ]
    },
    {
        name: "Bulgaria",
        code: "BG",
        regionName: "Region",
        regions: [
            {
                name: "Blagoevgrad"
            },
            {
                name: "Burgas"
            },
            {
                name: "Varna"
            },
            {
                name: "Veliko Tarnovo"
            },
            {
                name: "Vidin"
            },
            {
                name: "Vratsa"
            },
            {
                name: "Gabrovo"
            },
            {
                name: "Dobrich"
            },
            {
                name: "Kardzhali"
            },
            {
                name: "Kyustendil"
            },
            {
                name: "Lovech"
            },
            {
                name: "Montana"
            },
            {
                name: "Pazardzhik"
            },
            {
                name: "Pernik"
            },
            {
                name: "Pleven"
            },
            {
                name: "Plovdiv"
            },
            {
                name: "Razgrad"
            },
            {
                name: "Ruse"
            },
            {
                name: "Silistra"
            },
            {
                name: "Sliven"
            },
            {
                name: "Smolyan"
            },
            {
                name: "Sofia-Grad"
            },
            {
                name: "Sofia"
            },
            {
                name: "Stara Zagora"
            },
            {
                name: "Targovishte"
            },
            {
                name: "Haskovo"
            },
            {
                name: "Shumen"
            },
            {
                name: "Yambol"
            }
        ],
        phoneCodes: [
            359
        ]
    },
    {
        name: "Burkina Faso",
        code: "BF",
        regionName: "Province",
        regions: [
            {
                name: "Boucle du Mouhoun"
            },
            {
                name: "Cascades"
            },
            {
                name: "Centre"
            },
            {
                name: "Centre-Est"
            },
            {
                name: "Centre-Nord"
            },
            {
                name: "Centre-Ouest"
            },
            {
                name: "Centre-Sud"
            },
            {
                name: "Est"
            },
            {
                name: "Hauts-Bassins"
            },
            {
                name: "Nord"
            },
            {
                name: "Plateau-Central"
            },
            {
                name: "Sahel"
            },
            {
                name: "Sud-Ouest"
            },
            {
                name: "Balé"
            },
            {
                name: "Bam"
            },
            {
                name: "Banwa"
            },
            {
                name: "Bazèga"
            },
            {
                name: "Bougouriba"
            },
            {
                name: "Boulgou"
            },
            {
                name: "Boulkiemdé"
            },
            {
                name: "Comoé"
            },
            {
                name: "Ganzourgou"
            },
            {
                name: "Gnagna"
            },
            {
                name: "Gourma"
            },
            {
                name: "Houet"
            },
            {
                name: "Ioba"
            },
            {
                name: "Kadiogo"
            },
            {
                name: "Kénédougou"
            },
            {
                name: "Komondjari"
            },
            {
                name: "Kompienga"
            },
            {
                name: "Koulpélogo"
            },
            {
                name: "Kossi"
            },
            {
                name: "Kouritenga"
            },
            {
                name: "Kourwéogo"
            },
            {
                name: "Léraba"
            },
            {
                name: "Loroum"
            },
            {
                name: "Mouhoun"
            },
            {
                name: "Namentenga"
            },
            {
                name: "Naouri"
            },
            {
                name: "Nayala"
            },
            {
                name: "Noumbiel"
            },
            {
                name: "Oubritenga"
            },
            {
                name: "Oudalan"
            },
            {
                name: "Passoré"
            },
            {
                name: "Poni"
            },
            {
                name: "Séno"
            },
            {
                name: "Sissili"
            },
            {
                name: "Sanmatenga"
            },
            {
                name: "Sanguié"
            },
            {
                name: "Soum"
            },
            {
                name: "Sourou"
            },
            {
                name: "Tapoa"
            },
            {
                name: "Tui"
            },
            {
                name: "Yagha"
            },
            {
                name: "Yatenga"
            },
            {
                name: "Ziro"
            },
            {
                name: "Zondoma"
            },
            {
                name: "Zoundwéogo"
            }
        ],
        phoneCodes: [
            226
        ]
    },
    {
        name: "Burundi",
        code: "BI",
        regionName: "Province",
        regions: [
            {
                name: "Bubanza"
            },
            {
                name: "Bujumbura Rural"
            },
            {
                name: "Bujumbura Mairie"
            },
            {
                name: "Bururi"
            },
            {
                name: "Cankuzo"
            },
            {
                name: "Cibitoke"
            },
            {
                name: "Gitega"
            },
            {
                name: "Kirundo"
            },
            {
                name: "Karuzi"
            },
            {
                name: "Kayanza"
            },
            {
                name: "Makamba"
            },
            {
                name: "Muramvya"
            },
            {
                name: "Mwaro"
            },
            {
                name: "Ngozi"
            },
            {
                name: "Rutana"
            },
            {
                name: "Ruyigi"
            }
        ],
        phoneCodes: [
            257
        ]
    },
    {
        name: "Cambodia",
        code: "KH",
        regionName: "Province",
        regions: [
            {
                name: "Banteay Mean Chey"
            },
            {
                name: "Krachoh"
            },
            {
                name: "Mondol Kiri"
            },
            {
                name: "Phnom Penh"
            },
            {
                name: "Preah Vihear"
            },
            {
                name: "Prey Veaeng"
            },
            {
                name: "Pousaat"
            },
            {
                name: "Rotanak Kiri"
            },
            {
                name: "Siem Reab"
            },
            {
                name: "Krong Preah Sihanouk"
            },
            {
                name: "Stueng Traeng"
            },
            {
                name: "Battambang"
            },
            {
                name: "Svaay Rieng"
            },
            {
                name: "Taakaev"
            },
            {
                name: "Otdar Mean Chey"
            },
            {
                name: "Krong Kaeb"
            },
            {
                name: "Krong Pailin"
            },
            {
                name: "Kampong Cham"
            },
            {
                name: "Kampong Chhnang"
            },
            {
                name: "Kampong Speu"
            },
            {
                name: "Kampong Thom"
            },
            {
                name: "Kampot"
            },
            {
                name: "Kandal"
            },
            {
                name: "Kach Kong"
            }
        ],
        phoneCodes: [
            855
        ]
    },
    {
        name: "Cameroon",
        code: "CM",
        regionName: "Province",
        regions: [
            {
                name: "Adamaoua"
            },
            {
                name: "Centre"
            },
            {
                name: "Far North"
            },
            {
                name: "East"
            },
            {
                name: "Littoral"
            },
            {
                name: "North"
            },
            {
                name: "North-West (Cameroon)"
            },
            {
                name: "West"
            },
            {
                name: "South"
            },
            {
                name: "South-West"
            }
        ],
        phoneCodes: [
            237
        ]
    },
    {
        name: "Canada",
        code: "CA",
        regionName: "Province",
        regions: [
            {
                name: "Alberta"
            },
            {
                name: "British Columbia"
            },
            {
                name: "Manitoba"
            },
            {
                name: "New Brunswick"
            },
            {
                name: "Newfoundland and Labrador"
            },
            {
                name: "Nova Scotia"
            },
            {
                name: "Northwest Territories"
            },
            {
                name: "Nunavut"
            },
            {
                name: "Ontario"
            },
            {
                name: "Prince Edward Island"
            },
            {
                name: "Quebec"
            },
            {
                name: "Saskatchewan"
            },
            {
                name: "Yukon Territory"
            }
        ],
        phoneCodes: [
            1
        ]
    },
    {
        name: "Cabo Verde",
        code: "CV",
        regionName: "Municipality",
        regions: [
            {
                name: "Ilhas de Barlavento"
            },
            {
                name: "Brava"
            },
            {
                name: "Boa Vista"
            },
            {
                name: "Santa Catarina"
            },
            {
                name: "Santa Catarina de Fogo"
            },
            {
                name: "Santa Cruz"
            },
            {
                name: "Maio"
            },
            {
                name: "Mosteiros"
            },
            {
                name: "Paul"
            },
            {
                name: "Porto Novo"
            },
            {
                name: "Praia"
            },
            {
                name: "Ribeira Brava"
            },
            {
                name: "Ribeira Grande"
            },
            {
                name: "Ribeira Grande de Santiago"
            },
            {
                name: "Ilhas de Sotavento"
            },
            {
                name: "São Domingos"
            },
            {
                name: "São Filipe"
            },
            {
                name: "Sal"
            },
            {
                name: "São Miguel"
            },
            {
                name: "São Lourenço dos Órgãos"
            },
            {
                name: "São Salvador do Mundo"
            },
            {
                name: "São Vicente"
            },
            {
                name: "Tarrafal"
            },
            {
                name: "Tarrafal de São Nicolau"
            }
        ],
        phoneCodes: [
            238
        ]
    },
    {
        name: "Cayman Islands",
        code: "KY",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            1
        ]
    },
    {
        name: "Central African Republic",
        code: "CF",
        regionName: "Prefecture",
        regions: [
            {
                name: "Ouham"
            },
            {
                name: "Bamingui-Bangoran"
            },
            {
                name: "Bangui"
            },
            {
                name: "Basse-Kotto"
            },
            {
                name: "Haute-Kotto"
            },
            {
                name: "Haut-Mbomou"
            },
            {
                name: "Haute-Sangha / Mambéré-Kadéï"
            },
            {
                name: "Gribingui"
            },
            {
                name: "Kémo-Gribingui"
            },
            {
                name: "Lobaye"
            },
            {
                name: "Mbomou"
            },
            {
                name: "Ombella-M'poko"
            },
            {
                name: "Nana-Mambéré"
            },
            {
                name: "Ouham-Pendé"
            },
            {
                name: "Sangha"
            },
            {
                name: "Ouaka"
            },
            {
                name: "Vakaga"
            }
        ],
        phoneCodes: [
            236
        ]
    },
    {
        name: "Chad",
        code: "TD",
        regionName: "Region",
        regions: [
            {
                name: "Al Baṭḩah"
            },
            {
                name: "Baḩr al Ghazāl"
            },
            {
                name: "Būrkū"
            },
            {
                name: "Shārī Bāqirmī"
            },
            {
                name: "Innīdī"
            },
            {
                name: "Qīrā"
            },
            {
                name: "Ḥajjar Lamīs"
            },
            {
                name: "Kānim"
            },
            {
                name: "Al Buḩayrah"
            },
            {
                name: "Lūqūn al Gharbī"
            },
            {
                name: "Lūqūn ash Sharqī"
            },
            {
                name: "Māndūl"
            },
            {
                name: "Shārī al Awsaṭ"
            },
            {
                name: "Māyū Kībbī ash Sharqī"
            },
            {
                name: "Māyū Kībbī al Gharbī"
            },
            {
                name: "Madīnat Injamīnā"
            },
            {
                name: "Waddāy"
            },
            {
                name: "Salāmāt"
            },
            {
                name: "Sīlā"
            },
            {
                name: "Tānjilī"
            },
            {
                name: "Tibastī"
            },
            {
                name: "Wādī Fīrā"
            }
        ],
        phoneCodes: [
            235
        ]
    },
    {
        name: "Chile",
        code: "CL",
        regionName: "Region",
        regions: [
            {
                name: "Aisén del General Carlos Ibáñez del Campo"
            },
            {
                name: "Antofagasta"
            },
            {
                name: "Arica y Parinacota"
            },
            {
                name: "Araucanía"
            },
            {
                name: "Atacama"
            },
            {
                name: "Bío-Bío"
            },
            {
                name: "Coquimbo"
            },
            {
                name: "Libertador General Bernardo O'Higgins"
            },
            {
                name: "Los Lagos"
            },
            {
                name: "Los Ríos"
            },
            {
                name: "Magallanes y Antártica Chilena"
            },
            {
                name: "Maule"
            },
            {
                name: "Región Metropolitana de Santiago"
            },
            {
                name: "Tarapacá"
            },
            {
                name: "Valparaíso"
            }
        ],
        phoneCodes: [
            56
        ]
    },
    {
        name: "China",
        code: "CN",
        regionName: "Province",
        regions: [
            {
                name: "Anhui Sheng"
            },
            {
                name: "Beijing Shi"
            },
            {
                name: "Chongqing Shi"
            },
            {
                name: "Fujian Sheng"
            },
            {
                name: "Guangdong Sheng"
            },
            {
                name: "Gansu Sheng"
            },
            {
                name: "Guangxi Zhuangzu Zizhiqu"
            },
            {
                name: "Guizhou Sheng"
            },
            {
                name: "Henan Sheng"
            },
            {
                name: "Hubei Sheng"
            },
            {
                name: "Hebei Sheng"
            },
            {
                name: "Hainan Sheng"
            },
            {
                name: "Hong Kong SAR (see also separate country code entry under HK)"
            },
            {
                name: "Heilongjiang Sheng"
            },
            {
                name: "Hunan Sheng"
            },
            {
                name: "Jilin Sheng"
            },
            {
                name: "Jiangsu Sheng"
            },
            {
                name: "Jiangxi Sheng"
            },
            {
                name: "Liaoning Sheng"
            },
            {
                name: "Macao SAR (see also separate country code entry under MO)"
            },
            {
                name: "Nei Mongol Zizhiqu"
            },
            {
                name: "Ningxia Huizi Zizhiqu"
            },
            {
                name: "Qinghai Sheng"
            },
            {
                name: "Sichuan Sheng"
            },
            {
                name: "Shandong Sheng"
            },
            {
                name: "Shanghai Shi"
            },
            {
                name: "Shaanxi Sheng"
            },
            {
                name: "Shanxi Sheng"
            },
            {
                name: "Tianjin Shi"
            },
            {
                name: "Taiwan Sheng (see also separate country code entry under TW)"
            },
            {
                name: "Xinjiang Uygur Zizhiqu"
            },
            {
                name: "Xizang Zizhiqu"
            },
            {
                name: "Yunnan Sheng"
            },
            {
                name: "Zhejiang Sheng"
            }
        ],
        phoneCodes: [
            86
        ]
    },
    {
        name: "Christmas Island",
        code: "CX",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            61
        ]
    },
    {
        name: "Cocos (Keeling) Islands",
        code: "CC",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            61
        ]
    },
    {
        name: "Colombia",
        code: "CO",
        regionName: "Department",
        regions: [
            {
                name: "Amazonas"
            },
            {
                name: "Antioquia"
            },
            {
                name: "Arauca"
            },
            {
                name: "Atlántico"
            },
            {
                name: "Bolívar"
            },
            {
                name: "Boyacá"
            },
            {
                name: "Caldas"
            },
            {
                name: "Caquetá"
            },
            {
                name: "Casanare"
            },
            {
                name: "Cauca"
            },
            {
                name: "Cesar"
            },
            {
                name: "Chocó"
            },
            {
                name: "Córdoba"
            },
            {
                name: "Cundinamarca"
            },
            {
                name: "Distrito Capital de Bogotá"
            },
            {
                name: "Guainía"
            },
            {
                name: "Guaviare"
            },
            {
                name: "Huila"
            },
            {
                name: "La Guajira"
            },
            {
                name: "Magdalena"
            },
            {
                name: "Meta"
            },
            {
                name: "Nariño"
            },
            {
                name: "Norte de Santander"
            },
            {
                name: "Putumayo"
            },
            {
                name: "Quindío"
            },
            {
                name: "Risaralda"
            },
            {
                name: "Santander"
            },
            {
                name: "San Andrés, Providencia y Santa Catalina"
            },
            {
                name: "Sucre"
            },
            {
                name: "Tolima"
            },
            {
                name: "Valle del Cauca"
            },
            {
                name: "Vaupés"
            },
            {
                name: "Vichada"
            }
        ],
        phoneCodes: [
            57
        ]
    },
    {
        name: "Comoros",
        code: "KM",
        regionName: "Island",
        regions: [
            {
                name: "Andjouân (Anjwān)"
            },
            {
                name: "Andjazîdja (Anjazījah)"
            },
            {
                name: "Moûhîlî (Mūhīlī)"
            }
        ],
        phoneCodes: [
            269
        ]
    },
    {
        name: "Congo",
        code: "CG",
        regionName: "Region",
        regions: [
            {
                name: "Bouenza"
            },
            {
                name: "Pool"
            },
            {
                name: "Sangha"
            },
            {
                name: "Plateaux"
            },
            {
                name: "Cuvette-Ouest"
            },
            {
                name: "Lékoumou"
            },
            {
                name: "Kouilou"
            },
            {
                name: "Likouala"
            },
            {
                name: "Cuvette"
            },
            {
                name: "Niari"
            },
            {
                name: "Brazzaville"
            }
        ],
        phoneCodes: [
            242
        ]
    },
    {
        name: "Congo (Democratic Republic of the)",
        code: "CD",
        regionName: "Province",
        regions: [
            {
                name: "Bas-Congo"
            },
            {
                name: "Bandundu"
            },
            {
                name: "Équateur"
            },
            {
                name: "Katanga"
            },
            {
                name: "Kasai-Oriental"
            },
            {
                name: "Kinshasa"
            },
            {
                name: "Kasai-Occidental"
            },
            {
                name: "Maniema"
            },
            {
                name: "Nord-Kivu"
            },
            {
                name: "Orientale"
            },
            {
                name: "Sud-Kivu"
            }
        ],
        phoneCodes: [
            243
        ]
    },
    {
        name: "Cook Islands",
        code: "CK",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            682
        ]
    },
    {
        name: "Costa Rica",
        code: "CR",
        regionName: "Province",
        regions: [
            {
                name: "Alajuela"
            },
            {
                name: "Cartago"
            },
            {
                name: "Guanacaste"
            },
            {
                name: "Heredia"
            },
            {
                name: "Limón"
            },
            {
                name: "Puntarenas"
            },
            {
                name: "San José"
            }
        ],
        phoneCodes: [
            506
        ]
    },
    {
        name: "Croatia",
        code: "HR",
        regionName: "County",
        regions: [
            {
                name: "Zagrebačka županija"
            },
            {
                name: "Krapinsko-zagorska županija"
            },
            {
                name: "Sisačko-moslavačka županija"
            },
            {
                name: "Karlovačka županija"
            },
            {
                name: "Varaždinska županija"
            },
            {
                name: "Koprivničko-križevačka županija"
            },
            {
                name: "Bjelovarsko-bilogorska županija"
            },
            {
                name: "Primorsko-goranska županija"
            },
            {
                name: "Ličko-senjska županija"
            },
            {
                name: "Virovitičko-podravska županija"
            },
            {
                name: "Požeško-slavonska županija"
            },
            {
                name: "Brodsko-posavska županija"
            },
            {
                name: "Zadarska županija"
            },
            {
                name: "Osječko-baranjska županija"
            },
            {
                name: "Šibensko-kninska županija"
            },
            {
                name: "Vukovarsko-srijemska županija"
            },
            {
                name: "Splitsko-dalmatinska županija"
            },
            {
                name: "Istarska županija"
            },
            {
                name: "Dubrovačko-neretvanska županija"
            },
            {
                name: "Međimurska županija"
            },
            {
                name: "Grad Zagreb"
            }
        ],
        phoneCodes: [
            385
        ]
    },
    {
        name: "Cuba",
        code: "CU",
        regionName: "Province",
        regions: [
            {
                name: "Pinar del Rio"
            },
            {
                name: "La Habana"
            },
            {
                name: "Ciudad de La Habana"
            },
            {
                name: "Matanzas"
            },
            {
                name: "Villa Clara"
            },
            {
                name: "Cienfuegos"
            },
            {
                name: "Sancti Spíritus"
            },
            {
                name: "Ciego de Ávila"
            },
            {
                name: "Camagüey"
            },
            {
                name: "Las Tunas"
            },
            {
                name: "Holguín"
            },
            {
                name: "Granma"
            },
            {
                name: "Santiago de Cuba"
            },
            {
                name: "Guantánamo"
            },
            {
                name: "Isla de la Juventud"
            }
        ],
        phoneCodes: [
            53
        ]
    },
    {
        name: "Curaçao",
        code: "CW",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            599
        ]
    },
    {
        name: "Cyprus",
        code: "CY",
        regionName: "District",
        regions: [
            {
                name: "Lefkosía"
            },
            {
                name: "Lemesós"
            },
            {
                name: "Lárnaka"
            },
            {
                name: "Ammóchostos"
            },
            {
                name: "Páfos"
            },
            {
                name: "Kerýneia"
            }
        ],
        phoneCodes: [
            357
        ]
    },
    {
        name: "Czech Republic",
        code: "CZ",
        regionName: "district",
        regions: [
            {
                name: "Praha, Hlavní mešto"
            },
            {
                name: "Praha 1"
            },
            {
                name: "Praha 2"
            },
            {
                name: "Praha 3"
            },
            {
                name: "Praha 4"
            },
            {
                name: "Praha 5"
            },
            {
                name: "Praha 6"
            },
            {
                name: "Praha 7"
            },
            {
                name: "Praha 8"
            },
            {
                name: "Praha 9"
            },
            {
                name: "Praha 10"
            },
            {
                name: "Praha 11"
            },
            {
                name: "Praha 12"
            },
            {
                name: "Praha 13"
            },
            {
                name: "Praha 14"
            },
            {
                name: "Praha 15"
            },
            {
                name: "Praha 16"
            },
            {
                name: "Praha 17"
            },
            {
                name: "Praha 18"
            },
            {
                name: "Praha 19"
            },
            {
                name: "Praha 20"
            },
            {
                name: "Praha 21"
            },
            {
                name: "Praha 22"
            },
            {
                name: "Středočeský kraj"
            },
            {
                name: "Benešov"
            },
            {
                name: "Beroun"
            },
            {
                name: "Kladno"
            },
            {
                name: "Kolín"
            },
            {
                name: "Kutná Hora"
            },
            {
                name: "Mělník"
            },
            {
                name: "Mladá Boleslav"
            },
            {
                name: "Nymburk"
            },
            {
                name: "Praha-východ"
            },
            {
                name: "Praha-západ"
            },
            {
                name: "Příbram"
            },
            {
                name: "Rakovník"
            },
            {
                name: "Jihočeský kraj"
            },
            {
                name: "České Budějovice"
            },
            {
                name: "Český Krumlov"
            },
            {
                name: "Jindřichův Hradec"
            },
            {
                name: "Písek"
            },
            {
                name: "Prachatice"
            },
            {
                name: "Strakonice"
            },
            {
                name: "Tábor"
            },
            {
                name: "Plzeňský kraj"
            },
            {
                name: "Domažlice"
            },
            {
                name: "Klatovy"
            },
            {
                name: "Plzeň-město"
            },
            {
                name: "Plzeň-jih"
            },
            {
                name: "Plzeň-sever"
            },
            {
                name: "Rokycany"
            },
            {
                name: "Tachov"
            },
            {
                name: "Karlovarský kraj"
            },
            {
                name: "Cheb"
            },
            {
                name: "Karlovy Vary"
            },
            {
                name: "Sokolov"
            },
            {
                name: "Ústecký kraj"
            },
            {
                name: "Děčín"
            },
            {
                name: "Chomutov"
            },
            {
                name: "Litoměřice"
            },
            {
                name: "Louny"
            },
            {
                name: "Most"
            },
            {
                name: "Teplice"
            },
            {
                name: "Ústí nad Labem"
            },
            {
                name: "Liberecký kraj"
            },
            {
                name: "Česká Lípa"
            },
            {
                name: "Jablonec nad Nisou"
            },
            {
                name: "Liberec"
            },
            {
                name: "Semily"
            },
            {
                name: "Královéhradecký kraj"
            },
            {
                name: "Hradec Králové"
            },
            {
                name: "Jičín"
            },
            {
                name: "Náchod"
            },
            {
                name: "Rychnov nad Kněžnou"
            },
            {
                name: "Trutnov"
            },
            {
                name: "Pardubický kraj"
            },
            {
                name: "Chrudim"
            },
            {
                name: "Pardubice"
            },
            {
                name: "Svitavy"
            },
            {
                name: "Ústí nad Orlicí"
            },
            {
                name: "Kraj Vysočina"
            },
            {
                name: "Havlíčkův Brod"
            },
            {
                name: "Jihlava"
            },
            {
                name: "Pelhřimov"
            },
            {
                name: "Třebíč"
            },
            {
                name: "Žďár nad Sázavou"
            },
            {
                name: "Jihomoravský kraj"
            },
            {
                name: "Blansko"
            },
            {
                name: "Brno-město"
            },
            {
                name: "Brno-venkov"
            },
            {
                name: "Břeclav"
            },
            {
                name: "Hodonín"
            },
            {
                name: "Vyškov"
            },
            {
                name: "Znojmo"
            },
            {
                name: "Olomoucký kraj"
            },
            {
                name: "Jeseník"
            },
            {
                name: "Olomouc"
            },
            {
                name: "Prostějov"
            },
            {
                name: "Přerov"
            },
            {
                name: "Šumperk"
            },
            {
                name: "Zlínský kraj"
            },
            {
                name: "Kroměříž"
            },
            {
                name: "Uherské Hradiště"
            },
            {
                name: "Vsetín"
            },
            {
                name: "Zlín"
            },
            {
                name: "Moravskoslezský kraj"
            },
            {
                name: "Bruntál"
            },
            {
                name: "Frýdek Místek"
            },
            {
                name: "Karviná"
            },
            {
                name: "Nový Jičín"
            },
            {
                name: "Opava"
            },
            {
                name: "Ostrava-město"
            }
        ],
        phoneCodes: [
            420
        ]
    },
    {
        name: "Denmark",
        code: "DK",
        regionName: "Region",
        regions: [
            {
                name: "Nordjylland"
            },
            {
                name: "Midtjylland"
            },
            {
                name: "Syddanmark"
            },
            {
                name: "Hovedstaden"
            },
            {
                name: "Sjælland"
            }
        ],
        phoneCodes: [
            45
        ]
    },
    {
        name: "Djibouti",
        code: "DJ",
        regionName: "Region",
        regions: [
            {
                name: "Arta"
            },
            {
                name: "Ali Sabieh"
            },
            {
                name: "Dikhil"
            },
            {
                name: "Djibouti"
            },
            {
                name: "Obock"
            },
            {
                name: "Tadjourah"
            }
        ],
        phoneCodes: [
            253
        ]
    },
    {
        name: "Dominica",
        code: "DM",
        regionName: "Parish",
        regions: [
            {
                name: "Saint Peter"
            },
            {
                name: "Saint Andrew"
            },
            {
                name: "Saint David"
            },
            {
                name: "Saint George"
            },
            {
                name: "Saint John"
            },
            {
                name: "Saint Joseph"
            },
            {
                name: "Saint Luke"
            },
            {
                name: "Saint Mark"
            },
            {
                name: "Saint Patrick"
            },
            {
                name: "Saint Paul"
            }
        ],
        phoneCodes: [
            1
        ]
    },
    {
        name: "Dominican Republic",
        code: "DO",
        regionName: "Province",
        regions: [
            {
                name: "Distrito Nacional (Santo Domingo)"
            },
            {
                name: "Azua"
            },
            {
                name: "Bahoruco"
            },
            {
                name: "Barahona"
            },
            {
                name: "Dajabón"
            },
            {
                name: "Duarte"
            },
            {
                name: "La Estrelleta [Elías Piña]"
            },
            {
                name: "El Seybo [El Seibo]"
            },
            {
                name: "Espaillat"
            },
            {
                name: "Independencia"
            },
            {
                name: "La Altagracia"
            },
            {
                name: "La Romana"
            },
            {
                name: "La Vega"
            },
            {
                name: "María Trinidad Sánchez"
            },
            {
                name: "Monte Cristi"
            },
            {
                name: "Pedernales"
            },
            {
                name: "Peravia"
            },
            {
                name: "Puerto Plata"
            },
            {
                name: "Salcedo"
            },
            {
                name: "Samaná"
            },
            {
                name: "San Cristóbal"
            },
            {
                name: "San Juan"
            },
            {
                name: "San Pedro de Macorís"
            },
            {
                name: "Sánchez Ramírez"
            },
            {
                name: "Santiago"
            },
            {
                name: "Santiago Rodríguez"
            },
            {
                name: "Valverde"
            },
            {
                name: "Monseñor Nouel"
            },
            {
                name: "Monte Plata"
            },
            {
                name: "Hato Mayor"
            }
        ],
        phoneCodes: [
            1
        ]
    },
    {
        name: "Ecuador",
        code: "EC",
        regionName: "Province",
        regions: [
            {
                name: "Azuay"
            },
            {
                name: "Bolívar"
            },
            {
                name: "Carchi"
            },
            {
                name: "Orellana"
            },
            {
                name: "Esmeraldas"
            },
            {
                name: "Cañar"
            },
            {
                name: "Guayas"
            },
            {
                name: "Chimborazo"
            },
            {
                name: "Imbabura"
            },
            {
                name: "Loja"
            },
            {
                name: "Manabí"
            },
            {
                name: "Napo"
            },
            {
                name: "El Oro"
            },
            {
                name: "Pichincha"
            },
            {
                name: "Los Ríos"
            },
            {
                name: "Morona-Santiago"
            },
            {
                name: "Santo Domingo de los Tsáchilas"
            },
            {
                name: "Santa Elena"
            },
            {
                name: "Tungurahua"
            },
            {
                name: "Sucumbíos"
            },
            {
                name: "Galápagos"
            },
            {
                name: "Cotopaxi"
            },
            {
                name: "Pastaza"
            },
            {
                name: "Zamora-Chinchipe"
            }
        ],
        phoneCodes: [
            593
        ]
    },
    {
        name: "Egypt",
        code: "EG",
        regionName: "Governorate",
        regions: [
            {
                name: "Al Iskandarīyah"
            },
            {
                name: "Aswān"
            },
            {
                name: "Asyūt"
            },
            {
                name: "Al Bahr al Ahmar"
            },
            {
                name: "Al Buhayrah"
            },
            {
                name: "Banī Suwayf"
            },
            {
                name: "Al Qāhirah"
            },
            {
                name: "Ad Daqahlīyah"
            },
            {
                name: "Dumyāt"
            },
            {
                name: "Al Fayyūm"
            },
            {
                name: "Al Gharbīyah"
            },
            {
                name: "Al Jīzah"
            },
            {
                name: "Ḩulwān"
            },
            {
                name: "Al Ismā`īlīyah"
            },
            {
                name: "Janūb Sīnā'"
            },
            {
                name: "Al Qalyūbīyah"
            },
            {
                name: "Kafr ash Shaykh"
            },
            {
                name: "Qinā"
            },
            {
                name: "Al Minyā"
            },
            {
                name: "Al Minūfīyah"
            },
            {
                name: "Matrūh"
            },
            {
                name: "Būr Sa`īd"
            },
            {
                name: "Sūhāj"
            },
            {
                name: "Ash Sharqīyah"
            },
            {
                name: "Shamal Sīnā'"
            },
            {
                name: "As Sādis min Uktūbar"
            },
            {
                name: "As Suways"
            },
            {
                name: "Al Wādī al Jadīd"
            }
        ],
        phoneCodes: [
            20
        ]
    },
    {
        name: "El Salvador",
        code: "SV",
        regionName: "Department",
        regions: [
            {
                name: "Ahuachapán"
            },
            {
                name: "Cabañas"
            },
            {
                name: "Chalatenango"
            },
            {
                name: "Cuscatlán"
            },
            {
                name: "La Libertad"
            },
            {
                name: "Morazán"
            },
            {
                name: "La Paz"
            },
            {
                name: "Santa Ana"
            },
            {
                name: "San Miguel"
            },
            {
                name: "Sonsonate"
            },
            {
                name: "San Salvador"
            },
            {
                name: "San Vicente"
            },
            {
                name: "La Unión"
            },
            {
                name: "Usulután"
            }
        ],
        phoneCodes: [
            503
        ]
    },
    {
        name: "Equatorial Guinea",
        code: "GQ",
        regionName: "Province",
        regions: [
            {
                name: "Annobón"
            },
            {
                name: "Bioko Norte"
            },
            {
                name: "Bioko Sur"
            },
            {
                name: "Región Continental"
            },
            {
                name: "Centro Sur"
            },
            {
                name: "Región Insular"
            },
            {
                name: "Kié-Ntem"
            },
            {
                name: "Litoral"
            },
            {
                name: "Wele-Nzas"
            }
        ],
        phoneCodes: [
            240
        ]
    },
    {
        name: "Eritrea",
        code: "ER",
        regionName: "Province",
        regions: [
            {
                name: "Ansabā"
            },
            {
                name: "Janūbī al Baḩrī al Aḩmar"
            },
            {
                name: "Al Janūbī"
            },
            {
                name: "Qāsh-Barkah"
            },
            {
                name: "Al Awsaţ"
            },
            {
                name: "Shimālī al Baḩrī al Aḩmar"
            }
        ],
        phoneCodes: [
            291
        ]
    },
    {
        name: "Estonia",
        code: "EE",
        regionName: "County",
        regions: [
            {
                name: "Harjumaa"
            },
            {
                name: "Hiiumaa"
            },
            {
                name: "Ida-Virumaa"
            },
            {
                name: "Jõgevamaa"
            },
            {
                name: "Järvamaa"
            },
            {
                name: "Läänemaa"
            },
            {
                name: "Lääne-Virumaa"
            },
            {
                name: "Põlvamaa"
            },
            {
                name: "Pärnumaa"
            },
            {
                name: "Raplamaa"
            },
            {
                name: "Saaremaa"
            },
            {
                name: "Tartumaa"
            },
            {
                name: "Valgamaa"
            },
            {
                name: "Viljandimaa"
            },
            {
                name: "Võrumaa"
            }
        ],
        phoneCodes: [
            372
        ]
    },
    {
        name: "Ethiopia",
        code: "ET",
        regionName: "State",
        regions: [
            {
                name: "Ādīs Ābeba"
            },
            {
                name: "Āfar"
            },
            {
                name: "Āmara"
            },
            {
                name: "Bīnshangul Gumuz"
            },
            {
                name: "Dirē Dawa"
            },
            {
                name: "Gambēla Hizboch"
            },
            {
                name: "Hārerī Hizb"
            },
            {
                name: "Oromīya"
            },
            {
                name: "YeDebub Bihēroch Bihēreseboch na Hizboch"
            },
            {
                name: "Sumalē"
            },
            {
                name: "Tigray"
            }
        ],
        phoneCodes: [
            251
        ]
    },
    {
        name: "Falkland Islands (Malvinas)",
        code: "FK",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            500
        ]
    },
    {
        name: "Faroe Islands",
        code: "FO",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            298
        ]
    },
    {
        name: "Fiji",
        code: "FJ",
        regionName: "Division",
        regions: [
            {
                name: "Central"
            },
            {
                name: "Eastern"
            },
            {
                name: "Northern"
            },
            {
                name: "Rotuma"
            },
            {
                name: "Western"
            }
        ],
        phoneCodes: [
            679
        ]
    },
    {
        name: "Finland",
        code: "FI",
        regionName: "Region",
        regions: [
            {
                name: "Ahvenanmaan maakunta"
            },
            {
                name: "Etelä-Karjala"
            },
            {
                name: "Etelä-Pohjanmaa"
            },
            {
                name: "Etelä-Savo"
            },
            {
                name: "Kainuu"
            },
            {
                name: "Kanta-Häme"
            },
            {
                name: "Keski-Pohjanmaa"
            },
            {
                name: "Keski-Suomi"
            },
            {
                name: "Kymenlaakso"
            },
            {
                name: "Lappi"
            },
            {
                name: "Pirkanmaa"
            },
            {
                name: "Pohjanmaa"
            },
            {
                name: "Pohjois-Karjala"
            },
            {
                name: "Pohjois-Pohjanmaa"
            },
            {
                name: "Pohjois-Savo"
            },
            {
                name: "Päijät-Häme"
            },
            {
                name: "Satakunta"
            },
            {
                name: "Uusimaa"
            },
            {
                name: "Varsinais-Suomi"
            }
        ],
        phoneCodes: [
            358
        ]
    },
    {
        name: "France",
        code: "FR",
        regionName: "Metropolitan department",
        regions: [
            {
                name: "Ain"
            },
            {
                name: "Aisne"
            },
            {
                name: "Allier"
            },
            {
                name: "Alpes-de-Haute-Provence"
            },
            {
                name: "Hautes-Alpes"
            },
            {
                name: "Alpes-Maritimes"
            },
            {
                name: "Ardèche"
            },
            {
                name: "Ardennes"
            },
            {
                name: "Ariège"
            },
            {
                name: "Aube"
            },
            {
                name: "Aude"
            },
            {
                name: "Aveyron"
            },
            {
                name: "Bouches-du-Rhône"
            },
            {
                name: "Calvados"
            },
            {
                name: "Cantal"
            },
            {
                name: "Charente"
            },
            {
                name: "Charente-Maritime"
            },
            {
                name: "Cher"
            },
            {
                name: "Corrèze"
            },
            {
                name: "Côte-d'Or"
            },
            {
                name: "Côtes-d'Armor"
            },
            {
                name: "Creuse"
            },
            {
                name: "Dordogne"
            },
            {
                name: "Doubs"
            },
            {
                name: "Drôme"
            },
            {
                name: "Eure"
            },
            {
                name: "Eure-et-Loir"
            },
            {
                name: "Finistère"
            },
            {
                name: "Corse-du-Sud"
            },
            {
                name: "Haute-Corse"
            },
            {
                name: "Gard"
            },
            {
                name: "Haute-Garonne"
            },
            {
                name: "Gers"
            },
            {
                name: "Gironde"
            },
            {
                name: "Hérault"
            },
            {
                name: "Ille-et-Vilaine"
            },
            {
                name: "Indre"
            },
            {
                name: "Indre-et-Loire"
            },
            {
                name: "Isère"
            },
            {
                name: "Jura"
            },
            {
                name: "Landes"
            },
            {
                name: "Loir-et-Cher"
            },
            {
                name: "Loire"
            },
            {
                name: "Haute-Loire"
            },
            {
                name: "Loire-Atlantique"
            },
            {
                name: "Loiret"
            },
            {
                name: "Lot"
            },
            {
                name: "Lot-et-Garonne"
            },
            {
                name: "Lozère"
            },
            {
                name: "Maine-et-Loire"
            },
            {
                name: "Manche"
            },
            {
                name: "Marne"
            },
            {
                name: "Haute-Marne"
            },
            {
                name: "Mayenne"
            },
            {
                name: "Meurthe-et-Moselle"
            },
            {
                name: "Meuse"
            },
            {
                name: "Morbihan"
            },
            {
                name: "Moselle"
            },
            {
                name: "Nièvre"
            },
            {
                name: "Nord"
            },
            {
                name: "Oise"
            },
            {
                name: "Orne"
            },
            {
                name: "Pas-de-Calais"
            },
            {
                name: "Puy-de-Dôme"
            },
            {
                name: "Pyrénées-Atlantiques"
            },
            {
                name: "Hautes-Pyrénées"
            },
            {
                name: "Pyrénées-Orientales"
            },
            {
                name: "Bas-Rhin"
            },
            {
                name: "Haut-Rhin"
            },
            {
                name: "Rhône"
            },
            {
                name: "Haute-Saône"
            },
            {
                name: "Saône-et-Loire"
            },
            {
                name: "Sarthe"
            },
            {
                name: "Savoie"
            },
            {
                name: "Haute-Savoie"
            },
            {
                name: "Paris"
            },
            {
                name: "Seine-Maritime"
            },
            {
                name: "Seine-et-Marne"
            },
            {
                name: "Yvelines"
            },
            {
                name: "Deux-Sèvres"
            },
            {
                name: "Somme"
            },
            {
                name: "Tarn"
            },
            {
                name: "Tarn-et-Garonne"
            },
            {
                name: "Var"
            },
            {
                name: "Vaucluse"
            },
            {
                name: "Vendée"
            },
            {
                name: "Vienne"
            },
            {
                name: "Haute-Vienne"
            },
            {
                name: "Vosges"
            },
            {
                name: "Yonne"
            },
            {
                name: "Territoire de Belfort"
            },
            {
                name: "Essonne"
            },
            {
                name: "Hauts-de-Seine"
            },
            {
                name: "Seine-Saint-Denis"
            },
            {
                name: "Val-de-Marne"
            },
            {
                name: "Val-d'Oise"
            },
            {
                name: "Auvergne-Rhône-Alpes"
            },
            {
                name: "Bourgogne-Franche-Comté"
            },
            {
                name: "Saint-Barthélemy"
            },
            {
                name: "Bretagne"
            },
            {
                name: "Corse"
            },
            {
                name: "Clipperton"
            },
            {
                name: "Centre-Val de Loire"
            },
            {
                name: "Grand-Est"
            },
            {
                name: "Guyane (française)"
            },
            {
                name: "Guadeloupe"
            },
            {
                name: "Guadeloupe"
            },
            {
                name: "Hauts-de-France"
            },
            {
                name: "Île-de-France"
            },
            {
                name: "La Réunion"
            },
            {
                name: "Mayotte"
            },
            {
                name: "Saint-Martin"
            },
            {
                name: "Martinique"
            },
            {
                name: "Nouvelle-Aquitaine"
            },
            {
                name: "Nouvelle-Calédonie"
            },
            {
                name: "Normandie"
            },
            {
                name: "Occitanie"
            },
            {
                name: "Provence-Alpes-Côte-d’Azur"
            },
            {
                name: "Pays-de-la-Loire"
            },
            {
                name: "Polynésie française"
            },
            {
                name: "Saint-Pierre-et-Miquelon"
            },
            {
                name: "La Réunion"
            },
            {
                name: "Terres australes françaises"
            },
            {
                name: "Wallis-et-Futuna"
            },
            {
                name: "Mayotte"
            }
        ],
        phoneCodes: [
            33
        ]
    },
    {
        name: "French Guiana",
        code: "GF",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            594
        ]
    },
    {
        name: "French Polynesia",
        code: "PF",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            689
        ]
    },
    {
        name: "French Southern Territories",
        code: "TF",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            262
        ]
    },
    {
        name: "Gabon",
        code: "GA",
        regionName: "Province",
        regions: [
            {
                name: "Estuaire"
            },
            {
                name: "Haut-Ogooué"
            },
            {
                name: "Moyen-Ogooué"
            },
            {
                name: "Ngounié"
            },
            {
                name: "Nyanga"
            },
            {
                name: "Ogooué-Ivindo"
            },
            {
                name: "Ogooué-Lolo"
            },
            {
                name: "Ogooué-Maritime"
            },
            {
                name: "Woleu-Ntem"
            }
        ],
        phoneCodes: [
            241
        ]
    },
    {
        name: "Gambia",
        code: "GM",
        regionName: "Division",
        regions: [
            {
                name: "Banjul"
            },
            {
                name: "Lower River"
            },
            {
                name: "Central River"
            },
            {
                name: "North Bank"
            },
            {
                name: "Upper River"
            },
            {
                name: "Western"
            }
        ],
        phoneCodes: [
            220
        ]
    },
    {
        name: "Georgia",
        code: "GE",
        regionName: "Region",
        regions: [
            {
                name: "Abkhazia"
            },
            {
                name: "Ajaria"
            },
            {
                name: "Guria"
            },
            {
                name: "Imeret’i"
            },
            {
                name: "Kakhet’i"
            },
            {
                name: "K’vemo K’art’li"
            },
            {
                name: "Mts’khet’a-Mt’ianet’i"
            },
            {
                name: "Racha-Lech’khumi-K’vemo Svanet’i"
            },
            {
                name: "Samts’khe-Javakhet’i"
            },
            {
                name: "Shida K’art’li"
            },
            {
                name: "Samegrelo-Zemo Svanet’i"
            },
            {
                name: "T’bilisi"
            }
        ],
        phoneCodes: [
            995
        ]
    },
    {
        name: "Germany",
        code: "DE",
        regionName: "State",
        regions: [
            {
                name: "Brandenburg"
            },
            {
                name: "Berlin"
            },
            {
                name: "Baden-Württemberg"
            },
            {
                name: "Bayern"
            },
            {
                name: "Bremen"
            },
            {
                name: "Hessen"
            },
            {
                name: "Hamburg"
            },
            {
                name: "Mecklenburg-Vorpommern"
            },
            {
                name: "Niedersachsen"
            },
            {
                name: "Nordrhein-Westfalen"
            },
            {
                name: "Rheinland-Pfalz"
            },
            {
                name: "Schleswig-Holstein"
            },
            {
                name: "Saarland"
            },
            {
                name: "Sachsen"
            },
            {
                name: "Sachsen-Anhalt"
            },
            {
                name: "Thüringen"
            }
        ],
        phoneCodes: [
            49
        ]
    },
    {
        name: "Ghana",
        code: "GH",
        regionName: "Region",
        regions: [
            {
                name: "Greater Accra"
            },
            {
                name: "Ashanti"
            },
            {
                name: "Brong-Ahafo"
            },
            {
                name: "Central"
            },
            {
                name: "Eastern"
            },
            {
                name: "Northern"
            },
            {
                name: "Volta"
            },
            {
                name: "Upper East"
            },
            {
                name: "Upper West"
            },
            {
                name: "Western"
            }
        ],
        phoneCodes: [
            233
        ]
    },
    {
        name: "Gibraltar",
        code: "GI",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            350
        ]
    },
    {
        name: "Greece",
        code: "GR",
        regionName: "Department",
        regions: [
            {
                name: "Aitolia kai Akarnania"
            },
            {
                name: "Voiotia"
            },
            {
                name: "Evvoias"
            },
            {
                name: "Evrytania"
            },
            {
                name: "Fthiotida"
            },
            {
                name: "Fokida"
            },
            {
                name: "Argolida"
            },
            {
                name: "Arkadia"
            },
            {
                name: "Achaïa"
            },
            {
                name: "Ileia"
            },
            {
                name: "Korinthia"
            },
            {
                name: "Lakonia"
            },
            {
                name: "Messinia"
            },
            {
                name: "Zakynthos"
            },
            {
                name: "Kerkyra"
            },
            {
                name: "Kefallonia"
            },
            {
                name: "Lefkada"
            },
            {
                name: "Arta"
            },
            {
                name: "Thesprotia"
            },
            {
                name: "Ioannina"
            },
            {
                name: "Preveza"
            },
            {
                name: "Karditsa"
            },
            {
                name: "Larisa"
            },
            {
                name: "Magnisia"
            },
            {
                name: "Trikala"
            },
            {
                name: "Grevena"
            },
            {
                name: "Drama"
            },
            {
                name: "Imathia"
            },
            {
                name: "Thessaloniki"
            },
            {
                name: "Kavala"
            },
            {
                name: "Kastoria"
            },
            {
                name: "Kilkis"
            },
            {
                name: "Kozani"
            },
            {
                name: "Pella"
            },
            {
                name: "Pieria"
            },
            {
                name: "Serres"
            },
            {
                name: "Florina"
            },
            {
                name: "Chalkidiki"
            },
            {
                name: "Agio Oros"
            },
            {
                name: "Evros"
            },
            {
                name: "Xanthi"
            },
            {
                name: "Rodopi"
            },
            {
                name: "Dodekanisos"
            },
            {
                name: "Kyklades"
            },
            {
                name: "Lesvos"
            },
            {
                name: "Samos"
            },
            {
                name: "Chios"
            },
            {
                name: "Irakleio"
            },
            {
                name: "Lasithi"
            },
            {
                name: "Rethymno"
            },
            {
                name: "Chania"
            },
            {
                name: "Anatoliki Makedonia kai Thraki"
            },
            {
                name: "Attiki"
            },
            {
                name: "Kentriki Makedonia"
            },
            {
                name: "Dytiki Makedonia"
            },
            {
                name: "Ipeiros"
            },
            {
                name: "Thessalia"
            },
            {
                name: "Ionia Nisia"
            },
            {
                name: "Dytiki Ellada"
            },
            {
                name: "Sterea Ellada"
            },
            {
                name: "Attiki"
            },
            {
                name: "Peloponnisos"
            },
            {
                name: "Voreio Aigaio"
            },
            {
                name: "Notio Aigaio"
            },
            {
                name: "Kriti"
            }
        ],
        phoneCodes: [
            30
        ]
    },
    {
        name: "Greenland",
        code: "GL",
        regionName: "Municipality",
        regions: [
            {
                name: "Kommune Kujalleq"
            },
            {
                name: "Qaasuitsup Kommunia"
            },
            {
                name: "Qeqqata Kommunia"
            },
            {
                name: "Kommuneqarfik Sermersooq"
            }
        ],
        phoneCodes: [
            299
        ]
    },
    {
        name: "Grenada",
        code: "GD",
        regionName: "Parish",
        regions: [
            {
                name: "Saint Andrew"
            },
            {
                name: "Saint David"
            },
            {
                name: "Saint George"
            },
            {
                name: "Saint John"
            },
            {
                name: "Saint Mark"
            },
            {
                name: "Saint Patrick"
            },
            {
                name: "Southern Grenadine Islands"
            }
        ],
        phoneCodes: [
            1
        ]
    },
    {
        name: "Guadeloupe",
        code: "GP",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            590
        ]
    },
    {
        name: "Guam",
        code: "GU",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            1
        ]
    },
    {
        name: "Guatemala",
        code: "GT",
        regionName: "Department",
        regions: [
            {
                name: "Alta Verapaz"
            },
            {
                name: "Baja Verapaz"
            },
            {
                name: "Chimaltenango"
            },
            {
                name: "Chiquimula"
            },
            {
                name: "Escuintla"
            },
            {
                name: "Guatemala"
            },
            {
                name: "Huehuetenango"
            },
            {
                name: "Izabal"
            },
            {
                name: "Jalapa"
            },
            {
                name: "Jutiapa"
            },
            {
                name: "Petén"
            },
            {
                name: "El Progreso"
            },
            {
                name: "Quiché"
            },
            {
                name: "Quetzaltenango"
            },
            {
                name: "Retalhuleu"
            },
            {
                name: "Sacatepéquez"
            },
            {
                name: "San Marcos"
            },
            {
                name: "Sololá"
            },
            {
                name: "Santa Rosa"
            },
            {
                name: "Suchitepéquez"
            },
            {
                name: "Totonicapán"
            },
            {
                name: "Zacapa"
            }
        ],
        phoneCodes: [
            502
        ]
    },
    {
        name: "Guernsey",
        code: "GG",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            44
        ]
    },
    {
        name: "Guinea",
        code: "GN",
        regionName: "Prefecture",
        regions: [
            {
                name: "Boké"
            },
            {
                name: "Beyla"
            },
            {
                name: "Boffa"
            },
            {
                name: "Boké"
            },
            {
                name: "Conakry"
            },
            {
                name: "Coyah"
            },
            {
                name: "Kindia"
            },
            {
                name: "Dabola"
            },
            {
                name: "Dinguiraye"
            },
            {
                name: "Dalaba"
            },
            {
                name: "Dubréka"
            },
            {
                name: "Faranah"
            },
            {
                name: "Faranah"
            },
            {
                name: "Forécariah"
            },
            {
                name: "Fria"
            },
            {
                name: "Gaoual"
            },
            {
                name: "Guékédou"
            },
            {
                name: "Kankan"
            },
            {
                name: "Kankan"
            },
            {
                name: "Koubia"
            },
            {
                name: "Kindia"
            },
            {
                name: "Kérouané"
            },
            {
                name: "Koundara"
            },
            {
                name: "Kouroussa"
            },
            {
                name: "Kissidougou"
            },
            {
                name: "Labé"
            },
            {
                name: "Labé"
            },
            {
                name: "Lélouma"
            },
            {
                name: "Lola"
            },
            {
                name: "Mamou"
            },
            {
                name: "Macenta"
            },
            {
                name: "Mandiana"
            },
            {
                name: "Mali"
            },
            {
                name: "Mamou"
            },
            {
                name: "Nzérékoré"
            },
            {
                name: "Nzérékoré"
            },
            {
                name: "Pita"
            },
            {
                name: "Siguiri"
            },
            {
                name: "Télimélé"
            },
            {
                name: "Tougué"
            },
            {
                name: "Yomou"
            }
        ],
        phoneCodes: [
            224
        ]
    },
    {
        name: "Guinea-Bissau",
        code: "GW",
        regionName: "Region",
        regions: [
            {
                name: "Bafatá"
            },
            {
                name: "Bolama"
            },
            {
                name: "Biombo"
            },
            {
                name: "Bissau"
            },
            {
                name: "Cacheu"
            },
            {
                name: "Gabú"
            },
            {
                name: "Leste"
            },
            {
                name: "Norte"
            },
            {
                name: "Oio"
            },
            {
                name: "Quinara"
            },
            {
                name: "Sul"
            },
            {
                name: "Tombali"
            }
        ],
        phoneCodes: [
            245
        ]
    },
    {
        name: "Guyana",
        code: "GY",
        regionName: "Region",
        regions: [
            {
                name: "Barima-Waini"
            },
            {
                name: "Cuyuni-Mazaruni"
            },
            {
                name: "Demerara-Mahaica"
            },
            {
                name: "East Berbice-Corentyne"
            },
            {
                name: "Essequibo Islands-West Demerara"
            },
            {
                name: "Mahaica-Berbice"
            },
            {
                name: "Pomeroon-Supenaam"
            },
            {
                name: "Potaro-Siparuni"
            },
            {
                name: "Upper Demerara-Berbice"
            },
            {
                name: "Upper Takutu-Upper Essequibo"
            }
        ],
        phoneCodes: [
            592
        ]
    },
    {
        name: "Haiti",
        code: "HT",
        regionName: "Department",
        regions: [
            {
                name: "Artibonite"
            },
            {
                name: "Centre"
            },
            {
                name: "Grande-Anse"
            },
            {
                name: "Nord"
            },
            {
                name: "Nord-Est"
            },
            {
                name: "Nord-Ouest"
            },
            {
                name: "Ouest"
            },
            {
                name: "Sud"
            },
            {
                name: "Sud-Est"
            }
        ],
        phoneCodes: [
            509
        ]
    },
    {
        name: "Heard Island and McDonald Islands",
        code: "HM",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            672
        ]
    },
    {
        name: "Holy See",
        code: "VA",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            379
        ]
    },
    {
        name: "Honduras",
        code: "HN",
        regionName: "Department",
        regions: [
            {
                name: "Atlántida"
            },
            {
                name: "Choluteca"
            },
            {
                name: "Colón"
            },
            {
                name: "Comayagua"
            },
            {
                name: "Copán"
            },
            {
                name: "Cortés"
            },
            {
                name: "El Paraíso"
            },
            {
                name: "Francisco Morazán"
            },
            {
                name: "Gracias a Dios"
            },
            {
                name: "Islas de la Bahía"
            },
            {
                name: "Intibucá"
            },
            {
                name: "Lempira"
            },
            {
                name: "La Paz"
            },
            {
                name: "Ocotepeque"
            },
            {
                name: "Olancho"
            },
            {
                name: "Santa Bárbara"
            },
            {
                name: "Valle"
            },
            {
                name: "Yoro"
            }
        ],
        phoneCodes: [
            504
        ]
    },
    {
        name: "Hong Kong",
        code: "HK",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            852
        ]
    },
    {
        name: "Hungary",
        code: "HU",
        regionName: "City with county rights",
        regions: [
            {
                name: "Baranya"
            },
            {
                name: "Békéscsaba"
            },
            {
                name: "Békés"
            },
            {
                name: "Bács-Kiskun"
            },
            {
                name: "Budapest"
            },
            {
                name: "Borsod-Abaúj-Zemplén"
            },
            {
                name: "Csongrád"
            },
            {
                name: "Debrecen"
            },
            {
                name: "Dunaújváros"
            },
            {
                name: "Eger"
            },
            {
                name: "Érd"
            },
            {
                name: "Fejér"
            },
            {
                name: "Győr-Moson-Sopron"
            },
            {
                name: "Győr"
            },
            {
                name: "Hajdú-Bihar"
            },
            {
                name: "Heves"
            },
            {
                name: "Hódmezővásárhely"
            },
            {
                name: "Jász-Nagykun-Szolnok"
            },
            {
                name: "Komárom-Esztergom"
            },
            {
                name: "Kecskemét"
            },
            {
                name: "Kaposvár"
            },
            {
                name: "Miskolc"
            },
            {
                name: "Nagykanizsa"
            },
            {
                name: "Nógrád"
            },
            {
                name: "Nyíregyháza"
            },
            {
                name: "Pest"
            },
            {
                name: "Pécs"
            },
            {
                name: "Szeged"
            },
            {
                name: "Székesfehérvár"
            },
            {
                name: "Szombathely"
            },
            {
                name: "Szolnok"
            },
            {
                name: "Sopron"
            },
            {
                name: "Somogy"
            },
            {
                name: "Szekszárd"
            },
            {
                name: "Salgótarján"
            },
            {
                name: "Szabolcs-Szatmár-Bereg"
            },
            {
                name: "Tatabánya"
            },
            {
                name: "Tolna"
            },
            {
                name: "Vas"
            },
            {
                name: "Veszprém (county)"
            },
            {
                name: "Veszprém"
            },
            {
                name: "Zala"
            },
            {
                name: "Zalaegerszeg"
            }
        ],
        phoneCodes: [
            36
        ]
    },
    {
        name: "Iceland",
        code: "IS",
        regionName: "Region",
        regions: [
            {
                name: "Reykjavík"
            },
            {
                name: "Höfuðborgarsvæðið"
            },
            {
                name: "Suðurnes"
            },
            {
                name: "Vesturland"
            },
            {
                name: "Vestfirðir"
            },
            {
                name: "Norðurland vestra"
            },
            {
                name: "Norðurland eystra"
            },
            {
                name: "Austurland"
            },
            {
                name: "Suðurland"
            }
        ],
        phoneCodes: [
            354
        ]
    },
    {
        name: "India",
        code: "IN",
        regionName: "State",
        regions: [
            {
                name: "Andaman and Nicobar Islands"
            },
            {
                name: "Andhra Pradesh"
            },
            {
                name: "Arunachal Pradesh"
            },
            {
                name: "Assam"
            },
            {
                name: "Bihar"
            },
            {
                name: "Chandigarh"
            },
            {
                name: "Chhattisgarh"
            },
            {
                name: "Daman and Diu"
            },
            {
                name: "Delhi"
            },
            {
                name: "Dadra and Nagar Haveli"
            },
            {
                name: "Goa"
            },
            {
                name: "Gujarat"
            },
            {
                name: "Himachal Pradesh"
            },
            {
                name: "Haryana"
            },
            {
                name: "Jharkhand"
            },
            {
                name: "Jammu and Kashmir"
            },
            {
                name: "Karnataka"
            },
            {
                name: "Kerala"
            },
            {
                name: "Lakshadweep"
            },
            {
                name: "Maharashtra"
            },
            {
                name: "Meghalaya"
            },
            {
                name: "Manipur"
            },
            {
                name: "Madhya Pradesh"
            },
            {
                name: "Mizoram"
            },
            {
                name: "Nagaland"
            },
            {
                name: "Odisha"
            },
            {
                name: "Punjab"
            },
            {
                name: "Puducherry"
            },
            {
                name: "Rajasthan"
            },
            {
                name: "Sikkim"
            },
            {
                name: "Telangana"
            },
            {
                name: "Tamil Nadu"
            },
            {
                name: "Tripura"
            },
            {
                name: "Uttar Pradesh"
            },
            {
                name: "Uttarakhand"
            },
            {
                name: "West Bengal"
            }
        ],
        phoneCodes: [
            91
        ]
    },
    {
        name: "Indonesia",
        code: "ID",
        regionName: "Province",
        regions: [
            {
                name: "Aceh"
            },
            {
                name: "Bali"
            },
            {
                name: "Bangka Belitung"
            },
            {
                name: "Bengkulu"
            },
            {
                name: "Banten"
            },
            {
                name: "Gorontalo"
            },
            {
                name: "Papua"
            },
            {
                name: "Jambi"
            },
            {
                name: "Jawa Barat"
            },
            {
                name: "Jawa Timur"
            },
            {
                name: "Jakarta Raya"
            },
            {
                name: "Jawa Tengah"
            },
            {
                name: "Jawa"
            },
            {
                name: "Kalimantan"
            },
            {
                name: "Kalimantan Barat"
            },
            {
                name: "Kalimantan Timur"
            },
            {
                name: "Kepulauan Riau"
            },
            {
                name: "Kalimantan Selatan"
            },
            {
                name: "Kalimantan Tengah"
            },
            {
                name: "Lampung"
            },
            {
                name: "Maluku"
            },
            {
                name: "Maluku"
            },
            {
                name: "Maluku Utara"
            },
            {
                name: "Nusa Tenggara Barat"
            },
            {
                name: "Nusa Tenggara Timur"
            },
            {
                name: "Nusa Tenggara"
            },
            {
                name: "Papua"
            },
            {
                name: "Papua Barat"
            },
            {
                name: "Riau"
            },
            {
                name: "Sulawesi Utara"
            },
            {
                name: "Sumatra Barat"
            },
            {
                name: "Sulawesi Tenggara"
            },
            {
                name: "Sulawesi"
            },
            {
                name: "Sumatera"
            },
            {
                name: "Sulawesi Selatan"
            },
            {
                name: "Sulawesi Barat"
            },
            {
                name: "Sumatra Selatan"
            },
            {
                name: "Sulawesi Tengah"
            },
            {
                name: "Sumatera Utara"
            },
            {
                name: "Yogyakarta"
            }
        ],
        phoneCodes: [
            62
        ]
    },
    {
        name: "Côte d'Ivoire",
        code: "CI",
        regionName: "Region",
        regions: [
            {
                name: "Lagunes (Région des)"
            },
            {
                name: "Haut-Sassandra (Région du)"
            },
            {
                name: "Savanes (Région des)"
            },
            {
                name: "Vallée du Bandama (Région de la)"
            },
            {
                name: "Moyen-Comoé (Région du)"
            },
            {
                name: "18 Montagnes (Région des)"
            },
            {
                name: "Lacs (Région des)"
            },
            {
                name: "Zanzan (Région du)"
            },
            {
                name: "Bas-Sassandra (Région du)"
            },
            {
                name: "Denguélé (Région du)"
            },
            {
                name: "Nzi-Comoé (Région)"
            },
            {
                name: "Marahoué (Région de la)"
            },
            {
                name: "Sud-Comoé (Région du)"
            },
            {
                name: "Worodouqou (Région du)"
            },
            {
                name: "Sud-Bandama (Région du)"
            },
            {
                name: "Agnébi (Région de l')"
            },
            {
                name: "Bafing (Région du)"
            },
            {
                name: "Fromager (Région du)"
            },
            {
                name: "Moyen-Cavally (Région du)"
            }
        ],
        phoneCodes: [
            225
        ]
    },
    {
        name: "Iran (Islamic Republic of)",
        code: "IR",
        regionName: "Province",
        regions: [
            {
                name: "Āzarbāyjān-e Sharqī"
            },
            {
                name: "Āzarbāyjān-e Gharbī"
            },
            {
                name: "Ardabīl"
            },
            {
                name: "Eşfahān"
            },
            {
                name: "Īlām"
            },
            {
                name: "Būshehr"
            },
            {
                name: "Tehrān"
            },
            {
                name: "Chahār Mahāll va Bakhtīārī"
            },
            {
                name: "Khūzestān"
            },
            {
                name: "Zanjān"
            },
            {
                name: "Semnān"
            },
            {
                name: "Sīstān va Balūchestān"
            },
            {
                name: "Fārs"
            },
            {
                name: "Kermān"
            },
            {
                name: "Kordestān"
            },
            {
                name: "Kermānshāh"
            },
            {
                name: "Kohgīlūyeh va Būyer Ahmad"
            },
            {
                name: "Gīlān"
            },
            {
                name: "Lorestān"
            },
            {
                name: "Māzandarān"
            },
            {
                name: "Markazī"
            },
            {
                name: "Hormozgān"
            },
            {
                name: "Hamadān"
            },
            {
                name: "Yazd"
            },
            {
                name: "Qom"
            },
            {
                name: "Golestān"
            },
            {
                name: "Qazvīn"
            },
            {
                name: "Khorāsān-e Janūbī"
            },
            {
                name: "Khorāsān-e Razavī"
            },
            {
                name: "Khorāsān-e Shemālī"
            }
        ],
        phoneCodes: [
            98
        ]
    },
    {
        name: "Iraq",
        code: "IQ",
        regionName: "Governorate",
        regions: [
            {
                name: "Al Anbar"
            },
            {
                name: "Arbil"
            },
            {
                name: "Al Basrah"
            },
            {
                name: "Babil"
            },
            {
                name: "Baghdad"
            },
            {
                name: "Dahuk"
            },
            {
                name: "Diyala"
            },
            {
                name: "Dhi Qar"
            },
            {
                name: "Karbala'"
            },
            {
                name: "Maysan"
            },
            {
                name: "Al Muthanna"
            },
            {
                name: "An Najef"
            },
            {
                name: "Ninawa"
            },
            {
                name: "Al Qadisiyah"
            },
            {
                name: "Salah ad Din"
            },
            {
                name: "As Sulaymaniyah"
            },
            {
                name: "At Ta'mim"
            },
            {
                name: "Wasit"
            }
        ],
        phoneCodes: [
            964
        ]
    },
    {
        name: "Ireland",
        code: "IE",
        regionName: "County",
        regions: [
            {
                name: "Connacht"
            },
            {
                name: "Clare"
            },
            {
                name: "Cavan"
            },
            {
                name: "Cork"
            },
            {
                name: "Carlow"
            },
            {
                name: "Dublin"
            },
            {
                name: "Donegal"
            },
            {
                name: "Galway"
            },
            {
                name: "Kildare"
            },
            {
                name: "Kilkenny"
            },
            {
                name: "Kerry"
            },
            {
                name: "Leinster"
            },
            {
                name: "Longford"
            },
            {
                name: "Louth"
            },
            {
                name: "Limerick"
            },
            {
                name: "Leitrim"
            },
            {
                name: "Laois"
            },
            {
                name: "Munster"
            },
            {
                name: "Meath"
            },
            {
                name: "Monaghan"
            },
            {
                name: "Mayo"
            },
            {
                name: "Offaly"
            },
            {
                name: "Roscommon"
            },
            {
                name: "Sligo"
            },
            {
                name: "Tipperary"
            },
            {
                name: "Ulster"
            },
            {
                name: "Waterford"
            },
            {
                name: "Westmeath"
            },
            {
                name: "Wicklow"
            },
            {
                name: "Wexford"
            }
        ],
        phoneCodes: [
            353
        ]
    },
    {
        name: "Isle of Man",
        code: "IM",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            44
        ]
    },
    {
        name: "Israel",
        code: "IL",
        regionName: "District",
        regions: [
            {
                name: "HaDarom"
            },
            {
                name: "Hefa"
            },
            {
                name: "Yerushalayim Al Quds"
            },
            {
                name: "HaMerkaz"
            },
            {
                name: "Tel-Aviv"
            },
            {
                name: "HaZafon"
            }
        ],
        phoneCodes: [
            972
        ]
    },
    {
        name: "Italy",
        code: "IT",
        regionName: "Province",
        regions: [
            {
                name: "Piemonte"
            },
            {
                name: "Valle d'Aosta"
            },
            {
                name: "Lombardia"
            },
            {
                name: "Trentino-Alto Adige"
            },
            {
                name: "Veneto"
            },
            {
                name: "Friuli-Venezia Giulia"
            },
            {
                name: "Liguria"
            },
            {
                name: "Emilia-Romagna"
            },
            {
                name: "Toscana"
            },
            {
                name: "Umbria"
            },
            {
                name: "Marche"
            },
            {
                name: "Lazio"
            },
            {
                name: "Abruzzo"
            },
            {
                name: "Molise"
            },
            {
                name: "Campania"
            },
            {
                name: "Puglia"
            },
            {
                name: "Basilicata"
            },
            {
                name: "Calabria"
            },
            {
                name: "Sicilia"
            },
            {
                name: "Sardegna"
            },
            {
                name: "Agrigento"
            },
            {
                name: "Alessandria"
            },
            {
                name: "Ancona"
            },
            {
                name: "Aosta"
            },
            {
                name: "Ascoli Piceno"
            },
            {
                name: "L'Aquila"
            },
            {
                name: "Arezzo"
            },
            {
                name: "Asti"
            },
            {
                name: "Avellino"
            },
            {
                name: "Bari"
            },
            {
                name: "Bergamo"
            },
            {
                name: "Biella"
            },
            {
                name: "Belluno"
            },
            {
                name: "Benevento"
            },
            {
                name: "Bologna"
            },
            {
                name: "Brindisi"
            },
            {
                name: "Brescia"
            },
            {
                name: "Barletta-Andria-Trani"
            },
            {
                name: "Bolzano"
            },
            {
                name: "Cagliari"
            },
            {
                name: "Campobasso"
            },
            {
                name: "Caserta"
            },
            {
                name: "Chieti"
            },
            {
                name: "Carbonia-Iglesias"
            },
            {
                name: "Caltanissetta"
            },
            {
                name: "Cuneo"
            },
            {
                name: "Como"
            },
            {
                name: "Cremona"
            },
            {
                name: "Cosenza"
            },
            {
                name: "Catania"
            },
            {
                name: "Catanzaro"
            },
            {
                name: "Enna"
            },
            {
                name: "Forlì-Cesena"
            },
            {
                name: "Ferrara"
            },
            {
                name: "Foggia"
            },
            {
                name: "Firenze"
            },
            {
                name: "Fermo"
            },
            {
                name: "Frosinone"
            },
            {
                name: "Genova"
            },
            {
                name: "Gorizia"
            },
            {
                name: "Grosseto"
            },
            {
                name: "Imperia"
            },
            {
                name: "Isernia"
            },
            {
                name: "Crotone"
            },
            {
                name: "Lecco"
            },
            {
                name: "Lecce"
            },
            {
                name: "Livorno"
            },
            {
                name: "Lodi"
            },
            {
                name: "Latina"
            },
            {
                name: "Lucca"
            },
            {
                name: "Monza e Brianza"
            },
            {
                name: "Macerata"
            },
            {
                name: "Messina"
            },
            {
                name: "Milano"
            },
            {
                name: "Mantova"
            },
            {
                name: "Modena"
            },
            {
                name: "Massa-Carrara"
            },
            {
                name: "Matera"
            },
            {
                name: "Napoli"
            },
            {
                name: "Novara"
            },
            {
                name: "Nuoro"
            },
            {
                name: "Ogliastra"
            },
            {
                name: "Oristano"
            },
            {
                name: "Olbia-Tempio"
            },
            {
                name: "Palermo"
            },
            {
                name: "Piacenza"
            },
            {
                name: "Padova"
            },
            {
                name: "Pescara"
            },
            {
                name: "Perugia"
            },
            {
                name: "Pisa"
            },
            {
                name: "Pordenone"
            },
            {
                name: "Prato"
            },
            {
                name: "Parma"
            },
            {
                name: "Pistoia"
            },
            {
                name: "Pesaro e Urbino"
            },
            {
                name: "Pavia"
            },
            {
                name: "Potenza"
            },
            {
                name: "Ravenna"
            },
            {
                name: "Reggio Calabria"
            },
            {
                name: "Reggio Emilia"
            },
            {
                name: "Ragusa"
            },
            {
                name: "Rieti"
            },
            {
                name: "Roma"
            },
            {
                name: "Rimini"
            },
            {
                name: "Rovigo"
            },
            {
                name: "Salerno"
            },
            {
                name: "Siena"
            },
            {
                name: "Sondrio"
            },
            {
                name: "La Spezia"
            },
            {
                name: "Siracusa"
            },
            {
                name: "Sassari"
            },
            {
                name: "Savona"
            },
            {
                name: "Taranto"
            },
            {
                name: "Teramo"
            },
            {
                name: "Trento"
            },
            {
                name: "Torino"
            },
            {
                name: "Trapani"
            },
            {
                name: "Terni"
            },
            {
                name: "Trieste"
            },
            {
                name: "Treviso"
            },
            {
                name: "Udine"
            },
            {
                name: "Varese"
            },
            {
                name: "Verbano-Cusio-Ossola"
            },
            {
                name: "Vercelli"
            },
            {
                name: "Venezia"
            },
            {
                name: "Vicenza"
            },
            {
                name: "Verona"
            },
            {
                name: "Medio Campidano"
            },
            {
                name: "Viterbo"
            },
            {
                name: "Vibo Valentia"
            }
        ],
        phoneCodes: [
            39
        ]
    },
    {
        name: "Jamaica",
        code: "JM",
        regionName: "Parish",
        regions: [
            {
                name: "Kingston"
            },
            {
                name: "Saint Andrew"
            },
            {
                name: "Saint Thomas"
            },
            {
                name: "Portland"
            },
            {
                name: "Saint Mary"
            },
            {
                name: "Saint Ann"
            },
            {
                name: "Trelawny"
            },
            {
                name: "Saint James"
            },
            {
                name: "Hanover"
            },
            {
                name: "Westmoreland"
            },
            {
                name: "Saint Elizabeth"
            },
            {
                name: "Manchester"
            },
            {
                name: "Clarendon"
            },
            {
                name: "Saint Catherine"
            }
        ],
        phoneCodes: [
            1
        ]
    },
    {
        name: "Japan",
        code: "JP",
        regionName: "Prefecture",
        regions: [
            {
                name: "Hokkaido"
            },
            {
                name: "Aomori"
            },
            {
                name: "Iwate"
            },
            {
                name: "Miyagi"
            },
            {
                name: "Akita"
            },
            {
                name: "Yamagata"
            },
            {
                name: "Fukushima"
            },
            {
                name: "Ibaraki"
            },
            {
                name: "Tochigi"
            },
            {
                name: "Gunma"
            },
            {
                name: "Saitama"
            },
            {
                name: "Chiba"
            },
            {
                name: "Tokyo"
            },
            {
                name: "Kanagawa"
            },
            {
                name: "Niigata"
            },
            {
                name: "Toyama"
            },
            {
                name: "Ishikawa"
            },
            {
                name: "Fukui"
            },
            {
                name: "Yamanashi"
            },
            {
                name: "Nagano"
            },
            {
                name: "Gifu"
            },
            {
                name: "Shizuoka"
            },
            {
                name: "Aichi"
            },
            {
                name: "Mie"
            },
            {
                name: "Shiga"
            },
            {
                name: "Kyoto"
            },
            {
                name: "Osaka"
            },
            {
                name: "Hyogo"
            },
            {
                name: "Nara"
            },
            {
                name: "Wakayama"
            },
            {
                name: "Tottori"
            },
            {
                name: "Shimane"
            },
            {
                name: "Okayama"
            },
            {
                name: "Hiroshima"
            },
            {
                name: "Yamaguchi"
            },
            {
                name: "Tokushima"
            },
            {
                name: "Kagawa"
            },
            {
                name: "Ehime"
            },
            {
                name: "Kochi"
            },
            {
                name: "Fukuoka"
            },
            {
                name: "Saga"
            },
            {
                name: "Nagasaki"
            },
            {
                name: "Kumamoto"
            },
            {
                name: "Oita"
            },
            {
                name: "Miyazaki"
            },
            {
                name: "Kagoshima"
            },
            {
                name: "Okinawa"
            }
        ],
        phoneCodes: [
            81
        ]
    },
    {
        name: "Jersey",
        code: "JE",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            44
        ]
    },
    {
        name: "Jordan",
        code: "JO",
        regionName: "Governorate",
        regions: [
            {
                name: "‘Ajlūn"
            },
            {
                name: "‘Ammān (Al ‘Aşimah)"
            },
            {
                name: "Al ‘Aqabah"
            },
            {
                name: "Aţ Ţafīlah"
            },
            {
                name: "Az Zarqā'"
            },
            {
                name: "Al Balqā'"
            },
            {
                name: "Irbid"
            },
            {
                name: "Jarash"
            },
            {
                name: "Al Karak"
            },
            {
                name: "Al Mafraq"
            },
            {
                name: "Mādabā"
            },
            {
                name: "Ma‘ān"
            }
        ],
        phoneCodes: [
            962
        ]
    },
    {
        name: "Kazakhstan",
        code: "KZ",
        regionName: "Region",
        regions: [
            {
                name: "Aqmola oblysy"
            },
            {
                name: "Aqtöbe oblysy"
            },
            {
                name: "Almaty"
            },
            {
                name: "Almaty oblysy"
            },
            {
                name: "Astana"
            },
            {
                name: "Atyraū oblysy"
            },
            {
                name: "Qaraghandy oblysy"
            },
            {
                name: "Qostanay oblysy"
            },
            {
                name: "Qyzylorda oblysy"
            },
            {
                name: "Mangghystaū oblysy"
            },
            {
                name: "Pavlodar oblysy"
            },
            {
                name: "Soltüstik Quzaqstan oblysy"
            },
            {
                name: "Shyghys Qazaqstan oblysy"
            },
            {
                name: "Ongtüstik Qazaqstan oblysy"
            },
            {
                name: "Batys Quzaqstan oblysy"
            },
            {
                name: "Zhambyl oblysy"
            }
        ],
        phoneCodes: [
            76,
            77
        ]
    },
    {
        name: "Kenya",
        code: "KE",
        regionName: "County",
        regions: [
            {
                name: "Baringo"
            },
            {
                name: "Bomet"
            },
            {
                name: "Bungoma"
            },
            {
                name: "Busia"
            },
            {
                name: "Elgeyo/Marakwet"
            },
            {
                name: "Embu"
            },
            {
                name: "Garissa"
            },
            {
                name: "Homa Bay"
            },
            {
                name: "Isiolo"
            },
            {
                name: "Kajiado"
            },
            {
                name: "Kakamega"
            },
            {
                name: "Kericho"
            },
            {
                name: "Kiambu"
            },
            {
                name: "Kilifi"
            },
            {
                name: "Kirinyaga"
            },
            {
                name: "Kisii"
            },
            {
                name: "Kisumu"
            },
            {
                name: "Kitui"
            },
            {
                name: "Kwale"
            },
            {
                name: "Laikipia"
            },
            {
                name: "Lamu"
            },
            {
                name: "Machakos"
            },
            {
                name: "Makueni"
            },
            {
                name: "Mandera"
            },
            {
                name: "Marsabit"
            },
            {
                name: "Meru"
            },
            {
                name: "Migori"
            },
            {
                name: "Mombasa"
            },
            {
                name: "Murang'a"
            },
            {
                name: "Nairobi City"
            },
            {
                name: "Nakuru"
            },
            {
                name: "Nandi"
            },
            {
                name: "Narok"
            },
            {
                name: "Nyamira"
            },
            {
                name: "Nyandarua"
            },
            {
                name: "Nyeri"
            },
            {
                name: "Samburu"
            },
            {
                name: "Siaya"
            },
            {
                name: "Taita/Taveta"
            },
            {
                name: "Tana River"
            },
            {
                name: "Tharaka-Nithi"
            },
            {
                name: "Trans Nzoia"
            },
            {
                name: "Turkana"
            },
            {
                name: "Uasin Gishu"
            },
            {
                name: "Vihiga"
            },
            {
                name: "Wajir"
            },
            {
                name: "West Pokot"
            }
        ],
        phoneCodes: [
            254
        ]
    },
    {
        name: "Kiribati",
        code: "KI",
        regionName: "Island group",
        regions: [
            {
                name: "Gilbert Islands"
            },
            {
                name: "Line Islands"
            },
            {
                name: "Phoenix Islands"
            }
        ],
        phoneCodes: [
            686
        ]
    },
    {
        name: "Kuwait",
        code: "KW",
        regionName: "Governorate",
        regions: [
            {
                name: "Al Ahmadi"
            },
            {
                name: "Al Farwānīyah"
            },
            {
                name: "Hawallī"
            },
            {
                name: "Al Jahrrā’"
            },
            {
                name: "Al Kuwayt (Al ‘Āşimah)"
            },
            {
                name: "Mubārak al Kabīr"
            }
        ],
        phoneCodes: [
            965
        ]
    },
    {
        name: "Kyrgyzstan",
        code: "KG",
        regionName: "Region",
        regions: [
            {
                name: "Batken"
            },
            {
                name: "Chü"
            },
            {
                name: "Bishkek"
            },
            {
                name: "Jalal-Abad"
            },
            {
                name: "Naryn"
            },
            {
                name: "Osh"
            },
            {
                name: "Talas"
            },
            {
                name: "Ysyk-Köl"
            }
        ],
        phoneCodes: [
            996
        ]
    },
    {
        name: "Lao People's Democratic Republic",
        code: "LA",
        regionName: "Province",
        regions: [
            {
                name: "Attapu"
            },
            {
                name: "Bokèo"
            },
            {
                name: "Bolikhamxai"
            },
            {
                name: "Champasak"
            },
            {
                name: "Houaphan"
            },
            {
                name: "Khammouan"
            },
            {
                name: "Louang Namtha"
            },
            {
                name: "Louangphabang"
            },
            {
                name: "Oudômxai"
            },
            {
                name: "Phôngsali"
            },
            {
                name: "Salavan"
            },
            {
                name: "Savannakhét"
            },
            {
                name: "Vientiane"
            },
            {
                name: "Vientiane"
            },
            {
                name: "Xaignabouli"
            },
            {
                name: "Xékong"
            },
            {
                name: "Xiangkhouang"
            },
            {
                name: "Xaisômboun"
            }
        ],
        phoneCodes: [
            856
        ]
    },
    {
        name: "Latvia",
        code: "LV",
        regionName: "Municipality",
        regions: [
            {
                name: "Aglonas novads"
            },
            {
                name: "Aizkraukles novads"
            },
            {
                name: "Aizputes novads"
            },
            {
                name: "Aknīstes novads"
            },
            {
                name: "Alojas novads"
            },
            {
                name: "Alsungas novads"
            },
            {
                name: "Alūksnes novads"
            },
            {
                name: "Amatas novads"
            },
            {
                name: "Apes novads"
            },
            {
                name: "Auces novads"
            },
            {
                name: "Ādažu novads"
            },
            {
                name: "Babītes novads"
            },
            {
                name: "Baldones novads"
            },
            {
                name: "Baltinavas novads"
            },
            {
                name: "Balvu novads"
            },
            {
                name: "Bauskas novads"
            },
            {
                name: "Beverīnas novads"
            },
            {
                name: "Brocēnu novads"
            },
            {
                name: "Burtnieku novads"
            },
            {
                name: "Carnikavas novads"
            },
            {
                name: "Cesvaines novads"
            },
            {
                name: "Cēsu novads"
            },
            {
                name: "Ciblas novads"
            },
            {
                name: "Dagdas novads"
            },
            {
                name: "Daugavpils novads"
            },
            {
                name: "Dobeles novads"
            },
            {
                name: "Dundagas novads"
            },
            {
                name: "Durbes novads"
            },
            {
                name: "Engures novads"
            },
            {
                name: "Ērgļu novads"
            },
            {
                name: "Garkalnes novads"
            },
            {
                name: "Grobiņas novads"
            },
            {
                name: "Gulbenes novads"
            },
            {
                name: "Iecavas novads"
            },
            {
                name: "Ikšķiles novads"
            },
            {
                name: "Ilūkstes novads"
            },
            {
                name: "Inčukalna novads"
            },
            {
                name: "Jaunjelgavas novads"
            },
            {
                name: "Jaunpiebalgas novads"
            },
            {
                name: "Jaunpils novads"
            },
            {
                name: "Jelgavas novads"
            },
            {
                name: "Jēkabpils novads"
            },
            {
                name: "Kandavas novads"
            },
            {
                name: "Kārsavas novads"
            },
            {
                name: "Kocēnu novads"
            },
            {
                name: "Kokneses novads"
            },
            {
                name: "Krāslavas novads"
            },
            {
                name: "Krimuldas novads"
            },
            {
                name: "Krustpils novads"
            },
            {
                name: "Kuldīgas novads"
            },
            {
                name: "Ķeguma novads"
            },
            {
                name: "Ķekavas novads"
            },
            {
                name: "Lielvārdes novads"
            },
            {
                name: "Limbažu novads"
            },
            {
                name: "Līgatnes novads"
            },
            {
                name: "Līvānu novads"
            },
            {
                name: "Lubānas novads"
            },
            {
                name: "Ludzas novads"
            },
            {
                name: "Madonas novads"
            },
            {
                name: "Mazsalacas novads"
            },
            {
                name: "Mālpils novads"
            },
            {
                name: "Mārupes novads"
            },
            {
                name: "Mērsraga novads"
            },
            {
                name: "Naukšēnu novads"
            },
            {
                name: "Neretas novads"
            },
            {
                name: "Nīcas novads"
            },
            {
                name: "Ogres novads"
            },
            {
                name: "Olaines novads"
            },
            {
                name: "Ozolnieku novads"
            },
            {
                name: "Pārgaujas novads"
            },
            {
                name: "Pāvilostas novads"
            },
            {
                name: "Pļaviņu novads"
            },
            {
                name: "Preiļu novads"
            },
            {
                name: "Priekules novads"
            },
            {
                name: "Priekuļu novads"
            },
            {
                name: "Raunas novads"
            },
            {
                name: "Rēzeknes novads"
            },
            {
                name: "Riebiņu novads"
            },
            {
                name: "Rojas novads"
            },
            {
                name: "Ropažu novads"
            },
            {
                name: "Rucavas novads"
            },
            {
                name: "Rugāju novads"
            },
            {
                name: "Rundāles novads"
            },
            {
                name: "Rūjienas novads"
            },
            {
                name: "Salas novads"
            },
            {
                name: "Salacgrīvas novads"
            },
            {
                name: "Salaspils novads"
            },
            {
                name: "Saldus novads"
            },
            {
                name: "Saulkrastu novads"
            },
            {
                name: "Sējas novads"
            },
            {
                name: "Siguldas novads"
            },
            {
                name: "Skrīveru novads"
            },
            {
                name: "Skrundas novads"
            },
            {
                name: "Smiltenes novads"
            },
            {
                name: "Stopiņu novads"
            },
            {
                name: "Strenču novads"
            },
            {
                name: "Talsu novads"
            },
            {
                name: "Tērvetes novads"
            },
            {
                name: "Tukuma novads"
            },
            {
                name: "Vaiņodes novads"
            },
            {
                name: "Valkas novads"
            },
            {
                name: "Varakļānu novads"
            },
            {
                name: "Vārkavas novads"
            },
            {
                name: "Vecpiebalgas novads"
            },
            {
                name: "Vecumnieku novads"
            },
            {
                name: "Ventspils novads"
            },
            {
                name: "Viesītes novads"
            },
            {
                name: "Viļakas novads"
            },
            {
                name: "Viļānu novads"
            },
            {
                name: "Zilupes novads"
            },
            {
                name: "Daugavpils"
            },
            {
                name: "Jelgava"
            },
            {
                name: "Jēkabpils"
            },
            {
                name: "Jūrmala"
            },
            {
                name: "Liepāja"
            },
            {
                name: "Rēzekne"
            },
            {
                name: "Rīga"
            },
            {
                name: "Ventspils"
            },
            {
                name: "Valmiera"
            }
        ],
        phoneCodes: [
            371
        ]
    },
    {
        name: "Lebanon",
        code: "LB",
        regionName: "Governorate",
        regions: [
            {
                name: "Aakkâr"
            },
            {
                name: "Liban-Nord"
            },
            {
                name: "Beyrouth"
            },
            {
                name: "Baalbek-Hermel"
            },
            {
                name: "Béqaa"
            },
            {
                name: "Liban-Sud"
            },
            {
                name: "Mont-Liban"
            },
            {
                name: "Nabatîyé"
            }
        ],
        phoneCodes: [
            961
        ]
    },
    {
        name: "Lesotho",
        code: "LS",
        regionName: "District",
        regions: [
            {
                name: "Maseru"
            },
            {
                name: "Butha-Buthe"
            },
            {
                name: "Leribe"
            },
            {
                name: "Berea"
            },
            {
                name: "Mafeteng"
            },
            {
                name: "Mohale's Hoek"
            },
            {
                name: "Quthing"
            },
            {
                name: "Qacha's Nek"
            },
            {
                name: "Mokhotlong"
            },
            {
                name: "Thaba-Tseka"
            }
        ],
        phoneCodes: [
            266
        ]
    },
    {
        name: "Liberia",
        code: "LR",
        regionName: "County",
        regions: [
            {
                name: "Bong"
            },
            {
                name: "Bomi"
            },
            {
                name: "Grand Cape Mount"
            },
            {
                name: "Grand Bassa"
            },
            {
                name: "Grand Gedeh"
            },
            {
                name: "Grand Kru"
            },
            {
                name: "Lofa"
            },
            {
                name: "Margibi"
            },
            {
                name: "Montserrado"
            },
            {
                name: "Maryland"
            },
            {
                name: "Nimba"
            },
            {
                name: "Rivercess"
            },
            {
                name: "Sinoe"
            }
        ],
        phoneCodes: [
            231
        ]
    },
    {
        name: "Libya",
        code: "LY",
        regionName: "Popularates",
        regions: [
            {
                name: "Banghāzī"
            },
            {
                name: "Al Buţnān"
            },
            {
                name: "Darnah"
            },
            {
                name: "Ghāt"
            },
            {
                name: "Al Jabal al Akhḑar"
            },
            {
                name: "Jaghbūb"
            },
            {
                name: "Al Jabal al Gharbī"
            },
            {
                name: "Al Jifārah"
            },
            {
                name: "Al Jufrah"
            },
            {
                name: "Al Kufrah"
            },
            {
                name: "Al Marqab"
            },
            {
                name: "Mişrātah"
            },
            {
                name: "Al Marj"
            },
            {
                name: "Murzuq"
            },
            {
                name: "Nālūt"
            },
            {
                name: "An Nuqaţ al Khams"
            },
            {
                name: "Sabhā"
            },
            {
                name: "Surt"
            },
            {
                name: "Ţarābulus"
            },
            {
                name: "Al Wāḩāt"
            },
            {
                name: "Wādī al Ḩayāt"
            },
            {
                name: "Wādī ash Shāţiʾ"
            },
            {
                name: "Az Zāwiyah"
            }
        ],
        phoneCodes: [
            218
        ]
    },
    {
        name: "Liechtenstein",
        code: "LI",
        regionName: "Commune",
        regions: [
            {
                name: "Balzers"
            },
            {
                name: "Eschen"
            },
            {
                name: "Gamprin"
            },
            {
                name: "Mauren"
            },
            {
                name: "Planken"
            },
            {
                name: "Ruggell"
            },
            {
                name: "Schaan"
            },
            {
                name: "Schellenberg"
            },
            {
                name: "Triesen"
            },
            {
                name: "Triesenberg"
            },
            {
                name: "Vaduz"
            }
        ],
        phoneCodes: [
            423
        ]
    },
    {
        name: "Lithuania",
        code: "LT",
        regionName: "County",
        regions: [
            {
                name: "Alytaus Apskritis"
            },
            {
                name: "Klaipėdos Apskritis"
            },
            {
                name: "Kauno Apskritis"
            },
            {
                name: "Marijampolės Apskritis"
            },
            {
                name: "Panevėžio Apskritis"
            },
            {
                name: "Šiaulių Apskritis"
            },
            {
                name: "Tauragés Apskritis"
            },
            {
                name: "Telšių Apskritis"
            },
            {
                name: "Utenos Apskritis"
            },
            {
                name: "Vilniaus Apskritis"
            }
        ],
        phoneCodes: [
            370
        ]
    },
    {
        name: "Luxembourg",
        code: "LU",
        regionName: "District",
        regions: [
            {
                name: "Diekirch"
            },
            {
                name: "Grevenmacher"
            },
            {
                name: "Luxembourg"
            }
        ],
        phoneCodes: [
            352
        ]
    },
    {
        name: "Macao",
        code: "MO",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            853
        ]
    },
    {
        name: "Macedonia (the former Yugoslav Republic of)",
        code: "MK",
        regionName: "Municipality",
        regions: [
            {
                name: "Aerodrom"
            },
            {
                name: "Aračinovo"
            },
            {
                name: "Berovo"
            },
            {
                name: "Bitola"
            },
            {
                name: "Bogdanci"
            },
            {
                name: "Bogovinje"
            },
            {
                name: "Bosilovo"
            },
            {
                name: "Brvenica"
            },
            {
                name: "Butel"
            },
            {
                name: "Valandovo"
            },
            {
                name: "Vasilevo"
            },
            {
                name: "Vevčani"
            },
            {
                name: "Veles"
            },
            {
                name: "Vinica"
            },
            {
                name: "Vraneštica"
            },
            {
                name: "Vrapčište"
            },
            {
                name: "Gazi Baba"
            },
            {
                name: "Gevgelija"
            },
            {
                name: "Gostivar"
            },
            {
                name: "Gradsko"
            },
            {
                name: "Debar"
            },
            {
                name: "Debarca"
            },
            {
                name: "Delčevo"
            },
            {
                name: "Demir Kapija"
            },
            {
                name: "Demir Hisar"
            },
            {
                name: "Dojran"
            },
            {
                name: "Dolneni"
            },
            {
                name: "Drugovo"
            },
            {
                name: "Gjorče Petrov"
            },
            {
                name: "Želino"
            },
            {
                name: "Zajas"
            },
            {
                name: "Zelenikovo"
            },
            {
                name: "Zrnovci"
            },
            {
                name: "Ilinden"
            },
            {
                name: "Jegunovce"
            },
            {
                name: "Kavadarci"
            },
            {
                name: "Karbinci"
            },
            {
                name: "Karpoš"
            },
            {
                name: "Kisela Voda"
            },
            {
                name: "Kičevo"
            },
            {
                name: "Konče"
            },
            {
                name: "Kočani"
            },
            {
                name: "Kratovo"
            },
            {
                name: "Kriva Palanka"
            },
            {
                name: "Krivogaštani"
            },
            {
                name: "Kruševo"
            },
            {
                name: "Kumanovo"
            },
            {
                name: "Lipkovo"
            },
            {
                name: "Lozovo"
            },
            {
                name: "Mavrovo-i-Rostuša"
            },
            {
                name: "Makedonska Kamenica"
            },
            {
                name: "Makedonski Brod"
            },
            {
                name: "Mogila"
            },
            {
                name: "Negotino"
            },
            {
                name: "Novaci"
            },
            {
                name: "Novo Selo"
            },
            {
                name: "Oslomej"
            },
            {
                name: "Ohrid"
            },
            {
                name: "Petrovec"
            },
            {
                name: "Pehčevo"
            },
            {
                name: "Plasnica"
            },
            {
                name: "Prilep"
            },
            {
                name: "Probištip"
            },
            {
                name: "Radoviš"
            },
            {
                name: "Rankovce"
            },
            {
                name: "Resen"
            },
            {
                name: "Rosoman"
            },
            {
                name: "Saraj"
            },
            {
                name: "Sveti Nikole"
            },
            {
                name: "Sopište"
            },
            {
                name: "Staro Nagoričane"
            },
            {
                name: "Struga"
            },
            {
                name: "Strumica"
            },
            {
                name: "Studeničani"
            },
            {
                name: "Tearce"
            },
            {
                name: "Tetovo"
            },
            {
                name: "Centar"
            },
            {
                name: "Centar Župa"
            },
            {
                name: "Čair"
            },
            {
                name: "Čaška"
            },
            {
                name: "Češinovo-Obleševo"
            },
            {
                name: "Čučer Sandevo"
            },
            {
                name: "Štip"
            },
            {
                name: "Šuto Orizari"
            }
        ],
        phoneCodes: [
            389
        ]
    },
    {
        name: "Madagascar",
        code: "MG",
        regionName: "Autonomous province",
        regions: [
            {
                name: "Toamasina"
            },
            {
                name: "Antsiranana"
            },
            {
                name: "Fianarantsoa"
            },
            {
                name: "Mahajanga"
            },
            {
                name: "Antananarivo"
            },
            {
                name: "Toliara"
            }
        ],
        phoneCodes: [
            261
        ]
    },
    {
        name: "Malawi",
        code: "MW",
        regionName: "District",
        regions: [
            {
                name: "Balaka"
            },
            {
                name: "Blantyre"
            },
            {
                name: "Central Region"
            },
            {
                name: "Chikwawa"
            },
            {
                name: "Chiradzulu"
            },
            {
                name: "Chitipa"
            },
            {
                name: "Dedza"
            },
            {
                name: "Dowa"
            },
            {
                name: "Karonga"
            },
            {
                name: "Kasungu"
            },
            {
                name: "Lilongwe"
            },
            {
                name: "Likoma"
            },
            {
                name: "Mchinji"
            },
            {
                name: "Mangochi"
            },
            {
                name: "Machinga"
            },
            {
                name: "Mulanje"
            },
            {
                name: "Mwanza"
            },
            {
                name: "Mzimba"
            },
            {
                name: "Northern Region"
            },
            {
                name: "Nkhata Bay"
            },
            {
                name: "Neno"
            },
            {
                name: "Ntchisi"
            },
            {
                name: "Nkhotakota"
            },
            {
                name: "Nsanje"
            },
            {
                name: "Ntcheu"
            },
            {
                name: "Phalombe"
            },
            {
                name: "Rumphi"
            },
            {
                name: "Southern Region"
            },
            {
                name: "Salima"
            },
            {
                name: "Thyolo"
            },
            {
                name: "Zomba"
            }
        ],
        phoneCodes: [
            265
        ]
    },
    {
        name: "Malaysia",
        code: "MY",
        regionName: "State",
        regions: [
            {
                name: "Johor"
            },
            {
                name: "Kedah"
            },
            {
                name: "Kelantan"
            },
            {
                name: "Melaka"
            },
            {
                name: "Negeri Sembilan"
            },
            {
                name: "Pahang"
            },
            {
                name: "Pulau Pinang"
            },
            {
                name: "Perak"
            },
            {
                name: "Perlis"
            },
            {
                name: "Selangor"
            },
            {
                name: "Terengganu"
            },
            {
                name: "Sabah"
            },
            {
                name: "Sarawak"
            },
            {
                name: "Wilayah Persekutuan Kuala Lumpur"
            },
            {
                name: "Wilayah Persekutuan Labuan"
            },
            {
                name: "Wilayah Persekutuan Putrajaya"
            }
        ],
        phoneCodes: [
            60
        ]
    },
    {
        name: "Maldives",
        code: "MV",
        regionName: "Administrative atoll",
        regions: [
            {
                name: "Alifu Dhaalu"
            },
            {
                name: "Seenu"
            },
            {
                name: "Alifu Alifu"
            },
            {
                name: "Lhaviyani"
            },
            {
                name: "Vaavu"
            },
            {
                name: "Laamu"
            },
            {
                name: "Haa Alifu"
            },
            {
                name: "Thaa"
            },
            {
                name: "Meemu"
            },
            {
                name: "Raa"
            },
            {
                name: "Faafu"
            },
            {
                name: "Dhaalu"
            },
            {
                name: "Baa"
            },
            {
                name: "Haa Dhaalu"
            },
            {
                name: "Shaviyani"
            },
            {
                name: "Noonu"
            },
            {
                name: "Kaafu"
            },
            {
                name: "Gaafu Alifu"
            },
            {
                name: "Gaafu Dhaalu"
            },
            {
                name: "Gnaviyani"
            },
            {
                name: "Central"
            },
            {
                name: "Male"
            },
            {
                name: "North Central"
            },
            {
                name: "North"
            },
            {
                name: "South Central"
            },
            {
                name: "South"
            },
            {
                name: "Upper North"
            },
            {
                name: "Upper South"
            }
        ],
        phoneCodes: [
            960
        ]
    },
    {
        name: "Mali",
        code: "ML",
        regionName: "Region",
        regions: [
            {
                name: "Kayes"
            },
            {
                name: "Koulikoro"
            },
            {
                name: "Sikasso"
            },
            {
                name: "Ségou"
            },
            {
                name: "Mopti"
            },
            {
                name: "Tombouctou"
            },
            {
                name: "Gao"
            },
            {
                name: "Kidal"
            },
            {
                name: "Bamako"
            }
        ],
        phoneCodes: [
            223
        ]
    },
    {
        name: "Malta",
        code: "MT",
        regionName: "Local council",
        regions: [
            {
                name: "Attard"
            },
            {
                name: "Balzan"
            },
            {
                name: "Birgu"
            },
            {
                name: "Birkirkara"
            },
            {
                name: "Birżebbuġa"
            },
            {
                name: "Bormla"
            },
            {
                name: "Dingli"
            },
            {
                name: "Fgura"
            },
            {
                name: "Floriana"
            },
            {
                name: "Fontana"
            },
            {
                name: "Gudja"
            },
            {
                name: "Gżira"
            },
            {
                name: "Għajnsielem"
            },
            {
                name: "Għarb"
            },
            {
                name: "Għargħur"
            },
            {
                name: "Għasri"
            },
            {
                name: "Għaxaq"
            },
            {
                name: "Ħamrun"
            },
            {
                name: "Iklin"
            },
            {
                name: "Isla"
            },
            {
                name: "Kalkara"
            },
            {
                name: "Kerċem"
            },
            {
                name: "Kirkop"
            },
            {
                name: "Lija"
            },
            {
                name: "Luqa"
            },
            {
                name: "Marsa"
            },
            {
                name: "Marsaskala"
            },
            {
                name: "Marsaxlokk"
            },
            {
                name: "Mdina"
            },
            {
                name: "Mellieħa"
            },
            {
                name: "Mġarr"
            },
            {
                name: "Mosta"
            },
            {
                name: "Mqabba"
            },
            {
                name: "Msida"
            },
            {
                name: "Mtarfa"
            },
            {
                name: "Munxar"
            },
            {
                name: "Nadur"
            },
            {
                name: "Naxxar"
            },
            {
                name: "Paola"
            },
            {
                name: "Pembroke"
            },
            {
                name: "Pietà"
            },
            {
                name: "Qala"
            },
            {
                name: "Qormi"
            },
            {
                name: "Qrendi"
            },
            {
                name: "Rabat Għawdex"
            },
            {
                name: "Rabat Malta"
            },
            {
                name: "Safi"
            },
            {
                name: "San Ġiljan"
            },
            {
                name: "San Ġwann"
            },
            {
                name: "San Lawrenz"
            },
            {
                name: "San Pawl il-Baħar"
            },
            {
                name: "Sannat"
            },
            {
                name: "Santa Luċija"
            },
            {
                name: "Santa Venera"
            },
            {
                name: "Siġġiewi"
            },
            {
                name: "Sliema"
            },
            {
                name: "Swieqi"
            },
            {
                name: "Ta’ Xbiex"
            },
            {
                name: "Tarxien"
            },
            {
                name: "Valletta"
            },
            {
                name: "Xagħra"
            },
            {
                name: "Xewkija"
            },
            {
                name: "Xgħajra"
            },
            {
                name: "Żabbar"
            },
            {
                name: "Żebbuġ Għawdex"
            },
            {
                name: "Żebbuġ Malta"
            },
            {
                name: "Żejtun"
            },
            {
                name: "Żurrieq"
            }
        ],
        phoneCodes: [
            356
        ]
    },
    {
        name: "Marshall Islands",
        code: "MH",
        regionName: "Municipality",
        regions: [
            {
                name: "Ailuk"
            },
            {
                name: "Ailinglaplap"
            },
            {
                name: "Arno"
            },
            {
                name: "Aur"
            },
            {
                name: "Ebon"
            },
            {
                name: "Enewetak"
            },
            {
                name: "Jabat"
            },
            {
                name: "Jaluit"
            },
            {
                name: "Kili"
            },
            {
                name: "Kwajalein"
            },
            {
                name: "Ralik chain"
            },
            {
                name: "Lae"
            },
            {
                name: "Lib"
            },
            {
                name: "Likiep"
            },
            {
                name: "Majuro"
            },
            {
                name: "Maloelap"
            },
            {
                name: "Mejit"
            },
            {
                name: "Mili"
            },
            {
                name: "Namdrik"
            },
            {
                name: "Namu"
            },
            {
                name: "Rongelap"
            },
            {
                name: "Ratak chain"
            },
            {
                name: "Ujae"
            },
            {
                name: "Utirik"
            },
            {
                name: "Wotje"
            },
            {
                name: "Wotho"
            }
        ],
        phoneCodes: [
            692
        ]
    },
    {
        name: "Martinique",
        code: "MQ",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            596
        ]
    },
    {
        name: "Mauritania",
        code: "MR",
        regionName: "Region",
        regions: [
            {
                name: "Hodh ech Chargui"
            },
            {
                name: "Hodh el Charbi"
            },
            {
                name: "Assaba"
            },
            {
                name: "Gorgol"
            },
            {
                name: "Brakna"
            },
            {
                name: "Trarza"
            },
            {
                name: "Adrar"
            },
            {
                name: "Dakhlet Nouadhibou"
            },
            {
                name: "Tagant"
            },
            {
                name: "Guidimaka"
            },
            {
                name: "Tiris Zemmour"
            },
            {
                name: "Inchiri"
            },
            {
                name: "Nouakchott"
            }
        ],
        phoneCodes: [
            222
        ]
    },
    {
        name: "Mauritius",
        code: "MU",
        regionName: "District",
        regions: [
            {
                name: "Agalega Islands"
            },
            {
                name: "Black River"
            },
            {
                name: "Beau Bassin-Rose Hill"
            },
            {
                name: "Cargados Carajos Shoals"
            },
            {
                name: "Curepipe"
            },
            {
                name: "Flacq"
            },
            {
                name: "Grand Port"
            },
            {
                name: "Moka"
            },
            {
                name: "Pamplemousses"
            },
            {
                name: "Port Louis"
            },
            {
                name: "Port Louis"
            },
            {
                name: "Plaines Wilhems"
            },
            {
                name: "Quatre Bornes"
            },
            {
                name: "Rodrigues Island"
            },
            {
                name: "Rivière du Rempart"
            },
            {
                name: "Savanne"
            },
            {
                name: "Vacoas-Phoenix"
            }
        ],
        phoneCodes: [
            230
        ]
    },
    {
        name: "Mayotte",
        code: "YT",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            262
        ]
    },
    {
        name: "Mexico",
        code: "MX",
        regionName: "State",
        regions: [
            {
                name: "Aguascalientes"
            },
            {
                name: "Baja California"
            },
            {
                name: "Baja California Sur"
            },
            {
                name: "Campeche"
            },
            {
                name: "Chihuahua"
            },
            {
                name: "Chiapas"
            },
            {
                name: "Ciudad de México"
            },
            {
                name: "Coahuila de Zaragoza"
            },
            {
                name: "Colima"
            },
            {
                name: "Durango"
            },
            {
                name: "Guerrero"
            },
            {
                name: "Guanajuato"
            },
            {
                name: "Hidalgo"
            },
            {
                name: "Jalisco"
            },
            {
                name: "México"
            },
            {
                name: "Michoacán de Ocampo"
            },
            {
                name: "Morelos"
            },
            {
                name: "Nayarit"
            },
            {
                name: "Nuevo León"
            },
            {
                name: "Oaxaca"
            },
            {
                name: "Puebla"
            },
            {
                name: "Querétaro"
            },
            {
                name: "Quintana Roo"
            },
            {
                name: "Sinaloa"
            },
            {
                name: "San Luis Potosí"
            },
            {
                name: "Sonora"
            },
            {
                name: "Tabasco"
            },
            {
                name: "Tamaulipas"
            },
            {
                name: "Tlaxcala"
            },
            {
                name: "Veracruz de Ignacio de la Llave"
            },
            {
                name: "Yucatán"
            },
            {
                name: "Zacatecas"
            }
        ],
        phoneCodes: [
            52
        ]
    },
    {
        name: "Micronesia (Federated States of)",
        code: "FM",
        regionName: "State",
        regions: [
            {
                name: "Kosrae"
            },
            {
                name: "Pohnpei"
            },
            {
                name: "Chuuk"
            },
            {
                name: "Yap"
            }
        ],
        phoneCodes: [
            691
        ]
    },
    {
        name: "Moldova (Republic of)",
        code: "MD",
        regionName: "District",
        regions: [
            {
                name: "Anenii Noi"
            },
            {
                name: "Bălți"
            },
            {
                name: "Tighina"
            },
            {
                name: "Briceni"
            },
            {
                name: "Basarabeasca"
            },
            {
                name: "Cahul"
            },
            {
                name: "Călărași"
            },
            {
                name: "Cimișlia"
            },
            {
                name: "Criuleni"
            },
            {
                name: "Căușeni"
            },
            {
                name: "Cantemir"
            },
            {
                name: "Chișinău"
            },
            {
                name: "Dondușeni"
            },
            {
                name: "Drochia"
            },
            {
                name: "Dubăsari"
            },
            {
                name: "Edineț"
            },
            {
                name: "Fălești"
            },
            {
                name: "Florești"
            },
            {
                name: "Găgăuzia, Unitatea teritorială autonomă"
            },
            {
                name: "Glodeni"
            },
            {
                name: "Hîncești"
            },
            {
                name: "Ialoveni"
            },
            {
                name: "Leova"
            },
            {
                name: "Nisporeni"
            },
            {
                name: "Ocnița"
            },
            {
                name: "Orhei"
            },
            {
                name: "Rezina"
            },
            {
                name: "Rîșcani"
            },
            {
                name: "Șoldănești"
            },
            {
                name: "Sîngerei"
            },
            {
                name: "Stînga Nistrului, unitatea teritorială din"
            },
            {
                name: "Soroca"
            },
            {
                name: "Strășeni"
            },
            {
                name: "Ștefan Vodă"
            },
            {
                name: "Taraclia"
            },
            {
                name: "Telenești"
            },
            {
                name: "Ungheni"
            }
        ],
        phoneCodes: [
            373
        ]
    },
    {
        name: "Monaco",
        code: "MC",
        regionName: "Quarter",
        regions: [
            {
                name: "La Colle"
            },
            {
                name: "La Condamine"
            },
            {
                name: "Fontvieille"
            },
            {
                name: "La Gare"
            },
            {
                name: "Jardin Exotique"
            },
            {
                name: "Larvotto"
            },
            {
                name: "Malbousquet"
            },
            {
                name: "Monte-Carlo"
            },
            {
                name: "Moneghetti"
            },
            {
                name: "Monaco-Ville"
            },
            {
                name: "Moulins"
            },
            {
                name: "Port-Hercule"
            },
            {
                name: "Sainte-Dévote"
            },
            {
                name: "La Source"
            },
            {
                name: "Spélugues"
            },
            {
                name: "Saint-Roman"
            },
            {
                name: "Vallon de la Rousse"
            }
        ],
        phoneCodes: [
            377
        ]
    },
    {
        name: "Mongolia",
        code: "MN",
        regionName: "Province",
        regions: [
            {
                name: "Orhon"
            },
            {
                name: "Darhan uul"
            },
            {
                name: "Hentiy"
            },
            {
                name: "Hövsgöl"
            },
            {
                name: "Hovd"
            },
            {
                name: "Uvs"
            },
            {
                name: "Töv"
            },
            {
                name: "Selenge"
            },
            {
                name: "Sühbaatar"
            },
            {
                name: "Ömnögovi"
            },
            {
                name: "Övörhangay"
            },
            {
                name: "Dzavhan"
            },
            {
                name: "Dundgovi"
            },
            {
                name: "Dornod"
            },
            {
                name: "Dornogovi"
            },
            {
                name: "Govi-Sumber"
            },
            {
                name: "Govi-Altay"
            },
            {
                name: "Bulgan"
            },
            {
                name: "Bayanhongor"
            },
            {
                name: "Bayan-Ölgiy"
            },
            {
                name: "Arhangay"
            },
            {
                name: "Ulanbaatar"
            }
        ],
        phoneCodes: [
            976
        ]
    },
    {
        name: "Montenegro",
        code: "ME",
        regionName: "Municipality",
        regions: [
            {
                name: "Andrijevica"
            },
            {
                name: "Bar"
            },
            {
                name: "Berane"
            },
            {
                name: "Bijelo Polje"
            },
            {
                name: "Budva"
            },
            {
                name: "Cetinje"
            },
            {
                name: "Danilovgrad"
            },
            {
                name: "Herceg-Novi"
            },
            {
                name: "Kolašin"
            },
            {
                name: "Kotor"
            },
            {
                name: "Mojkovac"
            },
            {
                name: "Nikšić"
            },
            {
                name: "Plav"
            },
            {
                name: "Pljevlja"
            },
            {
                name: "Plužine"
            },
            {
                name: "Podgorica"
            },
            {
                name: "Rožaje"
            },
            {
                name: "Šavnik"
            },
            {
                name: "Tivat"
            },
            {
                name: "Ulcinj"
            },
            {
                name: "Žabljak"
            }
        ],
        phoneCodes: [
            382
        ]
    },
    {
        name: "Montserrat",
        code: "MS",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            1
        ]
    },
    {
        name: "Morocco",
        code: "MA",
        regionName: "Province",
        regions: [
            {
                name: "Tanger-Tétouan-Al Hoceïma"
            },
            {
                name: "L'Oriental"
            },
            {
                name: "Fès-Meknès"
            },
            {
                name: "Rabat-Salé-Kénitra"
            },
            {
                name: "Béni Mellal-Khénifra"
            },
            {
                name: "Casablanca-Settat"
            },
            {
                name: "Marrakech-Safi"
            },
            {
                name: "Drâa-Tafilalet"
            },
            {
                name: "Souss-Massa"
            },
            {
                name: "Guelmim-Oued Noun (EH-partial)"
            },
            {
                name: "Laâyoune-Sakia El Hamra (EH-partial)"
            },
            {
                name: "Dakhla-Oued Ed-Dahab (EH)"
            },
            {
                name: "Agadir-Ida-Ou-Tanane"
            },
            {
                name: "Aousserd (EH)"
            },
            {
                name: "Assa-Zag (EH-partial)"
            },
            {
                name: "Azilal"
            },
            {
                name: "Béni Mellal"
            },
            {
                name: "Berkane"
            },
            {
                name: "Benslimane"
            },
            {
                name: "Boujdour (EH)"
            },
            {
                name: "Boulemane"
            },
            {
                name: "Berrechid"
            },
            {
                name: "Casablanca"
            },
            {
                name: "Chefchaouen"
            },
            {
                name: "Chichaoua"
            },
            {
                name: "Chtouka-Ait Baha"
            },
            {
                name: "Driouch"
            },
            {
                name: "Errachidia"
            },
            {
                name: "Essaouira"
            },
            {
                name: "Es-Semara (EH-partial)"
            },
            {
                name: "Fahs-Anjra"
            },
            {
                name: "Fès"
            },
            {
                name: "Figuig"
            },
            {
                name: "Fquih Ben Salah"
            },
            {
                name: "Guelmim"
            },
            {
                name: "Guercif"
            },
            {
                name: "El Hajeb"
            },
            {
                name: "Al Haouz"
            },
            {
                name: "Al Hoceïma"
            },
            {
                name: "Ifrane"
            },
            {
                name: "Inezgane-Ait Melloul"
            },
            {
                name: "El Jadida"
            },
            {
                name: "Jerada"
            },
            {
                name: "Kénitra"
            },
            {
                name: "El Kelâa des Sraghna"
            },
            {
                name: "Khemisset"
            },
            {
                name: "Khenifra"
            },
            {
                name: "Khouribga"
            },
            {
                name: "Laâyoune (EH)"
            },
            {
                name: "Larache"
            },
            {
                name: "Marrakech"
            },
            {
                name: "M’diq-Fnideq"
            },
            {
                name: "Médiouna"
            },
            {
                name: "Meknès"
            },
            {
                name: "Midelt"
            },
            {
                name: "Mohammadia"
            },
            {
                name: "Moulay Yacoub"
            },
            {
                name: "Nador"
            },
            {
                name: "Nouaceur"
            },
            {
                name: "Ouarzazate"
            },
            {
                name: "Oued Ed-Dahab (EH)"
            },
            {
                name: "Oujda-Angad"
            },
            {
                name: "Ouezzane"
            },
            {
                name: "Rabat"
            },
            {
                name: "Rehamna"
            },
            {
                name: "Safi"
            },
            {
                name: "Salé"
            },
            {
                name: "Sefrou"
            },
            {
                name: "Settat"
            },
            {
                name: "Sidi Bennour"
            },
            {
                name: "Sidi Ifni"
            },
            {
                name: "Sidi Kacem"
            },
            {
                name: "Sidi Slimane"
            },
            {
                name: "Skhirate-Témara"
            },
            {
                name: "Tarfaya (EH-partial)"
            },
            {
                name: "Taourirt"
            },
            {
                name: "Taounate"
            },
            {
                name: "Taroudant"
            },
            {
                name: "Tata"
            },
            {
                name: "Taza"
            },
            {
                name: "Tétouan"
            },
            {
                name: "Tinghir"
            },
            {
                name: "Tiznit"
            },
            {
                name: "Tanger-Assilah"
            },
            {
                name: "Tan-Tan (EH-partial)"
            },
            {
                name: "Youssoufia"
            },
            {
                name: "Zagora"
            }
        ],
        phoneCodes: [
            212
        ]
    },
    {
        name: "Mozambique",
        code: "MZ",
        regionName: "Province",
        regions: [
            {
                name: "Niassa"
            },
            {
                name: "Manica"
            },
            {
                name: "Gaza"
            },
            {
                name: "Inhambane"
            },
            {
                name: "Maputo"
            },
            {
                name: "Maputo (city)"
            },
            {
                name: "Numpula"
            },
            {
                name: "Cabo Delgado"
            },
            {
                name: "Zambezia"
            },
            {
                name: "Sofala"
            },
            {
                name: "Tete"
            }
        ],
        phoneCodes: [
            258
        ]
    },
    {
        name: "Myanmar",
        code: "MM",
        regionName: "Division",
        regions: [
            {
                name: "Sagaing"
            },
            {
                name: "Bago"
            },
            {
                name: "Magway"
            },
            {
                name: "Mandalay"
            },
            {
                name: "Tanintharyi"
            },
            {
                name: "Yangon"
            },
            {
                name: "Ayeyarwady"
            },
            {
                name: "Kachin"
            },
            {
                name: "Kayah"
            },
            {
                name: "Kayin"
            },
            {
                name: "Chin"
            },
            {
                name: "Mon"
            },
            {
                name: "Rakhine"
            },
            {
                name: "Shan"
            }
        ],
        phoneCodes: [
            95
        ]
    },
    {
        name: "Namibia",
        code: "NA",
        regionName: "Region",
        regions: [
            {
                name: "Caprivi"
            },
            {
                name: "Erongo"
            },
            {
                name: "Hardap"
            },
            {
                name: "Karas"
            },
            {
                name: "Khomas"
            },
            {
                name: "Kunene"
            },
            {
                name: "Otjozondjupa"
            },
            {
                name: "Omaheke"
            },
            {
                name: "Okavango"
            },
            {
                name: "Oshana"
            },
            {
                name: "Omusati"
            },
            {
                name: "Oshikoto"
            },
            {
                name: "Ohangwena"
            }
        ],
        phoneCodes: [
            264
        ]
    },
    {
        name: "Nauru",
        code: "NR",
        regionName: "District",
        regions: [
            {
                name: "Aiwo"
            },
            {
                name: "Anabar"
            },
            {
                name: "Anetan"
            },
            {
                name: "Anibare"
            },
            {
                name: "Baiti"
            },
            {
                name: "Boe"
            },
            {
                name: "Buada"
            },
            {
                name: "Denigomodu"
            },
            {
                name: "Ewa"
            },
            {
                name: "Ijuw"
            },
            {
                name: "Meneng"
            },
            {
                name: "Nibok"
            },
            {
                name: "Uaboe"
            },
            {
                name: "Yaren"
            }
        ],
        phoneCodes: [
            674
        ]
    },
    {
        name: "Nepal",
        code: "NP",
        regionName: "Zone",
        regions: [
            {
                name: "Madhyamanchal"
            },
            {
                name: "Madhya Pashchimanchal"
            },
            {
                name: "Pashchimanchal"
            },
            {
                name: "Purwanchal"
            },
            {
                name: "Sudur Pashchimanchal"
            },
            {
                name: "Bagmati"
            },
            {
                name: "Bheri"
            },
            {
                name: "Dhawalagiri"
            },
            {
                name: "Gandaki"
            },
            {
                name: "Janakpur"
            },
            {
                name: "Karnali"
            },
            {
                name: "Kosi"
            },
            {
                name: "Lumbini"
            },
            {
                name: "Mahakali"
            },
            {
                name: "Mechi"
            },
            {
                name: "Narayani"
            },
            {
                name: "Rapti"
            },
            {
                name: "Sagarmatha"
            },
            {
                name: "Seti"
            }
        ],
        phoneCodes: [
            977
        ]
    },
    {
        name: "Netherlands",
        code: "NL",
        regionName: "Province",
        regions: [
            {
                name: "Aruba"
            },
            {
                name: "Bonaire"
            },
            {
                name: "Saba"
            },
            {
                name: "Sint Eustatius"
            },
            {
                name: "Curaçao"
            },
            {
                name: "Drenthe"
            },
            {
                name: "Flevoland"
            },
            {
                name: "Friesland"
            },
            {
                name: "Gelderland"
            },
            {
                name: "Groningen"
            },
            {
                name: "Limburg"
            },
            {
                name: "Noord-Brabant"
            },
            {
                name: "Noord-Holland"
            },
            {
                name: "Overijssel"
            },
            {
                name: "Sint Maarten"
            },
            {
                name: "Utrecht"
            },
            {
                name: "Zeeland"
            },
            {
                name: "Zuid-Holland"
            }
        ],
        phoneCodes: [
            31
        ]
    },
    {
        name: "New Caledonia",
        code: "NC",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            687
        ]
    },
    {
        name: "New Zealand",
        code: "NZ",
        regionName: "Regional council",
        regions: [
            {
                name: "Auckland"
            },
            {
                name: "Bay of Plenty"
            },
            {
                name: "Canterbury"
            },
            {
                name: "Chatham Islands Territory"
            },
            {
                name: "Gisborne District"
            },
            {
                name: "Hawke's Bay"
            },
            {
                name: "Marlborough District"
            },
            {
                name: "Manawatu-Wanganui"
            },
            {
                name: "North Island"
            },
            {
                name: "Nelson City"
            },
            {
                name: "Northland"
            },
            {
                name: "Otago"
            },
            {
                name: "South Island"
            },
            {
                name: "Southland"
            },
            {
                name: "Tasman District"
            },
            {
                name: "Taranaki"
            },
            {
                name: "Wellington"
            },
            {
                name: "Waikato"
            },
            {
                name: "West Coast"
            }
        ],
        phoneCodes: [
            64
        ]
    },
    {
        name: "Nicaragua",
        code: "NI",
        regionName: "Department",
        regions: [
            {
                name: "Atlántico Norte"
            },
            {
                name: "Atlántico Sur"
            },
            {
                name: "Boaco"
            },
            {
                name: "Carazo"
            },
            {
                name: "Chinandega"
            },
            {
                name: "Chontales"
            },
            {
                name: "Estelí"
            },
            {
                name: "Granada"
            },
            {
                name: "Jinotega"
            },
            {
                name: "León"
            },
            {
                name: "Madriz"
            },
            {
                name: "Managua"
            },
            {
                name: "Masaya"
            },
            {
                name: "Matagalpa"
            },
            {
                name: "Nueva Segovia"
            },
            {
                name: "Rivas"
            },
            {
                name: "Río San Juan"
            }
        ],
        phoneCodes: [
            505
        ]
    },
    {
        name: "Niger",
        code: "NE",
        regionName: "Department",
        regions: [
            {
                name: "Agadez"
            },
            {
                name: "Diffa"
            },
            {
                name: "Dosso"
            },
            {
                name: "Maradi"
            },
            {
                name: "Tahoua"
            },
            {
                name: "Tillabéri"
            },
            {
                name: "Zinder"
            },
            {
                name: "Niamey"
            }
        ],
        phoneCodes: [
            227
        ]
    },
    {
        name: "Nigeria",
        code: "NG",
        regionName: "State",
        regions: [
            {
                name: "Abia"
            },
            {
                name: "Adamawa"
            },
            {
                name: "Akwa Ibom"
            },
            {
                name: "Anambra"
            },
            {
                name: "Bauchi"
            },
            {
                name: "Benue"
            },
            {
                name: "Borno"
            },
            {
                name: "Bayelsa"
            },
            {
                name: "Cross River"
            },
            {
                name: "Delta"
            },
            {
                name: "Ebonyi"
            },
            {
                name: "Edo"
            },
            {
                name: "Ekiti"
            },
            {
                name: "Enugu"
            },
            {
                name: "Abuja Capital Territory"
            },
            {
                name: "Gombe"
            },
            {
                name: "Imo"
            },
            {
                name: "Jigawa"
            },
            {
                name: "Kaduna"
            },
            {
                name: "Kebbi"
            },
            {
                name: "Kano"
            },
            {
                name: "Kogi"
            },
            {
                name: "Katsina"
            },
            {
                name: "Kwara"
            },
            {
                name: "Lagos"
            },
            {
                name: "Nassarawa"
            },
            {
                name: "Niger"
            },
            {
                name: "Ogun"
            },
            {
                name: "Ondo"
            },
            {
                name: "Osun"
            },
            {
                name: "Oyo"
            },
            {
                name: "Plateau"
            },
            {
                name: "Rivers"
            },
            {
                name: "Sokoto"
            },
            {
                name: "Taraba"
            },
            {
                name: "Yobe"
            },
            {
                name: "Zamfara"
            }
        ],
        phoneCodes: [
            234
        ]
    },
    {
        name: "Niue",
        code: "NU",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            683
        ]
    },
    {
        name: "Norfolk Island",
        code: "NF",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            672
        ]
    },
    {
        name: "Korea (Democratic People's Republic of)",
        code: "KP",
        regionName: "Province",
        regions: [
            {
                name: "P’yŏngyang"
            },
            {
                name: "P’yŏngan-namdo"
            },
            {
                name: "P’yŏngan-bukto"
            },
            {
                name: "Chagang-do"
            },
            {
                name: "Hwanghae-namdo"
            },
            {
                name: "Hwanghae-bukto"
            },
            {
                name: "Kangwŏn-do"
            },
            {
                name: "Hamgyŏng-namdo"
            },
            {
                name: "Hamgyŏng-bukto"
            },
            {
                name: "Yanggang-do"
            },
            {
                name: "Nasŏn (Najin-Sŏnbong)"
            }
        ],
        phoneCodes: [
            850
        ]
    },
    {
        name: "Northern Mariana Islands",
        code: "MP",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            1
        ]
    },
    {
        name: "Norway",
        code: "NO",
        regionName: "County",
        regions: [
            {
                name: "Østfold"
            },
            {
                name: "Akershus"
            },
            {
                name: "Oslo"
            },
            {
                name: "Hedmark"
            },
            {
                name: "Oppland"
            },
            {
                name: "Buskerud"
            },
            {
                name: "Vestfold"
            },
            {
                name: "Telemark"
            },
            {
                name: "Aust-Agder"
            },
            {
                name: "Vest-Agder"
            },
            {
                name: "Rogaland"
            },
            {
                name: "Hordaland"
            },
            {
                name: "Sogn og Fjordane"
            },
            {
                name: "Møre og Romsdal"
            },
            {
                name: "Nordland"
            },
            {
                name: "Troms"
            },
            {
                name: "Finnmark"
            },
            {
                name: "Svalbard (Arctic Region)"
            },
            {
                name: "Jan Mayen (Arctic Region)"
            },
            {
                name: "Trøndelag"
            }
        ],
        phoneCodes: [
            47
        ]
    },
    {
        name: "Oman",
        code: "OM",
        regionName: "Region",
        regions: [
            {
                name: "Al Bāţinah"
            },
            {
                name: "Al Buraymī"
            },
            {
                name: "Ad Dākhilīya"
            },
            {
                name: "Masqaţ"
            },
            {
                name: "Musandam"
            },
            {
                name: "Ash Sharqīyah"
            },
            {
                name: "Al Wusţá"
            },
            {
                name: "Az̧ Z̧āhirah"
            },
            {
                name: "Z̧ufār"
            }
        ],
        phoneCodes: [
            968
        ]
    },
    {
        name: "Pakistan",
        code: "PK",
        regionName: "Province",
        regions: [
            {
                name: "Balochistan"
            },
            {
                name: "Gilgit-Baltistan"
            },
            {
                name: "Islamabad"
            },
            {
                name: "Azad Kashmir"
            },
            {
                name: "Khyber Pakhtunkhwa"
            },
            {
                name: "Punjab"
            },
            {
                name: "Sindh"
            },
            {
                name: "Federally Administered Tribal Areas"
            }
        ],
        phoneCodes: [
            92
        ]
    },
    {
        name: "Palau",
        code: "PW",
        regionName: "State",
        regions: [
            {
                name: "Aimeliik"
            },
            {
                name: "Airai"
            },
            {
                name: "Angaur"
            },
            {
                name: "Hatobohei"
            },
            {
                name: "Kayangel"
            },
            {
                name: "Koror"
            },
            {
                name: "Melekeok"
            },
            {
                name: "Ngaraard"
            },
            {
                name: "Ngarchelong"
            },
            {
                name: "Ngardmau"
            },
            {
                name: "Ngatpang"
            },
            {
                name: "Ngchesar"
            },
            {
                name: "Ngeremlengui"
            },
            {
                name: "Ngiwal"
            },
            {
                name: "Peleliu"
            },
            {
                name: "Sonsorol"
            }
        ],
        phoneCodes: [
            680
        ]
    },
    {
        name: "Palestine, State of",
        code: "PS",
        regionName: "Governorate",
        regions: [
            {
                name: "Bethlehem"
            },
            {
                name: "Deir El Balah"
            },
            {
                name: "Gaza"
            },
            {
                name: "Hebron"
            },
            {
                name: "Jerusalem"
            },
            {
                name: "Jenin"
            },
            {
                name: "Jericho - Al Aghwar"
            },
            {
                name: "Khan Yunis"
            },
            {
                name: "Nablus"
            },
            {
                name: "North Gaza"
            },
            {
                name: "Qalqilya"
            },
            {
                name: "Ramallah"
            },
            {
                name: "Rafah"
            },
            {
                name: "Salfit"
            },
            {
                name: "Tubas"
            },
            {
                name: "Tulkarm"
            }
        ],
        phoneCodes: [
            970
        ]
    },
    {
        name: "Panama",
        code: "PA",
        regionName: "Province",
        regions: [
            {
                name: "Bocas del Toro"
            },
            {
                name: "Coclé"
            },
            {
                name: "Colón"
            },
            {
                name: "Chiriquí"
            },
            {
                name: "Darién"
            },
            {
                name: "Herrera"
            },
            {
                name: "Los Santos"
            },
            {
                name: "Panamá"
            },
            {
                name: "Veraguas"
            },
            {
                name: "Emberá"
            },
            {
                name: "Kuna Yala"
            },
            {
                name: "Ngöbe-Buglé"
            }
        ],
        phoneCodes: [
            507
        ]
    },
    {
        name: "Papua New Guinea",
        code: "PG",
        regionName: "Province",
        regions: [
            {
                name: "Chimbu"
            },
            {
                name: "Central"
            },
            {
                name: "East New Britain"
            },
            {
                name: "Eastern Highlands"
            },
            {
                name: "Enga"
            },
            {
                name: "East Sepik"
            },
            {
                name: "Gulf"
            },
            {
                name: "Milne Bay"
            },
            {
                name: "Morobe"
            },
            {
                name: "Madang"
            },
            {
                name: "Manus"
            },
            {
                name: "National Capital District (Port Moresby)"
            },
            {
                name: "New Ireland"
            },
            {
                name: "Northern"
            },
            {
                name: "Bougainville"
            },
            {
                name: "Sandaun"
            },
            {
                name: "Southern Highlands"
            },
            {
                name: "West New Britain"
            },
            {
                name: "Western Highlands"
            },
            {
                name: "Western"
            }
        ],
        phoneCodes: [
            675
        ]
    },
    {
        name: "Paraguay",
        code: "PY",
        regionName: "Department",
        regions: [
            {
                name: "Concepción"
            },
            {
                name: "Alto Paraná"
            },
            {
                name: "Central"
            },
            {
                name: "Ñeembucú"
            },
            {
                name: "Amambay"
            },
            {
                name: "Canindeyú"
            },
            {
                name: "Presidente Hayes"
            },
            {
                name: "Alto Paraguay"
            },
            {
                name: "Boquerón"
            },
            {
                name: "San Pedro"
            },
            {
                name: "Cordillera"
            },
            {
                name: "Guairá"
            },
            {
                name: "Caaguazú"
            },
            {
                name: "Caazapá"
            },
            {
                name: "Itapúa"
            },
            {
                name: "Misiones"
            },
            {
                name: "Paraguarí"
            },
            {
                name: "Asunción"
            }
        ],
        phoneCodes: [
            595
        ]
    },
    {
        name: "Peru",
        code: "PE",
        regionName: "Region",
        regions: [
            {
                name: "Amazonas"
            },
            {
                name: "Ancash"
            },
            {
                name: "Apurímac"
            },
            {
                name: "Arequipa"
            },
            {
                name: "Ayacucho"
            },
            {
                name: "Cajamarca"
            },
            {
                name: "El Callao"
            },
            {
                name: "Cusco [Cuzco]"
            },
            {
                name: "Huánuco"
            },
            {
                name: "Huancavelica"
            },
            {
                name: "Ica"
            },
            {
                name: "Junín"
            },
            {
                name: "La Libertad"
            },
            {
                name: "Lambayeque"
            },
            {
                name: "Lima"
            },
            {
                name: "Municipalidad Metropolitana de Lima"
            },
            {
                name: "Loreto"
            },
            {
                name: "Madre de Dios"
            },
            {
                name: "Moquegua"
            },
            {
                name: "Pasco"
            },
            {
                name: "Piura"
            },
            {
                name: "Puno"
            },
            {
                name: "San Martín"
            },
            {
                name: "Tacna"
            },
            {
                name: "Tumbes"
            },
            {
                name: "Ucayali"
            }
        ],
        phoneCodes: [
            51
        ]
    },
    {
        name: "Philippines",
        code: "PH",
        regionName: "Province",
        regions: [
            {
                name: "National Capital Region"
            },
            {
                name: "Ilocos (Region I)"
            },
            {
                name: "Cagayan Valley (Region II)"
            },
            {
                name: "Central Luzon (Region III)"
            },
            {
                name: "Bicol (Region V)"
            },
            {
                name: "Western Visayas (Region VI)"
            },
            {
                name: "Central Visayas (Region VII)"
            },
            {
                name: "Eastern Visayas (Region VIII)"
            },
            {
                name: "Zamboanga Peninsula (Region IX)"
            },
            {
                name: "Northern Mindanao (Region X)"
            },
            {
                name: "Davao (Region XI)"
            },
            {
                name: "Soccsksargen (Region XII)"
            },
            {
                name: "Caraga (Region XIII)"
            },
            {
                name: "Autonomous Region in Muslim Mindanao (ARMM)"
            },
            {
                name: "Cordillera Administrative Region (CAR)"
            },
            {
                name: "CALABARZON (Region IV-A)"
            },
            {
                name: "MIMAROPA (Region IV-B)"
            },
            {
                name: "Abra"
            },
            {
                name: "Agusan del Norte"
            },
            {
                name: "Agusan del Sur"
            },
            {
                name: "Aklan"
            },
            {
                name: "Albay"
            },
            {
                name: "Antique"
            },
            {
                name: "Apayao"
            },
            {
                name: "Aurora"
            },
            {
                name: "Batasn"
            },
            {
                name: "Basilan"
            },
            {
                name: "Benguet"
            },
            {
                name: "Biliran"
            },
            {
                name: "Bohol"
            },
            {
                name: "Batangas"
            },
            {
                name: "Batanes"
            },
            {
                name: "Bukidnon"
            },
            {
                name: "Bulacan"
            },
            {
                name: "Cagayan"
            },
            {
                name: "Camiguin"
            },
            {
                name: "Camarines Norte"
            },
            {
                name: "Capiz"
            },
            {
                name: "Camarines Sur"
            },
            {
                name: "Catanduanes"
            },
            {
                name: "Cavite"
            },
            {
                name: "Cebu"
            },
            {
                name: "Compostela Valley"
            },
            {
                name: "Davao Oriental"
            },
            {
                name: "Davao del Sur"
            },
            {
                name: "Davao del Norte"
            },
            {
                name: "Dinagat Islands"
            },
            {
                name: "Eastern Samar"
            },
            {
                name: "Guimaras"
            },
            {
                name: "Ifugao"
            },
            {
                name: "Iloilo"
            },
            {
                name: "Ilocos Norte"
            },
            {
                name: "Ilocos Sur"
            },
            {
                name: "Isabela"
            },
            {
                name: "Kalinga-Apayso"
            },
            {
                name: "Laguna"
            },
            {
                name: "Lanao del Norte"
            },
            {
                name: "Lanao del Sur"
            },
            {
                name: "Leyte"
            },
            {
                name: "La Union"
            },
            {
                name: "Marinduque"
            },
            {
                name: "Maguindanao"
            },
            {
                name: "Masbate"
            },
            {
                name: "Mindoro Occidental"
            },
            {
                name: "Mindoro Oriental"
            },
            {
                name: "Mountain Province"
            },
            {
                name: "Misamis Occidental"
            },
            {
                name: "Misamis Oriental"
            },
            {
                name: "North Cotabato"
            },
            {
                name: "Negros Occidental"
            },
            {
                name: "Negros Oriental"
            },
            {
                name: "Northern Samar"
            },
            {
                name: "Nueva Ecija"
            },
            {
                name: "Nueva Vizcaya"
            },
            {
                name: "Pampanga"
            },
            {
                name: "Pangasinan"
            },
            {
                name: "Palawan"
            },
            {
                name: "Quezon"
            },
            {
                name: "Quirino"
            },
            {
                name: "Rizal"
            },
            {
                name: "Romblon"
            },
            {
                name: "Sarangani"
            },
            {
                name: "South Cotabato"
            },
            {
                name: "Siquijor"
            },
            {
                name: "Southern Leyte"
            },
            {
                name: "Sulu"
            },
            {
                name: "Sorsogon"
            },
            {
                name: "Sultan Kudarat"
            },
            {
                name: "Surigao del Norte"
            },
            {
                name: "Surigao del Sur"
            },
            {
                name: "Tarlac"
            },
            {
                name: "Tawi-Tawi"
            },
            {
                name: "Western Samar"
            },
            {
                name: "Zamboanga del Norte"
            },
            {
                name: "Zamboanga del Sur"
            },
            {
                name: "Zambales"
            },
            {
                name: "Zamboanga Sibugay"
            }
        ],
        phoneCodes: [
            63
        ]
    },
    {
        name: "Pitcairn",
        code: "PN",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            64
        ]
    },
    {
        name: "Poland",
        code: "PL",
        regionName: "Province",
        regions: [
            {
                name: "Dolnośląskie"
            },
            {
                name: "Kujawsko-pomorskie"
            },
            {
                name: "Lubuskie"
            },
            {
                name: "Łódzkie"
            },
            {
                name: "Lubelskie"
            },
            {
                name: "Małopolskie"
            },
            {
                name: "Mazowieckie"
            },
            {
                name: "Opolskie"
            },
            {
                name: "Podlaskie"
            },
            {
                name: "Podkarpackie"
            },
            {
                name: "Pomorskie"
            },
            {
                name: "Świętokrzyskie"
            },
            {
                name: "Śląskie"
            },
            {
                name: "Warmińsko-mazurskie"
            },
            {
                name: "Wielkopolskie"
            },
            {
                name: "Zachodniopomorskie"
            }
        ],
        phoneCodes: [
            48
        ]
    },
    {
        name: "Portugal",
        code: "PT",
        regionName: "District",
        regions: [
            {
                name: "Aveiro"
            },
            {
                name: "Beja"
            },
            {
                name: "Braga"
            },
            {
                name: "Bragança"
            },
            {
                name: "Castelo Branco"
            },
            {
                name: "Coimbra"
            },
            {
                name: "Évora"
            },
            {
                name: "Faro"
            },
            {
                name: "Guarda"
            },
            {
                name: "Leiria"
            },
            {
                name: "Lisboa"
            },
            {
                name: "Portalegre"
            },
            {
                name: "Porto"
            },
            {
                name: "Santarém"
            },
            {
                name: "Setúbal"
            },
            {
                name: "Viana do Castelo"
            },
            {
                name: "Vila Real"
            },
            {
                name: "Viseu"
            },
            {
                name: "Região Autónoma dos Açores"
            },
            {
                name: "Região Autónoma da Madeira"
            }
        ],
        phoneCodes: [
            351
        ]
    },
    {
        name: "Puerto Rico",
        code: "PR",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            1
        ]
    },
    {
        name: "Qatar",
        code: "QA",
        regionName: "Municipality",
        regions: [
            {
                name: "Ad Dawhah"
            },
            {
                name: "Al Khawr wa adh Dhakhīrah"
            },
            {
                name: "Ash Shamal"
            },
            {
                name: "Ar Rayyan"
            },
            {
                name: "Umm Salal"
            },
            {
                name: "Al Wakrah"
            },
            {
                name: "Az̧ Z̧a‘āyin"
            }
        ],
        phoneCodes: [
            974
        ]
    },
    {
        name: "Republic of Kosovo",
        code: "XK",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            383
        ]
    },
    {
        name: "Réunion",
        code: "RE",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            262
        ]
    },
    {
        name: "Romania",
        code: "RO",
        regionName: "Department",
        regions: [
            {
                name: "Alba"
            },
            {
                name: "Argeș"
            },
            {
                name: "Arad"
            },
            {
                name: "București"
            },
            {
                name: "Bacău"
            },
            {
                name: "Bihor"
            },
            {
                name: "Bistrița-Năsăud"
            },
            {
                name: "Brăila"
            },
            {
                name: "Botoșani"
            },
            {
                name: "Brașov"
            },
            {
                name: "Buzău"
            },
            {
                name: "Cluj"
            },
            {
                name: "Călărași"
            },
            {
                name: "Caraș-Severin"
            },
            {
                name: "Constanța"
            },
            {
                name: "Covasna"
            },
            {
                name: "Dâmbovița"
            },
            {
                name: "Dolj"
            },
            {
                name: "Gorj"
            },
            {
                name: "Galați"
            },
            {
                name: "Giurgiu"
            },
            {
                name: "Hunedoara"
            },
            {
                name: "Harghita"
            },
            {
                name: "Ilfov"
            },
            {
                name: "Ialomița"
            },
            {
                name: "Iași"
            },
            {
                name: "Mehedinți"
            },
            {
                name: "Maramureș"
            },
            {
                name: "Mureș"
            },
            {
                name: "Neamț"
            },
            {
                name: "Olt"
            },
            {
                name: "Prahova"
            },
            {
                name: "Sibiu"
            },
            {
                name: "Sălaj"
            },
            {
                name: "Satu Mare"
            },
            {
                name: "Suceava"
            },
            {
                name: "Tulcea"
            },
            {
                name: "Timiș"
            },
            {
                name: "Teleorman"
            },
            {
                name: "Vâlcea"
            },
            {
                name: "Vrancea"
            },
            {
                name: "Vaslui"
            }
        ],
        phoneCodes: [
            40
        ]
    },
    {
        name: "Russian Federation",
        code: "RU",
        regionName: "Administrative Region",
        regions: [
            {
                name: "Adygeya, Respublika"
            },
            {
                name: "Altay, Respublika"
            },
            {
                name: "Altayskiy kray"
            },
            {
                name: "Amurskaya oblast'"
            },
            {
                name: "Arkhangel'skaya oblast'"
            },
            {
                name: "Astrakhanskaya oblast'"
            },
            {
                name: "Bashkortostan, Respublika"
            },
            {
                name: "Belgorodskaya oblast'"
            },
            {
                name: "Bryanskaya oblast'"
            },
            {
                name: "Buryatiya, Respublika"
            },
            {
                name: "Chechenskaya Respublika"
            },
            {
                name: "Chelyabinskaya oblast'"
            },
            {
                name: "Chukotskiy avtonomnyy okrug"
            },
            {
                name: "Chuvashskaya Respublika"
            },
            {
                name: "Dagestan, Respublika"
            },
            {
                name: "Respublika Ingushetiya"
            },
            {
                name: "Irkutiskaya oblast'"
            },
            {
                name: "Ivanovskaya oblast'"
            },
            {
                name: "Kamchatskiy kray"
            },
            {
                name: "Kabardino-Balkarskaya Respublika"
            },
            {
                name: "Karachayevo-Cherkesskaya Respublika"
            },
            {
                name: "Krasnodarskiy kray"
            },
            {
                name: "Kemerovskaya oblast'"
            },
            {
                name: "Kaliningradskaya oblast'"
            },
            {
                name: "Kurganskaya oblast'"
            },
            {
                name: "Khabarovskiy kray"
            },
            {
                name: "Khanty-Mansiysky avtonomnyy okrug-Yugra"
            },
            {
                name: "Kirovskaya oblast'"
            },
            {
                name: "Khakasiya, Respublika"
            },
            {
                name: "Kalmykiya, Respublika"
            },
            {
                name: "Kaluzhskaya oblast'"
            },
            {
                name: "Komi, Respublika"
            },
            {
                name: "Kostromskaya oblast'"
            },
            {
                name: "Kareliya, Respublika"
            },
            {
                name: "Kurskaya oblast'"
            },
            {
                name: "Krasnoyarskiy kray"
            },
            {
                name: "Leningradskaya oblast'"
            },
            {
                name: "Lipetskaya oblast'"
            },
            {
                name: "Magadanskaya oblast'"
            },
            {
                name: "Mariy El, Respublika"
            },
            {
                name: "Mordoviya, Respublika"
            },
            {
                name: "Moskovskaya oblast'"
            },
            {
                name: "Moskva"
            },
            {
                name: "Murmanskaya oblast'"
            },
            {
                name: "Nenetskiy avtonomnyy okrug"
            },
            {
                name: "Novgorodskaya oblast'"
            },
            {
                name: "Nizhegorodskaya oblast'"
            },
            {
                name: "Novosibirskaya oblast'"
            },
            {
                name: "Omskaya oblast'"
            },
            {
                name: "Orenburgskaya oblast'"
            },
            {
                name: "Orlovskaya oblast'"
            },
            {
                name: "Permskiy kray"
            },
            {
                name: "Penzenskaya oblast'"
            },
            {
                name: "Primorskiy kray"
            },
            {
                name: "Pskovskaya oblast'"
            },
            {
                name: "Rostovskaya oblast'"
            },
            {
                name: "Ryazanskaya oblast'"
            },
            {
                name: "Sakha, Respublika [Yakutiya]"
            },
            {
                name: "Sakhalinskaya oblast'"
            },
            {
                name: "Samaraskaya oblast'"
            },
            {
                name: "Saratovskaya oblast'"
            },
            {
                name: "Severnaya Osetiya-Alaniya, Respublika"
            },
            {
                name: "Smolenskaya oblast'"
            },
            {
                name: "Sankt-Peterburg"
            },
            {
                name: "Stavropol'skiy kray"
            },
            {
                name: "Sverdlovskaya oblast'"
            },
            {
                name: "Tatarstan, Respublika"
            },
            {
                name: "Tambovskaya oblast'"
            },
            {
                name: "Tomskaya oblast'"
            },
            {
                name: "Tul'skaya oblast'"
            },
            {
                name: "Tverskaya oblast'"
            },
            {
                name: "Tyva, Respublika [Tuva]"
            },
            {
                name: "Tyumenskaya oblast'"
            },
            {
                name: "Udmurtskaya Respublika"
            },
            {
                name: "Ul'yanovskaya oblast'"
            },
            {
                name: "Volgogradskaya oblast'"
            },
            {
                name: "Vladimirskaya oblast'"
            },
            {
                name: "Vologodskaya oblast'"
            },
            {
                name: "Voronezhskaya oblast'"
            },
            {
                name: "Yamalo-Nenetskiy avtonomnyy okrug"
            },
            {
                name: "Yaroslavskaya oblast'"
            },
            {
                name: "Yevreyskaya avtonomnaya oblast'"
            },
            {
                name: "Zabajkal'skij kraj"
            }
        ],
        phoneCodes: [
            7
        ]
    },
    {
        name: "Rwanda",
        code: "RW",
        regionName: "Province",
        regions: [
            {
                name: "Ville de Kigali"
            },
            {
                name: "Est"
            },
            {
                name: "Nord"
            },
            {
                name: "Ouest"
            },
            {
                name: "Sud"
            }
        ],
        phoneCodes: [
            250
        ]
    },
    {
        name: "Saint Barthélemy",
        code: "BL",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            590
        ]
    },
    {
        name: "Saint Helena, Ascension and Tristan da Cunha",
        code: "SH",
        regionName: "Geographical Entity",
        regions: [
            {
                name: "Ascension"
            },
            {
                name: "Saint Helena"
            },
            {
                name: "Tristan da Cunha"
            }
        ],
        phoneCodes: [
            290
        ]
    },
    {
        name: "Saint Kitts and Nevis",
        code: "KN",
        regionName: "Parish",
        regions: [
            {
                name: "Christ Church Nichola Town"
            },
            {
                name: "Saint Anne Sandy Point"
            },
            {
                name: "Saint George Basseterre"
            },
            {
                name: "Saint George Gingerland"
            },
            {
                name: "Saint James Windward"
            },
            {
                name: "Saint John Capisterre"
            },
            {
                name: "Saint John Figtree"
            },
            {
                name: "Saint Mary Cayon"
            },
            {
                name: "Saint Paul Capisterre"
            },
            {
                name: "Saint Paul Charlestown"
            },
            {
                name: "Saint Peter Basseterre"
            },
            {
                name: "Saint Thomas Lowland"
            },
            {
                name: "Saint Thomas Middle Island"
            },
            {
                name: "Trinity Palmetto Point"
            },
            {
                name: "Saint Kitts"
            },
            {
                name: "Nevis"
            }
        ],
        phoneCodes: [
            1
        ]
    },
    {
        name: "Saint Lucia",
        code: "LC",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            1
        ]
    },
    {
        name: "Saint Martin (French part)",
        code: "MF",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            590
        ]
    },
    {
        name: "Saint Pierre and Miquelon",
        code: "PM",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            508
        ]
    },
    {
        name: "Saint Vincent and the Grenadines",
        code: "VC",
        regionName: "Parish",
        regions: [
            {
                name: "Charlotte"
            },
            {
                name: "Saint Andrew"
            },
            {
                name: "Saint David"
            },
            {
                name: "Saint George"
            },
            {
                name: "Saint Patrick"
            },
            {
                name: "Grenadines"
            }
        ],
        phoneCodes: [
            1
        ]
    },
    {
        name: "Samoa",
        code: "WS",
        regionName: "District",
        regions: [
            {
                name: "A'ana"
            },
            {
                name: "Aiga-i-le-Tai"
            },
            {
                name: "Atua"
            },
            {
                name: "Fa'asaleleaga"
            },
            {
                name: "Gaga'emauga"
            },
            {
                name: "Gagaifomauga"
            },
            {
                name: "Palauli"
            },
            {
                name: "Satupa'itea"
            },
            {
                name: "Tuamasaga"
            },
            {
                name: "Va'a-o-Fonoti"
            },
            {
                name: "Vaisigano"
            }
        ],
        phoneCodes: [
            685
        ]
    },
    {
        name: "San Marino",
        code: "SM",
        regionName: "Municipalities",
        regions: [
            {
                name: "Acquaviva"
            },
            {
                name: "Chiesanuova"
            },
            {
                name: "Domagnano"
            },
            {
                name: "Faetano"
            },
            {
                name: "Fiorentino"
            },
            {
                name: "Borgo Maggiore"
            },
            {
                name: "San Marino"
            },
            {
                name: "Montegiardino"
            },
            {
                name: "Serravalle"
            }
        ],
        phoneCodes: [
            378
        ]
    },
    {
        name: "Sao Tome and Principe",
        code: "ST",
        regionName: "Municipality",
        regions: [
            {
                name: "Príncipe"
            },
            {
                name: "São Tomé"
            }
        ],
        phoneCodes: [
            239
        ]
    },
    {
        name: "Saudi Arabia",
        code: "SA",
        regionName: "Province",
        regions: [
            {
                name: "Ar Riyāḍ"
            },
            {
                name: "Makkah"
            },
            {
                name: "Al Madīnah"
            },
            {
                name: "Ash Sharqīyah"
            },
            {
                name: "Al Qaşīm"
            },
            {
                name: "Ḥā'il"
            },
            {
                name: "Tabūk"
            },
            {
                name: "Al Ḥudūd ash Shamāliyah"
            },
            {
                name: "Jīzan"
            },
            {
                name: "Najrān"
            },
            {
                name: "Al Bāhah"
            },
            {
                name: "Al Jawf"
            },
            {
                name: "`Asīr"
            }
        ],
        phoneCodes: [
            966
        ]
    },
    {
        name: "Senegal",
        code: "SN",
        regionName: "Region",
        regions: [
            {
                name: "Diourbel"
            },
            {
                name: "Dakar"
            },
            {
                name: "Fatick"
            },
            {
                name: "Kaffrine"
            },
            {
                name: "Kolda"
            },
            {
                name: "Kédougou"
            },
            {
                name: "Kaolack"
            },
            {
                name: "Louga"
            },
            {
                name: "Matam"
            },
            {
                name: "Sédhiou"
            },
            {
                name: "Saint-Louis"
            },
            {
                name: "Tambacounda"
            },
            {
                name: "Thiès"
            },
            {
                name: "Ziguinchor"
            }
        ],
        phoneCodes: [
            221
        ]
    },
    {
        name: "Serbia",
        code: "RS",
        regionName: "District",
        regions: [
            {
                name: "Beograd"
            },
            {
                name: "Severnobački okrug"
            },
            {
                name: "Srednjebanatski okrug"
            },
            {
                name: "Severnobanatski okrug"
            },
            {
                name: "Južnobanatski okrug"
            },
            {
                name: "Zapadnobački okrug"
            },
            {
                name: "Južnobački okrug"
            },
            {
                name: "Sremski okrug"
            },
            {
                name: "Mačvanski okrug"
            },
            {
                name: "Kolubarski okrug"
            },
            {
                name: "Podunavski okrug"
            },
            {
                name: "Braničevski okrug"
            },
            {
                name: "Šumadijski okrug"
            },
            {
                name: "Pomoravski okrug"
            },
            {
                name: "Borski okrug"
            },
            {
                name: "Zaječarski okrug"
            },
            {
                name: "Zlatiborski okrug"
            },
            {
                name: "Moravički okrug"
            },
            {
                name: "Raški okrug"
            },
            {
                name: "Rasinski okrug"
            },
            {
                name: "Nišavski okrug"
            },
            {
                name: "Toplički okrug"
            },
            {
                name: "Pirotski okrug"
            },
            {
                name: "Jablanički okrug"
            },
            {
                name: "Pčinjski okrug"
            },
            {
                name: "Kosovski okrug"
            },
            {
                name: "Pećki okrug"
            },
            {
                name: "Prizrenski okrug"
            },
            {
                name: "Kosovsko-Mitrovački okrug"
            },
            {
                name: "Kosovsko-Pomoravski okrug"
            },
            {
                name: "Kosovo-Metohija"
            },
            {
                name: "Vojvodina"
            }
        ],
        phoneCodes: [
            381
        ]
    },
    {
        name: "Seychelles",
        code: "SC",
        regionName: "District",
        regions: [
            {
                name: "Anse aux Pins"
            },
            {
                name: "Anse Boileau"
            },
            {
                name: "Anse Etoile"
            },
            {
                name: "Anse Louis"
            },
            {
                name: "Anse Royale"
            },
            {
                name: "Baie Lazare"
            },
            {
                name: "Baie Sainte Anne"
            },
            {
                name: "Beau Vallon"
            },
            {
                name: "Bel Air"
            },
            {
                name: "Bel Ombre"
            },
            {
                name: "Cascade"
            },
            {
                name: "Glacis"
            },
            {
                name: "Grand Anse Mahe"
            },
            {
                name: "Grand Anse Praslin"
            },
            {
                name: "La Digue"
            },
            {
                name: "English River"
            },
            {
                name: "Mont Buxton"
            },
            {
                name: "Mont Fleuri"
            },
            {
                name: "Plaisance"
            },
            {
                name: "Pointe Larue"
            },
            {
                name: "Port Glaud"
            },
            {
                name: "Saint Louis"
            },
            {
                name: "Takamaka"
            },
            {
                name: "Les Mamelles"
            },
            {
                name: "Roche Caiman"
            }
        ],
        phoneCodes: [
            248
        ]
    },
    {
        name: "Sierra Leone",
        code: "SL",
        regionName: "Province",
        regions: [
            {
                name: "Eastern"
            },
            {
                name: "Northern"
            },
            {
                name: "Southern (Sierra Leone)"
            },
            {
                name: "Western Area (Freetown)"
            }
        ],
        phoneCodes: [
            232
        ]
    },
    {
        name: "Singapore",
        code: "SG",
        regionName: "district",
        regions: [
            {
                name: "Central Singapore"
            },
            {
                name: "North East"
            },
            {
                name: "North West"
            },
            {
                name: "South East"
            },
            {
                name: "South West"
            }
        ],
        phoneCodes: [
            65
        ]
    },
    {
        name: "Sint Maarten (Dutch part)",
        code: "SX",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            1
        ]
    },
    {
        name: "Slovakia",
        code: "SK",
        regionName: "Region",
        regions: [
            {
                name: "Banskobystrický kraj"
            },
            {
                name: "Bratislavský kraj"
            },
            {
                name: "Košický kraj"
            },
            {
                name: "Nitriansky kraj"
            },
            {
                name: "Prešovský kraj"
            },
            {
                name: "Trnavský kraj"
            },
            {
                name: "Trenčiansky kraj"
            },
            {
                name: "Žilinský kraj"
            }
        ],
        phoneCodes: [
            421
        ]
    },
    {
        name: "Slovenia",
        code: "SI",
        regionName: "Municipality",
        regions: [
            {
                name: "Ajdovščina"
            },
            {
                name: "Beltinci"
            },
            {
                name: "Bled"
            },
            {
                name: "Bohinj"
            },
            {
                name: "Borovnica"
            },
            {
                name: "Bovec"
            },
            {
                name: "Brda"
            },
            {
                name: "Brezovica"
            },
            {
                name: "Brežice"
            },
            {
                name: "Tišina"
            },
            {
                name: "Celje"
            },
            {
                name: "Cerklje na Gorenjskem"
            },
            {
                name: "Cerknica"
            },
            {
                name: "Cerkno"
            },
            {
                name: "Črenšovci"
            },
            {
                name: "Črna na Koroškem"
            },
            {
                name: "Črnomelj"
            },
            {
                name: "Destrnik"
            },
            {
                name: "Divača"
            },
            {
                name: "Dobrepolje"
            },
            {
                name: "Dobrova-Polhov Gradec"
            },
            {
                name: "Dol pri Ljubljani"
            },
            {
                name: "Domžale"
            },
            {
                name: "Dornava"
            },
            {
                name: "Dravograd"
            },
            {
                name: "Duplek"
            },
            {
                name: "Gorenja vas-Poljane"
            },
            {
                name: "Gorišnica"
            },
            {
                name: "Gornja Radgona"
            },
            {
                name: "Gornji Grad"
            },
            {
                name: "Gornji Petrovci"
            },
            {
                name: "Grosuplje"
            },
            {
                name: "Šalovci"
            },
            {
                name: "Hrastnik"
            },
            {
                name: "Hrpelje-Kozina"
            },
            {
                name: "Idrija"
            },
            {
                name: "Ig"
            },
            {
                name: "Ilirska Bistrica"
            },
            {
                name: "Ivančna Gorica"
            },
            {
                name: "Izola/Isola"
            },
            {
                name: "Jesenice"
            },
            {
                name: "Juršinci"
            },
            {
                name: "Kamnik"
            },
            {
                name: "Kanal"
            },
            {
                name: "Kidričevo"
            },
            {
                name: "Kobarid"
            },
            {
                name: "Kobilje"
            },
            {
                name: "Kočevje"
            },
            {
                name: "Komen"
            },
            {
                name: "Koper/Capodistria"
            },
            {
                name: "Kozje"
            },
            {
                name: "Kranj"
            },
            {
                name: "Kranjska Gora"
            },
            {
                name: "Krško"
            },
            {
                name: "Kungota"
            },
            {
                name: "Kuzma"
            },
            {
                name: "Laško"
            },
            {
                name: "Lenart"
            },
            {
                name: "Lendava/Lendva"
            },
            {
                name: "Litija"
            },
            {
                name: "Ljubljana"
            },
            {
                name: "Ljubno"
            },
            {
                name: "Ljutomer"
            },
            {
                name: "Logatec"
            },
            {
                name: "Loška dolina"
            },
            {
                name: "Loški Potok"
            },
            {
                name: "Luče"
            },
            {
                name: "Lukovica"
            },
            {
                name: "Majšperk"
            },
            {
                name: "Maribor"
            },
            {
                name: "Medvode"
            },
            {
                name: "Mengeš"
            },
            {
                name: "Metlika"
            },
            {
                name: "Mežica"
            },
            {
                name: "Miren-Kostanjevica"
            },
            {
                name: "Mislinja"
            },
            {
                name: "Moravče"
            },
            {
                name: "Moravske Toplice"
            },
            {
                name: "Mozirje"
            },
            {
                name: "Murska Sobota"
            },
            {
                name: "Muta"
            },
            {
                name: "Naklo"
            },
            {
                name: "Nazarje"
            },
            {
                name: "Nova Gorica"
            },
            {
                name: "Novo mesto"
            },
            {
                name: "Odranci"
            },
            {
                name: "Ormož"
            },
            {
                name: "Osilnica"
            },
            {
                name: "Pesnica"
            },
            {
                name: "Piran/Pirano"
            },
            {
                name: "Pivka"
            },
            {
                name: "Podčetrtek"
            },
            {
                name: "Podvelka"
            },
            {
                name: "Postojna"
            },
            {
                name: "Preddvor"
            },
            {
                name: "Ptuj"
            },
            {
                name: "Puconci"
            },
            {
                name: "Rače-Fram"
            },
            {
                name: "Radeče"
            },
            {
                name: "Radenci"
            },
            {
                name: "Radlje ob Dravi"
            },
            {
                name: "Radovljica"
            },
            {
                name: "Ravne na Koroškem"
            },
            {
                name: "Ribnica"
            },
            {
                name: "Rogašovci"
            },
            {
                name: "Rogaška Slatina"
            },
            {
                name: "Rogatec"
            },
            {
                name: "Ruše"
            },
            {
                name: "Semič"
            },
            {
                name: "Sevnica"
            },
            {
                name: "Sežana"
            },
            {
                name: "Slovenj Gradec"
            },
            {
                name: "Slovenska Bistrica"
            },
            {
                name: "Slovenske Konjice"
            },
            {
                name: "Starče"
            },
            {
                name: "Sveti Jurij"
            },
            {
                name: "Šenčur"
            },
            {
                name: "Šentilj"
            },
            {
                name: "Šentjernej"
            },
            {
                name: "Šentjur"
            },
            {
                name: "Škocjan"
            },
            {
                name: "Škofja Loka"
            },
            {
                name: "Škofljica"
            },
            {
                name: "Šmarje pri Jelšah"
            },
            {
                name: "Šmartno ob Paki"
            },
            {
                name: "Šoštanj"
            },
            {
                name: "Štore"
            },
            {
                name: "Tolmin"
            },
            {
                name: "Trbovlje"
            },
            {
                name: "Trebnje"
            },
            {
                name: "Tržič"
            },
            {
                name: "Turnišče"
            },
            {
                name: "Velenje"
            },
            {
                name: "Velike Lašče"
            },
            {
                name: "Videm"
            },
            {
                name: "Vipava"
            },
            {
                name: "Vitanje"
            },
            {
                name: "Vodice"
            },
            {
                name: "Vojnik"
            },
            {
                name: "Vrhnika"
            },
            {
                name: "Vuzenica"
            },
            {
                name: "Zagorje ob Savi"
            },
            {
                name: "Zavrč"
            },
            {
                name: "Zreče"
            },
            {
                name: "Železniki"
            },
            {
                name: "Žiri"
            },
            {
                name: "Benedikt"
            },
            {
                name: "Bistrica ob Sotli"
            },
            {
                name: "Bloke"
            },
            {
                name: "Braslovče"
            },
            {
                name: "Cankova"
            },
            {
                name: "Cerkvenjak"
            },
            {
                name: "Dobje"
            },
            {
                name: "Dobrna"
            },
            {
                name: "Dobrovnik/Dobronak"
            },
            {
                name: "Dolenjske Toplice"
            },
            {
                name: "Grad"
            },
            {
                name: "Hajdina"
            },
            {
                name: "Hoče-Slivnica"
            },
            {
                name: "Hodoš/Hodos"
            },
            {
                name: "Horjul"
            },
            {
                name: "Jezersko"
            },
            {
                name: "Komenda"
            },
            {
                name: "Kostel"
            },
            {
                name: "Križevci"
            },
            {
                name: "Lovrenc na Pohorju"
            },
            {
                name: "Markovci"
            },
            {
                name: "Miklavž na Dravskem polju"
            },
            {
                name: "Mirna Peč"
            },
            {
                name: "Oplotnica"
            },
            {
                name: "Podlehnik"
            },
            {
                name: "Polzela"
            },
            {
                name: "Prebold"
            },
            {
                name: "Prevalje"
            },
            {
                name: "Razkrižje"
            },
            {
                name: "Ribnica na Pohorju"
            },
            {
                name: "Selnica ob Dravi"
            },
            {
                name: "Sodražica"
            },
            {
                name: "Solčava"
            },
            {
                name: "Sveta Ana"
            },
            {
                name: "Sveta Andraž v Slovenskih Goricah"
            },
            {
                name: "Šempeter-Vrtojba"
            },
            {
                name: "Tabor"
            },
            {
                name: "Trnovska vas"
            },
            {
                name: "Trzin"
            },
            {
                name: "Velika Polana"
            },
            {
                name: "Veržej"
            },
            {
                name: "Vransko"
            },
            {
                name: "Žalec"
            },
            {
                name: "Žetale"
            },
            {
                name: "Žirovnica"
            },
            {
                name: "Žužemberk"
            },
            {
                name: "Šmartno pri Litiji"
            },
            {
                name: "Apače"
            },
            {
                name: "Cirkulane"
            },
            {
                name: "Kosanjevica na Krki"
            },
            {
                name: "Makole"
            },
            {
                name: "Mokronog-Trebelno"
            },
            {
                name: "Poljčane"
            },
            {
                name: "Renče-Vogrsko"
            },
            {
                name: "Središče ob Dravi"
            },
            {
                name: "Straža"
            },
            {
                name: "Sveta Trojica v Slovenskih Goricah"
            },
            {
                name: "Sveti Tomaž"
            },
            {
                name: "Šmarjeske Topliče"
            },
            {
                name: "Gorje"
            },
            {
                name: "Log-Dragomer"
            },
            {
                name: "Rečica ob Savinji"
            },
            {
                name: "Sveti Jurij v Slovenskih Goricah"
            },
            {
                name: "Šentrupert"
            }
        ],
        phoneCodes: [
            386
        ]
    },
    {
        name: "Solomon Islands",
        code: "SB",
        regionName: "Province",
        regions: [
            {
                name: "Central"
            },
            {
                name: "Choiseul"
            },
            {
                name: "Capital Territory (Honiara)"
            },
            {
                name: "Guadalcanal"
            },
            {
                name: "Isabel"
            },
            {
                name: "Makira"
            },
            {
                name: "Malaita"
            },
            {
                name: "Rennell and Bellona"
            },
            {
                name: "Temotu"
            },
            {
                name: "Western"
            }
        ],
        phoneCodes: [
            677
        ]
    },
    {
        name: "Somalia",
        code: "SO",
        regionName: "Region",
        regions: [
            {
                name: "Awdal"
            },
            {
                name: "Bakool"
            },
            {
                name: "Banaadir"
            },
            {
                name: "Bari"
            },
            {
                name: "Bay"
            },
            {
                name: "Galguduud"
            },
            {
                name: "Gedo"
            },
            {
                name: "Hiirsan"
            },
            {
                name: "Jubbada Dhexe"
            },
            {
                name: "Jubbada Hoose"
            },
            {
                name: "Mudug"
            },
            {
                name: "Nugaal"
            },
            {
                name: "Saneag"
            },
            {
                name: "Shabeellaha Dhexe"
            },
            {
                name: "Shabeellaha Hoose"
            },
            {
                name: "Sool"
            },
            {
                name: "Togdheer"
            },
            {
                name: "Woqooyi Galbeed"
            }
        ],
        phoneCodes: [
            252
        ]
    },
    {
        name: "South Africa",
        code: "ZA",
        regionName: "Province",
        regions: [
            {
                name: "Eastern Cape"
            },
            {
                name: "Free State"
            },
            {
                name: "Gauteng"
            },
            {
                name: "Limpopo"
            },
            {
                name: "Mpumalanga"
            },
            {
                name: "Northern Cape"
            },
            {
                name: "Kwazulu-Natal"
            },
            {
                name: "North-West (South Africa)"
            },
            {
                name: "Western Cape"
            }
        ],
        phoneCodes: [
            27
        ]
    },
    {
        name: "South Georgia and the South Sandwich Islands",
        code: "GS",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            500
        ]
    },
    {
        name: "Korea (Republic of)",
        code: "KR",
        regionName: "Province",
        regions: [
            {
                name: "Seoul Teugbyeolsi"
            },
            {
                name: "Busan Gwang'yeogsi"
            },
            {
                name: "Daegu Gwang'yeogsi"
            },
            {
                name: "Incheon Gwang'yeogsi"
            },
            {
                name: "Gwangju Gwang'yeogsi"
            },
            {
                name: "Daejeon Gwang'yeogsi"
            },
            {
                name: "Ulsan Gwang'yeogsi"
            },
            {
                name: "Gyeonggido"
            },
            {
                name: "Gang'weondo"
            },
            {
                name: "Chungcheongbukdo"
            },
            {
                name: "Chungcheongnamdo"
            },
            {
                name: "Jeonrabukdo"
            },
            {
                name: "Jeonranamdo"
            },
            {
                name: "Gyeongsangbukdo"
            },
            {
                name: "Gyeongsangnamdo"
            },
            {
                name: "Jejudo"
            }
        ],
        phoneCodes: [
            82
        ]
    },
    {
        name: "South Sudan",
        code: "SS",
        regionName: "State",
        regions: [
            {
                name: "Northern Bahr el Ghazal"
            },
            {
                name: "Western Bahr el Ghazal"
            },
            {
                name: "Central Equatoria"
            },
            {
                name: "Eastern Equatoria"
            },
            {
                name: "Western Equatoria"
            },
            {
                name: "Jonglei"
            },
            {
                name: "Lakes"
            },
            {
                name: "Upper Nile"
            },
            {
                name: "Unity"
            },
            {
                name: "Warrap"
            }
        ],
        phoneCodes: [
            211
        ]
    },
    {
        name: "Spain",
        code: "ES",
        regionName: "Province",
        regions: [
            {
                name: "Alicante"
            },
            {
                name: "Albacete"
            },
            {
                name: "Almería"
            },
            {
                name: "Andalucía"
            },
            {
                name: "Aragón"
            },
            {
                name: "Asturias, Principado de"
            },
            {
                name: "Ávila"
            },
            {
                name: "Barcelona"
            },
            {
                name: "Badajoz"
            },
            {
                name: "Bizkaia"
            },
            {
                name: "Burgos"
            },
            {
                name: "A Coruña"
            },
            {
                name: "Cádiz"
            },
            {
                name: "Cantabria"
            },
            {
                name: "Cáceres"
            },
            {
                name: "Ceuta"
            },
            {
                name: "Castilla y León"
            },
            {
                name: "Castilla-La Mancha"
            },
            {
                name: "Canarias"
            },
            {
                name: "Córdoba"
            },
            {
                name: "Ciudad Real"
            },
            {
                name: "Castellón"
            },
            {
                name: "Catalunya"
            },
            {
                name: "Cuenca"
            },
            {
                name: "Extremadura"
            },
            {
                name: "Galicia"
            },
            {
                name: "Las Palmas"
            },
            {
                name: "Girona"
            },
            {
                name: "Granada"
            },
            {
                name: "Guadalajara"
            },
            {
                name: "Huelva"
            },
            {
                name: "Huesca"
            },
            {
                name: "Illes Balears"
            },
            {
                name: "Jaén"
            },
            {
                name: "Lleida"
            },
            {
                name: "León"
            },
            {
                name: "La Rioja"
            },
            {
                name: "Lugo"
            },
            {
                name: "Madrid"
            },
            {
                name: "Málaga"
            },
            {
                name: "Murcia, Región de"
            },
            {
                name: "Madrid, Comunidad de"
            },
            {
                name: "Melilla"
            },
            {
                name: "Murcia"
            },
            {
                name: "Navarra / Nafarroa"
            },
            {
                name: "Navarra, Comunidad Foral de / Nafarroako Foru Komunitatea"
            },
            {
                name: "Asturias"
            },
            {
                name: "Ourense"
            },
            {
                name: "Palencia"
            },
            {
                name: "Balears"
            },
            {
                name: "Pontevedra"
            },
            {
                name: "País Vasco / Euskal Herria"
            },
            {
                name: "La Rioja"
            },
            {
                name: "Cantabria"
            },
            {
                name: "Salamanca"
            },
            {
                name: "Sevilla"
            },
            {
                name: "Segovia"
            },
            {
                name: "Soria"
            },
            {
                name: "Gipuzkoa"
            },
            {
                name: "Tarragona"
            },
            {
                name: "Teruel"
            },
            {
                name: "Santa Cruz de Tenerife"
            },
            {
                name: "Toledo"
            },
            {
                name: "Valencia / València"
            },
            {
                name: "Valladolid"
            },
            {
                name: "Valenciana, Comunidad / Valenciana, Comunitat"
            },
            {
                name: "Álava"
            },
            {
                name: "Zaragoza"
            },
            {
                name: "Zamora"
            }
        ],
        phoneCodes: [
            34
        ]
    },
    {
        name: "Sri Lanka",
        code: "LK",
        regionName: "District",
        regions: [
            {
                name: "Basnāhira paḷāta"
            },
            {
                name: "Kŏḷamba"
            },
            {
                name: "Gampaha"
            },
            {
                name: "Kaḷutara"
            },
            {
                name: "Madhyama paḷāta"
            },
            {
                name: "Mahanuvara"
            },
            {
                name: "Mātale"
            },
            {
                name: "Nuvara Ĕliya"
            },
            {
                name: "Dakuṇu paḷāta"
            },
            {
                name: "Gālla"
            },
            {
                name: "Mātara"
            },
            {
                name: "Hambantŏṭa"
            },
            {
                name: "Uturu paḷāta"
            },
            {
                name: "Yāpanaya"
            },
            {
                name: "Kilinŏchchi"
            },
            {
                name: "Mannārama"
            },
            {
                name: "Vavuniyāva"
            },
            {
                name: "Mulativ"
            },
            {
                name: "Næ̆gĕnahira paḷāta"
            },
            {
                name: "Maḍakalapuva"
            },
            {
                name: "Ampāara"
            },
            {
                name: "Trikuṇāmalaya"
            },
            {
                name: "Vayamba paḷāta"
            },
            {
                name: "Kuruṇægala"
            },
            {
                name: "Puttalama"
            },
            {
                name: "Uturumæ̆da paḷāta"
            },
            {
                name: "Anurādhapura"
            },
            {
                name: "Pŏḷŏnnaruva"
            },
            {
                name: "Ūva paḷāta"
            },
            {
                name: "Badulla"
            },
            {
                name: "Mŏṇarāgala"
            },
            {
                name: "Sabaragamuva paḷāta"
            },
            {
                name: "Ratnapura"
            },
            {
                name: "Kægalla"
            }
        ],
        phoneCodes: [
            94
        ]
    },
    {
        name: "Sudan",
        code: "SD",
        regionName: "State",
        regions: [
            {
                name: "Zalingei"
            },
            {
                name: "Sharq Dārfūr"
            },
            {
                name: "Shamāl Dārfūr"
            },
            {
                name: "Janūb Dārfūr"
            },
            {
                name: "Gharb Dārfūr"
            },
            {
                name: "Al Qaḑārif"
            },
            {
                name: "Al Jazīrah"
            },
            {
                name: "Kassalā"
            },
            {
                name: "Al Kharţūm"
            },
            {
                name: "Shamāl Kurdufān"
            },
            {
                name: "Janūb Kurdufān"
            },
            {
                name: "An Nīl al Azraq"
            },
            {
                name: "Ash Shamālīyah"
            },
            {
                name: "An Nīl"
            },
            {
                name: "An Nīl al Abyaḑ"
            },
            {
                name: "Al Baḩr al Aḩmar"
            },
            {
                name: "Sinnār"
            }
        ],
        phoneCodes: [
            249
        ]
    },
    {
        name: "Suriname",
        code: "SR",
        regionName: "District",
        regions: [
            {
                name: "Brokopondo"
            },
            {
                name: "Commewijne"
            },
            {
                name: "Coronie"
            },
            {
                name: "Marowijne"
            },
            {
                name: "Nickerie"
            },
            {
                name: "Paramaribo"
            },
            {
                name: "Para"
            },
            {
                name: "Saramacca"
            },
            {
                name: "Sipaliwini"
            },
            {
                name: "Wanica"
            }
        ],
        phoneCodes: [
            597
        ]
    },
    {
        name: "Svalbard and Jan Mayen",
        code: "SJ",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            47
        ]
    },
    {
        name: "Swaziland",
        code: "SZ",
        regionName: "District",
        regions: [
            {
                name: "Hhohho"
            },
            {
                name: "Lubombo"
            },
            {
                name: "Manzini"
            },
            {
                name: "Shiselweni"
            }
        ],
        phoneCodes: [
            268
        ]
    },
    {
        name: "Sweden",
        code: "SE",
        regionName: "County",
        regions: [
            {
                name: "Stockholms län"
            },
            {
                name: "Västerbottens län"
            },
            {
                name: "Norrbottens län"
            },
            {
                name: "Uppsala län"
            },
            {
                name: "Södermanlands län"
            },
            {
                name: "Östergötlands län"
            },
            {
                name: "Jönköpings län"
            },
            {
                name: "Kronobergs län"
            },
            {
                name: "Kalmar län"
            },
            {
                name: "Gotlands län"
            },
            {
                name: "Blekinge län"
            },
            {
                name: "Skåne län"
            },
            {
                name: "Hallands län"
            },
            {
                name: "Västra Götalands län"
            },
            {
                name: "Värmlands län"
            },
            {
                name: "Örebro län"
            },
            {
                name: "Västmanlands län"
            },
            {
                name: "Dalarnas län"
            },
            {
                name: "Gävleborgs län"
            },
            {
                name: "Västernorrlands län"
            },
            {
                name: "Jämtlands län"
            }
        ],
        phoneCodes: [
            46
        ]
    },
    {
        name: "Switzerland",
        code: "CH",
        regionName: "Canton",
        regions: [
            {
                name: "Aargau"
            },
            {
                name: "Appenzell Innerrhoden"
            },
            {
                name: "Appenzell Ausserrhoden"
            },
            {
                name: "Bern"
            },
            {
                name: "Basel-Landschaft"
            },
            {
                name: "Basel-Stadt"
            },
            {
                name: "Fribourg"
            },
            {
                name: "Genève"
            },
            {
                name: "Glarus"
            },
            {
                name: "Graubünden"
            },
            {
                name: "Jura"
            },
            {
                name: "Luzern"
            },
            {
                name: "Neuchâtel"
            },
            {
                name: "Nidwalden"
            },
            {
                name: "Obwalden"
            },
            {
                name: "Sankt Gallen"
            },
            {
                name: "Schaffhausen"
            },
            {
                name: "Solothurn"
            },
            {
                name: "Schwyz"
            },
            {
                name: "Thurgau"
            },
            {
                name: "Ticino"
            },
            {
                name: "Uri"
            },
            {
                name: "Vaud"
            },
            {
                name: "Valais"
            },
            {
                name: "Zug"
            },
            {
                name: "Zürich"
            }
        ],
        phoneCodes: [
            41
        ]
    },
    {
        name: "Syrian Arab Republic",
        code: "SY",
        regionName: "Governorate",
        regions: [
            {
                name: "Dimashq"
            },
            {
                name: "Dar'a"
            },
            {
                name: "Dayr az Zawr"
            },
            {
                name: "Al Hasakah"
            },
            {
                name: "Homs"
            },
            {
                name: "Halab"
            },
            {
                name: "Hamah"
            },
            {
                name: "Idlib"
            },
            {
                name: "Al Ladhiqiyah"
            },
            {
                name: "Al Qunaytirah"
            },
            {
                name: "Ar Raqqah"
            },
            {
                name: "Rif Dimashq"
            },
            {
                name: "As Suwayda'"
            },
            {
                name: "Tartus"
            }
        ],
        phoneCodes: [
            963
        ]
    },
    {
        name: "Taiwan",
        code: "TW",
        regionName: "District",
        regions: [
            {
                name: "Changhua"
            },
            {
                name: "Chiay City"
            },
            {
                name: "Chiayi"
            },
            {
                name: "Hsinchu"
            },
            {
                name: "Hsinchui City"
            },
            {
                name: "Hualien"
            },
            {
                name: "Ilan"
            },
            {
                name: "Keelung City"
            },
            {
                name: "Kaohsiung City"
            },
            {
                name: "Kaohsiung"
            },
            {
                name: "Miaoli"
            },
            {
                name: "Nantou"
            },
            {
                name: "Penghu"
            },
            {
                name: "Pingtung"
            },
            {
                name: "Taoyuan"
            },
            {
                name: "Tainan City"
            },
            {
                name: "Tainan"
            },
            {
                name: "Taipei City"
            },
            {
                name: "Taipei"
            },
            {
                name: "Taitung"
            },
            {
                name: "Taichung City"
            },
            {
                name: "Taichung"
            },
            {
                name: "Yunlin"
            }
        ],
        phoneCodes: [
            886
        ]
    },
    {
        name: "Tajikistan",
        code: "TJ",
        regionName: "Region",
        regions: [
            {
                name: "Gorno-Badakhshan"
            },
            {
                name: "Khatlon"
            },
            {
                name: "Sughd"
            }
        ],
        phoneCodes: [
            992
        ]
    },
    {
        name: "Tanzania, United Republic of",
        code: "TZ",
        regionName: "Region",
        regions: [
            {
                name: "Arusha"
            },
            {
                name: "Dar-es-Salaam"
            },
            {
                name: "Dodoma"
            },
            {
                name: "Iringa"
            },
            {
                name: "Kagera"
            },
            {
                name: "Kaskazini Pemba"
            },
            {
                name: "Kaskazini Unguja"
            },
            {
                name: "Kigoma"
            },
            {
                name: "Kilimanjaro"
            },
            {
                name: "Kusini Pemba"
            },
            {
                name: "Kusini Unguja"
            },
            {
                name: "Lindi"
            },
            {
                name: "Mara"
            },
            {
                name: "Mbeya"
            },
            {
                name: "Mjini Magharibi"
            },
            {
                name: "Morogoro"
            },
            {
                name: "Mtwara"
            },
            {
                name: "Mwanza"
            },
            {
                name: "Pwani"
            },
            {
                name: "Rukwa"
            },
            {
                name: "Ruvuma"
            },
            {
                name: "Shinyanga"
            },
            {
                name: "Singida"
            },
            {
                name: "Tabora"
            },
            {
                name: "Tanga"
            },
            {
                name: "Manyara"
            }
        ],
        phoneCodes: [
            255
        ]
    },
    {
        name: "Thailand",
        code: "TH",
        regionName: "Province",
        regions: [
            {
                name: "Krung Thep Maha Nakhon Bangkok"
            },
            {
                name: "Samut Prakan"
            },
            {
                name: "Nonthaburi"
            },
            {
                name: "Pathum Thani"
            },
            {
                name: "Phra Nakhon Si Ayutthaya"
            },
            {
                name: "Ang Thong"
            },
            {
                name: "Lop Buri"
            },
            {
                name: "Sing Buri"
            },
            {
                name: "Chai Nat"
            },
            {
                name: "Saraburi"
            },
            {
                name: "Chon Buri"
            },
            {
                name: "Rayong"
            },
            {
                name: "Chanthaburi"
            },
            {
                name: "Trat"
            },
            {
                name: "Chachoengsao"
            },
            {
                name: "Prachin Buri"
            },
            {
                name: "Nakhon Nayok"
            },
            {
                name: "Sa Kaeo"
            },
            {
                name: "Nakhon Ratchasima"
            },
            {
                name: "Buri Ram"
            },
            {
                name: "Surin"
            },
            {
                name: "Si Sa Ket"
            },
            {
                name: "Ubon Ratchathani"
            },
            {
                name: "Yasothon"
            },
            {
                name: "Chaiyaphum"
            },
            {
                name: "Amnat Charoen"
            },
            {
                name: "Nong Bua Lam Phu"
            },
            {
                name: "Khon Kaen"
            },
            {
                name: "Udon Thani"
            },
            {
                name: "Loei"
            },
            {
                name: "Nong Khai"
            },
            {
                name: "Maha Sarakham"
            },
            {
                name: "Roi Et"
            },
            {
                name: "Kalasin"
            },
            {
                name: "Sakon Nakhon"
            },
            {
                name: "Nakhon Phanom"
            },
            {
                name: "Mukdahan"
            },
            {
                name: "Chiang Mai"
            },
            {
                name: "Lamphun"
            },
            {
                name: "Lampang"
            },
            {
                name: "Uttaradit"
            },
            {
                name: "Phrae"
            },
            {
                name: "Nan"
            },
            {
                name: "Phayao"
            },
            {
                name: "Chiang Rai"
            },
            {
                name: "Mae Hong Son"
            },
            {
                name: "Nakhon Sawan"
            },
            {
                name: "Uthai Thani"
            },
            {
                name: "Kamphaeng Phet"
            },
            {
                name: "Tak"
            },
            {
                name: "Sukhothai"
            },
            {
                name: "Phitsanulok"
            },
            {
                name: "Phichit"
            },
            {
                name: "Phetchabun"
            },
            {
                name: "Ratchaburi"
            },
            {
                name: "Kanchanaburi"
            },
            {
                name: "Suphan Buri"
            },
            {
                name: "Nakhon Pathom"
            },
            {
                name: "Samut Sakhon"
            },
            {
                name: "Samut Songkhram"
            },
            {
                name: "Phetchaburi"
            },
            {
                name: "Prachuap Khiri Khan"
            },
            {
                name: "Nakhon Si Thammarat"
            },
            {
                name: "Krabi"
            },
            {
                name: "Phangnga"
            },
            {
                name: "Phuket"
            },
            {
                name: "Surat Thani"
            },
            {
                name: "Ranong"
            },
            {
                name: "Chumphon"
            },
            {
                name: "Songkhla"
            },
            {
                name: "Satun"
            },
            {
                name: "Trang"
            },
            {
                name: "Phatthalung"
            },
            {
                name: "Pattani"
            },
            {
                name: "Yala"
            },
            {
                name: "Narathiwat"
            },
            {
                name: "Phatthaya"
            }
        ],
        phoneCodes: [
            66
        ]
    },
    {
        name: "Timor-Leste",
        code: "TL",
        regionName: "District",
        regions: [
            {
                name: "Aileu"
            },
            {
                name: "Ainaro"
            },
            {
                name: "Baucau"
            },
            {
                name: "Bobonaro"
            },
            {
                name: "Cova Lima"
            },
            {
                name: "Díli"
            },
            {
                name: "Ermera"
            },
            {
                name: "Lautem"
            },
            {
                name: "Liquiça"
            },
            {
                name: "Manufahi"
            },
            {
                name: "Manatuto"
            },
            {
                name: "Oecussi"
            },
            {
                name: "Viqueque"
            }
        ],
        phoneCodes: [
            670
        ]
    },
    {
        name: "Togo",
        code: "TG",
        regionName: "Region",
        regions: [
            {
                name: "Région du Centre"
            },
            {
                name: "Région de la Kara"
            },
            {
                name: "Région Maritime"
            },
            {
                name: "Région des Plateaux"
            },
            {
                name: "Région des Savannes"
            }
        ],
        phoneCodes: [
            228
        ]
    },
    {
        name: "Tokelau",
        code: "TK",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            690
        ]
    },
    {
        name: "Tonga",
        code: "TO",
        regionName: "Division",
        regions: [
            {
                name: "'Eua"
            },
            {
                name: "Ha'apai"
            },
            {
                name: "Niuas"
            },
            {
                name: "Tongatapu"
            },
            {
                name: "Vava'u"
            }
        ],
        phoneCodes: [
            676
        ]
    },
    {
        name: "Trinidad and Tobago",
        code: "TT",
        regionName: "Region",
        regions: [
            {
                name: "Arima"
            },
            {
                name: "Chaguanas"
            },
            {
                name: "Couva-Tabaquite-Talparo"
            },
            {
                name: "Diego Martin"
            },
            {
                name: "Eastern Tobago"
            },
            {
                name: "Penal-Debe"
            },
            {
                name: "Port of Spain"
            },
            {
                name: "Princes Town"
            },
            {
                name: "Point Fortin"
            },
            {
                name: "Rio Claro-Mayaro"
            },
            {
                name: "San Fernando"
            },
            {
                name: "Sangre Grande"
            },
            {
                name: "Siparia"
            },
            {
                name: "San Juan-Laventille"
            },
            {
                name: "Tunapuna-Piarco"
            },
            {
                name: "Western Tobago"
            }
        ],
        phoneCodes: [
            1
        ]
    },
    {
        name: "Tunisia",
        code: "TN",
        regionName: "Governorate",
        regions: [
            {
                name: "Tunis"
            },
            {
                name: "Ariana"
            },
            {
                name: "Ben Arous"
            },
            {
                name: "La Manouba"
            },
            {
                name: "Nabeul"
            },
            {
                name: "Zaghouan"
            },
            {
                name: "Bizerte"
            },
            {
                name: "Béja"
            },
            {
                name: "Jendouba"
            },
            {
                name: "Le Kef"
            },
            {
                name: "Siliana"
            },
            {
                name: "Kairouan"
            },
            {
                name: "Kasserine"
            },
            {
                name: "Sidi Bouzid"
            },
            {
                name: "Sousse"
            },
            {
                name: "Monastir"
            },
            {
                name: "Mahdia"
            },
            {
                name: "Sfax"
            },
            {
                name: "Gafsa"
            },
            {
                name: "Tozeur"
            },
            {
                name: "Kebili"
            },
            {
                name: "Gabès"
            },
            {
                name: "Medenine"
            },
            {
                name: "Tataouine"
            }
        ],
        phoneCodes: [
            216
        ]
    },
    {
        name: "Turkey",
        code: "TR",
        regionName: "Province",
        regions: [
            {
                name: "Adana"
            },
            {
                name: "Adıyaman"
            },
            {
                name: "Afyonkarahisar"
            },
            {
                name: "Ağrı"
            },
            {
                name: "Amasya"
            },
            {
                name: "Ankara"
            },
            {
                name: "Antalya"
            },
            {
                name: "Artvin"
            },
            {
                name: "Aydın"
            },
            {
                name: "Balıkesir"
            },
            {
                name: "Bilecik"
            },
            {
                name: "Bingöl"
            },
            {
                name: "Bitlis"
            },
            {
                name: "Bolu"
            },
            {
                name: "Burdur"
            },
            {
                name: "Bursa"
            },
            {
                name: "Çanakkale"
            },
            {
                name: "Çankırı"
            },
            {
                name: "Çorum"
            },
            {
                name: "Denizli"
            },
            {
                name: "Diyarbakır"
            },
            {
                name: "Edirne"
            },
            {
                name: "Elazığ"
            },
            {
                name: "Erzincan"
            },
            {
                name: "Erzurum"
            },
            {
                name: "Eskişehir"
            },
            {
                name: "Gaziantep"
            },
            {
                name: "Giresun"
            },
            {
                name: "Gümüşhane"
            },
            {
                name: "Hakkâri"
            },
            {
                name: "Hatay"
            },
            {
                name: "Isparta"
            },
            {
                name: "Mersin"
            },
            {
                name: "İstanbul"
            },
            {
                name: "İzmir"
            },
            {
                name: "Kars"
            },
            {
                name: "Kastamonu"
            },
            {
                name: "Kayseri"
            },
            {
                name: "Kırklareli"
            },
            {
                name: "Kırşehir"
            },
            {
                name: "Kocaeli"
            },
            {
                name: "Konya"
            },
            {
                name: "Kütahya"
            },
            {
                name: "Malatya"
            },
            {
                name: "Manisa"
            },
            {
                name: "Kahramanmaraş"
            },
            {
                name: "Mardin"
            },
            {
                name: "Muğla"
            },
            {
                name: "Muş"
            },
            {
                name: "Nevşehir"
            },
            {
                name: "Niğde"
            },
            {
                name: "Ordu"
            },
            {
                name: "Rize"
            },
            {
                name: "Sakarya"
            },
            {
                name: "Samsun"
            },
            {
                name: "Siirt"
            },
            {
                name: "Sinop"
            },
            {
                name: "Sivas"
            },
            {
                name: "Tekirdağ"
            },
            {
                name: "Tokat"
            },
            {
                name: "Trabzon"
            },
            {
                name: "Tunceli"
            },
            {
                name: "Şanlıurfa"
            },
            {
                name: "Uşak"
            },
            {
                name: "Van"
            },
            {
                name: "Yozgat"
            },
            {
                name: "Zonguldak"
            },
            {
                name: "Aksaray"
            },
            {
                name: "Bayburt"
            },
            {
                name: "Karaman"
            },
            {
                name: "Kırıkkale"
            },
            {
                name: "Batman"
            },
            {
                name: "Şırnak"
            },
            {
                name: "Bartın"
            },
            {
                name: "Ardahan"
            },
            {
                name: "Iğdır"
            },
            {
                name: "Yalova"
            },
            {
                name: "Karabük"
            },
            {
                name: "Kilis"
            },
            {
                name: "Osmaniye"
            },
            {
                name: "Düzce"
            }
        ],
        phoneCodes: [
            90
        ]
    },
    {
        name: "Turkmenistan",
        code: "TM",
        regionName: "Region",
        regions: [
            {
                name: "Ahal"
            },
            {
                name: "Balkan"
            },
            {
                name: "Daşoguz"
            },
            {
                name: "Lebap"
            },
            {
                name: "Mary"
            },
            {
                name: "Aşgabat"
            }
        ],
        phoneCodes: [
            993
        ]
    },
    {
        name: "Turks and Caicos Islands",
        code: "TC",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            1
        ]
    },
    {
        name: "Tuvalu",
        code: "TV",
        regionName: "Island council",
        regions: [
            {
                name: "Funafuti"
            },
            {
                name: "Niutao"
            },
            {
                name: "Nukufetau"
            },
            {
                name: "Nukulaelae"
            },
            {
                name: "Nanumea"
            },
            {
                name: "Nanumanga"
            },
            {
                name: "Nui"
            },
            {
                name: "Vaitupu"
            }
        ],
        phoneCodes: [
            688
        ]
    },
    {
        name: "Uganda",
        code: "UG",
        regionName: "District",
        regions: [
            {
                name: "Kalangala"
            },
            {
                name: "Kampala"
            },
            {
                name: "Kiboga"
            },
            {
                name: "Luwero"
            },
            {
                name: "Masaka"
            },
            {
                name: "Mpigi"
            },
            {
                name: "Mubende"
            },
            {
                name: "Mukono"
            },
            {
                name: "Nakasongola"
            },
            {
                name: "Rakai"
            },
            {
                name: "Sembabule"
            },
            {
                name: "Kayunga"
            },
            {
                name: "Wakiso"
            },
            {
                name: "Mityana"
            },
            {
                name: "Nakaseke"
            },
            {
                name: "Lyantonde"
            },
            {
                name: "Bugiri"
            },
            {
                name: "Busia"
            },
            {
                name: "Iganga"
            },
            {
                name: "Jinja"
            },
            {
                name: "Kamuli"
            },
            {
                name: "Kapchorwa"
            },
            {
                name: "Katakwi"
            },
            {
                name: "Kumi"
            },
            {
                name: "Mbale"
            },
            {
                name: "Pallisa"
            },
            {
                name: "Soroti"
            },
            {
                name: "Tororo"
            },
            {
                name: "Kaberamaido"
            },
            {
                name: "Mayuge"
            },
            {
                name: "Sironko"
            },
            {
                name: "Amuria"
            },
            {
                name: "Budaka"
            },
            {
                name: "Bukwa"
            },
            {
                name: "Butaleja"
            },
            {
                name: "Kaliro"
            },
            {
                name: "Manafwa"
            },
            {
                name: "Namutumba"
            },
            {
                name: "Bududa"
            },
            {
                name: "Bukedea"
            },
            {
                name: "Adjumani"
            },
            {
                name: "Apac"
            },
            {
                name: "Arua"
            },
            {
                name: "Gulu"
            },
            {
                name: "Kitgum"
            },
            {
                name: "Kotido"
            },
            {
                name: "Lira"
            },
            {
                name: "Moroto"
            },
            {
                name: "Moyo"
            },
            {
                name: "Nebbi"
            },
            {
                name: "Nakapiripirit"
            },
            {
                name: "Pader"
            },
            {
                name: "Yumbe"
            },
            {
                name: "Amolatar"
            },
            {
                name: "Kaabong"
            },
            {
                name: "Koboko"
            },
            {
                name: "Abim"
            },
            {
                name: "Dokolo"
            },
            {
                name: "Amuru"
            },
            {
                name: "Maracha"
            },
            {
                name: "Oyam"
            },
            {
                name: "Bundibugyo"
            },
            {
                name: "Bushenyi"
            },
            {
                name: "Hoima"
            },
            {
                name: "Kabale"
            },
            {
                name: "Kabarole"
            },
            {
                name: "Kasese"
            },
            {
                name: "Kibaale"
            },
            {
                name: "Kisoro"
            },
            {
                name: "Masindi"
            },
            {
                name: "Mbarara"
            },
            {
                name: "Ntungamo"
            },
            {
                name: "Rukungiri"
            },
            {
                name: "Kamwenge"
            },
            {
                name: "Kanungu"
            },
            {
                name: "Kyenjojo"
            },
            {
                name: "Ibanda"
            },
            {
                name: "Isingiro"
            },
            {
                name: "Kiruhura"
            },
            {
                name: "Buliisa"
            },
            {
                name: "Central"
            },
            {
                name: "Eastern"
            },
            {
                name: "Northern"
            },
            {
                name: "Western"
            }
        ],
        phoneCodes: [
            256
        ]
    },
    {
        name: "Ukraine",
        code: "UA",
        regionName: "Province",
        regions: [
            {
                name: "Vinnyts'ka Oblast'"
            },
            {
                name: "Volyns'ka Oblast'"
            },
            {
                name: "Luhans'ka Oblast'"
            },
            {
                name: "Dnipropetrovs'ka Oblast'"
            },
            {
                name: "Donets'ka Oblast'"
            },
            {
                name: "Zhytomyrs'ka Oblast'"
            },
            {
                name: "Zakarpats'ka Oblast'"
            },
            {
                name: "Zaporiz'ka Oblast'"
            },
            {
                name: "Ivano-Frankivs'ka Oblast'"
            },
            {
                name: "Kyïvs'ka mis'ka rada"
            },
            {
                name: "Kyïvs'ka Oblast'"
            },
            {
                name: "Kirovohrads'ka Oblast'"
            },
            {
                name: "Sevastopol"
            },
            {
                name: "Respublika Krym"
            },
            {
                name: "L'vivs'ka Oblast'"
            },
            {
                name: "Mykolaïvs'ka Oblast'"
            },
            {
                name: "Odes'ka Oblast'"
            },
            {
                name: "Poltavs'ka Oblast'"
            },
            {
                name: "Rivnens'ka Oblast'"
            },
            {
                name: "Sums 'ka Oblast'"
            },
            {
                name: "Ternopil's'ka Oblast'"
            },
            {
                name: "Kharkivs'ka Oblast'"
            },
            {
                name: "Khersons'ka Oblast'"
            },
            {
                name: "Khmel'nyts'ka Oblast'"
            },
            {
                name: "Cherkas'ka Oblast'"
            },
            {
                name: "Chernihivs'ka Oblast'"
            },
            {
                name: "Chernivets'ka Oblast'"
            }
        ],
        phoneCodes: [
            380
        ]
    },
    {
        name: "United Arab Emirates",
        code: "AE",
        regionName: "Emirate",
        regions: [
            {
                name: "'Ajmān"
            },
            {
                name: "Abū Ȥaby [Abu Dhabi]"
            },
            {
                name: "Dubayy"
            },
            {
                name: "Al Fujayrah"
            },
            {
                name: "Ra’s al Khaymah"
            },
            {
                name: "Ash Shāriqah"
            },
            {
                name: "Umm al Qaywayn"
            }
        ],
        phoneCodes: [
            971
        ]
    },
    {
        name: "United Kingdom of Great Britain and Northern Ireland",
        code: "GB",
        regionName: "Unitary authority",
        regions: [
            {
                name: "Armagh, Banbridge and Craigavon"
            },
            {
                name: "Aberdeenshire"
            },
            {
                name: "Aberdeen City"
            },
            {
                name: "Argyll and Bute"
            },
            {
                name: "Isle of Anglesey; Sir Ynys Môn"
            },
            {
                name: "Ards and North Down"
            },
            {
                name: "Antrim and Newtownabbey"
            },
            {
                name: "Angus"
            },
            {
                name: "Bath and North East Somerset"
            },
            {
                name: "Blackburn with Darwen"
            },
            {
                name: "Bedford"
            },
            {
                name: "Barking and Dagenham"
            },
            {
                name: "Brent"
            },
            {
                name: "Bexley"
            },
            {
                name: "Belfast"
            },
            {
                name: "Bridgend; Pen-y-bont ar Ogwr"
            },
            {
                name: "Blaenau Gwent"
            },
            {
                name: "Birmingham"
            },
            {
                name: "Buckinghamshire"
            },
            {
                name: "Bournemouth"
            },
            {
                name: "Barnet"
            },
            {
                name: "Brighton and Hove"
            },
            {
                name: "Barnsley"
            },
            {
                name: "Bolton"
            },
            {
                name: "Blackpool"
            },
            {
                name: "Bracknell Forest"
            },
            {
                name: "Bradford"
            },
            {
                name: "Bromley"
            },
            {
                name: "Bristol, City of"
            },
            {
                name: "Bury"
            },
            {
                name: "Cambridgeshire"
            },
            {
                name: "Caerphilly; Caerffili"
            },
            {
                name: "Central Bedfordshire"
            },
            {
                name: "Causeway Coast and Glens"
            },
            {
                name: "Ceredigion; Sir Ceredigion"
            },
            {
                name: "Cheshire East"
            },
            {
                name: "Cheshire West and Chester"
            },
            {
                name: "Calderdale"
            },
            {
                name: "Clackmannanshire"
            },
            {
                name: "Cumbria"
            },
            {
                name: "Camden"
            },
            {
                name: "Carmarthenshire; Sir Gaerfyrddin"
            },
            {
                name: "Cornwall"
            },
            {
                name: "Coventry"
            },
            {
                name: "Cardiff; Caerdydd"
            },
            {
                name: "Croydon"
            },
            {
                name: "Conwy"
            },
            {
                name: "Darlington"
            },
            {
                name: "Derbyshire"
            },
            {
                name: "Denbighshire; Sir Ddinbych"
            },
            {
                name: "Derby"
            },
            {
                name: "Devon"
            },
            {
                name: "Dumfries and Galloway"
            },
            {
                name: "Doncaster"
            },
            {
                name: "Dundee City"
            },
            {
                name: "Dorset"
            },
            {
                name: "Derry and Strabane"
            },
            {
                name: "Dudley"
            },
            {
                name: "Durham County"
            },
            {
                name: "Ealing"
            },
            {
                name: "England and Wales"
            },
            {
                name: "East Ayrshire"
            },
            {
                name: "Edinburgh, City of"
            },
            {
                name: "East Dunbartonshire"
            },
            {
                name: "East Lothian"
            },
            {
                name: "Eilean Siar"
            },
            {
                name: "Enfield"
            },
            {
                name: "England"
            },
            {
                name: "East Renfrewshire"
            },
            {
                name: "East Riding of Yorkshire"
            },
            {
                name: "Essex"
            },
            {
                name: "East Sussex"
            },
            {
                name: "Falkirk"
            },
            {
                name: "Fife"
            },
            {
                name: "Flintshire; Sir y Fflint"
            },
            {
                name: "Fermanagh and Omagh"
            },
            {
                name: "Gateshead"
            },
            {
                name: "Great Britain"
            },
            {
                name: "Glasgow City"
            },
            {
                name: "Gloucestershire"
            },
            {
                name: "Greenwich"
            },
            {
                name: "Gwynedd"
            },
            {
                name: "Halton"
            },
            {
                name: "Hampshire"
            },
            {
                name: "Havering"
            },
            {
                name: "Hackney"
            },
            {
                name: "Herefordshire"
            },
            {
                name: "Hillingdon"
            },
            {
                name: "Highland"
            },
            {
                name: "Hammersmith and Fulham"
            },
            {
                name: "Hounslow"
            },
            {
                name: "Hartlepool"
            },
            {
                name: "Hertfordshire"
            },
            {
                name: "Harrow"
            },
            {
                name: "Haringey"
            },
            {
                name: "Isles of Scilly"
            },
            {
                name: "Isle of Wight"
            },
            {
                name: "Islington"
            },
            {
                name: "Inverclyde"
            },
            {
                name: "Kensington and Chelsea"
            },
            {
                name: "Kent"
            },
            {
                name: "Kingston upon Hull"
            },
            {
                name: "Kirklees"
            },
            {
                name: "Kingston upon Thames"
            },
            {
                name: "Knowsley"
            },
            {
                name: "Lancashire"
            },
            {
                name: "Lisburn and Castlereagh"
            },
            {
                name: "Lambeth"
            },
            {
                name: "Leicester"
            },
            {
                name: "Leeds"
            },
            {
                name: "Leicestershire"
            },
            {
                name: "Lewisham"
            },
            {
                name: "Lincolnshire"
            },
            {
                name: "Liverpool"
            },
            {
                name: "London, City of"
            },
            {
                name: "Luton"
            },
            {
                name: "Manchester"
            },
            {
                name: "Middlesbrough"
            },
            {
                name: "Medway"
            },
            {
                name: "Mid and East Antrim"
            },
            {
                name: "Milton Keynes"
            },
            {
                name: "Midlothian"
            },
            {
                name: "Monmouthshire; Sir Fynwy"
            },
            {
                name: "Merton"
            },
            {
                name: "Moray"
            },
            {
                name: "Merthyr Tydfil; Merthyr Tudful"
            },
            {
                name: "Mid Ulster"
            },
            {
                name: "North Ayrshire"
            },
            {
                name: "Northumberland"
            },
            {
                name: "North East Lincolnshire"
            },
            {
                name: "Newcastle upon Tyne"
            },
            {
                name: "Norfolk"
            },
            {
                name: "Nottingham"
            },
            {
                name: "Northern Ireland"
            },
            {
                name: "North Lanarkshire"
            },
            {
                name: "North Lincolnshire"
            },
            {
                name: "Newry, Mourne and Down"
            },
            {
                name: "North Somerset"
            },
            {
                name: "Northamptonshire"
            },
            {
                name: "Neath Port Talbot; Castell-nedd Port Talbot"
            },
            {
                name: "Nottinghamshire"
            },
            {
                name: "North Tyneside"
            },
            {
                name: "Newham"
            },
            {
                name: "Newport; Casnewydd"
            },
            {
                name: "North Yorkshire"
            },
            {
                name: "Oldham"
            },
            {
                name: "Orkney Islands"
            },
            {
                name: "Oxfordshire"
            },
            {
                name: "Pembrokeshire; Sir Benfro"
            },
            {
                name: "Perth and Kinross"
            },
            {
                name: "Plymouth"
            },
            {
                name: "Poole"
            },
            {
                name: "Portsmouth"
            },
            {
                name: "Powys"
            },
            {
                name: "Peterborough"
            },
            {
                name: "Redcar and Cleveland"
            },
            {
                name: "Rochdale"
            },
            {
                name: "Rhondda, Cynon, Taff; Rhondda, Cynon, Taf"
            },
            {
                name: "Redbridge"
            },
            {
                name: "Reading"
            },
            {
                name: "Renfrewshire"
            },
            {
                name: "Richmond upon Thames"
            },
            {
                name: "Rotherham"
            },
            {
                name: "Rutland"
            },
            {
                name: "Sandwell"
            },
            {
                name: "South Ayrshire"
            },
            {
                name: "Scottish Borders, The"
            },
            {
                name: "Scotland"
            },
            {
                name: "Suffolk"
            },
            {
                name: "Sefton"
            },
            {
                name: "South Gloucestershire"
            },
            {
                name: "Sheffield"
            },
            {
                name: "St. Helens"
            },
            {
                name: "Shropshire"
            },
            {
                name: "Stockport"
            },
            {
                name: "Salford"
            },
            {
                name: "Slough"
            },
            {
                name: "South Lanarkshire"
            },
            {
                name: "Sunderland"
            },
            {
                name: "Solihull"
            },
            {
                name: "Somerset"
            },
            {
                name: "Southend-on-Sea"
            },
            {
                name: "Surrey"
            },
            {
                name: "Stoke-on-Trent"
            },
            {
                name: "Stirling"
            },
            {
                name: "Southampton"
            },
            {
                name: "Sutton"
            },
            {
                name: "Staffordshire"
            },
            {
                name: "Stockton-on-Tees"
            },
            {
                name: "South Tyneside"
            },
            {
                name: "Swansea; Abertawe"
            },
            {
                name: "Swindon"
            },
            {
                name: "Southwark"
            },
            {
                name: "Tameside"
            },
            {
                name: "Telford and Wrekin"
            },
            {
                name: "Thurrock"
            },
            {
                name: "Torbay"
            },
            {
                name: "Torfaen; Tor-faen"
            },
            {
                name: "Trafford"
            },
            {
                name: "Tower Hamlets"
            },
            {
                name: "United Kingdom"
            },
            {
                name: "Vale of Glamorgan, The; Bro Morgannwg"
            },
            {
                name: "Warwickshire"
            },
            {
                name: "West Berkshire"
            },
            {
                name: "West Dunbartonshire"
            },
            {
                name: "Waltham Forest"
            },
            {
                name: "Wigan"
            },
            {
                name: "Wiltshire"
            },
            {
                name: "Wakefield"
            },
            {
                name: "Walsall"
            },
            {
                name: "West Lothian"
            },
            {
                name: "Wales; Cymru"
            },
            {
                name: "Wolverhampton"
            },
            {
                name: "Wandsworth"
            },
            {
                name: "Windsor and Maidenhead"
            },
            {
                name: "Wokingham"
            },
            {
                name: "Worcestershire"
            },
            {
                name: "Wirral"
            },
            {
                name: "Warrington"
            },
            {
                name: "Wrexham; Wrecsam"
            },
            {
                name: "Westminster"
            },
            {
                name: "West Sussex"
            },
            {
                name: "York"
            },
            {
                name: "Shetland Islands"
            }
        ],
        phoneCodes: [
            44
        ]
    },
    {
        name: "United States of America",
        priority: 1,
        code: "US",
        altNames: [
            "United States"
        ],
        regionName: "State",
        regions: [
            {
                name: "Alaska",
                code: "AK"
            },
            {
                name: "Alabama",
                code: "AL"
            },
            {
                name: "Arkansas",
                code: "AR"
            },
            {
                name: "American Samoa",
                code: "AS"
            },
            {
                name: "Arizona",
                code: "AZ"
            },
            {
                name: "California",
                code: "CA"
            },
            {
                name: "Colorado",
                code: "CO"
            },
            {
                name: "Connecticut",
                code: "CT"
            },
            {
                name: "District of Columbia",
                code: "DC"
            },
            {
                name: "Delaware",
                code: "DE"
            },
            {
                name: "Florida",
                code: "FL"
            },
            {
                name: "Georgia",
                code: "GA"
            },
            {
                name: "Guam",
                code: "GU"
            },
            {
                name: "Hawaii",
                code: "HI"
            },
            {
                name: "Iowa",
                code: "IA"
            },
            {
                name: "Idaho",
                code: "ID"
            },
            {
                name: "Illinois",
                code: "IL"
            },
            {
                name: "Indiana",
                code: "IN"
            },
            {
                name: "Kansas",
                code: "KS"
            },
            {
                name: "Kentucky",
                code: "KY"
            },
            {
                name: "Louisiana",
                code: "LA"
            },
            {
                name: "Massachusetts",
                code: "MA"
            },
            {
                name: "Maryland",
                code: "MD"
            },
            {
                name: "Maine",
                code: "ME"
            },
            {
                name: "Michigan",
                code: "MI"
            },
            {
                name: "Minnesota",
                code: "MN"
            },
            {
                name: "Missouri",
                code: "MO"
            },
            {
                name: "Northern Mariana Islands",
                code: "MP"
            },
            {
                name: "Mississippi",
                code: "MS"
            },
            {
                name: "Montana",
                code: "MT"
            },
            {
                name: "North Carolina",
                code: "NC"
            },
            {
                name: "North Dakota",
                code: "ND"
            },
            {
                name: "Nebraska",
                code: "NE"
            },
            {
                name: "New Hampshire",
                code: "NH"
            },
            {
                name: "New Jersey",
                code: "NJ"
            },
            {
                name: "New Mexico",
                code: "NM"
            },
            {
                name: "Nevada",
                code: "NV"
            },
            {
                name: "New York",
                code: "NY"
            },
            {
                name: "Ohio",
                code: "OH"
            },
            {
                name: "Oklahoma",
                code: "OK"
            },
            {
                name: "Oregon",
                code: "OR"
            },
            {
                name: "Pennsylvania",
                code: "PA"
            },
            {
                name: "Puerto Rico",
                code: "PR"
            },
            {
                name: "Rhode Island",
                code: "RI"
            },
            {
                name: "South Carolina",
                code: "SC"
            },
            {
                name: "South Dakota",
                code: "SD"
            },
            {
                name: "Tennessee",
                code: "TN"
            },
            {
                name: "Texas",
                code: "TX"
            },
            {
                name: "United States Minor Outlying Islands",
                code: "UM"
            },
            {
                name: "Utah",
                code: "UT"
            },
            {
                name: "Virginia",
                code: "VA"
            },
            {
                name: "Virgin Islands",
                code: "VI"
            },
            {
                name: "Vermont",
                code: "VT"
            },
            {
                name: "Washington",
                code: "WA"
            },
            {
                name: "Wisconsin",
                code: "WI"
            },
            {
                name: "West Virginia",
                code: "WV"
            },
            {
                name: "Wyoming",
                code: "WY"
            }
        ],
        phoneCodes: [
            1
        ]
    },
    {
        name: "Uruguay",
        code: "UY",
        regionName: "Department",
        regions: [
            {
                name: "Artigas"
            },
            {
                name: "Canelones"
            },
            {
                name: "Cerro Largo"
            },
            {
                name: "Colonia"
            },
            {
                name: "Durazno"
            },
            {
                name: "Florida"
            },
            {
                name: "Flores"
            },
            {
                name: "Lavalleja"
            },
            {
                name: "Maldonado"
            },
            {
                name: "Montevideo"
            },
            {
                name: "Paysandú"
            },
            {
                name: "Río Negro"
            },
            {
                name: "Rocha"
            },
            {
                name: "Rivera"
            },
            {
                name: "Salto"
            },
            {
                name: "San José"
            },
            {
                name: "Soriano"
            },
            {
                name: "Tacuarembó"
            },
            {
                name: "Treinta y Tres"
            }
        ],
        phoneCodes: [
            598
        ]
    },
    {
        name: "Uzbekistan",
        code: "UZ",
        regionName: "Region",
        regions: [
            {
                name: "Andijon"
            },
            {
                name: "Buxoro"
            },
            {
                name: "Farg'ona"
            },
            {
                name: "Jizzax"
            },
            {
                name: "Namangan"
            },
            {
                name: "Navoiy"
            },
            {
                name: "Qashqadaryo"
            },
            {
                name: "Qoraqalpog'iston Respublikasi"
            },
            {
                name: "Samarqand"
            },
            {
                name: "Sirdaryo"
            },
            {
                name: "Surxondaryo"
            },
            {
                name: "Toshkent"
            },
            {
                name: "Toshkent"
            },
            {
                name: "Xorazm"
            }
        ],
        phoneCodes: [
            998
        ]
    },
    {
        name: "Vanuatu",
        code: "VU",
        regionName: "Province",
        regions: [
            {
                name: "Malampa"
            },
            {
                name: "Pénama"
            },
            {
                name: "Sanma"
            },
            {
                name: "Shéfa"
            },
            {
                name: "Taféa"
            },
            {
                name: "Torba"
            }
        ],
        phoneCodes: [
            678
        ]
    },
    {
        name: "Venezuela (Bolivarian Republic of)",
        code: "VE",
        regionName: "State",
        regions: [
            {
                name: "Distrito Federal"
            },
            {
                name: "Anzoátegui"
            },
            {
                name: "Apure"
            },
            {
                name: "Aragua"
            },
            {
                name: "Barinas"
            },
            {
                name: "Bolívar"
            },
            {
                name: "Carabobo"
            },
            {
                name: "Cojedes"
            },
            {
                name: "Falcón"
            },
            {
                name: "Guárico"
            },
            {
                name: "Lara"
            },
            {
                name: "Mérida"
            },
            {
                name: "Miranda"
            },
            {
                name: "Monagas"
            },
            {
                name: "Nueva Esparta"
            },
            {
                name: "Portuguesa"
            },
            {
                name: "Sucre"
            },
            {
                name: "Táchira"
            },
            {
                name: "Trujillo"
            },
            {
                name: "Yaracuy"
            },
            {
                name: "Zulia"
            },
            {
                name: "Dependencias Federales"
            },
            {
                name: "Vargas"
            },
            {
                name: "Delta Amacuro"
            },
            {
                name: "Amazonas"
            }
        ],
        phoneCodes: [
            58
        ]
    },
    {
        name: "Viet Nam",
        code: "VN",
        regionName: "Province",
        regions: [
            {
                name: "Lai Châu"
            },
            {
                name: "Lào Cai"
            },
            {
                name: "Hà Giang"
            },
            {
                name: "Cao Bằng"
            },
            {
                name: "Sơn La"
            },
            {
                name: "Yên Bái"
            },
            {
                name: "Tuyên Quang"
            },
            {
                name: "Lạng Sơn"
            },
            {
                name: "Quảng Ninh"
            },
            {
                name: "Hoà Bình"
            },
            {
                name: "Hà Tây"
            },
            {
                name: "Ninh Bình"
            },
            {
                name: "Thái Bình"
            },
            {
                name: "Thanh Hóa"
            },
            {
                name: "Nghệ An"
            },
            {
                name: "Hà Tỉnh"
            },
            {
                name: "Quảng Bình"
            },
            {
                name: "Quảng Trị"
            },
            {
                name: "Thừa Thiên-Huế"
            },
            {
                name: "Quảng Nam"
            },
            {
                name: "Kon Tum"
            },
            {
                name: "Quảng Ngãi"
            },
            {
                name: "Gia Lai"
            },
            {
                name: "Bình Định"
            },
            {
                name: "Phú Yên"
            },
            {
                name: "Đắc Lắk"
            },
            {
                name: "Khánh Hòa"
            },
            {
                name: "Lâm Đồng"
            },
            {
                name: "Ninh Thuận"
            },
            {
                name: "Tây Ninh"
            },
            {
                name: "Đồng Nai"
            },
            {
                name: "Bình Thuận"
            },
            {
                name: "Long An"
            },
            {
                name: "Bà Rịa-Vũng Tàu"
            },
            {
                name: "An Giang"
            },
            {
                name: "Đồng Tháp"
            },
            {
                name: "Tiền Giang"
            },
            {
                name: "Kiên Giang"
            },
            {
                name: "Vĩnh Long"
            },
            {
                name: "Bến Tre"
            },
            {
                name: "Trà Vinh"
            },
            {
                name: "Sóc Trăng"
            },
            {
                name: "Bắc Kạn"
            },
            {
                name: "Bắc Giang"
            },
            {
                name: "Bạc Liêu"
            },
            {
                name: "Bắc Ninh"
            },
            {
                name: "Bình Dương"
            },
            {
                name: "Bình Phước"
            },
            {
                name: "Cà Mau"
            },
            {
                name: "Hải Duong"
            },
            {
                name: "Hà Nam"
            },
            {
                name: "Hưng Yên"
            },
            {
                name: "Nam Định"
            },
            {
                name: "Phú Thọ"
            },
            {
                name: "Thái Nguyên"
            },
            {
                name: "Vĩnh Phúc"
            },
            {
                name: "Điện Biên"
            },
            {
                name: "Đắk Nông"
            },
            {
                name: "Hậu Giang"
            },
            {
                name: "Cần Thơ"
            },
            {
                name: "Đà Nẵng"
            },
            {
                name: "Hà Nội"
            },
            {
                name: "Hải Phòng"
            },
            {
                name: "Hồ Chí Minh [Sài Gòn]"
            }
        ],
        phoneCodes: [
            84
        ]
    },
    {
        name: "Wallis and Futuna",
        code: "WF",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            681
        ]
    },
    {
        name: "Western Sahara",
        code: "EH",
        regionName: "Region",
        regions: [],
        phoneCodes: [
            212
        ]
    },
    {
        name: "Yemen",
        code: "YE",
        regionName: "Governorate",
        regions: [
            {
                name: "Abyān"
            },
            {
                name: "'Adan"
            },
            {
                name: "'Amrān"
            },
            {
                name: "Al Bayḑā'"
            },
            {
                name: "Aḑ Ḑāli‘"
            },
            {
                name: "Dhamār"
            },
            {
                name: "Ḩaḑramawt"
            },
            {
                name: "Ḩajjah"
            },
            {
                name: "Ibb"
            },
            {
                name: "Al Jawf"
            },
            {
                name: "Laḩij"
            },
            {
                name: "Ma'rib"
            },
            {
                name: "Al Mahrah"
            },
            {
                name: "Al Ḩudaydah"
            },
            {
                name: "Al Maḩwīt"
            },
            {
                name: "Raymah"
            },
            {
                name: "Şa'dah"
            },
            {
                name: "Shabwah"
            },
            {
                name: "Şan'ā'"
            },
            {
                name: "Tā'izz"
            }
        ],
        phoneCodes: [
            967
        ]
    },
    {
        name: "Zambia",
        code: "ZM",
        regionName: "Province",
        regions: [
            {
                name: "Western"
            },
            {
                name: "Central"
            },
            {
                name: "Eastern"
            },
            {
                name: "Luapula"
            },
            {
                name: "Northern"
            },
            {
                name: "North-Western"
            },
            {
                name: "Southern (Zambia)"
            },
            {
                name: "Copperbelt"
            },
            {
                name: "Lusaka"
            }
        ],
        phoneCodes: [
            260
        ]
    },
    {
        name: "Zimbabwe",
        code: "ZW",
        regionName: "Province",
        regions: [
            {
                name: "Bulawayo"
            },
            {
                name: "Harare"
            },
            {
                name: "Manicaland"
            },
            {
                name: "Mashonaland Central"
            },
            {
                name: "Mashonaland East"
            },
            {
                name: "Midlands"
            },
            {
                name: "Matabeleland North"
            },
            {
                name: "Matabeleland South"
            },
            {
                name: "Masvingo"
            },
            {
                name: "Mashonaland West"
            }
        ],
        phoneCodes: [
            263
        ]
    }
];

class CountryService {
    constructor() {
        this._countries = CountryData.sort((a, b) => (a.priority ?? Number.MAX_VALUE) - (b.priority ?? Number.MAX_VALUE));
        /**
         * List of countries, sorted by priority allowing often-selected countries to appear
         * at the top of the list and to prioritize type-ahead and partial-matching selections.
         */
        this.countries$ = of(this._countries);
        /**
         * A sorted list of country phone-codes, e.g. "1" for United States as in 1-720-434-2934.
         */
        this.phoneCodes$ = of(flattenArray(this._countries.map(c => c.phoneCodes), true).sort((a, b) => a - b));
    }
    static { this.ɵfac = function CountryService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || CountryService)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: CountryService, factory: CountryService.ɵfac }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CountryService, [{
        type: Injectable
    }], null, null); })();

function StlrPhoneComponent_For_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "option", 1);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const code_r1 = ctx.$implicit;
    i0.ɵɵproperty("value", code_r1);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" +", code_r1, " ");
} }
class StlrPhoneComponent extends StlrFormComponentValueAccessor {
    onFocusOut(event) {
        const element = event.relatedTarget;
        if (element == null ||
            (element !== this.inputRef?.nativeElement
                && !this._elementRef.nativeElement.contains(element)
                && element !== this.autocompleteComp?.panelRef?.nativeElement
                && !this.autocompleteComp?.optionRefs?.toArray().map(opt => opt.nativeElement).includes(element))) {
            if (this.ngControl?.control?.updateOn === 'blur') {
                this._onChange(this.value);
            }
            this._onTouched();
        }
    }
    get phoneCodeInvalid() {
        return this.required && !this.value?.phoneCode;
    }
    get phoneNumberInvalid() {
        const control = this.ngControl?.control;
        const invalid = (this.required && !this.value?.phoneNumber)
            || (control?.errors
                ? ['maxlength', 'minlength', 'pattern'].some(x => Object.keys(control.errors).map(key => key).includes(x))
                : false);
        return invalid;
    }
    /** @deprecated Use FormControl or set the [value] input. This will be removed in v3. */
    get phone() {
        return this.value;
    }
    set phone(value) {
        this.value = value;
    }
    /** @deprecated Use FormControl or set the [value] input. This will be removed in v3. */
    set phoneCode(control) {
        this._phoneCode = control.value;
        this.value = { phoneCode: this._phoneCode ?? null, phoneNumber: this._phoneNumber ?? null };
    }
    /** @deprecated Use FormControl or set the [value] input. This will be removed in v3. */
    set phoneNumber(control) {
        this._phoneNumber = control.value;
        this.value = { phoneCode: this._phoneCode ?? null, phoneNumber: this._phoneNumber ?? null };
    }
    constructor(ngControl, validator) {
        super(ngControl);
        this.controlType = 'stlr-phone';
        this.countryCodeName = () => this.name ? `${this.name}_autocomplete` : null;
        this.phoneName = () => this.name ? `${this.name}_input` : null;
        this._countryService = inject(CountryService);
        this._elementRef = inject(ElementRef);
        this.phoneCodes$ = this._countryService.phoneCodes$;
        /**
         * Set a maximum phone number length.
         * The default is 14.
         */
        this.maxLength = 14;
        /**
         * Set a minimum phone number length.
         * The default is 5.
         */
        this.minLength = 5;
        /**
         * Apply a regular expression as a validator.
         * The default allows numbers and some special characters.
         */
        this.pattern = new RegExp(/^[0-9*()_+\-=;:,.]*$/);
        this.countryCodeDisplayFn = (code) => {
            return code ? `+${code}` : null;
        };
        this.countryCodeValueFn = (text) => {
            const normalizedCode = text?.replace(/\D/g, '');
            return this.phoneCodes$.pipe(first(), map(codes => codes.find(c => c.toString() === normalizedCode) ?? null));
        };
        if (validator)
            validator.phoneComponent = this;
    }
    handleCodeChange(phoneCode) {
        const value = { phoneCode, phoneNumber: this.value?.phoneNumber ?? null };
        this._handleChange(value);
    }
    handleNumberChange(phoneNumber) {
        const value = { phoneCode: this.value?.phoneCode ?? null, phoneNumber };
        this._handleChange(value);
    }
    _handleChange(value) {
        const hasChanged = !isEqual(super.value, value);
        this.value = value;
        if (hasChanged) {
            const emitValue = super.value?.phoneCode && super.value?.phoneNumber ? super.value : null;
            if (this.ngControl?.control?.updateOn === 'change')
                this._onChange(emitValue);
            this.changed.emit(emitValue);
        }
    }
    static { this.ɵfac = function StlrPhoneComponent_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || StlrPhoneComponent)(i0.ɵɵdirectiveInject(i1.NgControl, 10), i0.ɵɵdirectiveInject(StlrPhoneValidatorDirective, 10)); }; }
    static { this.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: StlrPhoneComponent, selectors: [["stlr-phone"]], viewQuery: function StlrPhoneComponent_Query(rf, ctx) { if (rf & 1) {
            i0.ɵɵviewQuery(StlrAutocompleteComponent, 7);
            i0.ɵɵviewQuery(StlrInputComponent, 7);
            i0.ɵɵviewQuery(StlrInputComponent, 7, ElementRef);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.autocompleteComp = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.inputComponent = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.inputRef = _t.first);
        } }, hostVars: 2, hostBindings: function StlrPhoneComponent_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("focusout", function StlrPhoneComponent_focusout_HostBindingHandler($event) { return ctx.onFocusOut($event); });
        } if (rf & 2) {
            i0.ɵɵattribute("phone-code-invalid", ctx.phoneCodeInvalid)("phone-number-invalid", ctx.phoneNumberInvalid);
        } }, inputs: { maxLength: "maxLength", minLength: "minLength", pattern: "pattern", phone: "phone", phoneCode: "phoneCode", phoneNumber: "phoneNumber" }, standalone: false, features: [i0.ɵɵProvidersFeature([
                {
                    provide: STLR_FORM_COMPONENT_BASE,
                    useExisting: forwardRef(() => StlrPhoneComponent),
                    multi: true
                }
            ]), i0.ɵɵInheritDefinitionFeature], decls: 6, vars: 17, consts: [["aria-label", "Country Code", "autocomplete", "tel-country-code", 1, "phone-code", 3, "changed", "disabled", "displayFn", "invalid", "name", "required", "value", "valueFn"], ["stlr-option", "", 3, "value"], ["stlr-input", "", "autocomplete", "tel-national", "aria-label", "Phone Number", "name", "Phone", "type", "tel", "width", "full", 3, "changed", "disabled", "enterKeyHint", "invalid", "name", "placeholder", "required", "value"]], template: function StlrPhoneComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵelementStart(0, "stlr-autocomplete", 0);
            i0.ɵɵlistener("changed", function StlrPhoneComponent_Template_stlr_autocomplete_changed_0_listener($event) { return ctx.handleCodeChange($event); });
            i0.ɵɵelement(1, "option", 1);
            i0.ɵɵrepeaterCreate(2, StlrPhoneComponent_For_3_Template, 2, 2, "option", 1, i0.ɵɵrepeaterTrackByIdentity);
            i0.ɵɵpipe(4, "async");
            i0.ɵɵelementEnd();
            i0.ɵɵelementStart(5, "input", 2);
            i0.ɵɵlistener("changed", function StlrPhoneComponent_Template_input_changed_5_listener($event) { return ctx.handleNumberChange($event); });
            i0.ɵɵelementEnd();
        } if (rf & 2) {
            i0.ɵɵproperty("disabled", ctx.disabled)("displayFn", ctx.countryCodeDisplayFn)("invalid", ctx.phoneCodeInvalid)("name", ctx.countryCodeName())("required", ctx.required)("value", (ctx.value == null ? null : ctx.value.phoneCode) ?? null)("valueFn", ctx.countryCodeValueFn);
            i0.ɵɵadvance();
            i0.ɵɵproperty("value", null);
            i0.ɵɵadvance();
            i0.ɵɵrepeater(i0.ɵɵpipeBind1(4, 15, ctx.phoneCodes$));
            i0.ɵɵadvance(3);
            i0.ɵɵproperty("disabled", ctx.disabled)("enterKeyHint", ctx.enterKeyHint)("invalid", ctx.phoneNumberInvalid)("name", ctx.phoneName())("placeholder", ctx.placeholder)("required", ctx.required)("value", (ctx.value == null ? null : ctx.value.phoneNumber) ?? null);
        } }, dependencies: [i1.NgSelectOption, i1.ɵNgSelectMultipleOption, StlrAutocompleteComponent, StlrInputComponent, StlrOptionComponent, i1$1.AsyncPipe], styles: ["[_nghost-%COMP%]{display:flex;flex-direction:row;gap:10px}[width=full][_nghost-%COMP%]{width:100%}.ng-touched.ng-invalid[_nghost-%COMP%]     input.invalid:not(.disabled):not(:focus), .ng-dirty.ng-invalid[_nghost-%COMP%]     input.invalid:not(.disabled):not(:focus){border-color:var(--stlr-color__error);background-color:var(--stlr-color__error--extra-light)}.ng-touched.ng-invalid[_nghost-%COMP%]   input.invalid[_ngcontent-%COMP%]:not(.disabled):not(:focus), .ng-dirty.ng-invalid[_nghost-%COMP%]   input.invalid[_ngcontent-%COMP%]:not(.disabled):not(:focus){border-color:var(--stlr-color__error);background-color:var(--stlr-color__error--extra-light)}.phone-code[_ngcontent-%COMP%]{width:100px}"], changeDetection: 0 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrPhoneComponent, [{
        type: Component,
        args: [{ selector: 'stlr-phone', changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                    {
                        provide: STLR_FORM_COMPONENT_BASE,
                        useExisting: forwardRef(() => StlrPhoneComponent),
                        multi: true
                    }
                ], standalone: false, template: "<stlr-autocomplete\n  aria-label=\"Country Code\"\n  autocomplete=\"tel-country-code\"\n  class=\"phone-code\"\n  (changed)=\"handleCodeChange($event)\"\n  [disabled]=\"disabled\"\n  [displayFn]=\"countryCodeDisplayFn\"\n  [invalid]=\"phoneCodeInvalid\"\n  [name]=\"countryCodeName()\"\n  [required]=\"required\"\n  [value]=\"value?.phoneCode ?? null\"\n  [valueFn]=\"countryCodeValueFn\"\n  >\n  <option stlr-option [value]=\"null\"></option>\n  @for (code of phoneCodes$ | async; track code) {\n    <option stlr-option [value]=\"code\">\n      +{{ code }}\n    </option>\n  }\n</stlr-autocomplete>\n\n<input\n  stlr-input\n  autocomplete=\"tel-national\"\n  aria-label=\"Phone Number\"\n  name=\"Phone\"\n  type=\"tel\"\n  width=\"full\"\n  (changed)=\"handleNumberChange($event)\"\n  [disabled]=\"disabled\"\n  [enterKeyHint]=\"enterKeyHint\"\n  [invalid]=\"phoneNumberInvalid\"\n  [name]=\"phoneName()\"\n  [placeholder]=\"placeholder\"\n  [required]=\"required\"\n  [value]=\"value?.phoneNumber ?? null\"\n  />\n", styles: [":host{display:flex;flex-direction:row;gap:10px}:host[width=full]{width:100%}:host.ng-touched.ng-invalid ::ng-deep input.invalid:not(.disabled):not(:focus),:host.ng-dirty.ng-invalid ::ng-deep input.invalid:not(.disabled):not(:focus){border-color:var(--stlr-color__error);background-color:var(--stlr-color__error--extra-light)}:host.ng-touched.ng-invalid input.invalid:not(.disabled):not(:focus),:host.ng-dirty.ng-invalid input.invalid:not(.disabled):not(:focus){border-color:var(--stlr-color__error);background-color:var(--stlr-color__error--extra-light)}.phone-code{width:100px}\n"] }]
    }], () => [{ type: i1.NgControl, decorators: [{
                type: Self
            }, {
                type: Optional
            }] }, { type: StlrPhoneValidatorDirective, decorators: [{
                type: Self
            }, {
                type: Optional
            }] }], { autocompleteComp: [{
            type: ViewChild,
            args: [StlrAutocompleteComponent, { static: true }]
        }], inputComponent: [{
            type: ViewChild,
            args: [StlrInputComponent, { static: true }]
        }], inputRef: [{
            type: ViewChild,
            args: [StlrInputComponent, { static: true, read: ElementRef }]
        }], onFocusOut: [{
            type: HostListener,
            args: ['focusout', ['$event']]
        }], phoneCodeInvalid: [{
            type: HostBinding,
            args: ['attr.phone-code-invalid']
        }], phoneNumberInvalid: [{
            type: HostBinding,
            args: ['attr.phone-number-invalid']
        }], maxLength: [{
            type: Input
        }], minLength: [{
            type: Input
        }], pattern: [{
            type: Input
        }], phone: [{
            type: Input
        }], phoneCode: [{
            type: Input
        }], phoneNumber: [{
            type: Input
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(StlrPhoneComponent, { className: "StlrPhoneComponent", filePath: "lib/phone/phone.component.ts", lineNumber: 39 }); })();

/**
 * @deprecated The PhoneComponent is now compatible with Validators.required. Remaining validators are internally applied.
 */
class PhoneValidators {
    /**
     * @deprecated The PhoneComponent is now compatible with the Angular built-in validator, Validators.required.
     * Requires both a phone code and a phone number.
     * @returns Validation errors or null
     */
    static required() {
        return (control) => {
            const { value } = control;
            return (!value?.phoneCode || !value?.phoneNumber) ? { required: true } : null;
        };
    }
}

const PHONE_API = [
    StlrPhoneComponent,
    StlrPhoneValidatorDirective
];

const STLR_RADIO_GROUP = new InjectionToken('STLR_RADIO_GROUP');

const _c0$5 = ["*"];
class StlrRadioComponent {
    constructor() {
        this._parentGroup = inject(STLR_RADIO_GROUP, { host: true });
        this._changeDetectorRef = inject(ChangeDetectorRef);
        this._checked = false;
        this._disabled = false;
        this._groupDisabled = false;
        /**
         * The value of this StlrRadio button. When selected, it will be applied to the corresponding StlrRadioGroup's [value].
         */
        this.value = null;
    }
    /**
     * @deprecated Input() decorator will be removed in v3. Only HostBinding will remain.
     * The checked state of this component is applied according to the StlrRadioGroup's
     * [value] property. Instead of setting this Input, set the StlrRadioGroup's [value]
     * property or the StlrRadioGroup's FormControl value. Once this is converted to just
     * a HostBinding, this no longer needs to be a getter/setter.
     */
    get checked() {
        return this._checked;
    }
    set checked(value) {
        this._checked = value;
        if (this._checked) {
            this._parentGroup.value = this.value;
        }
    }
    /**
     * Disables this StlrRadio button.
     */
    get disabled() {
        return this._groupDisabled || this._disabled;
    }
    set disabled(isDisabled) {
        this._disabled = coerceBooleanProperty(isDisabled);
    }
    /**
     * Internal-use only. Set this StlrRadio button's disabled state at the group-level. It's original disabled state is
     * tracked so that once the group-level disabled state is removed, this button's original disabled state is restored.
     * @param isDisabled Whether or not this button is disabled at the group-level.
     */
    setGroupDisabled(isDisabled) {
        this._groupDisabled = isDisabled;
        this._changeDetectorRef.markForCheck();
    }
    handleChange(event) {
        if (event.target.checked) {
            this._parentGroup.handleChange(this.value);
        }
    }
    static { this.ɵfac = function StlrRadioComponent_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || StlrRadioComponent)(); }; }
    static { this.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: StlrRadioComponent, selectors: [["stlr-radio"]], hostVars: 4, hostBindings: function StlrRadioComponent_HostBindings(rf, ctx) { if (rf & 2) {
            i0.ɵɵclassProp("checked", ctx.checked)("disabled", ctx.disabled);
        } }, inputs: { checked: [2, "checked", "checked", (value) => coerceBooleanProperty(value)], disabled: "disabled", name: "name", value: "value" }, standalone: false, ngContentSelectors: _c0$5, decls: 3, vars: 6, consts: [["type", "radio", 3, "change", "checked", "disabled", "name", "value"]], template: function StlrRadioComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵelementStart(0, "label")(1, "input", 0);
            i0.ɵɵlistener("change", function StlrRadioComponent_Template_input_change_1_listener($event) { return ctx.handleChange($event); });
            i0.ɵɵelementEnd();
            i0.ɵɵprojection(2);
            i0.ɵɵelementEnd();
        } if (rf & 2) {
            i0.ɵɵadvance();
            i0.ɵɵproperty("checked", ctx.checked)("disabled", ctx.disabled)("name", ctx.name)("value", ctx.value);
            i0.ɵɵattribute("aria-checked", ctx.checked)("aria-disabled", ctx.disabled);
        } }, styles: ["[_nghost-%COMP%]   input[_ngcontent-%COMP%]{position:relative;height:16px;width:16px;background-color:#fff;border:2px solid var(--stlr-color__info--dark);border-radius:3px;appearance:none;outline:none;transition:all .2s;flex-shrink:0;cursor:pointer}[_nghost-%COMP%]   input[_ngcontent-%COMP%]:checked{background-color:var(--stlr-color__info);border:2px solid var(--stlr-color__info)}[_nghost-%COMP%]   input[disabled][_ngcontent-%COMP%]{color:var(--stlr-color__disabled);border:solid 2px var(--stlr-color__disabled);background-color:#fff3;cursor:default}[_nghost-%COMP%]   input[_ngcontent-%COMP%]{border-radius:50%}[_nghost-%COMP%]   input[_ngcontent-%COMP%]:checked:before{content:\"\";position:absolute;display:block;width:100%;height:100%;background:var(--stlr-color__info);box-sizing:border-box;border:solid 2px white;border-radius:50%;top:0;left:0}[_nghost-%COMP%]   input[_ngcontent-%COMP%]{margin:0 var(--space--8) 0 0;z-index:2}[_nghost-%COMP%]   input[_ngcontent-%COMP%]:focus{border:solid 2px var(--stlr-color__info);outline:solid 2px var(--stlr-color__info)}[_nghost-%COMP%]   label[_ngcontent-%COMP%]{position:relative;display:inline-flex;align-items:center;font-size:.85em;line-height:1;transition:all .2s;cursor:pointer}[_nghost-%COMP%]   label[_ngcontent-%COMP%]   input[_ngcontent-%COMP%]{z-index:2}[_nghost-%COMP%]   label[type=legacy][_ngcontent-%COMP%]{padding:5px;background-color:#0000;font-size:14px;color:var(--stlr-color__copy--subdued)}[_nghost-%COMP%]   label[type=legacy][_ngcontent-%COMP%]   small[_ngcontent-%COMP%]{color:var(--stlr-color__disabled)}[_nghost-%COMP%]   label.disabled[_ngcontent-%COMP%]{color:var(--stlr-color__disabled)}[_nghost-%COMP%]   label.disabled[_ngcontent-%COMP%]   input[_ngcontent-%COMP%]{border-color:var(--stlr-color__disabled)}"], changeDetection: 0 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrRadioComponent, [{
        type: Component,
        args: [{ selector: 'stlr-radio', changeDetection: ChangeDetectionStrategy.OnPush, standalone: false, template: "<label>\n    <input\n        type=\"radio\"\n        (change)=\"handleChange($event)\"\n        [attr.aria-checked]=\"checked\"\n        [attr.aria-disabled]=\"disabled\"\n        [checked]=\"checked\"\n        [disabled]=\"disabled\"\n        [name]=\"name\"\n        [value]=\"value\"\n    />\n    <ng-content></ng-content>\n</label>\n", styles: [":host input{position:relative;height:16px;width:16px;background-color:#fff;border:2px solid var(--stlr-color__info--dark);border-radius:3px;appearance:none;outline:none;transition:all .2s;flex-shrink:0;cursor:pointer}:host input:checked{background-color:var(--stlr-color__info);border:2px solid var(--stlr-color__info)}:host input[disabled]{color:var(--stlr-color__disabled);border:solid 2px var(--stlr-color__disabled);background-color:#fff3;cursor:default}:host input{border-radius:50%}:host input:checked:before{content:\"\";position:absolute;display:block;width:100%;height:100%;background:var(--stlr-color__info);box-sizing:border-box;border:solid 2px white;border-radius:50%;top:0;left:0}:host input{margin:0 var(--space--8) 0 0;z-index:2}:host input:focus{border:solid 2px var(--stlr-color__info);outline:solid 2px var(--stlr-color__info)}:host label{position:relative;display:inline-flex;align-items:center;font-size:.85em;line-height:1;transition:all .2s;cursor:pointer}:host label input{z-index:2}:host label[type=legacy]{padding:5px;background-color:#0000;font-size:14px;color:var(--stlr-color__copy--subdued)}:host label[type=legacy] small{color:var(--stlr-color__disabled)}:host label.disabled{color:var(--stlr-color__disabled)}:host label.disabled input{border-color:var(--stlr-color__disabled)}\n"] }]
    }], null, { checked: [{
            type: Input,
            args: [{ transform: (value) => coerceBooleanProperty(value) }]
        }, {
            type: HostBinding,
            args: ['class.checked']
        }], disabled: [{
            type: Input
        }, {
            type: HostBinding,
            args: ['class.disabled']
        }], name: [{
            type: Input
        }], value: [{
            type: Input
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(StlrRadioComponent, { className: "StlrRadioComponent", filePath: "lib/radio-group/radio/radio.component.ts", lineNumber: 19 }); })();

const _c0$4 = ["*"];
class StlrRadioGroupComponent extends StlrFormComponentValueAccessor {
    constructor() {
        super(...arguments);
        this.controlType = 'stlr-radio-group';
        this.uuid = generateUUID(2);
        this._hostClass = true;
        this.role = 'radiogroup';
        /**
         * Sets the orientation of the radio group as horizontal or vertical.
         * Default = vertical.
         */
        this.orientation = 'vertical';
        this.tabindex = 0;
    }
    /**
     * Allows for dynamically updating the list of radio buttons in the case of conditionally-displayed buttons.
     */
    get radios() {
        return this._radios;
    }
    set radios(radios) {
        this._radios = radios;
        this._updateRadioProperties();
    }
    onBlur() {
        if (this.ngControl?.control?.updateOn === 'blur')
            this._onChange(this.value);
        this._onTouched();
    }
    /**
     * The name of this radio group. This value is applied to all of the StlrRadio buttons.
     * Default = "stlr-radio-group-[UUID]".
     */
    get name() {
        return super.name ?? `${this.controlType}-${this.uuid}`;
    }
    set name(name) {
        super.name = name;
        this._radios?.forEach(r => r.name = name);
    }
    get isHorizontal() {
        return this.orientation === 'horizontal';
    }
    get isVertical() {
        return this.orientation === 'vertical';
    }
    get disabled() {
        return this.ngControl?.control?.disabled ?? super.disabled;
    }
    set disabled(isDisabled) {
        super.disabled = isDisabled;
        this._radios?.forEach(r => r.setGroupDisabled(this.disabled));
    }
    get value() {
        return super.value;
    }
    set value(value) {
        super.value = value;
        const radio = this._radios?.find(r => r.value === super.value);
        if (radio && !radio.checked)
            radio.checked = true;
    }
    setDisabledState(isDisabled) {
        super.setDisabledState(isDisabled);
        this._radios?.forEach(r => r.setGroupDisabled(this.disabled));
    }
    writeValue(value) {
        super.writeValue(value);
        const radio = this._radios?.find(r => r.value === this.value);
        if (radio && !radio.checked)
            radio.checked = true;
    }
    handleChange(value) {
        const hasChanged = !isEqual(this.value, value);
        this.value = value;
        if (hasChanged) {
            if (this.ngControl?.control?.updateOn === 'change')
                this._onChange(this.value);
            this.changed.emit(this.value);
        }
    }
    _updateRadioProperties() {
        this._radios?.forEach(r => {
            r.name = this.name;
            r.setGroupDisabled(this.disabled);
        });
        const radio = this._radios?.find(r => r.value === this.value);
        if (radio && !radio.checked)
            radio.checked = true;
    }
    static { this.ɵfac = /*@__PURE__*/ (() => { let ɵStlrRadioGroupComponent_BaseFactory; return function StlrRadioGroupComponent_Factory(__ngFactoryType__) { return (ɵStlrRadioGroupComponent_BaseFactory || (ɵStlrRadioGroupComponent_BaseFactory = i0.ɵɵgetInheritedFactory(StlrRadioGroupComponent)))(__ngFactoryType__ || StlrRadioGroupComponent); }; })(); }
    static { this.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: StlrRadioGroupComponent, selectors: [["stlr-radio-group"]], contentQueries: function StlrRadioGroupComponent_ContentQueries(rf, ctx, dirIndex) { if (rf & 1) {
            i0.ɵɵcontentQuery(dirIndex, StlrRadioComponent, 4);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.radios = _t);
        } }, hostVars: 8, hostBindings: function StlrRadioGroupComponent_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("focusout", function StlrRadioGroupComponent_focusout_HostBindingHandler() { return ctx.onBlur(); });
        } if (rf & 2) {
            i0.ɵɵattribute("role", ctx.role)("aria-orientation", ctx.orientation);
            i0.ɵɵclassProp("stlr-radio-group", ctx._hostClass)("horizontal", ctx.isHorizontal)("vertical", ctx.isVertical);
        } }, inputs: { name: "name", orientation: "orientation" }, standalone: false, features: [i0.ɵɵProvidersFeature([
                {
                    provide: STLR_RADIO_GROUP,
                    useExisting: forwardRef(() => StlrRadioGroupComponent),
                },
                {
                    provide: STLR_FORM_COMPONENT_BASE,
                    useExisting: forwardRef(() => StlrRadioGroupComponent),
                },
            ]), i0.ɵɵInheritDefinitionFeature], ngContentSelectors: _c0$4, decls: 1, vars: 0, template: function StlrRadioGroupComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵprojection(0);
        } }, styles: ["[_nghost-%COMP%]{display:flex;flex-direction:row;align-items:center;justify-content:flex-start;gap:.5rem}.horizontal[_nghost-%COMP%]{flex-direction:row}.vertical[_nghost-%COMP%]{flex-direction:column;align-items:flex-start}.ng-touched.ng-invalid[_nghost-%COMP%], .ng-invalid.ng-dirty[_nghost-%COMP%]{color:var(--stlr-color__error)}.ng-touched.ng-invalid[_nghost-%COMP%]   input[_ngcontent-%COMP%], .ng-invalid.ng-dirty[_nghost-%COMP%]   input[_ngcontent-%COMP%]{border-color:var(--stlr-color__error)}"], changeDetection: 0 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrRadioGroupComponent, [{
        type: Component,
        args: [{ selector: 'stlr-radio-group', changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                    {
                        provide: STLR_RADIO_GROUP,
                        useExisting: forwardRef(() => StlrRadioGroupComponent),
                    },
                    {
                        provide: STLR_FORM_COMPONENT_BASE,
                        useExisting: forwardRef(() => StlrRadioGroupComponent),
                    },
                ], standalone: false, template: "<ng-content></ng-content>\n", styles: [":host{display:flex;flex-direction:row;align-items:center;justify-content:flex-start;gap:.5rem}:host.horizontal{flex-direction:row}:host.vertical{flex-direction:column;align-items:flex-start}:host.ng-touched.ng-invalid,:host.ng-invalid.ng-dirty{color:var(--stlr-color__error)}:host.ng-touched.ng-invalid input,:host.ng-invalid.ng-dirty input{border-color:var(--stlr-color__error)}\n"] }]
    }], null, { radios: [{
            type: ContentChildren,
            args: [StlrRadioComponent]
        }], onBlur: [{
            type: HostListener,
            args: ['focusout']
        }], _hostClass: [{
            type: HostBinding,
            args: ['class.stlr-radio-group']
        }], role: [{
            type: HostBinding,
            args: ['attr.role']
        }], name: [{
            type: Input
        }], orientation: [{
            type: Input
        }, {
            type: HostBinding,
            args: ['attr.aria-orientation']
        }], isHorizontal: [{
            type: HostBinding,
            args: ['class.horizontal']
        }], isVertical: [{
            type: HostBinding,
            args: ['class.vertical']
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(StlrRadioGroupComponent, { className: "StlrRadioGroupComponent", filePath: "lib/radio-group/radio-group.component.ts", lineNumber: 36 }); })();

const RADIO_GROUP_API = [StlrRadioComponent, StlrRadioGroupComponent];

const _c0$3 = ["select"];
const _c1$2 = ["*"];
class StlrSelectComponent extends StlrFormComponentValueAccessor {
    constructor() {
        super(...arguments);
        this.controlType = 'stlr-select';
        this.autocomplete = 'off';
    }
    get elementRef() {
        return this._elementRef;
    }
    /**
     * Updates this component with the passed value and executes change events.
     * This is called by the Option Component.
     * @param value The value to apply to this component.
     */
    handleChange(value) {
        const hasChanged = !isEqual(this.value, value);
        this.value = value;
        if (hasChanged) {
            if (this.ngControl?.control?.updateOn === 'change')
                this._onChange(this.value);
            this.changed.emit(this.value);
        }
    }
    handleSelectChange($event) {
        const { value } = $event.target;
        const number = parseInt(value);
        const calcValue = (value === 'null' || value === 'undefined'
            ? null
            : (this._options?.length ?? 0) > 0
                ? typeof this._options?.last.value === 'number' && !isNaN(number)
                    ? number
                    : value
                : this.value && typeof this.value === 'number' && !isNaN(number)
                    ? number
                    : value);
        this.handleChange(calcValue);
    }
    handleSelectBlur() {
        if (this.ngControl?.control?.updateOn === 'blur')
            this._onChange(this.value);
        this._onTouched();
    }
    static { this.ɵfac = /*@__PURE__*/ (() => { let ɵStlrSelectComponent_BaseFactory; return function StlrSelectComponent_Factory(__ngFactoryType__) { return (ɵStlrSelectComponent_BaseFactory || (ɵStlrSelectComponent_BaseFactory = i0.ɵɵgetInheritedFactory(StlrSelectComponent)))(__ngFactoryType__ || StlrSelectComponent); }; })(); }
    static { this.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: StlrSelectComponent, selectors: [["stlr-select"]], contentQueries: function StlrSelectComponent_ContentQueries(rf, ctx, dirIndex) { if (rf & 1) {
            i0.ɵɵcontentQuery(dirIndex, StlrOptionComponent, 4);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._options = _t);
        } }, viewQuery: function StlrSelectComponent_Query(rf, ctx) { if (rf & 1) {
            i0.ɵɵviewQuery(_c0$3, 7, ElementRef);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._elementRef = _t.first);
        } }, inputs: { autocomplete: "autocomplete" }, standalone: false, features: [i0.ɵɵProvidersFeature([
                {
                    provide: STLR_OPTION_PARENT_COMPONENT,
                    useExisting: forwardRef(() => StlrSelectComponent),
                },
                {
                    provide: STLR_FORM_COMPONENT_BASE,
                    useExisting: forwardRef(() => StlrSelectComponent),
                },
            ]), i0.ɵɵInheritDefinitionFeature], ngContentSelectors: _c1$2, decls: 3, vars: 19, consts: [["select", ""], [3, "blur", "change", "disabled", "id", "name", "tabindex", "value", "required"]], template: function StlrSelectComponent_Template(rf, ctx) { if (rf & 1) {
            const _r1 = i0.ɵɵgetCurrentView();
            i0.ɵɵprojectionDef();
            i0.ɵɵelementStart(0, "select", 1, 0);
            i0.ɵɵlistener("blur", function StlrSelectComponent_Template_select_blur_0_listener() { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.handleSelectBlur()); })("change", function StlrSelectComponent_Template_select_change_0_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.handleSelectChange($event)); });
            i0.ɵɵprojection(2);
            i0.ɵɵelementEnd();
        } if (rf & 2) {
            i0.ɵɵclassProp("disabled", ctx.disabled)("invalid", ctx.invalid);
            i0.ɵɵproperty("disabled", ctx.disabled)("id", ctx.id)("name", ctx.name)("tabindex", ctx.tabindex)("value", ctx.value)("required", ctx.required);
            i0.ɵɵattribute("aria-describedby", ctx.ariaDescribedBy)("aria-disabled", ctx.disabled)("aria-invalid", ctx.invalid)("aria-label", ctx.ariaLabel)("aria-labelledby", ctx.ariaLabelledBy)("aria-required", ctx.required)("autocomplete", ctx.autocomplete)("enterkeyhint", ctx.enterKeyHint)("placeholder", ctx.placeholder);
        } }, styles: ["[_nghost-%COMP%]{position:relative}.full[_nghost-%COMP%]{width:100%}[_nghost-%COMP%]:after{font-family:Material Icons;content:\"expand_more\";display:flex;position:absolute;right:10px;top:0;bottom:0;margin:auto;align-items:center;font-size:20px;-webkit-user-select:none;-khtml-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none;pointer-events:none}.ng-touched.ng-invalid[_nghost-%COMP%]   select[_ngcontent-%COMP%]:not(.disabled):not(:focus), .ng-dirty.ng-invalid[_nghost-%COMP%]   select[_ngcontent-%COMP%]:not(.disabled):not(:focus){border-color:var(--stlr-color__error);background-color:var(--stlr-color__error--extra-light)}[_nghost-%COMP%]   select[_ngcontent-%COMP%]{box-sizing:border-box;padding:0 10px;border:solid 1px rgba(0,0,0,.25);background-color:#fff;color:var(--stlr-color__copy);appearance:none;border-radius:var(--stlr-border-radius);outline:solid 1px rgba(255,255,255,0);transition:all .2s;box-shadow:0 0 2px #59a9ff00;font-family:var(--stlr-font-family--copy)}[_nghost-%COMP%]   select[_ngcontent-%COMP%]:not(textarea){height:2.25rem}[_nghost-%COMP%]   select[_ngcontent-%COMP%]::-webkit-search-cancel-button{-webkit-appearance:none;height:16px;width:16px;display:block;background-color:#0000004d;border-radius:10px;background-repeat:no-repeat;background-position:center center;cursor:pointer;background-image:url(\"data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9.5 3.205L8.795 2.5L6 5.295L3.205 2.5L2.5 3.205L5.295 6L2.5 8.795L3.205 9.5L6 6.705L8.795 9.5L9.5 8.795L6.705 6L9.5 3.205Z' fill='white'/%3E%3C/svg%3E%0A\")}[_nghost-%COMP%]   select[_ngcontent-%COMP%]:hover{box-shadow:0 0 6px #0000001a}[_nghost-%COMP%]   select[_ngcontent-%COMP%]:focus{border:solid 1px var(--stlr-color__info);outline:solid 1px var(--stlr-color__info)}[_nghost-%COMP%]   select[disabled][_ngcontent-%COMP%]{color:var(--stlr-color__disabled--light);border:solid 1px rgba(0,0,0,.2);background-color:#00000006}[_nghost-%COMP%]   select.ng-invalid.ng-touched[_ngcontent-%COMP%], [_nghost-%COMP%]   select.ng-invalid.ng-dirty[_ngcontent-%COMP%]{border-color:var(--stlr-color__error);background-color:var(--stlr-color__error--extra-light)}[_nghost-%COMP%]   select.ng-invalid.ng-touched[_ngcontent-%COMP%]:focus, [_nghost-%COMP%]   select.ng-invalid.ng-dirty[_ngcontent-%COMP%]:focus{background-color:#fff;border:solid 1px var(--stlr-color__info)}[_nghost-%COMP%]   select[_ngcontent-%COMP%]:focus::placeholder{color:transparent}[_nghost-%COMP%]   select[_ngcontent-%COMP%]{padding:0 30px 0 10px;width:100%;border-radius:var(--stlr-border-radius);background:#fff}"], changeDetection: 0 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrSelectComponent, [{
        type: Component,
        args: [{ selector: 'stlr-select', changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                    {
                        provide: STLR_OPTION_PARENT_COMPONENT,
                        useExisting: forwardRef(() => StlrSelectComponent),
                    },
                    {
                        provide: STLR_FORM_COMPONENT_BASE,
                        useExisting: forwardRef(() => StlrSelectComponent),
                    },
                ], standalone: false, template: "<select\n    #select\n    (blur)=\"handleSelectBlur()\"\n    (change)=\"handleSelectChange($event)\"\n    [attr.aria-describedby]=\"ariaDescribedBy\"\n    [attr.aria-disabled]=\"disabled\"\n    [attr.aria-invalid]=\"invalid\"\n    [attr.aria-label]=\"ariaLabel\"\n    [attr.aria-labelledby]=\"ariaLabelledBy\"\n    [attr.aria-required]=\"required\"\n    [attr.autocomplete]=\"autocomplete\"\n    [attr.enterkeyhint]=\"enterKeyHint\"\n    [attr.placeholder]=\"placeholder\"\n    [class.disabled]=\"disabled\"\n    [class.invalid]=\"invalid\"\n    [disabled]=\"disabled\"\n    [id]=\"id\"\n    [name]=\"name\"\n    [tabindex]=\"tabindex\"\n    [value]=\"value\"\n    [required]=\"required\"\n>\n    <ng-content></ng-content>\n</select>\n", styles: [":host{position:relative}:host.full{width:100%}:host:after{font-family:Material Icons;content:\"expand_more\";display:flex;position:absolute;right:10px;top:0;bottom:0;margin:auto;align-items:center;font-size:20px;-webkit-user-select:none;-khtml-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none;pointer-events:none}:host.ng-touched.ng-invalid select:not(.disabled):not(:focus),:host.ng-dirty.ng-invalid select:not(.disabled):not(:focus){border-color:var(--stlr-color__error);background-color:var(--stlr-color__error--extra-light)}:host select{box-sizing:border-box;padding:0 10px;border:solid 1px rgba(0,0,0,.25);background-color:#fff;color:var(--stlr-color__copy);appearance:none;border-radius:var(--stlr-border-radius);outline:solid 1px rgba(255,255,255,0);transition:all .2s;box-shadow:0 0 2px #59a9ff00;font-family:var(--stlr-font-family--copy)}:host select:not(textarea){height:2.25rem}:host select::-webkit-search-cancel-button{-webkit-appearance:none;height:16px;width:16px;display:block;background-color:#0000004d;border-radius:10px;background-repeat:no-repeat;background-position:center center;cursor:pointer;background-image:url(\"data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9.5 3.205L8.795 2.5L6 5.295L3.205 2.5L2.5 3.205L5.295 6L2.5 8.795L3.205 9.5L6 6.705L8.795 9.5L9.5 8.795L6.705 6L9.5 3.205Z' fill='white'/%3E%3C/svg%3E%0A\")}:host select:hover{box-shadow:0 0 6px #0000001a}:host select:focus{border:solid 1px var(--stlr-color__info);outline:solid 1px var(--stlr-color__info)}:host select[disabled]{color:var(--stlr-color__disabled--light);border:solid 1px rgba(0,0,0,.2);background-color:#00000006}:host select.ng-invalid.ng-touched,:host select.ng-invalid.ng-dirty{border-color:var(--stlr-color__error);background-color:var(--stlr-color__error--extra-light)}:host select.ng-invalid.ng-touched:focus,:host select.ng-invalid.ng-dirty:focus{background-color:#fff;border:solid 1px var(--stlr-color__info)}:host select:focus::placeholder{color:transparent}:host select{padding:0 30px 0 10px;width:100%;border-radius:var(--stlr-border-radius);background:#fff}\n"] }]
    }], null, { _elementRef: [{
            type: ViewChild,
            args: ['select', { static: true, read: ElementRef }]
        }], _options: [{
            type: ContentChildren,
            args: [StlrOptionComponent]
        }], autocomplete: [{
            type: Input
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(StlrSelectComponent, { className: "StlrSelectComponent", filePath: "lib/select/select.component.ts", lineNumber: 26 }); })();

const SELECT_API = [StlrSelectComponent];

const _c0$2 = ["stlr-toggle", ""];
class StlrToggleComponent extends StlrFormComponentValueAccessor {
    constructor() {
        super(...arguments);
        this.controlType = 'stlr-toggle';
        this.type = 'checkbox';
        this.tabindex = null;
        this.ariaDescribedBy = null;
        this.ariaLabel = null;
        this.ariaLabelledBy = null;
    }
    onChange(event) {
        const checked = event.target.checked;
        const hasChanged = !isEqual(this.checked, checked);
        this.value = checked;
        if (hasChanged) {
            if (this.ngControl?.control?.updateOn === 'change')
                this._onChange(this.value);
            this.changed.emit(this.value);
        }
    }
    onBlur() {
        if (this.ngControl?.control?.updateOn === 'blur')
            this._onChange(this.value);
        this._onTouched();
    }
    get name() {
        return super.name;
    }
    set name(name) {
        super.name = name;
    }
    set checked(value) {
        super.value = value;
        this.changeDetectorRef.markForCheck();
    }
    get checked() {
        return this.value ?? false;
    }
    get disabled() {
        return super.disabled;
    }
    set disabled(isDisabled) {
        super.disabled = isDisabled;
    }
    get required() {
        return super.required;
    }
    set required(isRequired) {
        super.required = isRequired;
    }
    get ariaDisabled() {
        return super.disabled;
    }
    get ariaInvalid() {
        return super.invalid;
    }
    get ariaRequired() {
        return super.required;
    }
    set invalid(isInvalid) {
        super.invalid = isInvalid;
    }
    get invalid() {
        return super.invalid;
    }
    writeValue(value) {
        this.checked = value ?? false;
    }
    static { this.ɵfac = /*@__PURE__*/ (() => { let ɵStlrToggleComponent_BaseFactory; return function StlrToggleComponent_Factory(__ngFactoryType__) { return (ɵStlrToggleComponent_BaseFactory || (ɵStlrToggleComponent_BaseFactory = i0.ɵɵgetInheritedFactory(StlrToggleComponent)))(__ngFactoryType__ || StlrToggleComponent); }; })(); }
    static { this.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: StlrToggleComponent, selectors: [["input", "stlr-toggle", ""], ["input", "stlrToggle", ""]], hostVars: 19, hostBindings: function StlrToggleComponent_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("change", function StlrToggleComponent_change_HostBindingHandler($event) { return ctx.onChange($event); })("focusout", function StlrToggleComponent_focusout_HostBindingHandler() { return ctx.onBlur(); });
        } if (rf & 2) {
            i0.ɵɵdomProperty("type", ctx.type)("id", ctx.id)("name", ctx.name)("checked", ctx.checked)("disabled", ctx.disabled)("required", ctx.required)("tabIndex", ctx.tabindex);
            i0.ɵɵattribute("aria-disabled", ctx.ariaDisabled)("aria-invalid", ctx.ariaInvalid)("aria-required", ctx.ariaRequired)("aria-describedby", ctx.ariaDescribedBy)("aria-label", ctx.ariaLabel)("aria-labelledby", ctx.ariaLabelledBy);
            i0.ɵɵclassProp("checked", ctx.checked)("disabled", ctx.disabled)("invalid", ctx.invalid);
        } }, inputs: { data: "data", checked: [2, "checked", "checked", (value) => coerceBooleanProperty(value)] }, standalone: false, features: [i0.ɵɵProvidersFeature([
                {
                    provide: STLR_FORM_COMPONENT_BASE,
                    useExisting: forwardRef(() => StlrToggleComponent),
                    multi: true
                },
            ]), i0.ɵɵInheritDefinitionFeature], attrs: _c0$2, decls: 0, vars: 0, template: function StlrToggleComponent_Template(rf, ctx) { }, styles: ["[_nghost-%COMP%]{position:relative;display:inline-block;max-width:40px;min-width:40px;height:24px;background-color:var(--stlr-color__disabled);border-radius:12px;transition:.1s ease-in-out;appearance:none;cursor:pointer}[_nghost-%COMP%]:before{position:absolute;content:\"\";height:16px;width:16px;left:4px;top:4px;background-color:#fff;transition:.1s ease-in-out;border-radius:50%}.checked[_nghost-%COMP%]{background-color:var(--stlr-color__info)}.checked[_nghost-%COMP%]:before{transform:translate(16px)}.disabled[_nghost-%COMP%]{background-color:var(--stlr-color__surface--light)}"], changeDetection: 0 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrToggleComponent, [{
        type: Component,
        args: [{ selector: 'input[stlr-toggle], input[stlrToggle]', template: '', changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                    {
                        provide: STLR_FORM_COMPONENT_BASE,
                        useExisting: forwardRef(() => StlrToggleComponent),
                        multi: true
                    },
                ], standalone: false, styles: [":host{position:relative;display:inline-block;max-width:40px;min-width:40px;height:24px;background-color:var(--stlr-color__disabled);border-radius:12px;transition:.1s ease-in-out;appearance:none;cursor:pointer}:host:before{position:absolute;content:\"\";height:16px;width:16px;left:4px;top:4px;background-color:#fff;transition:.1s ease-in-out;border-radius:50%}:host.checked{background-color:var(--stlr-color__info)}:host.checked:before{transform:translate(16px)}:host.disabled{background-color:var(--stlr-color__surface--light)}\n"] }]
    }], null, { onChange: [{
            type: HostListener,
            args: ['change', ['$event']]
        }], onBlur: [{
            type: HostListener,
            args: ['focusout']
        }], type: [{
            type: HostBinding
        }], id: [{
            type: HostBinding
        }], data: [{
            type: Input
        }], name: [{
            type: HostBinding
        }], checked: [{
            type: Input,
            args: [{ transform: (value) => coerceBooleanProperty(value) }]
        }, {
            type: HostBinding,
            args: ['checked']
        }, {
            type: HostBinding,
            args: ['class.checked']
        }], disabled: [{
            type: HostBinding,
            args: ['disabled']
        }, {
            type: HostBinding,
            args: ['class.disabled']
        }], required: [{
            type: HostBinding
        }], ariaDisabled: [{
            type: HostBinding,
            args: ['attr.aria-disabled']
        }], ariaInvalid: [{
            type: HostBinding,
            args: ['attr.aria-invalid']
        }], ariaRequired: [{
            type: HostBinding,
            args: ['attr.aria-required']
        }], invalid: [{
            type: HostBinding,
            args: ['class.invalid']
        }], tabindex: [{
            type: HostBinding
        }], ariaDescribedBy: [{
            type: HostBinding,
            args: ['attr.aria-describedby']
        }], ariaLabel: [{
            type: HostBinding,
            args: ['attr.aria-label']
        }], ariaLabelledBy: [{
            type: HostBinding,
            args: ['attr.aria-labelledby']
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(StlrToggleComponent, { className: "StlrToggleComponent", filePath: "lib/toggle/toggle.component.ts", lineNumber: 29 }); })();

const TOGGLE_API = [StlrToggleComponent];

function StlrRegionSelectComponent_For_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "option", 1);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const region_r1 = ctx.$implicit;
    i0.ɵɵproperty("value", region_r1.name);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", region_r1.name, " ");
} }
const BASE_REGION_NAME$1 = 'Region';
class StlrRegionSelectComponent extends StlrFormComponentValueAccessor {
    constructor() {
        super(...arguments);
        // Temporarily required for backwards compatibility with regionData input
        this._countryService = inject(CountryService);
        this.controlType = 'stlr-region-select';
        this.regionsSubject = new ReplaySubject(1);
        this.regions$ = this.regionsSubject.asObservable();
        this.regionNameSubject = new ReplaySubject(1);
        this.regionName$ = this.regionNameSubject.asObservable();
        /**
         * Automatically selects the first matching country using 'startsWith' when the user leaves the input.
         * If this is false and the input has no match, no country will be selected and the input will revert
         * to its original value.
         * Default = true.
         */
        this.allowPartialMatching = true;
        /**
         * Apply the filter whenever the drop down opens or re-opens. If false, the filter is not
         * applied and all possible countries are displayed until the user changes the value.
         * Default = true.
         */
        this.applyFilterOnOpen = true;
        /**
         * A browser hint for auto-filling form data.
         */
        this.autocomplete = 'address-level1';
        /**
         * Determines how auto-filtering matches user input with countries.
         * Default = 'contains'.
         */
        this.autoFilterMatching = 'contains';
        /**
         * Close the drop down if it is open and the input element is clicked.
         * Default = true.
         */
        this.closeOnClick = true;
        /**
         * Show autocomplete suggestions when clicking on the input element.
         * Default = true.
         */
        this.openOnClick = true;
        /**
         * Show autocomplete suggestions when entering (e.g. tabbing into) the input element.
         * Default = false.
         */
        this.openOnFocus = false;
        /**
         * Show autocomplete suggestions when inputting characters into the input element.
         * Default = false.
         */
        this.openOnInput = false;
        /**
         * Identifies how to handle non-option-matching input.
         * When true, revert to the previous value.
         * When false, set the value to null.
         * Default = false.
         */
        this.revertOnNoMatch = false;
        /**
         * Auto-select a country based on continuing user input.
         * Default = true.
         */
        this.useTypeAhead = true;
        this.width = null;
        /**
         * Emits IRegion whenever the value for this component is set.
         * Contains the region name and, if available, the region code, e.g. CA, NC, AZ, CO.
         */
        this.region = new EventEmitter();
        this.displayFn = (codeOrName) => this.regions$.pipe(first(), map(regions => regions.find(r => r.name.toLowerCase() === codeOrName?.toLowerCase() ||
            (r.code && r.code.toLowerCase() === codeOrName?.toLowerCase()))?.name
            ?? null));
        this.valueFn = (codeOrName) => this.regions$.pipe(first(), map(regions => regions.find(r => r.name.toLowerCase() === codeOrName?.toLowerCase() ||
            (r.code && r.code.toLowerCase() === codeOrName?.toLowerCase()))?.name
            ?? null));
    }
    set country(country) {
        const previousCountryCode = this._country?.code;
        this._country = country;
        this.regionsSubject.next(this._country?.regions ?? []);
        this.regionNameSubject.next(this._country?.regionName ?? BASE_REGION_NAME$1);
        this.required = (this._country?.regions?.length ?? 0) > 0;
        // Specifically check for undefined, which means it has never been set before.
        if (previousCountryCode !== undefined && previousCountryCode !== country?.code) {
            this.handleChange(null); // Reset value if country has previously been set and it changes
        }
    }
    get country() {
        return this._country ?? null;
    }
    /** @deprecated Set the country input instead. Will be removed in v3. */
    set regionData(data) {
        const previousRegionData = this._regionData;
        this._regionData = data ?? null;
        this._countryService.countries$.pipe(first()).subscribe(countries => {
            const regions = countries.find(c => (data?.regions?.length ?? 0) > 0 &&
                isEqual(c.regions.map(r => r.name), data.regions))?.regions || data?.regions?.map(r => ({ name: r })) || [];
            this.regionsSubject.next(regions);
            this.regionNameSubject.next(data?.name ?? BASE_REGION_NAME$1);
            this.required = regions.length > 0;
        });
        // Specifically check for undefined, which means it has never been set before.
        if (previousRegionData !== undefined && !isEqual((previousRegionData ?? null), (data ?? null))) {
            this.handleChange(null); // Reset value if region data has already been set and it changes
        }
    }
    ngOnInit() {
        super.ngOnInit();
        this.selectorId = `${this.id}-container`;
    }
    writeValue(name) {
        super.writeValue(name);
        this.region.emit(name ? (this._country?.regions?.find(r => r.name?.toLowerCase() === name.toLowerCase()) ?? null) : null);
    }
    handleChange(name) {
        const hasChanged = !isEqual(this.value, name);
        this.value = name;
        if (hasChanged) {
            if (this.ngControl?.control?.updateOn === 'change')
                this._onChange(this.value);
            this.changed.emit(this.value);
            this.region.emit(name ? (this._country?.regions?.find(r => r.name?.toLowerCase() === name.toLowerCase()) ?? null) : null);
        }
    }
    handleBlur() {
        if (this.ngControl?.control?.updateOn === 'blur')
            this._onChange(this.value);
        this._onTouched();
    }
    static { this.ɵfac = /*@__PURE__*/ (() => { let ɵStlrRegionSelectComponent_BaseFactory; return function StlrRegionSelectComponent_Factory(__ngFactoryType__) { return (ɵStlrRegionSelectComponent_BaseFactory || (ɵStlrRegionSelectComponent_BaseFactory = i0.ɵɵgetInheritedFactory(StlrRegionSelectComponent)))(__ngFactoryType__ || StlrRegionSelectComponent); }; })(); }
    static { this.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: StlrRegionSelectComponent, selectors: [["stlr-region-select"]], hostVars: 3, hostBindings: function StlrRegionSelectComponent_HostBindings(rf, ctx) { if (rf & 2) {
            i0.ɵɵdomProperty("id", ctx.selectorId);
            i0.ɵɵclassProp("full", ctx.width);
        } }, inputs: { allowPartialMatching: "allowPartialMatching", applyFilterOnOpen: "applyFilterOnOpen", autocomplete: "autocomplete", autoFilterMatching: "autoFilterMatching", country: "country", closeOnClick: "closeOnClick", openOnClick: "openOnClick", openOnFocus: "openOnFocus", openOnInput: "openOnInput", regionData: "regionData", revertOnNoMatch: "revertOnNoMatch", useTypeAhead: "useTypeAhead", width: "width" }, outputs: { region: "region" }, standalone: false, features: [i0.ɵɵProvidersFeature([
                {
                    provide: STLR_FORM_COMPONENT_BASE,
                    useExisting: forwardRef(() => StlrRegionSelectComponent),
                    multi: true
                },
            ]), i0.ɵɵInheritDefinitionFeature], decls: 6, vars: 33, consts: [[3, "focusLost", "changed", "allowNewOptions", "allowPartialMatching", "applyFilterOnOpen", "aria-describedby", "aria-label", "aria-labelledby", "autocomplete", "autoFilter", "autoFilterMatching", "closeOnClick", "disabled", "displayFn", "enterKeyHint", "id", "invalid", "name", "openOnClick", "openOnFocus", "openOnInput", "placeholder", "required", "revertOnNoMatch", "tabindex", "useTypeAhead", "valueFn", "value", "width"], ["stlrOption", "", 3, "value"]], template: function StlrRegionSelectComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵelementStart(0, "stlr-autocomplete", 0);
            i0.ɵɵpipe(1, "async");
            i0.ɵɵpipe(2, "async");
            i0.ɵɵlistener("focusLost", function StlrRegionSelectComponent_Template_stlr_autocomplete_focusLost_0_listener() { return ctx.handleBlur(); })("changed", function StlrRegionSelectComponent_Template_stlr_autocomplete_changed_0_listener($event) { return ctx.handleChange($event); });
            i0.ɵɵrepeaterCreate(3, StlrRegionSelectComponent_For_4_Template, 2, 2, "option", 1, i0.ɵɵrepeaterTrackByIdentity);
            i0.ɵɵpipe(5, "async");
            i0.ɵɵelementEnd();
        } if (rf & 2) {
            let tmp_19_0;
            i0.ɵɵproperty("allowNewOptions", false)("allowPartialMatching", ctx.allowPartialMatching)("applyFilterOnOpen", ctx.applyFilterOnOpen);
            i0.ɵɵariaProperty("aria-describedby", ctx.ariaDescribedBy)("aria-label", ctx.ariaLabel)("aria-labelledby", ctx.ariaLabelledBy);
            i0.ɵɵproperty("autocomplete", ctx.autocomplete)("autoFilter", true)("autoFilterMatching", ctx.autoFilterMatching)("closeOnClick", ctx.closeOnClick)("disabled", ctx.disabled)("displayFn", ctx.displayFn)("enterKeyHint", ctx.enterKeyHint)("id", ctx.id)("invalid", ctx.invalid)("name", ctx.name)("openOnClick", ctx.openOnClick)("openOnFocus", ctx.openOnFocus)("openOnInput", ctx.openOnInput)("placeholder", ctx.placeholder || "Select " + (((tmp_19_0 = i0.ɵɵpipeBind1(1, 27, ctx.regionName$)) == null ? null : tmp_19_0.match("(A|E|I|O|U).*")) ? "an" : "a") + " " + i0.ɵɵpipeBind1(2, 29, ctx.regionName$))("required", ctx.required)("revertOnNoMatch", ctx.revertOnNoMatch)("tabindex", ctx.tabindex)("useTypeAhead", ctx.useTypeAhead)("valueFn", ctx.valueFn)("value", ctx.value)("width", ctx.width);
            i0.ɵɵadvance(3);
            i0.ɵɵrepeater(i0.ɵɵpipeBind1(5, 31, ctx.regions$));
        } }, dependencies: [i1.NgSelectOption, i1.ɵNgSelectMultipleOption, StlrAutocompleteComponent, StlrOptionComponent, i1$1.AsyncPipe], styles: ["[_nghost-%COMP%]{display:flex;position:relative}.full[_nghost-%COMP%]{width:100%}.ng-touched.ng-invalid[_nghost-%COMP%]     input:not(.disabled):not(:focus), .ng-dirty.ng-invalid[_nghost-%COMP%]     input:not(.disabled):not(:focus){border-color:var(--stlr-color__error);background-color:var(--stlr-color__error--extra-light)}[_nghost-%COMP%]     input{box-sizing:border-box;padding:0 10px;border:solid 1px rgba(0,0,0,.25);background-color:#fff;color:var(--stlr-color__copy);appearance:none;border-radius:var(--stlr-border-radius);outline:solid 1px rgba(255,255,255,0);transition:all .2s;box-shadow:0 0 2px #59a9ff00;font-family:var(--stlr-font-family--copy)}[_nghost-%COMP%]     input:not(textarea){height:2.25rem}[_nghost-%COMP%]     input::-webkit-search-cancel-button{-webkit-appearance:none;height:16px;width:16px;display:block;background-color:#0000004d;border-radius:10px;background-repeat:no-repeat;background-position:center center;cursor:pointer;background-image:url(\"data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9.5 3.205L8.795 2.5L6 5.295L3.205 2.5L2.5 3.205L5.295 6L2.5 8.795L3.205 9.5L6 6.705L8.795 9.5L9.5 8.795L6.705 6L9.5 3.205Z' fill='white'/%3E%3C/svg%3E%0A\")}[_nghost-%COMP%]     input:hover{box-shadow:0 0 6px #0000001a}[_nghost-%COMP%]     input:focus{border:solid 1px var(--stlr-color__info);outline:solid 1px var(--stlr-color__info)}[_nghost-%COMP%]     input[disabled]{color:var(--stlr-color__disabled--light);border:solid 1px rgba(0,0,0,.2);background-color:#00000006}[_nghost-%COMP%]     input.ng-invalid.ng-touched, [_nghost-%COMP%]     input.ng-invalid.ng-dirty{border-color:var(--stlr-color__error);background-color:var(--stlr-color__error--extra-light)}[_nghost-%COMP%]     input.ng-invalid.ng-touched:focus, [_nghost-%COMP%]     input.ng-invalid.ng-dirty:focus{background-color:#fff;border:solid 1px var(--stlr-color__info)}[_nghost-%COMP%]     input{width:100%}[_nghost-%COMP%]     input:focus::placeholder{color:transparent}[_nghost-%COMP%]     input::-webkit-calendar-picker-indicator{display:none!important}"], changeDetection: 0 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrRegionSelectComponent, [{
        type: Component,
        args: [{ selector: 'stlr-region-select', changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                    {
                        provide: STLR_FORM_COMPONENT_BASE,
                        useExisting: forwardRef(() => StlrRegionSelectComponent),
                        multi: true
                    },
                ], standalone: false, template: "<stlr-autocomplete\n  (focusLost)=\"handleBlur()\"\n  (changed)=\"handleChange($event)\"\n  [allowNewOptions]=\"false\"\n  [allowPartialMatching]=\"allowPartialMatching\"\n  [applyFilterOnOpen]=\"applyFilterOnOpen\"\n  [aria-describedby]=\"ariaDescribedBy\"\n  [aria-label]=\"ariaLabel\"\n  [aria-labelledby]=\"ariaLabelledBy\"\n  [autocomplete]=\"autocomplete\"\n  [autoFilter]=\"true\"\n  [autoFilterMatching]=\"autoFilterMatching\"\n  [closeOnClick]=\"closeOnClick\"\n  [disabled]=\"disabled\"\n  [displayFn]=\"displayFn\"\n  [enterKeyHint]=\"enterKeyHint\"\n  [id]=\"id\"\n  [invalid]=\"invalid\"\n  [name]=\"name\"\n  [openOnClick]=\"openOnClick\"\n  [openOnFocus]=\"openOnFocus\"\n  [openOnInput]=\"openOnInput\"\n  [placeholder]=\"placeholder || ('Select ' + ((regionName$ | async)?.match('(A|E|I|O|U).*') ? 'an' : 'a') + ' ' + (regionName$ | async))\"\n  [required]=\"required\"\n  [revertOnNoMatch]=\"revertOnNoMatch\"\n  [tabindex]=\"tabindex\"\n  [useTypeAhead]=\"useTypeAhead\"\n  [valueFn]=\"valueFn\"\n  [value]=\"value\"\n  [width]=\"width\">\n  @for (region of regions$ | async; track region) {\n    <option stlrOption [value]=\"region.name\">\n      {{ region.name }}\n    </option>\n  }\n</stlr-autocomplete>", styles: [":host{display:flex;position:relative}:host.full{width:100%}:host.ng-touched.ng-invalid ::ng-deep input:not(.disabled):not(:focus),:host.ng-dirty.ng-invalid ::ng-deep input:not(.disabled):not(:focus){border-color:var(--stlr-color__error);background-color:var(--stlr-color__error--extra-light)}:host ::ng-deep input{box-sizing:border-box;padding:0 10px;border:solid 1px rgba(0,0,0,.25);background-color:#fff;color:var(--stlr-color__copy);appearance:none;border-radius:var(--stlr-border-radius);outline:solid 1px rgba(255,255,255,0);transition:all .2s;box-shadow:0 0 2px #59a9ff00;font-family:var(--stlr-font-family--copy)}:host ::ng-deep input:not(textarea){height:2.25rem}:host ::ng-deep input::-webkit-search-cancel-button{-webkit-appearance:none;height:16px;width:16px;display:block;background-color:#0000004d;border-radius:10px;background-repeat:no-repeat;background-position:center center;cursor:pointer;background-image:url(\"data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9.5 3.205L8.795 2.5L6 5.295L3.205 2.5L2.5 3.205L5.295 6L2.5 8.795L3.205 9.5L6 6.705L8.795 9.5L9.5 8.795L6.705 6L9.5 3.205Z' fill='white'/%3E%3C/svg%3E%0A\")}:host ::ng-deep input:hover{box-shadow:0 0 6px #0000001a}:host ::ng-deep input:focus{border:solid 1px var(--stlr-color__info);outline:solid 1px var(--stlr-color__info)}:host ::ng-deep input[disabled]{color:var(--stlr-color__disabled--light);border:solid 1px rgba(0,0,0,.2);background-color:#00000006}:host ::ng-deep input.ng-invalid.ng-touched,:host ::ng-deep input.ng-invalid.ng-dirty{border-color:var(--stlr-color__error);background-color:var(--stlr-color__error--extra-light)}:host ::ng-deep input.ng-invalid.ng-touched:focus,:host ::ng-deep input.ng-invalid.ng-dirty:focus{background-color:#fff;border:solid 1px var(--stlr-color__info)}:host ::ng-deep input{width:100%}:host ::ng-deep input:focus::placeholder{color:transparent}:host ::ng-deep input::-webkit-calendar-picker-indicator{display:none!important}\n"] }]
    }], null, { allowPartialMatching: [{
            type: Input
        }], applyFilterOnOpen: [{
            type: Input
        }], autocomplete: [{
            type: Input
        }], autoFilterMatching: [{
            type: Input
        }], country: [{
            type: Input
        }], closeOnClick: [{
            type: Input
        }], openOnClick: [{
            type: Input
        }], openOnFocus: [{
            type: Input
        }], openOnInput: [{
            type: Input
        }], regionData: [{
            type: Input
        }], revertOnNoMatch: [{
            type: Input
        }], useTypeAhead: [{
            type: Input
        }], width: [{
            type: Input
        }, {
            type: HostBinding,
            args: ['class.full']
        }], region: [{
            type: Output
        }], selectorId: [{
            type: HostBinding,
            args: ['id']
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(StlrRegionSelectComponent, { className: "StlrRegionSelectComponent", filePath: "lib/region-select/region-select.component.ts", lineNumber: 37 }); })();

const REGION_SELECT_API = [
    StlrRegionSelectComponent,
];

/** @deprecated The component input that expects this is deprecated. This will be removed in the next major release. */
class RegionData {
    constructor(name, regions) {
        this.name = name;
        this.regions = regions;
    }
}

function StlrCountrySelectComponent_For_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "option", 1);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const country_r1 = ctx.$implicit;
    i0.ɵɵproperty("value", country_r1.code);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", country_r1.name, " ");
} }
const BASE_REGION_NAME = 'Region';
class StlrCountrySelectComponent extends StlrFormComponentValueAccessor {
    constructor() {
        super(...arguments);
        this._countryService = inject(CountryService);
        this.controlType = 'stlr-country-select';
        this.countries$ = this._countryService.countries$;
        /**
         * Automatically selects the first matching country using 'startsWith' when the user leaves the input.
         * If this is false and the input has no match, no country will be selected and the input will revert
         * to its original value.
         * Default = true.
         */
        this.allowPartialMatching = true;
        /**
         * Apply the filter whenever the drop down opens or re-opens. If false, the filter is not
         * applied and all possible countries are displayed until the user changes the value.
         * Default = true.
         */
        this.applyFilterOnOpen = true;
        /**
         * A browser hint for auto-filling form data.
         */
        this.autocomplete = 'country country-name';
        /**
         * Determines how auto-filtering matches user input with countries.
         * Default = 'contains'.
         */
        this.autoFilterMatching = 'contains';
        /**
         * Close the drop down if it is open and the input element is clicked.
         * Default = true.
         */
        this.closeOnClick = true;
        /**
         * Show autocomplete suggestions when clicking on the input element.
         * Default = true.
         */
        this.openOnClick = true;
        /**
         * Show autocomplete suggestions when entering (e.g. tabbing into) the input element.
         * Default = false.
         */
        this.openOnFocus = false;
        /**
         * Show autocomplete suggestions when inputting characters into the input element.
         * Default = false.
         */
        this.openOnInput = false;
        this.placeholder = 'Select a Country';
        /**
         * Identifies how to handle non-option-matching input.
         * When true, revert to the previous value.
         * When false, set the value to null.
         * Default = false.
         */
        this.revertOnNoMatch = false;
        /**
         * Auto-select a country based on continuing user input.
         * Default = true.
         */
        this.useTypeAhead = true;
        this.width = null;
        this.countrySubject = new ReplaySubject(1);
        /**
         * Emits ICountry whenever the value for this component is set.
         * Contains country, region and telephone-code data.
         */
        this.country = this.countrySubject.asObservable();
        /**
         * Emits the current region name for the selected country, e.g. State, Province, etc.
         * If no country is selected, it emits "Region" as the default.
         */
        this.regionName = this.countrySubject.pipe(map(country => country?.regionName ?? BASE_REGION_NAME));
        this.showRegionsSubject = new ReplaySubject(1);
        /**
         * Identifies if the Region Selector should be displayed.
         * This is [true] if the country is null or the country has regions.
         */
        this.showRegions = this.showRegionsSubject.asObservable();
        /** @deprecated Will be removed in v3. */
        this.currentRegionDataSubject = new ReplaySubject(1);
        /** @deprecated Use the regionName Output. Will be removed in v3. */
        this.currentRegionData$ = this.currentRegionDataSubject.asObservable();
        /** @deprecated Will be removed in v3. */
        this.hasRegionsSubject = new ReplaySubject(1);
        /** @deprecated Use showRegions$ instead. Will be removed in v3. */
        this.hasRegions$ = this.hasRegionsSubject.asObservable();
        // Some older records in our DB might store the country name as the underlying value instead of the country code
        this.displayFn = (codeOrName) => codeOrName
            ? this.countries$.pipe(map(countries => countries.find(c => c.code.toLowerCase() === codeOrName?.toLowerCase() ||
                c.name.toLowerCase() === codeOrName?.toLowerCase() ||
                c.altNames?.find(altName => altName.toLowerCase() === codeOrName?.toLowerCase()))?.name ?? ''))
            : of('');
        // The user may input either the country code or the country name
        this.valueFn = (codeOrName) => codeOrName
            ? this.countries$.pipe(map(countries => countries.find(c => c.code.toLowerCase() === codeOrName?.toLowerCase() ||
                c.name.toLowerCase() === codeOrName?.toLowerCase() ||
                c.altNames?.find(altName => altName.toLowerCase() === codeOrName?.toLowerCase()))?.code ?? null))
            : of(null);
    }
    ngOnInit() {
        super.ngOnInit();
        this.selectorId = `${this.id}-container`;
    }
    get value() {
        return super.value;
    }
    set value(code) {
        super.value = code;
        this.setCountry();
        this.setRegionData();
    }
    writeValue(code) {
        super.writeValue(code);
        this.setCountry();
        this.setRegionData();
    }
    setCountry() {
        this.countries$.pipe(first()).subscribe(countries => {
            const country = countries.find(c => c.code.toLowerCase() === this.value?.toLowerCase()) ?? null;
            this.countrySubject.next(country);
            this.showRegionsSubject.next(country == null || country.regions.length > 0);
        });
    }
    /** @deprecated To be removed in next major release. Will be removed in v3. */
    setRegionData() {
        this.countries$.pipe(first()).subscribe(countries => {
            const country = countries.find(c => c.code.toLowerCase() === this.value?.toLowerCase()) ?? null;
            const data = new RegionData(country?.regionName ?? 'Region', [
                ...(country?.regions ?? []).map(r => r.name)
            ]);
            this.currentRegionDataSubject.next(data);
            this.hasRegionsSubject.next(this.value == null || (data?.regions.length ?? 0) > 0);
        });
    }
    handleChange(code) {
        const hasChanged = !isEqual(this.value, code);
        this.value = code;
        if (hasChanged) {
            if (this.ngControl?.control?.updateOn === 'change')
                this._onChange(this.value);
            this.changed.emit(this.value);
        }
    }
    handleBlur() {
        if (this.ngControl?.control?.updateOn === 'blur')
            this._onChange(this.value);
        this._onTouched();
    }
    static { this.ɵfac = /*@__PURE__*/ (() => { let ɵStlrCountrySelectComponent_BaseFactory; return function StlrCountrySelectComponent_Factory(__ngFactoryType__) { return (ɵStlrCountrySelectComponent_BaseFactory || (ɵStlrCountrySelectComponent_BaseFactory = i0.ɵɵgetInheritedFactory(StlrCountrySelectComponent)))(__ngFactoryType__ || StlrCountrySelectComponent); }; })(); }
    static { this.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: StlrCountrySelectComponent, selectors: [["stlr-country-select"]], hostVars: 3, hostBindings: function StlrCountrySelectComponent_HostBindings(rf, ctx) { if (rf & 2) {
            i0.ɵɵdomProperty("id", ctx.selectorId);
            i0.ɵɵclassProp("full", ctx.width);
        } }, inputs: { allowPartialMatching: "allowPartialMatching", applyFilterOnOpen: "applyFilterOnOpen", autocomplete: "autocomplete", autoFilterMatching: "autoFilterMatching", closeOnClick: "closeOnClick", openOnClick: "openOnClick", openOnFocus: "openOnFocus", openOnInput: "openOnInput", placeholder: "placeholder", revertOnNoMatch: "revertOnNoMatch", useTypeAhead: "useTypeAhead", width: "width" }, outputs: { country: "country", regionName: "regionName", showRegions: "showRegions" }, standalone: false, features: [i0.ɵɵProvidersFeature([
                {
                    provide: STLR_FORM_COMPONENT_BASE,
                    useExisting: forwardRef(() => StlrCountrySelectComponent),
                    multi: true
                },
            ]), i0.ɵɵInheritDefinitionFeature], decls: 4, vars: 29, consts: [[3, "focusLost", "changed", "allowNewOptions", "allowPartialMatching", "applyFilterOnOpen", "aria-describedby", "aria-label", "aria-labelledby", "autocomplete", "autoFilter", "autoFilterMatching", "closeOnClick", "disabled", "displayFn", "enterKeyHint", "id", "invalid", "name", "openOnClick", "openOnFocus", "openOnInput", "placeholder", "required", "revertOnNoMatch", "tabindex", "useTypeAhead", "valueFn", "value", "width"], ["stlrOption", "", 3, "value"]], template: function StlrCountrySelectComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵelementStart(0, "stlr-autocomplete", 0);
            i0.ɵɵlistener("focusLost", function StlrCountrySelectComponent_Template_stlr_autocomplete_focusLost_0_listener() { return ctx.handleBlur(); })("changed", function StlrCountrySelectComponent_Template_stlr_autocomplete_changed_0_listener($event) { return ctx.handleChange($event); });
            i0.ɵɵrepeaterCreate(1, StlrCountrySelectComponent_For_2_Template, 2, 2, "option", 1, i0.ɵɵrepeaterTrackByIdentity);
            i0.ɵɵpipe(3, "async");
            i0.ɵɵelementEnd();
        } if (rf & 2) {
            i0.ɵɵproperty("allowNewOptions", false)("allowPartialMatching", ctx.allowPartialMatching)("applyFilterOnOpen", ctx.applyFilterOnOpen);
            i0.ɵɵariaProperty("aria-describedby", ctx.ariaDescribedBy)("aria-label", ctx.ariaLabel)("aria-labelledby", ctx.ariaLabelledBy);
            i0.ɵɵproperty("autocomplete", ctx.autocomplete)("autoFilter", true)("autoFilterMatching", ctx.autoFilterMatching)("closeOnClick", ctx.closeOnClick)("disabled", ctx.disabled)("displayFn", ctx.displayFn)("enterKeyHint", ctx.enterKeyHint)("id", ctx.id)("invalid", ctx.invalid)("name", ctx.name)("openOnClick", ctx.openOnClick)("openOnFocus", ctx.openOnFocus)("openOnInput", ctx.openOnInput)("placeholder", ctx.placeholder)("required", ctx.required)("revertOnNoMatch", ctx.revertOnNoMatch)("tabindex", ctx.tabindex)("useTypeAhead", ctx.useTypeAhead)("valueFn", ctx.valueFn)("value", ctx.value)("width", ctx.width);
            i0.ɵɵadvance();
            i0.ɵɵrepeater(i0.ɵɵpipeBind1(3, 27, ctx.countries$));
        } }, dependencies: [i1.NgSelectOption, i1.ɵNgSelectMultipleOption, StlrAutocompleteComponent, StlrOptionComponent, i1$1.AsyncPipe], styles: ["[_nghost-%COMP%]{display:flex;position:relative}.full[_nghost-%COMP%]{width:100%}.ng-touched.ng-invalid[_nghost-%COMP%]     input:not(.disabled):not(:focus), .ng-dirty.ng-invalid[_nghost-%COMP%]     input:not(.disabled):not(:focus){border-color:var(--stlr-color__error);background-color:var(--stlr-color__error--extra-light)}[_nghost-%COMP%]     input{box-sizing:border-box;padding:0 10px;border:solid 1px rgba(0,0,0,.25);background-color:#fff;color:var(--stlr-color__copy);appearance:none;border-radius:var(--stlr-border-radius);outline:solid 1px rgba(255,255,255,0);transition:all .2s;box-shadow:0 0 2px #59a9ff00;font-family:var(--stlr-font-family--copy)}[_nghost-%COMP%]     input:not(textarea){height:2.25rem}[_nghost-%COMP%]     input::-webkit-search-cancel-button{-webkit-appearance:none;height:16px;width:16px;display:block;background-color:#0000004d;border-radius:10px;background-repeat:no-repeat;background-position:center center;cursor:pointer;background-image:url(\"data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9.5 3.205L8.795 2.5L6 5.295L3.205 2.5L2.5 3.205L5.295 6L2.5 8.795L3.205 9.5L6 6.705L8.795 9.5L9.5 8.795L6.705 6L9.5 3.205Z' fill='white'/%3E%3C/svg%3E%0A\")}[_nghost-%COMP%]     input:hover{box-shadow:0 0 6px #0000001a}[_nghost-%COMP%]     input:focus{border:solid 1px var(--stlr-color__info);outline:solid 1px var(--stlr-color__info)}[_nghost-%COMP%]     input[disabled]{color:var(--stlr-color__disabled--light);border:solid 1px rgba(0,0,0,.2);background-color:#00000006}[_nghost-%COMP%]     input.ng-invalid.ng-touched, [_nghost-%COMP%]     input.ng-invalid.ng-dirty{border-color:var(--stlr-color__error);background-color:var(--stlr-color__error--extra-light)}[_nghost-%COMP%]     input.ng-invalid.ng-touched:focus, [_nghost-%COMP%]     input.ng-invalid.ng-dirty:focus{background-color:#fff;border:solid 1px var(--stlr-color__info)}[_nghost-%COMP%]     input{width:100%}[_nghost-%COMP%]     input:focus::placeholder{color:transparent}[_nghost-%COMP%]     input::-webkit-calendar-picker-indicator{display:none!important}"], changeDetection: 0 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrCountrySelectComponent, [{
        type: Component,
        args: [{ selector: 'stlr-country-select', changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                    {
                        provide: STLR_FORM_COMPONENT_BASE,
                        useExisting: forwardRef(() => StlrCountrySelectComponent),
                        multi: true
                    },
                ], standalone: false, template: "<stlr-autocomplete\n  (focusLost)=\"handleBlur()\"\n  (changed)=\"handleChange($event)\"\n  [allowNewOptions]=\"false\"\n  [allowPartialMatching]=\"allowPartialMatching\"\n  [applyFilterOnOpen]=\"applyFilterOnOpen\"\n  [aria-describedby]=\"ariaDescribedBy\"\n  [aria-label]=\"ariaLabel\"\n  [aria-labelledby]=\"ariaLabelledBy\"\n  [autocomplete]=\"autocomplete\"\n  [autoFilter]=\"true\"\n  [autoFilterMatching]=\"autoFilterMatching\"\n  [closeOnClick]=\"closeOnClick\"\n  [disabled]=\"disabled\"\n  [displayFn]=\"displayFn\"\n  [enterKeyHint]=\"enterKeyHint\"\n  [id]=\"id\"\n  [invalid]=\"invalid\"\n  [name]=\"name\"\n  [openOnClick]=\"openOnClick\"\n  [openOnFocus]=\"openOnFocus\"\n  [openOnInput]=\"openOnInput\"\n  [placeholder]=\"placeholder\"\n  [required]=\"required\"\n  [revertOnNoMatch]=\"revertOnNoMatch\"\n  [tabindex]=\"tabindex\"\n  [useTypeAhead]=\"useTypeAhead\"\n  [valueFn]=\"valueFn\"\n  [value]=\"value\"\n  [width]=\"width\">\n  @for (country of countries$ | async; track country) {\n    <option stlrOption [value]=\"country.code\">\n      {{ country.name }}\n    </option>\n  }\n</stlr-autocomplete>\n", styles: [":host{display:flex;position:relative}:host.full{width:100%}:host.ng-touched.ng-invalid ::ng-deep input:not(.disabled):not(:focus),:host.ng-dirty.ng-invalid ::ng-deep input:not(.disabled):not(:focus){border-color:var(--stlr-color__error);background-color:var(--stlr-color__error--extra-light)}:host ::ng-deep input{box-sizing:border-box;padding:0 10px;border:solid 1px rgba(0,0,0,.25);background-color:#fff;color:var(--stlr-color__copy);appearance:none;border-radius:var(--stlr-border-radius);outline:solid 1px rgba(255,255,255,0);transition:all .2s;box-shadow:0 0 2px #59a9ff00;font-family:var(--stlr-font-family--copy)}:host ::ng-deep input:not(textarea){height:2.25rem}:host ::ng-deep input::-webkit-search-cancel-button{-webkit-appearance:none;height:16px;width:16px;display:block;background-color:#0000004d;border-radius:10px;background-repeat:no-repeat;background-position:center center;cursor:pointer;background-image:url(\"data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9.5 3.205L8.795 2.5L6 5.295L3.205 2.5L2.5 3.205L5.295 6L2.5 8.795L3.205 9.5L6 6.705L8.795 9.5L9.5 8.795L6.705 6L9.5 3.205Z' fill='white'/%3E%3C/svg%3E%0A\")}:host ::ng-deep input:hover{box-shadow:0 0 6px #0000001a}:host ::ng-deep input:focus{border:solid 1px var(--stlr-color__info);outline:solid 1px var(--stlr-color__info)}:host ::ng-deep input[disabled]{color:var(--stlr-color__disabled--light);border:solid 1px rgba(0,0,0,.2);background-color:#00000006}:host ::ng-deep input.ng-invalid.ng-touched,:host ::ng-deep input.ng-invalid.ng-dirty{border-color:var(--stlr-color__error);background-color:var(--stlr-color__error--extra-light)}:host ::ng-deep input.ng-invalid.ng-touched:focus,:host ::ng-deep input.ng-invalid.ng-dirty:focus{background-color:#fff;border:solid 1px var(--stlr-color__info)}:host ::ng-deep input{width:100%}:host ::ng-deep input:focus::placeholder{color:transparent}:host ::ng-deep input::-webkit-calendar-picker-indicator{display:none!important}\n"] }]
    }], null, { allowPartialMatching: [{
            type: Input
        }], applyFilterOnOpen: [{
            type: Input
        }], autocomplete: [{
            type: Input
        }], autoFilterMatching: [{
            type: Input
        }], closeOnClick: [{
            type: Input
        }], openOnClick: [{
            type: Input
        }], openOnFocus: [{
            type: Input
        }], openOnInput: [{
            type: Input
        }], placeholder: [{
            type: Input
        }], revertOnNoMatch: [{
            type: Input
        }], useTypeAhead: [{
            type: Input
        }], width: [{
            type: Input
        }, {
            type: HostBinding,
            args: ['class.full']
        }], country: [{
            type: Output
        }], regionName: [{
            type: Output
        }], showRegions: [{
            type: Output
        }], selectorId: [{
            type: HostBinding,
            args: ['id']
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(StlrCountrySelectComponent, { className: "StlrCountrySelectComponent", filePath: "lib/country-select/country-select.component.ts", lineNumber: 35 }); })();

const COUNTRY_SELECT_API = [
    StlrCountrySelectComponent,
];

// NOTE: This is a temporary location for this and needs to be moved to the utilities library.
// For some reason, it will not import into apps so it is temporarily here for use in the progress bar component.
/**
 * Constrains the value to a numeric range. If the value falls outside of the
 * range, this function changes the value to either the min or max value.
 * It accepts a number or a string that contains a number. parseInt() is used to
 * convert strings to numbers.
 * @param min The minimum allowed value.
 * @param max The maximum allowed value.
 * @returns The value as a number, constrained between min and max.
 */
const Range = (min, max) => {
    return (target, key) => {
        const privateKey = Symbol(); // Unique key to store the value privately
        Object.defineProperty(target, key, {
            get() {
                return this[privateKey];
            },
            set(newValue) {
                const num = typeof newValue === 'string' ? parseInt(newValue, 10) : newValue;
                this[privateKey] = Math.min(Math.max(num, min), max);
            },
            enumerable: true,
            configurable: true,
        });
    };
};

const _c0$1 = ["input"];
const _c1$1 = () => [];
function StlrCodeEntryComponent_For_3_Template(rf, ctx) { if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "input", 2, 0);
    i0.ɵɵpipe(2, "async");
    i0.ɵɵpipe(3, "async");
    i0.ɵɵlistener("click", function StlrCodeEntryComponent_For_3_Template_input_click_0_listener() { i0.ɵɵrestoreView(_r1); const input_r2 = i0.ɵɵreference(1); const ctx_r2 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r2.selectInputText(input_r2)); })("copy", function StlrCodeEntryComponent_For_3_Template_input_copy_0_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView($event.preventDefault()); })("cut", function StlrCodeEntryComponent_For_3_Template_input_cut_0_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView($event.preventDefault()); })("keydown", function StlrCodeEntryComponent_For_3_Template_input_keydown_0_listener($event) { i0.ɵɵrestoreView(_r1); const ctx_r2 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r2.handleKeyDown($event)); })("keyup", function StlrCodeEntryComponent_For_3_Template_input_keyup_0_listener($event) { i0.ɵɵrestoreView(_r1); const ctx_r2 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r2.handleKeyUp($event)); })("paste", function StlrCodeEntryComponent_For_3_Template_input_paste_0_listener($event) { i0.ɵɵrestoreView(_r1); const ctx_r2 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r2.handlePaste($event)); });
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ɵ$index_4_r4 = ctx.$index;
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵstyleMap(i0.ɵɵpipeBind1(2, 6, ctx_r2.inputStyles$));
    i0.ɵɵproperty("autocomplete", ɵ$index_4_r4 === 0 ? "one-time-code" : "off")("id", "otc-" + (ɵ$index_4_r4 + 1))("inputMode", ctx_r2.type === "alpha" ? "text" : ctx_r2.type === "numeric" ? "decimal" : "text")("value", i0.ɵɵpipeBind1(3, 8, ctx_r2.code(ɵ$index_4_r4)));
} }
// Zero-Width Space: Used to hold a place in the underlying value-string when the
// user applies the backspace or delete keys in the middle of the text. It prevents
// the text from shifting through the boxes.
const PLACEHOLDER = '\u200B';
class StlrCodeEntryComponent extends StlrFormComponentValueAccessor {
    constructor() {
        super(...arguments);
        this.controlType = 'stlr-code-entry';
        /**
         * Used by the template for each input element. Spreads the
         * code from this.value across an array which the input elements
         * bind to.
         */
        this._codeSubject = new ReplaySubject(1);
        this.code = (index) => this._codeSubject.pipe(map(value => value && value[index] ? value[index] : ''));
        /**
         * Dynamic styles applied to the input boxes -> allows for adjustments to
         * size, font, and spacing when the count-property changes in value.
         */
        this._inputStylesSubject = new ReplaySubject(1);
        this.inputStyles$ = this._inputStylesSubject.asObservable();
        /**
         * Dynamic styles applied to the containing div element -> allows for
         * adjustments to size and spacing when the count-property changes in value.
         */
        this._divStylesSubject = new ReplaySubject(1);
        this.divStyles$ = this._divStylesSubject.asObservable();
        /**
         * The data type allowed for the code. Options are alpha, numeric,
         * and alphanumeric.
         * @default 'alphanumeric'
         */
        this.type = 'alphanumeric';
        /**
         * The number of characters in the code. The number of characters is
         * constrained to a range from 2 to 10.
         * @default 6
         */
        this.count = 6;
        /**
         * If "type" = 'alpha' or 'alphanumeric', transform letters to the specified case.
         * @default null
         */
        this.transformCase = null;
        /**
         * Emits an event when the value changes. This is redundant since the (changed)
         * event does the same thing.
         * @deprecated Use (changed) instead. Will be removed in a future version.
         */
        this.updated = new EventEmitter();
        /**
         * Emits when the user pastes in a code. The value emitted by this event
         * is transformed according to the properties and restrictions of this
         * component. It applies relevant casing changes, removes restricted
         * characters, and trims the length to fit the allowed number of characters.
         */
        this.pasted = new EventEmitter();
        /**
         * Emits an event when all input boxes are filled.
         */
        this.completed = new EventEmitter();
    }
    onResize() {
        this._updateStyles();
    }
    /**
     * Updates the form control if updateOn='blur' and the focus leaves this
     * component. Updates are skipped if the focus remains within any of the
     * input elements of this component.
     * @param event The focus-out event.
     * @protected
     */
    focusOut(event) {
        if (event.relatedTarget != null &&
            this._inputElementRefs?.map(ref => ref.nativeElement).find(ele => ele === event.relatedTarget)) {
            return;
        }
        if (this.ngControl?.control?.updateOn === 'blur') {
            this._updateFormControl();
        }
        this._onTouched();
    }
    /**
     * Updates the input box and parent element's styles when the number
     * of input boxes is changed. This affects width, height, font-size, etc.
     * @param changes
     */
    ngOnChanges(changes) {
        if (changes['count']) {
            this._updateStyles();
        }
    }
    ngAfterViewInit() {
        this._updateStyles();
        this.changeDetectorRef.detectChanges();
    }
    /**
     * The calculated/transformed value. When setting the value, this filters
     * out restricted characters, transforms the case, if relevant, and trims
     * the length according to the number of input boxes (count).
     */
    get value() {
        return super.value;
    }
    set value(value) {
        const previousValue = this.value;
        super.value = this._getTransformedCase(this._removeRestrictedCharacters(value))?.substring(0, this.count) || null;
        const valueChanged = !isEqual(this.value, previousValue);
        if (valueChanged) {
            this._codeSubject.next(this.value?.split('') ?? []);
            if (this.ngControl?.control?.updateOn === 'change') {
                this._updateFormControl();
            }
            this.updated.emit(this.value);
            this.changed.emit(this.value);
            if (this._valueLengthValidates()) {
                this.completed.emit(this.value);
            }
        }
    }
    /**
     * Called by the FormControl and applies the value from the FormControl,
     * to this component, e.g. control.setValue or the control's initial-value.
     * This writes directly to the underlying value property, skipping any
     * transformations that occur in this.value.
     * @param value The value from the FormControl.
     */
    writeValue(value) {
        super.value = value;
        this._codeSubject.next(this.value?.split('') ?? []);
    }
    /**
     * Intercepts character keys (non-operative), transforms the character
     * directly in the input box, updates this.value with all input box values,
     * and moves the focus to the next input box.
     * @param event The keyboard event.
     * @protected
     */
    handleKeyDown(event) {
        if (!this._isOperativeKey(event) && !this._isKeyRestricted(event)) {
            event.target.value = this._getTransformedCase(this._removeRestrictedCharacters(event.key));
            this._applyValueFromInputs();
            this._moveNext(event);
        }
    }
    /**
     * Handles operative keys (delete, backspace, arrows).
     * @param event The keyboard event.
     * @protected
     */
    handleKeyUp(event) {
        if (this._isOperativeKey(event)) {
            this._handleOperativeKey(event);
        }
    }
    /**
     * Selects the character in the input box, if one exists. This provides a
     * better user experience when moving between input boxes.
     * @param inputElement The HTMLInputElement to which to apply the selection range.
     * @protected
     */
    selectInputText(inputElement) {
        inputElement.setSelectionRange(0, 1);
    }
    /**
     * Retrieves data from the clipboard and sets this.value to it. If, after
     * data transformation, the data is not null, a paste event is emitted
     * containing the transformed data.
     * @param event The clipboard event.
     * @protected
     */
    handlePaste(event) {
        const text = event.clipboardData?.getData('text');
        if (text) {
            this.value = text;
            event.preventDefault();
            event.target.blur();
            if (this.value != null) {
                this.pasted.emit(this.value);
            }
        }
    }
    /**
     * Updates the form control with the current value, replacing all
     * instances of PLACEHOLDER with an empty string.
     * @private
     */
    _updateFormControl() {
        this._onChange(this.value?.replace(new RegExp(PLACEHOLDER, 'g'), '') || null);
    }
    /**
     * Joins all the input element values into a string and applies it
     * to this.value.
     * @private
     */
    _applyValueFromInputs() {
        this.value = this._inputElementRefs?.map(ref => ref.nativeElement.value).join('') ?? null;
    }
    /**
     * Validates this.value's length as equal to this.count after replacing
     * all instances of PLACEHOLDER with an empty string.
     * @returns True if the character-length of the value matches the
     * number of input boxes (count).
     * @private
     */
    _valueLengthValidates() {
        return (this.value?.replace(new RegExp(PLACEHOLDER, 'g'), '').length ?? 0) === this.count;
    }
    /**
     * Identifies if the pressed key corresponds with a restricted character
     * according to the component's type-property. Note that this eventually
     * calls event.preventDefault() if the character is restricted.
     * @param event The keyboard event.
     * @returns True if the key is restricted.
     * @private
     */
    _isKeyRestricted(event) {
        switch (this.type) {
            case 'alpha':
                return this._restrictToAlpha(event);
            case 'numeric':
                return this._restrictToNumeric(event);
            case 'alphanumeric':
                return this._restrictToAlphaNumeric(event);
            default:
                throw new Error('Unknown input type');
        }
    }
    /**
     * Replaces characters identified as restricted by the component's type-property
     * with an empty string.
     * @param value The string to transform.
     * @returns The transformed string.
     * @private
     */
    _removeRestrictedCharacters(value) {
        if (this.type === 'alpha') {
            return value?.replace(/[0-9 !@#$%^&*)(+=._\]-]/g, '') ?? null;
        }
        else if (this.type === 'numeric') {
            return value?.replace(/[a-zA-Z !@#$%^&*)(+=._\]-]/g, '') ?? null;
        }
        return value?.replace(/[ !@#$%^&*)(+=._\]-]/g, '') ?? null;
    }
    /**
     * Transforms the case of the passed string to upper or lower case based
     * on the component's transform-property.
     * @param value The string to transform.
     * @returns The transformed string.
     * @private
     */
    _getTransformedCase(value) {
        if (this.transformCase && value && (this.type === 'alpha' || this.type === 'alphanumeric')) {
            if (this.transformCase === 'lower' && /[A-Z].*/.test(value)) {
                return value.toLowerCase();
            }
            else if (this.transformCase === 'upper' && /[a-z].*/.test(value)) {
                return value.toUpperCase();
            }
        }
        return value;
    }
    /**
     * Blocks non-alpha characters. This calls event.preventDefault() if
     * the character is restricted.
     * @param event The keyboard event.
     * @returns True if blocked.
     * @private
     */
    _restrictToAlpha(event) {
        if (/^[a-zA-Z]+$/.test(event.key)) {
            return false;
        }
        event.preventDefault();
        return true;
    }
    /**
     * Blocks non-numeric characters. This calls event.preventDefault() if
     * the character is restricted.
     * @param event The keyboard event.
     * @returns True if blocked.
     * @private
     */
    _restrictToNumeric(event) {
        if (/^[0-9]+$/.test(event.key)) {
            return false;
        }
        event.preventDefault();
        return true;
    }
    /**
     * Blocks non-alphanumeric characters. This calls event.preventDefault() if
     * the character is restricted.
     * @param event The keyboard event.
     * @returns True if blocked.
     * @private
     */
    _restrictToAlphaNumeric(event) {
        if (/^[a-zA-Z0-9]+$/.test(event.key)) {
            return false;
        }
        event.preventDefault();
        return true;
    }
    /**
     * Determines if the pressed key is an operative key, e.g. Control, Alt, Shift-only.
     * @param event The keyboard event.
     * @returns True if the pressed key is an operative key.
     * @private
     */
    _isOperativeKey(event) {
        return event.ctrlKey || event.altKey || event.metaKey || event.key === 'Shift' ||
            !/^[a-zA-Z0-9`!@#$%^&*()_\-+=[\]{};':"\\|,.<>/?~ ]$/.test(event.key);
    }
    /**
     * Handles operative keys. For Delete and Backspace, a placeholder (invisible)
     * character is applied to keep characters from shifting across input boxes.
     * Movement between input boxes is also applied based on the particular key.
     * @param event The keyboard event.
     * @private
     */
    _handleOperativeKey(event) {
        if (event.key.toLowerCase() === 'delete' || event.key.toLowerCase() === 'backspace') {
            event.target.value = PLACEHOLDER;
            this._applyValueFromInputs();
        }
        if (event.key.toLowerCase() === 'backspace' || event.key.toLowerCase() === 'arrowleft') {
            this._movePrevious(event);
        }
        else if (event.key.toLowerCase() === 'arrowright') {
            this._moveNext(event);
        }
    }
    /**
     * Moves the focus to the next input box, if available, and selects the character
     * so the user can easily type over it if necessary.
     * @param event The keyboard event.
     * @private
     */
    _moveNext(event) {
        const inputElement = event.target;
        const allInputElements = this._inputElementRefs?.map(ref => ref.nativeElement);
        if (allInputElements) {
            const index = allInputElements.findIndex(element => element === inputElement);
            if (index > -1 && index < (this.count - 1)) {
                allInputElements[index + 1].focus();
                this.selectInputText(allInputElements[index + 1]);
                event.preventDefault();
            }
            else if (index === this.count - 1) {
                allInputElements[index].blur();
            }
        }
    }
    /**
     * Moves the focus to the previous input box, if available, and selects the character
     * so the user can easily type over it if necessary.
     * @param event
     * @private
     */
    _movePrevious(event) {
        const inputElement = event.target;
        const allInputElements = this._inputElementRefs?.map(ref => ref.nativeElement);
        if (allInputElements) {
            const index = allInputElements.findIndex(element => element === inputElement);
            if (index > 0) {
                if (event.key.toLowerCase() === 'backspace' && allInputElements[index].value) {
                    allInputElements[index].value = '';
                }
                allInputElements[index - 1].focus();
                this.selectInputText(allInputElements[index - 1]);
                event.preventDefault();
            }
        }
    }
    /**
     * Calculates various styles so the input boxes are properly sized and spaced
     * according to the number of input boxes (count) and the available space
     * provided by the parent element. This applies width, height, input box padding,
     * font-size, and a dynamically-sized gap between the boxes.
     * @private
     */
    _updateStyles() {
        const inputStyles = `
            width: ${((90 / this.count) + (10 / Math.pow(this.count, 2)))}%;
            height: auto;
            aspect-ratio: 1/1;
            padding: ${(1.8 / this.count)}%;
            font-size: ${((this._inputElementRefs?.first.nativeElement.offsetWidth ?? 0) * .61)}px;
        `;
        const divStyles = `
            gap: ${(10 / this.count)}%;
        `;
        this._inputStylesSubject.next(inputStyles);
        this._divStylesSubject.next(divStyles);
    }
    static { this.ɵfac = /*@__PURE__*/ (() => { let ɵStlrCodeEntryComponent_BaseFactory; return function StlrCodeEntryComponent_Factory(__ngFactoryType__) { return (ɵStlrCodeEntryComponent_BaseFactory || (ɵStlrCodeEntryComponent_BaseFactory = i0.ɵɵgetInheritedFactory(StlrCodeEntryComponent)))(__ngFactoryType__ || StlrCodeEntryComponent); }; })(); }
    static { this.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: StlrCodeEntryComponent, selectors: [["stlr-code-entry"]], viewQuery: function StlrCodeEntryComponent_Query(rf, ctx) { if (rf & 1) {
            i0.ɵɵviewQuery(_c0$1, 5, ElementRef);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._inputElementRefs = _t);
        } }, hostBindings: function StlrCodeEntryComponent_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("resize", function StlrCodeEntryComponent_resize_HostBindingHandler() { return ctx.onResize(); }, i0.ɵɵresolveWindow)("focusout", function StlrCodeEntryComponent_focusout_HostBindingHandler($event) { return ctx.focusOut($event); });
        } }, inputs: { type: "type", count: [2, "count", "count", (value) => Number(value)], transformCase: "transformCase" }, outputs: { updated: "updated", pasted: "pasted", completed: "completed" }, standalone: false, features: [i0.ɵɵProvidersFeature([
                {
                    provide: STLR_FORM_COMPONENT_BASE,
                    useExisting: forwardRef(() => StlrCodeEntryComponent),
                    multi: true
                },
            ]), i0.ɵɵInheritDefinitionFeature, i0.ɵɵNgOnChangesFeature], decls: 4, vars: 5, consts: [["input", ""], ["type", "text", 3, "autocomplete", "id", "inputMode", "style", "value"], ["type", "text", 3, "click", "copy", "cut", "keydown", "keyup", "paste", "autocomplete", "id", "inputMode", "value"]], template: function StlrCodeEntryComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵelementStart(0, "div");
            i0.ɵɵpipe(1, "async");
            i0.ɵɵrepeaterCreate(2, StlrCodeEntryComponent_For_3_Template, 4, 10, "input", 1, i0.ɵɵrepeaterTrackByIndex);
            i0.ɵɵelementEnd();
        } if (rf & 2) {
            i0.ɵɵstyleMap(i0.ɵɵpipeBind1(1, 2, ctx.divStyles$));
            i0.ɵɵadvance(2);
            i0.ɵɵrepeater(i0.ɵɵpureFunction0(4, _c1$1).constructor(ctx.count));
        } }, dependencies: [i1$1.AsyncPipe], styles: [".ng-touched.ng-invalid[_nghost-%COMP%]:not(.disabled):not(:focus)   div[_ngcontent-%COMP%] > input[_ngcontent-%COMP%], .ng-dirty.ng-invalid[_nghost-%COMP%]:not(.disabled):not(:focus)   div[_ngcontent-%COMP%] > input[_ngcontent-%COMP%]{border-color:var(--stlr-color__error);background-color:var(--stlr-color__error--extra-light)}div[_ngcontent-%COMP%]{display:flex;flex-direction:row}div[_ngcontent-%COMP%]   input[_ngcontent-%COMP%]{box-sizing:border-box;padding:0 10px;border:solid 1px rgba(0,0,0,.25);background-color:#fff;color:var(--stlr-color__copy);appearance:none;border-radius:var(--stlr-border-radius);outline:solid 1px rgba(255,255,255,0);transition:all .2s;box-shadow:0 0 2px #59a9ff00;font-family:var(--stlr-font-family--copy)}div[_ngcontent-%COMP%]   input[_ngcontent-%COMP%]:not(textarea){height:2.25rem}div[_ngcontent-%COMP%]   input[_ngcontent-%COMP%]::-webkit-search-cancel-button{-webkit-appearance:none;height:16px;width:16px;display:block;background-color:#0000004d;border-radius:10px;background-repeat:no-repeat;background-position:center center;cursor:pointer;background-image:url(\"data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9.5 3.205L8.795 2.5L6 5.295L3.205 2.5L2.5 3.205L5.295 6L2.5 8.795L3.205 9.5L6 6.705L8.795 9.5L9.5 8.795L6.705 6L9.5 3.205Z' fill='white'/%3E%3C/svg%3E%0A\")}div[_ngcontent-%COMP%]   input[_ngcontent-%COMP%]:hover{box-shadow:0 0 6px #0000001a}div[_ngcontent-%COMP%]   input[_ngcontent-%COMP%]:focus{border:solid 1px var(--stlr-color__info);outline:solid 1px var(--stlr-color__info)}div[_ngcontent-%COMP%]   input[disabled][_ngcontent-%COMP%]{color:var(--stlr-color__disabled--light);border:solid 1px rgba(0,0,0,.2);background-color:#00000006}div[_ngcontent-%COMP%]   input.ng-invalid.ng-touched[_ngcontent-%COMP%], div[_ngcontent-%COMP%]   input.ng-invalid.ng-dirty[_ngcontent-%COMP%]{border-color:var(--stlr-color__error);background-color:var(--stlr-color__error--extra-light)}div[_ngcontent-%COMP%]   input.ng-invalid.ng-touched[_ngcontent-%COMP%]:focus, div[_ngcontent-%COMP%]   input.ng-invalid.ng-dirty[_ngcontent-%COMP%]:focus{background-color:#fff;border:solid 1px var(--stlr-color__info)}div[_ngcontent-%COMP%]   input[_ngcontent-%COMP%]:focus::placeholder{color:transparent}div[_ngcontent-%COMP%]   input[_ngcontent-%COMP%]{text-align:center}"], changeDetection: 0 }); }
}
__decorate([
    Range(2, 10),
    __metadata("design:type", Number)
], StlrCodeEntryComponent.prototype, "count", void 0);
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrCodeEntryComponent, [{
        type: Component,
        args: [{ selector: 'stlr-code-entry', changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                    {
                        provide: STLR_FORM_COMPONENT_BASE,
                        useExisting: forwardRef(() => StlrCodeEntryComponent),
                        multi: true
                    },
                ], standalone: false, template: "<div [style]=\"divStyles$ | async\">\n  @for (i of [].constructor(count); track idx; let idx = $index) {\n    <input\n      #input\n      type=\"text\"\n      [autocomplete]=\"idx === 0 ? 'one-time-code' : 'off'\"\n      [id]=\"'otc-' + (idx+1)\"\n      [inputMode]=\"type === 'alpha' ? 'text' : type === 'numeric' ? 'decimal' : 'text'\"\n      [style]=\"inputStyles$ | async\"\n      [value]=\"code(idx) | async\"\n      (click)=\"selectInputText(input)\"\n      (copy)=\"$event.preventDefault()\"\n      (cut)=\"$event.preventDefault()\"\n      (keydown)=\"handleKeyDown($event)\"\n      (keyup)=\"handleKeyUp($event)\"\n      (paste)=\"handlePaste($event)\"\n      />\n  }\n</div>\n", styles: [":host.ng-touched.ng-invalid:not(.disabled):not(:focus) div>input,:host.ng-dirty.ng-invalid:not(.disabled):not(:focus) div>input{border-color:var(--stlr-color__error);background-color:var(--stlr-color__error--extra-light)}div{display:flex;flex-direction:row}div input{box-sizing:border-box;padding:0 10px;border:solid 1px rgba(0,0,0,.25);background-color:#fff;color:var(--stlr-color__copy);appearance:none;border-radius:var(--stlr-border-radius);outline:solid 1px rgba(255,255,255,0);transition:all .2s;box-shadow:0 0 2px #59a9ff00;font-family:var(--stlr-font-family--copy)}div input:not(textarea){height:2.25rem}div input::-webkit-search-cancel-button{-webkit-appearance:none;height:16px;width:16px;display:block;background-color:#0000004d;border-radius:10px;background-repeat:no-repeat;background-position:center center;cursor:pointer;background-image:url(\"data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9.5 3.205L8.795 2.5L6 5.295L3.205 2.5L2.5 3.205L5.295 6L2.5 8.795L3.205 9.5L6 6.705L8.795 9.5L9.5 8.795L6.705 6L9.5 3.205Z' fill='white'/%3E%3C/svg%3E%0A\")}div input:hover{box-shadow:0 0 6px #0000001a}div input:focus{border:solid 1px var(--stlr-color__info);outline:solid 1px var(--stlr-color__info)}div input[disabled]{color:var(--stlr-color__disabled--light);border:solid 1px rgba(0,0,0,.2);background-color:#00000006}div input.ng-invalid.ng-touched,div input.ng-invalid.ng-dirty{border-color:var(--stlr-color__error);background-color:var(--stlr-color__error--extra-light)}div input.ng-invalid.ng-touched:focus,div input.ng-invalid.ng-dirty:focus{background-color:#fff;border:solid 1px var(--stlr-color__info)}div input:focus::placeholder{color:transparent}div input{text-align:center}\n"] }]
    }], null, { onResize: [{
            type: HostListener,
            args: ['window:resize']
        }], focusOut: [{
            type: HostListener,
            args: ['focusout', ['$event']]
        }], _inputElementRefs: [{
            type: ViewChildren,
            args: ['input', { read: ElementRef }]
        }], type: [{
            type: Input
        }], count: [{
            type: Input,
            args: [{ transform: (value) => Number(value) }]
        }], transformCase: [{
            type: Input
        }], updated: [{
            type: Output
        }], pasted: [{
            type: Output
        }], completed: [{
            type: Output
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(StlrCodeEntryComponent, { className: "StlrCodeEntryComponent", filePath: "lib/code-entry/code-entry.component.ts", lineNumber: 27 }); })();

const CODE_ENTRY_API = [StlrCodeEntryComponent];

const _c0 = [[["", "stlrOption", ""]]];
const _c1 = ["[stlrOption]"];
function StlrMultiSelectComponent_Conditional_4_For_3_Template(rf, ctx) { if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "li")(1, "stlr-checkbox", 6);
    i0.ɵɵlistener("changed", function StlrMultiSelectComponent_Conditional_4_For_3_Template_stlr_checkbox_changed_1_listener() { const option_r2 = i0.ɵɵrestoreView(_r1).$implicit; const ctx_r2 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r2.toggleSelection(option_r2)); });
    i0.ɵɵtext(2);
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const option_r2 = ctx.$implicit;
    const ctx_r2 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance();
    i0.ɵɵproperty("checked", ctx_r2.isSelected(option_r2));
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", option_r2.label, " ");
} }
function StlrMultiSelectComponent_Conditional_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 3)(1, "ul", 5);
    i0.ɵɵrepeaterCreate(2, StlrMultiSelectComponent_Conditional_4_For_3_Template, 3, 2, "li", null, i0.ɵɵrepeaterTrackByIdentity);
    i0.ɵɵpipe(4, "async");
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵadvance(2);
    i0.ɵɵrepeater(i0.ɵɵpipeBind1(4, 0, ctx_r2.options$));
} }
class StlrMultiSelectComponent {
    constructor() {
        this.cdr = inject(ChangeDetectorRef);
        this.el = inject(ElementRef);
        this.placeholder = 'Select options';
        this.disabled = false;
        /** Tracks whether the dropdown panel is open */
        this.dropdownOpen = false;
        /** In multi‑select mode the value is an array */
        this.value = [];
        /** ControlValueAccessor callbacks */
        this.onChange = () => { };
        this.onTouched = () => { };
        /** Local copy of options as an array of objects with `label` and `value` */
        this.optionsSubject = new BehaviorSubject([]);
        this.options$ = this.optionsSubject.asObservable();
    }
    onDocumentClick(event) {
        // Check if the click target is outside the component
        if (this.dropdownOpen && !this.el.nativeElement.contains(event.target)) {
            this.dropdownOpen = false;
            this.cdr.markForCheck();
        }
    }
    ngAfterContentInit() {
        if (this._options) {
            const updateOptions = () => {
                const opts = this._options?.toArray().map(opt => ({
                    label: opt.getLabel(),
                    value: opt.value,
                })) || [];
                this.optionsSubject.next(opts);
            };
            updateOptions();
            this._options.changes.subscribe(updateOptions);
        }
    }
    writeValue(value) {
        this.value = value || [];
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(disabled) {
        this.disabled = disabled;
    }
    toggleDropdown() {
        if (!this.disabled) {
            this.dropdownOpen = !this.dropdownOpen;
        }
    }
    /**
     * Determines whether a given option is currently selected.
     */
    isSelected(option) {
        return (Array.isArray(this.value) && this.value.indexOf(option.value) > -1);
    }
    /**
     * Toggle the selection of a given option.
     */
    toggleSelection(option) {
        if (this.disabled) {
            return;
        }
        const index = this.value.indexOf(option.value);
        let newValue;
        if (index > -1) {
            newValue = [
                ...this.value.slice(0, index),
                ...this.value.slice(index + 1),
            ];
        }
        else {
            newValue = [...this.value, option.value];
        }
        this.value = newValue;
        this.onChange(this.value);
        this.onTouched();
    }
    /**
     * Returns an array of labels for the currently selected options.
     */
    get selectedLabels() {
        if (!this.optionsSubject.value || !this.value) {
            return [];
        }
        return this.optionsSubject.value
            .filter((opt) => this.value.indexOf(opt.value) > -1)
            .map((opt) => opt.label);
    }
    static { this.ɵfac = function StlrMultiSelectComponent_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || StlrMultiSelectComponent)(); }; }
    static { this.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: StlrMultiSelectComponent, selectors: [["stlr-multi-select"]], contentQueries: function StlrMultiSelectComponent_ContentQueries(rf, ctx, dirIndex) { if (rf & 1) {
            i0.ɵɵcontentQuery(dirIndex, StlrOptionComponent, 4);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._options = _t);
        } }, hostBindings: function StlrMultiSelectComponent_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("click", function StlrMultiSelectComponent_click_HostBindingHandler($event) { return ctx.onDocumentClick($event); }, i0.ɵɵresolveDocument);
        } }, inputs: { placeholder: "placeholder", disabled: "disabled" }, standalone: false, features: [i0.ɵɵProvidersFeature([
                {
                    provide: NG_VALUE_ACCESSOR,
                    useExisting: forwardRef(() => StlrMultiSelectComponent),
                    multi: true,
                },
            ])], ngContentSelectors: _c1, decls: 7, vars: 6, consts: [[1, "stlr-multi-select-container"], ["type", "button", 1, "stlr-multi-select-toggle", 3, "click", "disabled"], [1, "caret"], [1, "stlr-multi-select-panel"], [2, "display", "none"], ["stlrLayout", "column", "stlrLayoutGap", "var(--stlr-multi-select--option-gap)"], [3, "changed", "checked"]], template: function StlrMultiSelectComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵprojectionDef(_c0);
            i0.ɵɵelementStart(0, "div", 0)(1, "button", 1);
            i0.ɵɵlistener("click", function StlrMultiSelectComponent_Template_button_click_1_listener() { return ctx.toggleDropdown(); });
            i0.ɵɵtext(2);
            i0.ɵɵelement(3, "span", 2);
            i0.ɵɵelementEnd();
            i0.ɵɵconditionalCreate(4, StlrMultiSelectComponent_Conditional_4_Template, 5, 2, "div", 3);
            i0.ɵɵelementEnd();
            i0.ɵɵelementStart(5, "div", 4);
            i0.ɵɵprojection(6);
            i0.ɵɵelementEnd();
        } if (rf & 2) {
            i0.ɵɵclassProp("disabled", ctx.disabled);
            i0.ɵɵadvance();
            i0.ɵɵproperty("disabled", ctx.disabled);
            i0.ɵɵattribute("aria-expanded", ctx.dropdownOpen);
            i0.ɵɵadvance();
            i0.ɵɵtextInterpolate1(" ", ctx.selectedLabels.length ? ctx.selectedLabels.join(", ") : ctx.placeholder, " ");
            i0.ɵɵadvance(2);
            i0.ɵɵconditional(ctx.dropdownOpen ? 4 : -1);
        } }, dependencies: [i1$2.StlrLayoutDirective, i1$2.StlrLayoutGapDirective, StlrCheckboxComponent, i1$1.AsyncPipe], styles: ["[_nghost-%COMP%]{--stlr-multi-select--panel-padding: 1rem;--stlr-multi-select--option-gap: var(--space-s)}.stlr-multi-select-container[_ngcontent-%COMP%]{position:relative;display:inline-block;width:100%}.stlr-multi-select-toggle[_ngcontent-%COMP%]{box-sizing:border-box;padding:0 10px;border:solid 1px rgba(0,0,0,.25);background-color:#fff;color:var(--stlr-color__copy);appearance:none;border-radius:var(--stlr-border-radius);outline:solid 1px rgba(255,255,255,0);transition:all .2s;box-shadow:0 0 2px #59a9ff00;font-family:var(--stlr-font-family--copy)}.stlr-multi-select-toggle[_ngcontent-%COMP%]:not(textarea){height:2.25rem}.stlr-multi-select-toggle[_ngcontent-%COMP%]::-webkit-search-cancel-button{-webkit-appearance:none;height:16px;width:16px;display:block;background-color:#0000004d;border-radius:10px;background-repeat:no-repeat;background-position:center center;cursor:pointer;background-image:url(\"data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9.5 3.205L8.795 2.5L6 5.295L3.205 2.5L2.5 3.205L5.295 6L2.5 8.795L3.205 9.5L6 6.705L8.795 9.5L9.5 8.795L6.705 6L9.5 3.205Z' fill='white'/%3E%3C/svg%3E%0A\")}.stlr-multi-select-toggle[_ngcontent-%COMP%]:hover{box-shadow:0 0 6px #0000001a}.stlr-multi-select-toggle[_ngcontent-%COMP%]:focus{border:solid 1px var(--stlr-color__info);outline:solid 1px var(--stlr-color__info)}.stlr-multi-select-toggle[disabled][_ngcontent-%COMP%]{color:var(--stlr-color__disabled--light);border:solid 1px rgba(0,0,0,.2);background-color:#00000006}.stlr-multi-select-toggle.ng-invalid.ng-touched[_ngcontent-%COMP%], .stlr-multi-select-toggle.ng-invalid.ng-dirty[_ngcontent-%COMP%]{border-color:var(--stlr-color__error);background-color:var(--stlr-color__error--extra-light)}.stlr-multi-select-toggle.ng-invalid.ng-touched[_ngcontent-%COMP%]:focus, .stlr-multi-select-toggle.ng-invalid.ng-dirty[_ngcontent-%COMP%]:focus{background-color:#fff;border:solid 1px var(--stlr-color__info)}.stlr-multi-select-toggle[_ngcontent-%COMP%]:focus::placeholder{color:transparent}.stlr-multi-select-toggle[_ngcontent-%COMP%]{width:100%;text-align:left;cursor:pointer}.stlr-multi-select-toggle[_ngcontent-%COMP%]   .caret[_ngcontent-%COMP%]:after{font-family:Material Icons;content:\"expand_more\";display:flex;position:absolute;right:10px;top:0;bottom:0;margin:auto;align-items:center;font-size:20px;-webkit-user-select:none;-khtml-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none;pointer-events:none}.stlr-multi-select-panel[_ngcontent-%COMP%]{position:absolute;top:calc(100% + .2rem);left:0;z-index:1000;width:100%;max-height:200px;overflow-y:auto;background:#fff;border:1px solid #ccc;box-shadow:0 2px 4px #0003}.stlr-multi-select-panel[_ngcontent-%COMP%]   ul[_ngcontent-%COMP%]{list-style:none;padding:var(--stlr-multi-select--panel-padding);margin:0}.stlr-multi-select-panel[_ngcontent-%COMP%]   li[_ngcontent-%COMP%]{margin-bottom:.2rem}"], changeDetection: 0 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrMultiSelectComponent, [{
        type: Component,
        args: [{ selector: 'stlr-multi-select', changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                    {
                        provide: NG_VALUE_ACCESSOR,
                        useExisting: forwardRef(() => StlrMultiSelectComponent),
                        multi: true,
                    },
                ], standalone: false, template: "<div class=\"stlr-multi-select-container\" [class.disabled]=\"disabled\">\n  <!-- Toggle button shows the current selection or a placeholder -->\n  <button\n    type=\"button\"\n    class=\"stlr-multi-select-toggle\"\n    (click)=\"toggleDropdown()\"\n    [attr.aria-expanded]=\"dropdownOpen\"\n    [disabled]=\"disabled\"\n    >\n    {{ selectedLabels.length ? selectedLabels.join(', ') : placeholder }}\n    <span class=\"caret\"></span>\n  </button>\n\n  <!-- Dropdown panel with checkboxes -->\n  @if (dropdownOpen) {\n    <div class=\"stlr-multi-select-panel\">\n      <ul stlrLayout=\"column\" stlrLayoutGap=\"var(--stlr-multi-select--option-gap)\">\n        @for (option of options$ | async; track option) {\n          <li>\n            <stlr-checkbox [checked]=\"isSelected(option)\" (changed)=\"toggleSelection(option)\">\n              {{ option.label }}\n            </stlr-checkbox>\n          </li>\n        }\n      </ul>\n    </div>\n  }\n</div>\n\n<div style=\"display: none\">\n  <!-- Project the option elements but hide them -->\n  <ng-content select=\"[stlrOption]\"></ng-content>\n</div>\n", styles: [":host{--stlr-multi-select--panel-padding: 1rem;--stlr-multi-select--option-gap: var(--space-s)}.stlr-multi-select-container{position:relative;display:inline-block;width:100%}.stlr-multi-select-toggle{box-sizing:border-box;padding:0 10px;border:solid 1px rgba(0,0,0,.25);background-color:#fff;color:var(--stlr-color__copy);appearance:none;border-radius:var(--stlr-border-radius);outline:solid 1px rgba(255,255,255,0);transition:all .2s;box-shadow:0 0 2px #59a9ff00;font-family:var(--stlr-font-family--copy)}.stlr-multi-select-toggle:not(textarea){height:2.25rem}.stlr-multi-select-toggle::-webkit-search-cancel-button{-webkit-appearance:none;height:16px;width:16px;display:block;background-color:#0000004d;border-radius:10px;background-repeat:no-repeat;background-position:center center;cursor:pointer;background-image:url(\"data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9.5 3.205L8.795 2.5L6 5.295L3.205 2.5L2.5 3.205L5.295 6L2.5 8.795L3.205 9.5L6 6.705L8.795 9.5L9.5 8.795L6.705 6L9.5 3.205Z' fill='white'/%3E%3C/svg%3E%0A\")}.stlr-multi-select-toggle:hover{box-shadow:0 0 6px #0000001a}.stlr-multi-select-toggle:focus{border:solid 1px var(--stlr-color__info);outline:solid 1px var(--stlr-color__info)}.stlr-multi-select-toggle[disabled]{color:var(--stlr-color__disabled--light);border:solid 1px rgba(0,0,0,.2);background-color:#00000006}.stlr-multi-select-toggle.ng-invalid.ng-touched,.stlr-multi-select-toggle.ng-invalid.ng-dirty{border-color:var(--stlr-color__error);background-color:var(--stlr-color__error--extra-light)}.stlr-multi-select-toggle.ng-invalid.ng-touched:focus,.stlr-multi-select-toggle.ng-invalid.ng-dirty:focus{background-color:#fff;border:solid 1px var(--stlr-color__info)}.stlr-multi-select-toggle:focus::placeholder{color:transparent}.stlr-multi-select-toggle{width:100%;text-align:left;cursor:pointer}.stlr-multi-select-toggle .caret:after{font-family:Material Icons;content:\"expand_more\";display:flex;position:absolute;right:10px;top:0;bottom:0;margin:auto;align-items:center;font-size:20px;-webkit-user-select:none;-khtml-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none;pointer-events:none}.stlr-multi-select-panel{position:absolute;top:calc(100% + .2rem);left:0;z-index:1000;width:100%;max-height:200px;overflow-y:auto;background:#fff;border:1px solid #ccc;box-shadow:0 2px 4px #0003}.stlr-multi-select-panel ul{list-style:none;padding:var(--stlr-multi-select--panel-padding);margin:0}.stlr-multi-select-panel li{margin-bottom:.2rem}\n"] }]
    }], null, { placeholder: [{
            type: Input
        }], disabled: [{
            type: Input
        }], onDocumentClick: [{
            type: HostListener,
            args: ['document:click', ['$event']]
        }], _options: [{
            type: ContentChildren,
            args: [StlrOptionComponent]
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(StlrMultiSelectComponent, { className: "StlrMultiSelectComponent", filePath: "lib/multi-select/multi-select.component.ts", lineNumber: 28 }); })();

const MULTISELECT_API = [StlrMultiSelectComponent];

const publicApi = [
    ...AUTOBUBBLER_API,
    ...AUTOCOMPLETE_API,
    ...CHECKBOX_API,
    ...COUNTRY_SELECT_API,
    ...REGION_SELECT_API,
    ...FORM_FIELD_API,
    ...INPUT_API,
    ...LEGACY_API,
    ...MULTISELECT_API,
    ...OPTION_API,
    ...PHONE_API,
    ...RADIO_GROUP_API,
    ...SELECT_API,
    ...TOGGLE_API,
    ...CODE_ENTRY_API
];
class StlrFormModule {
    static { this.ɵfac = function StlrFormModule_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || StlrFormModule)(); }; }
    static { this.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: StlrFormModule }); }
    static { this.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ providers: [CountryService], imports: [CommonModule, ReactiveFormsModule, StlrIconModule, StlrFlexLayoutModule, ReactiveFormsModule] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StlrFormModule, [{
        type: NgModule,
        args: [{
                declarations: [Colorable, ...publicApi],
                imports: [CommonModule, ReactiveFormsModule, StlrIconModule, StlrFlexLayoutModule],
                exports: [ReactiveFormsModule, Colorable, ...publicApi],
                providers: [CountryService],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(StlrFormModule, { declarations: [Colorable, StlrAutoBubbleComponent, StlrAutoBubbleGroupComponent, StlrAutobubblerComponent, StlrBubblePrefixDirective, StlrBubblePopperDirective, StlrBubbleSuffixDirective, StlrAutocompleteComponent, StlrCheckboxComponent, StlrCountrySelectComponent, StlrRegionSelectComponent, StlrErrorDirective, StlrFormFieldComponent, StlrHintDirective, StlrLabelDirective, StlrPrefixDirective, StlrSuffixDirective, StlrInputComponent, StlrFormComponent, StlrFormFieldGroupComponent, StlrLabelBlockComponent, StlrMultiSelectComponent, StlrOptionComponent, StlrPhoneComponent, StlrPhoneValidatorDirective, StlrRadioComponent, StlrRadioGroupComponent, StlrSelectComponent, StlrToggleComponent, StlrCodeEntryComponent], imports: [CommonModule, ReactiveFormsModule, StlrIconModule, StlrFlexLayoutModule], exports: [ReactiveFormsModule, Colorable, StlrAutoBubbleComponent, StlrAutoBubbleGroupComponent, StlrAutobubblerComponent, StlrBubblePrefixDirective, StlrBubblePopperDirective, StlrBubbleSuffixDirective, StlrAutocompleteComponent, StlrCheckboxComponent, StlrCountrySelectComponent, StlrRegionSelectComponent, StlrErrorDirective, StlrFormFieldComponent, StlrHintDirective, StlrLabelDirective, StlrPrefixDirective, StlrSuffixDirective, StlrInputComponent, StlrFormComponent, StlrFormFieldGroupComponent, StlrLabelBlockComponent, StlrMultiSelectComponent, StlrOptionComponent, StlrPhoneComponent, StlrPhoneValidatorDirective, StlrRadioComponent, StlrRadioGroupComponent, StlrSelectComponent, StlrToggleComponent, StlrCodeEntryComponent] }); })();

/** @deprecated Use ICountry. This will be removed in the next major release. */
class Country {
    constructor(country) {
        this.countryName = country.countryName;
        this.countryCode = country.countryCode;
        this.regionName = country.regionName;
        this.regions = [...country.regions];
        this.telephoneCodes = [...country.telephoneCodes];
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { AUTOBUBBLER_API, AUTOCOMPLETE_API, CHECKBOX_API, CODE_ENTRY_API, COUNTRY_SELECT_API, Colorable, Country, FORM_FIELD_API, INPUT_API, LEGACY_API, MULTISELECT_API, OPTION_API, PHONE_API, PhoneValidators, RADIO_GROUP_API, REGION_SELECT_API, RegionData, SELECT_API, STLR_ERROR, STLR_FORM_COMPONENT_BASE, STLR_FORM_FIELD, STLR_PREFIX, STLR_SUFFIX, StlrAutoBubbleComponent, StlrAutoBubbleGroupComponent, StlrAutobubblerComponent, StlrAutocompleteComponent, StlrBubblePopperDirective, StlrBubblePrefixDirective, StlrBubbleSuffixDirective, StlrCheckboxComponent, StlrCodeEntryComponent, StlrCountrySelectComponent, StlrErrorDirective, StlrFormComponent, StlrFormFieldComponent, StlrFormFieldGroupComponent, StlrFormModule, StlrHintDirective, StlrInputComponent, StlrLabelBlockComponent, StlrLabelDirective, StlrMultiSelectComponent, StlrOptionComponent, StlrPhoneComponent, StlrPhoneValidatorDirective, StlrPrefixDirective, StlrRadioComponent, StlrRadioGroupComponent, StlrRegionSelectComponent, StlrSelectComponent, StlrSuffixDirective, StlrToggleComponent, TOGGLE_API, forEachControlIn, stellarFormArray, stellarFormControl, stellarFormGroup };

